/**
 * Chinese (Simplified) dictionary — canonical source for the ops-console
 * key space.
 *
 * `en.ts` re-uses this dictionary's type (`typeof zhCN`) so every key
 * that exists here must exist there. Adding a new string: add the key
 * to this file first, then add the English translation. Removing a
 * string: delete from both files and also sweep call sites for the key.
 *
 * Conventions
 * - Keys are lowercase dot-paths: `feature.subfeature.detail`.
 * - Placeholders use `{name}` syntax; the `t()` formatter substitutes
 *   on render. Unknown placeholders render literally so missing params
 *   fail loud in QA rather than silently eat text.
 * - Punctuation: Chinese sentences end in `。`, lists use `、`; follow
 *   the surrounding copy for spacing. English sentences follow a normal
 *   period. Do not include trailing whitespace.
 * - Identifiers that surface verbatim in the UI (vendor ids, SQL table
 *   names, API path fragments, code-block literals) stay in the JSX
 *   rather than living in the dictionary — they are not translatable
 *   content and would drift if mirrored.
 * - Labels of CONTRACT vocabulary (metering variant fields, closed tier
 *   values, unit codes) are NOT authored here: their single source is
 *   `@nadoutong/contracts`' bilingual label constants (#1883), shared
 *   with the tenant catalog's billing document. The dictionary entries
 *   below reference `.zh` of those constants so the console cannot
 *   spell a second version of the vendor's billing words.
 */
import {
  METERING_VARIANT_FIELD_LABELS,
  UNIT_USAGE_UNIT_LABELS,
  VIDEO_INPUT_VARIANT_LABELS,
} from '@nadoutong/contracts';

export const zhCN = {
  // ── <head> metadata ─────────────────────────────────────────────
  // Synced to `document.title` and `<meta name="description">` at
  // runtime by `I18nProvider`. The static value in `index.html` is the
  // zh-CN first-paint fallback; an en-preferring visitor's provider
  // effect rewrites both on mount.
  'meta.title': '哪都通运维控制台',
  'meta.description':
    '哪都通运维控制台:上游管理、模型定价、礼品卡发放、账户与审计、收入与支付对账。',

  // ── Common / shared ─────────────────────────────────────────────
  'common.retry': '重试',
  'common.cancel': '取消',
  'common.close': '关闭',
  'common.confirm': '确认',
  'common.save': '保存',
  'common.saving': '保存中…',
  'common.delete': '删除',
  'common.deleting': '删除中…',
  'common.loading': '加载中…',
  'common.network_error': '网络错误',
  'common.previous': '上一页',
  'common.next': '下一页',
  'common.pagination.first': '第一页',
  'common.pagination.last': '最后一页',
  'common.pagination.skip_prev': '向前 5 页',
  'common.pagination.skip_next': '向后 5 页',
  'common.pagination.jump': '跳至页码',
  'common.pagination.jump_submit': '跳转',
  'common.pagination.page': '第 {page} 页',
  'common.never': '从未',
  'common.copied': '已复制到剪贴板',
  'common.click_to_copy': '点击复制',
  'common.copy_anywhere': '点击此区块任意位置复制',
  'common.refresh': '刷新',
  'common.yes': '是',
  'common.no': '否',

  // Locale-switcher native labels live in `types.ts` as
  // `LOCALE_NATIVE_LABELS` — they are UI chrome (the name of each
  // language in its own script) rather than translatable content.
  'i18n.switcher.aria_label': '语言',

  // ── Auth guard ──────────────────────────────────────────────────
  'auth.checking': '正在检查会话…',

  // ── Login ───────────────────────────────────────────────────────
  'login.brand': 'Ops Console',
  'login.subtitle': '运维登录',
  'login.email': '邮箱',
  'login.password': '密码',
  'login.submit': '登录',
  'login.submitting': '登录中…',
  'login.github': '使用 GitHub 登录',
  'login.feishu': '使用飞书登录',
  'login.error.empty_fields': '请填写邮箱和密码',
  'login.github_error.not_linked': '这个 GitHub 账户尚未绑定运维用户。',
  'login.github_error.disabled': '这个运维用户已被禁用。',
  'login.github_error.state': 'GitHub 登录已过期，请重试。',
  'login.github_error.generic': 'GitHub 登录失败，请重试或使用密码登录。',
  'login.feishu_error.not_linked': '这个飞书账户尚未绑定运维用户。',
  'login.feishu_error.disabled': '这个运维用户已被禁用。',
  'login.feishu_error.state': '飞书登录已过期，请重试。',
  'login.feishu_error.tenant': '这个飞书账户不属于允许登录的租户。',
  'login.feishu_error.generic': '飞书登录失败，请重试或使用密码登录。',

  // ── Shell chrome ────────────────────────────────────────────────
  'shell.brand.title': 'Ops Console',
  'shell.brand.subtitle': '哪都通 · α-7 工作站',
  'shell.nav.aria_label': '主导航',
  'shell.group.business': '业务大盘',
  'shell.group.customers': '客户管理',
  'shell.group.finance': '财务',
  'shell.group.system': '系统',
  'shell.group.supply': '上游供给',
  'shell.group.marketplace': '模型市场',
  'shell.group.governance': '治理',
  'shell.nav.business_overview': '驾驶舱',
  'shell.nav.customer_manager_workspace': '客户经理工作台',
  'shell.nav.customer_manager_request_logs': '组合请求日志',
  'shell.nav.customer_pool': '客户池',
  'shell.nav.crm_usage': '客户用量',
  'shell.nav.lead_pricing': '销售定价',
  'shell.nav.crm_commission': '佣金业绩',
  'shell.nav.crm_leaderboard': '销售榜',
  'shell.nav.invite_codes': '邀请码',
  'shell.nav.approval_queue': '审批队列',
  'shell.nav.approvals_mine': '我的审批申请',
  'shell.nav.business_metrics': '计量计价中心',
  'shell.nav.traffic_monitor': '流量监控',
  'shell.nav.business_risk': '风险',
  'shell.nav.request_logs': '请求日志',
  'shell.nav.support_requests': '租户请求查询',
  'shell.nav.support_tickets': '工单',
  'shell.nav.announcements': '租户公告',
  'shell.nav.upstreams': '上游',
  'shell.nav.tenant_routing': '租户路由',
  'shell.nav.guaranteed_service': '重点保障中心',
  'shell.nav.quotas': '配额中心',
  'shell.nav.service_levels': '服务等级',
  'shell.nav.egress_profiles': '出入栈配置',
  'shell.nav.egress_lines': '边缘网关',
  'shell.nav.firewall_rulesets': '防火墙规则集',
  'shell.nav.models': '模型',
  'shell.nav.gift_cards': '礼品卡',
  'shell.nav.billing_statements': '客户账单',
  'shell.nav.credit_settings': '授信默认值',
  'shell.nav.credit_overdue': '授信监控',
  'shell.nav.credit_grants': '历史授信',
  'shell.nav.accounts': '账户',
  'shell.nav.customer_ownership': '客户归属',
  'shell.nav.billing_anomalies': '账务异常',
  'shell.nav.balance_alerts': '余额告警',
  'shell.nav.codex_capacity_alerts': 'Codex 容量告警',
  'shell.nav.email_notices': '邮件通知',
  'shell.nav.receipts': '收款单',
  'shell.nav.commission_settlement': '佣金结算',
  'shell.nav.payments_orders': '支付订单',
  'shell.nav.payment_providers': '支付通道',
  'shell.nav.fx_policy': '汇率策略',
  'shell.nav.namespaces': '命名空间',
  'shell.nav.model_market': '模型市场',
  'shell.nav.model_plans': '模型方案',
  'shell.nav.sku_activation_readiness': 'SKU 上线就绪',
  'shell.nav.default_key_namespaces': '新密钥默认命名空间',
  'shell.nav.reports': '报表',
  'shell.nav.webhook_events': 'Webhook 事件',
  'shell.nav.vps': 'VPS 节点',
  'shell.nav.cron_runs': '定时任务运行',
  'shell.nav.router_client_identities': '客户端身份',
  'shell.nav.ops_users': '运维账户',
  'shell.nav.dr_check': '灾备演练',
  'shell.nav.health_perspective': '系统健康透视',
  'shell.nav.audit': '审计',
  'shell.nav.permissions': '权限矩阵',
  'shell.nav.error_policy': '错误映射',

  'permissions.kicker': '治理',
  'permissions.title': '权限矩阵',
  'permissions.subtitle': '只读查看运维控制台导航和 ops-api endpoint 的角色访问矩阵。',
  'permissions.action.ops_users': '运维账户',
  'permissions.generated': '生成时间',
  'permissions.generated.pending': '加载后显示',
  'permissions.loading': '正在加载权限…',
  'permissions.error': '加载权限矩阵失败。',
  'permissions.summary.roles': '角色',
  'permissions.summary.ui_routes': 'UI 路由',
  'permissions.summary.api_endpoints': 'API endpoints',
  'permissions.summary.admin_only_endpoints': '管理员专属 endpoints',
  'permissions.role_legend': '角色说明',
  'permissions.role.assignable': '可分配',
  'permissions.role.bootstrap': '引导角色',
  'permissions.role.inherits': '继承：',
  'permissions.role.inherits_none': '无',
  'permissions.filter.label': '筛选',
  'permissions.filter.placeholder': '路径、分类或操作',
  'permissions.mode.all': '全部',
  'permissions.mode.admin_only': '管理员专属',
  'permissions.mode.shared': '共享',
  'permissions.table.ui_routes': 'UI 路由',
  'permissions.table.api_endpoints': 'API endpoints',
  'permissions.table.rows': '{count} 行',
  'permissions.table.empty': '没有匹配当前筛选条件的权限项。',
  'permissions.column.method': '方法',
  'permissions.column.path': '路径',
  'permissions.column.category': '分类',
  'permissions.column.operation': '操作',
  'permissions.column.allowed_roles': '允许角色',
  'permissions.roles.all_authenticated': '所有已登录运维角色',
  'permissions.roles.bearer_token': '仅限 Bearer 令牌',
  'permissions.role.short.super': '超管',
  'permissions.role.short.admin': '管理',
  'permissions.role.short.support': '支持',
  'permissions.role.short.customer': '客户',
  'permissions.role.short.finance': '财务',
  'permissions.role.short.supply': '供给',
  'permissions.role.short.runtime': '运行',
  'permissions.role.label.super': '超级管理员',
  'permissions.role.label.admin': '管理员',
  'permissions.role.label.support': '支持',
  'permissions.role.label.customer': '客户经理',
  'permissions.role.label.lead': '销售主管',
  'permissions.role.label.finance': '财务经理',
  'permissions.role.label.supply': '供需经理',
  'permissions.role.label.runtime': '运行操作员',
  'permissions.role.note.super': '应急角色；不通过普通运维用户流程授予。',
  'permissions.role.note.admin': '通用运维管理角色；同时继承支持侧只读路径。',
  'permissions.role.note.support': '支持请求和有限用量排查路径。',
  'permissions.role.note.customer': '客户经理工作台、客户池和客户组合请求日志。',
  'permissions.role.note.finance':
    '收款单财务处理面（核验入账/驳回/作废，epic #1031）；其余财务 endpoint 仍由管理员控制。',
  'permissions.role.note.supply': '预留的供给范围角色；当前大多数供给 endpoint 仍由管理员控制。',
  'permissions.role.note.runtime': '可访问运行指挥工作台，但不获得完整管理员导航。',
  'shell.signed_in_fallback': '已登录',
  'shell.role.fallback': '待授权',
  'shell.logout': '退出',
  'shell.page.console_fallback': '运维控制台',
  'shell.sidebar.collapse': '收起侧栏',
  'shell.sidebar.expand': '展开侧栏',
  'shell.nav.open': '打开导航',
  'shell.nav.close': '关闭导航',
  'shell.cmdk.trigger': '搜索页面 / 账户 / 请求…',
  'shell.cmdk.placeholder': '输入页面名、账户邮箱或 req_ 请求 ID',
  'shell.cmdk.empty': '没有匹配的结果',
  'shell.cmdk.aria_label': '命令面板',
  'shell.cmdk.footer_hint': '↑↓ 选择 · Enter 打开 · Esc 关闭',
  'shell.cmdk.group.requests': '请求',
  'shell.cmdk.group.accounts': '账户',
  'shell.cmdk.group.recent': '最近访问',
  'shell.cmdk.open_request': '在请求日志中打开 {id}',
  'shell.risk.clear': '风险清零',
  'shell.risk.open': '风险 {count}',
  'shell.risk.open_aria': '打开风险队列',
  'shell.risk.balance_check_at': '余额来源核对：{at} 计算',
  'shell.risk.balance_check_never': '余额来源核对尚未计算，打开风险队列即计算',
  'shell.risk.balance_check_suffix': '核对 {at}',
  'shell.risk.unchecked': '余额核对未计算',
  'shell.risk.stale': '余额核对已过期',
  'shell.approvals.pending': '待审批 {count}',
  'shell.approvals.open_aria': '打开审批队列',
  'shell.account_section': '账号',
  'mobile.sort.label': '排序',
  'mobile.sort.aria_label': '选择排序字段',
  'mobile.sort.unsorted': '默认顺序',
  'mobile.sort.dir_asc': '升序',
  'mobile.sort.dir_desc': '降序',

  // ── Approval center (epic #1030 E1-S3, #1039) ───────────────────
  'approvals.title': '审批中心',
  'approvals.subtitle': '销售权限外操作的统一审批入口:待办队列与我的申请。',
  'approvals.tabs.aria_label': '审批中心视图',
  'approvals.tab.queue': '待办队列',
  'approvals.tab.mine': '我的申请',
  'approvals.kind.customer_transfer': '客户转移 / 释放',
  'approvals.kind.gift_over_quota': '礼品超额度',
  'approvals.kind.discount_below_sales_cost': '低于销售成本折扣',
  'approvals.kind.credit_application': '账期申请',
  'approvals.kind.temporary_credit_application': '临时授信',
  'approvals.status.pending': '待审批',
  'approvals.status.approved': '已通过',
  'approvals.status.rejected': '已拒绝',
  'approvals.status.cancelled': '已取消',
  'approvals.col.kind': '类型',
  'approvals.col.customer': '客户',
  'approvals.col.amount': '金额',
  'approvals.col.cycle': '账期',
  'approvals.col.target': '对象',
  'approvals.col.requester': '申请人',
  'approvals.col.age': '等待',
  'approvals.col.summary': '摘要',
  'approvals.col.actions': '操作',
  'approvals.col.status': '状态',
  'approvals.col.submitted_at': '提交时间',
  'approvals.col.timeline': '进度',
  'approvals.action.approve': '通过',
  'approvals.action.reject': '拒绝',
  'approvals.decide.approve_title': '通过这条审批申请',
  'approvals.decide.reject_title': '拒绝这条审批申请',
  'approvals.decide.note_label': '备注',
  'approvals.decide.note_required_hint': '拒绝时必须填写备注。',
  'approvals.toast.approved': '已通过该申请。',
  'approvals.toast.rejected': '已拒绝该申请。',
  'approvals.toast.stage_actor_conflict': '您已经批准过该申请的前序阶段，此阶段需由其他审批人决定。',
  'approvals.queue.decided_elsewhere': '已被其他审批人处理',
  'approvals.queue.own_request': '本人提交的申请——需由其他审批人处理',
  'approvals.queue.empty': '暂无待审批申请。',
  'approvals.mine.empty': '还没有提交过审批申请。',
  'approvals.mine.expand': '展开时间线',
  'approvals.mine.collapse': '收起时间线',
  // Credit-application approval detail (#1957).
  'approvals.credit.detail_toggle': '合同',
  'approvals.credit.open_customer_360': '打开客户 360',
  'approvals.credit.applied_rate': '锁定汇率',
  'approvals.credit.reason': '申请理由',
  'approvals.credit.reason_unset': '未填写',
  'approvals.credit.registered_at': '客户注册时间',
  'approvals.credit.payment_summary': '既往付款表现',
  'approvals.timeline.submitted': '已提交',
  'approvals.timeline.stage_cleared': '第 {stage} 级已通过',
  'approvals.timeline.qualified_stage_role': '按审批角色',
  'approvals.timeline.qualified_admin_bypass': '管理员越权签批',
  'approvals.stage.chip': '第 {current}/{total} 级',
  'approvals.load_error': '审批数据加载失败。',
  'approvals.pagination.aria_label': '审批列表分页',

  // ── Manual receipts (epic #1031 E2-R5, #1063) ───────────────────
  // Receipt console: finance verification queue + scoped list + detail
  // drawer + sales entry dialog. Money copy always names the receipt
  // currency; the FX snapshot section renders server-written facts.
  // Commission settlement (epic #1034 E5-M4, #1146)
  'commission_settlement.title': '佣金结算',
  'commission_settlement.subtitle': '财务确认（锁定）佣金单、追加锁后调整、登记线下打款标记。',
  'commission_settlement.load_error': '加载佣金单失败。',
  'commission_settlement.filter.status': '状态',
  'commission_settlement.filter.brand': '品牌',
  'commission_settlement.filter.period': '账期',
  'commission_settlement.filter.period_placeholder': 'YYYY-MM',
  'commission_settlement.status.all': '全部状态',
  'commission_settlement.status.draft': '草稿',
  'commission_settlement.status.confirmed': '已确认',
  'commission_settlement.brand.all': '全部品牌',
  'commission_settlement.col.owner': '归属销售',
  'commission_settlement.col.brand': '品牌',
  'commission_settlement.col.period': '账期',
  'commission_settlement.col.status': '状态',
  'commission_settlement.col.stored_total': '确认金额',
  'commission_settlement.col.adjustments': '调整合计',
  'commission_settlement.col.displayed_total': '实际金额',
  'commission_settlement.col.payout': '打款',
  'commission_settlement.col.actions': '操作',
  'commission_settlement.empty': '没有符合条件的佣金单。',
  'commission_settlement.action.detail': '详情',
  'commission_settlement.pagination.aria_label': '佣金单分页',
  'commission_settlement.payout.marked': '已打款',
  'commission_settlement.payout.pending': '待打款',
  'commission_settlement.drawer.title': '佣金单详情',
  'commission_settlement.drawer.owner': '归属销售',
  'commission_settlement.drawer.brand': '品牌',
  'commission_settlement.drawer.period': '账期',
  'commission_settlement.drawer.status': '状态',
  'commission_settlement.drawer.stored_total': '确认金额（锁定）',
  'commission_settlement.drawer.adjustments_total': '调整合计',
  'commission_settlement.drawer.displayed_total': '实际金额',
  'commission_settlement.drawer.payout_marker': '打款标记',
  'commission_settlement.drawer.payout_none': '未打款',
  'commission_settlement.drawer.negative_note': '实际金额为负：调整 / 退款回扣的合计已超过确认金额。',
  'commission_settlement.drawer.rows_title': '明细',
  'commission_settlement.rows.source_type': '类型',
  'commission_settlement.rows.reference': '来源单据',
  'commission_settlement.rows.net': '净额',
  'commission_settlement.rows.commission': '佣金',
  'commission_settlement.rows.reason': '原因',
  'commission_settlement.rows.empty': '暂无明细。',
  'commission_settlement.source_type.receipt': '收款单',
  'commission_settlement.source_type.payment_order': '在线订单',
  'commission_settlement.source_type.clawback': '退款回扣',
  'commission_settlement.source_type.adjustment': '调整',
  'commission_settlement.confirm.button': '确认锁定',
  'commission_settlement.confirm.title': '确认锁定佣金单？',
  'commission_settlement.confirm.body': '确认后佣金单将锁定且不再由重算改动，后续修正只能以调整行追加。',
  'commission_settlement.confirm.cta': '确认锁定',
  'commission_settlement.confirm.toast': '佣金单已确认锁定。',
  'commission_settlement.confirm.period_open':
    '当前周期尚未结束，暂不可锁定：提前锁定会冻结本月佣金，之后入账的回款、订单与退款冲回都将被静默丢弃。请待周期结束后再锁定。',
  'commission_settlement.adjust.title': '追加调整',
  'commission_settlement.adjust.amount_label': '金额（USD）',
  'commission_settlement.adjust.amount_hint': '有符号金额：正数上调、负数下调，非零。',
  'commission_settlement.adjust.reason_label': '原因',
  'commission_settlement.adjust.reason_placeholder': '说明本次调整的原因（必填）',
  'commission_settlement.adjust.submit': '追加调整',
  'commission_settlement.adjust.toast': '调整行已追加。',
  'commission_settlement.adjust.invalid_amount': '请输入非零的金额（最多 6 位小数）。',
  'commission_settlement.payout.title': '打款标记',
  'commission_settlement.payout.reference_label': '打款凭证',
  'commission_settlement.payout.reference_placeholder': '如银行流水号 / 备注',
  'commission_settlement.payout.submit': '登记打款',
  'commission_settlement.payout.toast': '打款标记已登记。',
  'commission_settlement.payout.done_note': '已登记打款标记（一次性，不可修改）。',
  'commission_settlement.payout.confirm_title': '确认登记打款标记？',
  'commission_settlement.payout.confirm_body': '打款标记为一次性登记，登记后无法通过界面修改或撤销，请确认凭证无误。',
  'commission_settlement.payout.confirm_cta': '确认登记',
  'receipts.title': '收款单',
  'receipts.subtitle.finance': '线下收款核销台:待核销队列、全部收款单与凭证附件。',
  'receipts.subtitle.sales': '我提交的线下收款单及其核销状态(按归属范围展示)。',
  'receipts.load_error': '收款单数据加载失败。',
  'receipts.pagination.aria_label': '收款单分页',
  'receipts.queue.title': '待核销队列',
  'receipts.queue.empty': '暂无待核销的收款单。',
  'receipts.queue.decided_elsewhere': '已被其他财务处理',
  'receipts.col.customer': '客户',
  'receipts.col.amount': '金额',
  'receipts.col.channel': '渠道',
  'receipts.col.fee': '手续费',
  'receipts.col.received_at': '到账时间',
  'receipts.col.entered_by': '录入人',
  'receipts.col.status': '状态',
  'receipts.col.submitted_at': '提交时间',
  'receipts.col.actions': '操作',
  'receipts.action.credit': '核销入账',
  'receipts.action.reject': '驳回',
  'receipts.action.void': '作废',
  'receipts.action.detail': '详情',
  'receipts.decide.credit_title': '核销并入账这笔收款',
  'receipts.decide.reject_title': '驳回这笔收款单',
  'receipts.decide.void_title': '作废这笔收款单',
  'receipts.decide.credit_hint':
    '入账金额始终等于核销金额:人民币按汇率策略折算,美元 1:1 —— 本操作不提交任何金额。',
  'receipts.decide.note_label': '备注',
  'receipts.decide.note_required_hint': '驳回或作废时必须填写备注。',
  'receipts.decide.error.not_implemented': '入账通道尚未在当前环境启用,请稍后再试。',
  'receipts.toast.credited': '已核销入账。',
  'receipts.toast.rejected': '已驳回该收款单。',
  'receipts.toast.voided': '已作废该收款单。',
  'receipts.list.title': '全部收款单',
  'receipts.list.empty': '没有符合筛选条件的收款单。',
  'receipts.filter.status': '状态筛选',
  'receipts.filter.status_all': '全部状态',
  'receipts.filter.brand': '品牌筛选',
  'receipts.filter.brand_all': '全部品牌',
  'receipts.filter.currency': '币种筛选',
  'receipts.filter.currency_all': '全部币种',
  'receipts.filter.channel': '渠道筛选',
  'receipts.filter.channel_all': '全部渠道',
  'receipts.status.submitted': '待核销',
  'receipts.status.credited': '已入账',
  'receipts.status.rejected': '已驳回',
  'receipts.status.voided': '已作废',
  'receipts.channel.bank': '银行转账',
  'receipts.channel.alipay': '支付宝',
  'receipts.channel.wechat': '微信',
  'receipts.channel.usdt': 'USDT',
  'receipts.channel.other': '其他',
  'receipts.customer_section.title': '收款单',
  'receipts.customer_section.empty': '该客户还没有收款单。',
  'receipts.entry.open': '记录收款',
  'receipts.detail.title': '收款单详情',
  'receipts.detail.error': '收款单详情加载失败。',
  'receipts.detail.amount': '金额',
  'receipts.detail.fee': '实际手续费',
  'receipts.detail.channel': '渠道',
  'receipts.detail.received_at': '到账时间',
  'receipts.detail.contract_ref': '合同编号',
  'receipts.detail.org': '客户组织',
  'receipts.detail.entered_by': '录入人',
  'receipts.detail.submitted_at': '提交时间',
  'receipts.detail.fx.title': '入账快照',
  'receipts.detail.fx.applied_rate': '适用汇率',
  'receipts.detail.fx.credited_usd': '入账金额 (USD)',
  'receipts.detail.fx.cny_gross': 'CNY 毛额',
  'receipts.detail.fx.cny_net': 'CNY 净额',
  'receipts.detail.fx.verified_by': '核销人',
  'receipts.detail.fx.owner_at_credit': '核销时客户经理',
  'receipts.detail.fx.credited_at': '入账时间',
  'receipts.detail.fx.balance_tx': '余额流水号',
  'receipts.detail.timeline.title': '状态时间线',
  'receipts.detail.timeline.submitted': '已提交',
  'receipts.detail.attachments.title': '凭证附件',
  'receipts.detail.attachments.upload': '上传凭证',
  'receipts.detail.attachments.download': '下载',
  'receipts.detail.attachments.empty': '暂无凭证附件。',
  'receipts.detail.attachments.cap_hint': '单个文件上限 {mb} MB。',
  'receipts.detail.attachments.toast.uploaded': '凭证已上传。',
  'receipts.submit.title': '记录线下收款',
  'receipts.submit.description':
    '录入实际收到的款项:金额、渠道与凭证。品牌与账户由系统按客户归属自动确定。',
  'receipts.submit.customer': '客户',
  'receipts.submit.amount_label': '到账金额',
  'receipts.submit.amount_hint': '按所选币种填写,最多两位小数。',
  'receipts.submit.currency_label': '币种',
  'receipts.submit.channel_label': '收款渠道',
  'receipts.submit.fee_label': '实际手续费',
  'receipts.submit.fee_hint': '选填,按收款币种的绝对金额,默认 0。',
  'receipts.submit.received_at_label': '到账时间',
  'receipts.submit.contract_ref_label': '合同编号(选填)',
  'receipts.submit.note_label': '备注(选填)',
  'receipts.submit.evidence_label': '凭证附件(选填)',
  'receipts.submit.evidence_hint': '转账回单等凭证,单个文件上限 {mb} MB。',
  'receipts.submit.confirm': '提交收款单',
  'receipts.submit.retry_upload': '重试上传凭证',
  'receipts.submit.upload_pending': '收款单已提交,但凭证上传未完成 —— 可重试上传或直接关闭。',
  'receipts.submit.toast.submitted': '收款单已提交,等待财务核销。',
  'receipts.submit.error.amount': '到账金额必须是大于 0 的数字,最多两位小数。',
  'receipts.submit.error.fee': '手续费必须是不小于 0 的数字,最多两位小数。',
  'receipts.submit.error.received_at': '请选择到账时间。',
  'receipts.submit.error.oversize': '附件超过 {mb} MB 上限,请压缩后重试。',
  'receipts.submit.error.upload_failed': '凭证上传失败:{detail}',
  'receipts.submit.error.evidence_reselect': '凭证文件已被清除,请重新选择后再重试上传。',
  'receipts.submit.company_missing_hint':
    '该客户尚未填写企业名称——请在客户 360 的联系方式卡片补全,便于财务核对。',
  'customer.company.missing': '(未填写)',
  'receipts.detail.company': '企业名称',
  'receipts.filter.company_placeholder': '搜索企业名称…',
  'receipts.filter.company_label': '按企业名称搜索',
  'receipts.filter.company_submit': '搜索',
  'receipts.filter.company_clear': '清除',
  'receipts.col.invoice': '发票',
  'receipts.filter.invoice': '发票状态筛选',
  'receipts.filter.invoice_all': '全部发票状态',
  'receipts.invoice.status.none': '无需发票',
  'receipts.invoice.status.open': '待开票',
  'receipts.invoice.status.issued': '已开票',
  'receipts.invoice.status.voided': '不开票',
  'receipts.invoice.mode.cn_tax': '国内税务发票',
  'receipts.invoice.mode.commercial': '形式发票',
  'receipts.invoice.title': '发票',
  'receipts.invoice.status_label': '发票状态',
  'receipts.invoice.mode_label': '开票模式',
  'receipts.invoice.tax_point_label': '票点',
  'receipts.invoice.title_label': '发票抬头',
  'receipts.invoice.tax_no_label': '税号',
  'receipts.invoice.decided_by': '财务经办人',
  'receipts.invoice.decided_at': '处理时间',
  'receipts.invoice.action.issue': '开票',
  'receipts.invoice.action.void': '作废发票',
  'receipts.invoice.issue_title': '确认开具发票?',
  'receipts.invoice.void_title': '确认不开这张发票?',
  'receipts.invoice.toast.issued': '发票已开具。',
  'receipts.invoice.toast.voided': '发票已作废。',
  'receipts.invoice.await_credit': '到账核实后才能处理发票',
  'receipts.submit.need_invoice_label': '客户需要发票',
  'receipts.submit.invoice_mode_hint.cn_tax':
    '国内税务发票——记录与客户确认的票点。票点在算佣金时扣公司净额,绝不减客户到账。',
  'receipts.submit.invoice_mode_hint.commercial': '该品牌开形式发票(commercial invoice),无票点。',
  'receipts.submit.tax_point_label': '票点(%)',
  'receipts.submit.tax_point_hint': '最多两位小数,上限 {cap}。',
  'receipts.submit.invoice_title_label': '发票抬头(可选)',
  'receipts.submit.tax_no_label': '税号(可选)',
  'receipts.submit.error.tax_point': '票点需为不小于 0 的百分数,最多两位小数。',
  'receipts.submit.error.tax_point_cap': '票点超过配置上限 {cap}。',
  'receipts.invoice_settings.title': '品牌开票设置',
  'receipts.invoice_settings.hint':
    '录入表单读取的品牌级开票配置:模式、默认票点与票点上限。保存后对新提交立即生效。',
  'receipts.invoice_settings.brand': '品牌',
  'receipts.invoice_settings.mode': '模式',
  'receipts.invoice_settings.default_tax_point': '默认票点(%)',
  'receipts.invoice_settings.max_tax_point': '票点上限(%)',
  'receipts.invoice_settings.error.max': 'cn-tax 模式需要大于 0% 的上限,最多两位小数。',
  'receipts.invoice_settings.error.default': '默认票点需可解析且不超过上限。',
  'receipts.invoice_settings.toast.saved': '{brand} 的开票设置已保存。',

  // ── Unified risk items (Epic #853 / #858) ──────────────────────
  // Dimension labels + per-dimension window semantics rendered by the
  // shared risk card on /business/risk, /business/overview, and the
  // /business/traffic risk queue.
  'risk.dimension.supply': '供给',
  'risk.dimension.finance': '财务',
  'risk.dimension.traffic': '流量',
  'risk.window.supply': '24 小时快照',
  'risk.window.finance': '未解决积压',
  'risk.window.traffic': '当前窗口',
  'risk.window.preset': '窗口 {preset}',
  'risk.action.view_requests': '查看请求',

  // ── Risk workbench (epic #997 / #1005) ─────────────────────────
  'risk_workbench.title': '风险工作台',
  'risk_workbench.subtitle': '按类型分组的风险队列，支持确认、解决，并直达处置页面。',
  'risk_workbench.updated': '更新于',
  'risk_workbench.loading': '正在加载风险队列…',
  'risk_workbench.error': '无法加载风险队列',
  'risk_workbench.empty': '当前没有待处理的风险。',
  'risk_workbench.empty_filtered': '没有符合筛选条件的风险。',
  'risk_workbench.summary.open': '待处理',
  'risk_workbench.summary.critical': '严重',
  'risk_workbench.summary.warning': '警告',
  'risk_workbench.summary.acked': '已确认',
  'risk_workbench.summary.resolved': '已解决',
  'risk_workbench.filter.severity': '严重程度',
  'risk_workbench.filter.dimension': '维度',
  'risk_workbench.filter.status': '状态',
  'risk_workbench.filter.all': '全部',
  'risk_workbench.severity.critical': '严重',
  'risk_workbench.severity.warning': '警告',
  'risk_workbench.severity.info': '信息',
  'risk_workbench.status.open': '待处理',
  'risk_workbench.status.acked': '已确认',
  'risk_workbench.status.resolved': '已解决',
  'risk_workbench.group.count': '{count} 项',
  'risk_workbench.group.expand': '展开分组',
  'risk_workbench.group.collapse': '收起分组',
  'risk_workbench.item.state_by': '{status} · 操作人 {actor}',
  'risk_workbench.action.ack': '确认',
  'risk_workbench.action.resolve': '解决',
  'risk_workbench.action.reopen': '重新打开',
  'risk_workbench.action.open_target': '前往处置',
  'risk_workbench.dialog.ack_title': '确认风险',
  'risk_workbench.dialog.resolve_title': '解决风险',
  'risk_workbench.dialog.reopen_title': '重新打开风险',
  'risk_workbench.dialog.note_label': '备注（可选）',
  'risk_workbench.dialog.note_placeholder': '记录处置说明或原因',
  'risk_workbench.dialog.submit': '提交',
  'risk_workbench.dialog.submitting': '提交中…',
  'risk_workbench.toast.acked': '风险已确认',
  'risk_workbench.toast.resolved': '风险已解决',
  'risk_workbench.toast.reopened': '风险已重新打开',

  // ── Authenticated landing ──────────────────────────────────────
  'ops_landing.status': '飞书登录已完成',
  'ops_landing.title': '账号待授权',
  'ops_landing.body': '当前账户尚未分配运维角色。请联系管理员在运维账户中设置角色。',
  'ops_landing.guidance':
    '在分配角色之前，控制台的所有工作区都无法访问。请联系运维管理员为你分配角色后重新登录——在此之前站内没有可用操作。',
  'ops_landing.logout': '退出登录',
  'ops_landing.account': '账户',
  'ops_landing.role': '角色',
  'ops_landing.role_pending': '待授权',

  // ── Idle authenticated home (router#2260) ──────────────────────
  'ops_home.title': '控制台',
  'ops_home.body': '从左侧选择要打开的工作区。本页不加载运营数据。',

  // ── Business dashboard ─────────────────────────────────────────
  'business.kicker': '经营驾驶舱',
  'business.supply.priced_skus': '已定价 SKU',
  'business.supply.unrouted_skus': '未路由 SKU',
  'business.supply.single_point_skus': '单点 SKU',
  'business.supply.low_funding': '低资金上游',
  'business.supply.unhealthy': '异常上游',
  'business.supply.sku_matrix': 'SKU x 上游覆盖矩阵',
  'business.supply.low_funding_title': '低资金 runway',
  'business.sku_econ.table_title': 'SKU 经济表',
  'business.sku_econ.legacy_bucket': '未归因历史用量',
  'business.sku_econ.legacy_hint': '切换前未记录 SKU 归因的历史用量',
  'business.sku_econ.col_margin': '毛利',
  'business.sku_econ.col_margin_pct': '毛利率',
  'business.sku_econ.col_revenue': '收入',
  // #1327 PR-9: the four-multiplier naming — this window's realized spend
  // is procurement cost, the same quantity the marketplace's procurement
  // multiplier prices. Label only; the aggregate is unchanged.
  'business.sku_econ.col_cost': '采购成本',
  'business.sku_econ.col_requests': '请求数',
  'business.sku_econ.col_share': '占比',
  'business.sku_econ.totals_label': '窗口合计',
  'business.sku_econ.runway_days': 'Runway(天)',
  'business.sku_econ.runway_pending': '待接入',
  'business.sku_econ.empty': '该窗口没有 SKU 用量。',
  'business.sku_econ.window_label': '窗口',
  'business.filters.window': '时间窗口',
  'business.filters.command_bar': '业务总览筛选命令栏',
  'business.filters.brand': '品牌',
  'business.window.today': '今日',
  'business.brand.all': '全部',
  'business.status.updated': '更新于',
  'business.status.covered': '已覆盖',
  'business.status.single_point': '单点',
  'business.status.unrouted': '未路由',
  'business.metric.active_customers': '活跃客户',
  'business.metric.new_accounts': '新增账户',
  'business.metric.verified_accounts': '已验证账户',
  'business.metric.keyed_accounts': '已建 key 账户',
  'business.metric.paying_customers': '付费客户',
  'business.table.sku': 'SKU',
  'business.table.coverage': '覆盖',
  'business.table.status': '状态',
  'business.loading.title': '加载业务数据',
  'business.loading.body': '正在从 gateway 和 platform 聚合指标。',
  'business.error.prefix': '无法加载业务大盘:',
  'business.empty.title': '暂无数据',
  'business.empty.body': '当前窗口没有业务数据。',
  'business.empty.no_low_funding': '暂无低资金上游。',
  'business.empty.no_risks': '当前没有开放风险。',
  'business.section.unavailable': '数据暂不可用',

  // ── Morning cockpit (#1004) ─────────────────────────────────────
  'cockpit.title': '晨间驾驶舱',
  'cockpit.subtitle': '五分钟看完昨天赚没赚钱、现金进出、上游预付还能撑几天、该催谁充值、当前火警和增长漏斗。',
  'cockpit.envelope.window': '统计窗口',
  'cockpit.envelope.compare': '对比窗口',
  'cockpit.envelope.generated': '生成于',
  'cockpit.envelope.stale': '数据可能已过期',
  'cockpit.window.today': '今日',
  'cockpit.window.yesterday': '昨日',
  'cockpit.window.current': '本窗口',
  'cockpit.window.previous': '上一窗口',
  'cockpit.margin.title': '真实毛利',
  'cockpit.margin.link': '打开真实盈亏',
  'cockpit.margin.margin': '利润率',
  'cockpit.margin.revenue': '现金收入',
  'cockpit.margin.cost': '上游成本',
  'cockpit.margin.gifted': '赠送成本',
  'cockpit.quality.balanced': '账实一致',
  'cockpit.quality.partial': '归因不全',
  'cockpit.quality.unreliable': '数据不可靠',
  'cockpit.cash.title': '现金进出',
  'cockpit.cash.net': '净现金流',
  'cockpit.cash.in': '用户充值',
  'cockpit.cash.fees': '通道手续费',
  'cockpit.cash.out': '上游付款',
  'cockpit.upstream.title': '上游预付 runway',
  'cockpit.upstream.link': '打开上游列表',
  'cockpit.upstream.total': '预付余额合计',
  'cockpit.upstream.stranded': '另有已耗尽批次内锁死、无法再动用的额度',
  'cockpit.upstream.empty': '暂无有预付余额的上游。',
  'cockpit.chase.title': '余额告急客户',
  'cockpit.chase.link': '打开客户列表',
  'cockpit.chase.low_balance_link': '查看全部低余额账户',
  'cockpit.chase.hint': '按剩余天数升序,先催最快耗尽的客户。',
  'cockpit.chase.empty': '窗口内没有需要跟进充值的客户。',
  'cockpit.runway.burn_window': '按近 {days} 天消耗速率',
  'cockpit.runway.days': '{days} 天',
  'cockpit.runway.no_burn': '窗口内无消耗',
  'cockpit.table.upstream': '上游',
  'cockpit.table.customer': '客户',
  'cockpit.table.days_left': '剩余天数',
  'cockpit.table.remaining': '剩余额度',
  'cockpit.table.burn_per_day': '日均消耗',
  'cockpit.fires.title': '当前火警',
  'cockpit.fires.link': '打开风险工作台',
  'cockpit.fires.showing': '展示最严重的 {shown}/{total} 条',
  'cockpit.funnel.title': '增长漏斗',
  'cockpit.funnel.link': '打开客户列表',
  'cockpit.funnel.cohort': '注册队列(窗口内新注册)',
  'cockpit.funnel.activity': '窗口活跃(全部账户)',
  'cockpit.funnel.conversion': '{pct} 转化',
  'cockpit.funnel.caveat': '注册队列按窗口内新注册账户统计;活跃/付费按全部账户的窗口行为统计,两组人群不构成同一条漏斗。',

  // ── Finance P&L ─────────────────────────────────────────────────
  'finance.pnl.window.this_month': '本月至今',
  'finance.pnl.window.last_month': '上月',
  'finance.pnl.window.all': '全部时间',
  'finance.pnl.currency_note': '页面主口径统一为人民币；美元信用余额按当前人民币展示汇率折算。',
  'finance.pnl.materialized_through': '用量汇总至 {date}',
  'finance.pnl.usage_fresh_as_of': '近期用量算至 {time}',
  'finance.pnl.error': '无法加载真实盈亏',
  'finance.pnl.retry': '重试',
  'finance.pnl.metric.operating_profit': '净经营盈亏',
  'finance.pnl.metric.cash_gross_profit': '现金业务毛利',
  'finance.pnl.metric.gifted_cost': '赠送信用成本',
  'finance.pnl.metric.net_cashflow': '净现金流',
  'finance.pnl.metric.margin': '利润率 {pct}',
  'finance.pnl.metric.cash_revenue': '现金收入 {amount}',
  'finance.pnl.metric.gifted_usage': '赠送消耗 {amount}',
  'finance.pnl.metric.cash_in': '现金流入 {amount}',
  'finance.pnl.flow.title': '资金流',
  'finance.pnl.flow.user_cash': '用户现金',
  'finance.pnl.flow.cash_in': '现金入账',
  'finance.pnl.flow.payment_fee': '支付手续费',
  'finance.pnl.flow.cash_liability': '现金余额负债',
  'finance.pnl.flow.usage': '使用确认',
  'finance.pnl.flow.cash_revenue': '现金余额收入',
  'finance.pnl.flow.cash_cost': '现金余额上游成本',
  'finance.pnl.flow.gift_cost': '赠送/信用上游成本',
  'finance.pnl.flow.upstream': '上游',
  'finance.pnl.flow.upstream_cash_out': '上游充值流出',
  'finance.pnl.flow.upstream_prepaid': '上游预付剩余',
  'finance.pnl.flow.gift_liability': '赠送余额负债',
  'finance.pnl.quality.title': '归因质量',
  'finance.pnl.quality.balance_coverage': '余额来源覆盖',
  'finance.pnl.quality.cost_coverage': '成本覆盖',
  'finance.pnl.quality.unknown_source': '未知来源',
  'finance.pnl.quality.missing_cost': '缺失成本',
  'finance.pnl.quality.unallocated_debits': '未归因扣费',
  'finance.pnl.quality.mismatch_accounts': '不一致账户',
  'finance.pnl.quality.status.balanced': '已平账',
  'finance.pnl.quality.status.partial': '部分归因',
  'finance.pnl.quality.status.unreliable': '不可靠',
  'finance.pnl.drill.payments': '支付订单',
  'finance.pnl.drill.gift_cards': '礼品卡',
  'finance.pnl.drill.upstreams': '上游资金',
  'finance.pnl.drill.reports': '月度报表',

  // ── Metrics hub (epic #1643) ────────────────────────────────────
  'metrics.title': '计量计价中心',
  'metrics.subtitle': '营收、上游成本与利润的统一日报视图，按模型 / 租户 / SKU 切片。',
  'metrics.window.aria': '统计窗口',
  'metrics.window.today': '今日',
  'metrics.window.7d': '最近 7 天',
  'metrics.window.30d': '最近 30 天',
  'metrics.window.custom': '自定义',
  'metrics.custom.from': '开始',
  'metrics.custom.to': '结束',
  'metrics.brand': '品牌',
  'metrics.brand.all': '全部',
  'metrics.tabs.aria': '计量计价页签',
  'metrics.tab.overview': '总览',
  'metrics.tab.models': '模型',
  'metrics.tab.platform_sku': '平台 SKU',
  'metrics.tab.upstream': '上游 SKU',
  'metrics.tab.tenants': '租户',
  'metrics.tab.usage': '用量',
  'metrics.tab.pnl': '盈亏',
  'metrics.tab.sku': 'SKU',
  'metrics.trend.days_aria': '趋势天数',
  'metrics.trend.days': '{days} 天',
  'metrics.chart.view_aria': '图表视图切换',
  'metrics.chart.view.chart': '图表',
  'metrics.chart.view.table': '表格',
  'metrics.chart.rollup_aria': '汇总粒度',
  'metrics.chart.rollup.day': '日',
  'metrics.chart.rollup.week': '周',
  'metrics.chart.rollup.month': '月',
  'metrics.chart.rollup.note': '周 = ISO 自然周（周一起始，窗口边缘的周可能不足 7 天）；月 = UTC+8 自然月。',
  'metrics.chart.col.bucket': '时间段',
  'metrics.chart.legend.show_all': '全选',
  'metrics.chart.legend.hide_all': '清空',
  'metrics.chart.all_hidden': '所有序列均已隐藏，点击图例项恢复显示。',
  'metrics.chart.tooltip_more': '…其余 {count} 项',
  'metrics.overview.requests': '请求数',
  'metrics.overview.requests.sub': '成功 {success} · 失败 {error}',
  'metrics.overview.tokens': 'Tokens 合计',
  'metrics.overview.tokens.sub': '输入 {input} · 输出 {output} · 缓存读 {cacheRead}',
  'metrics.overview.revenue': '营收（CNY）',
  'metrics.overview.revenue.sub': '含赠金消耗 {amount}',
  'metrics.overview.upstream_cost': '上游成本',
  'metrics.overview.upstream_cost.sub': '采购快照口径',
  'metrics.overview.l2_profit': 'L2 利润',
  'metrics.overview.l2_profit.sub': '毛利率 {pct}',
  'metrics.overview.operating_profit': '真实经营利润',
  'metrics.overview.operating_profit.sub': '经营口径 · 含赠金成本',
  'metrics.overview.quality.balanced': '已平账',
  'metrics.overview.quality.partial': '部分归因',
  'metrics.overview.quality.unreliable': '不可靠',
  'metrics.overview.usage_error': '用量数据暂不可用，其余指标不受影响。',
  'metrics.overview.revenue_error': '营收数据暂不可用，其余指标不受影响。',
  'metrics.overview.pnl_error': '经营利润数据暂不可用，其余指标不受影响。',
  'metrics.overview.trend.title': '每日营收与利润趋势',
  'metrics.overview.trend.revenue': '营收',
  'metrics.overview.trend.profit': 'L2 利润',
  'metrics.overview.trend.empty': '窗口内暂无每日营收数据。',
  'metrics.overview.trend.error': '无法加载每日趋势。',
  'metrics.overview.trend.rate_unavailable': '营收序列因汇率不可用未展示',
  'metrics.models.trend.title': 'Top 模型 · 每日收费趋势',
  'metrics.models.trend.truncated': '仅绘制收费 Top {shown} · 共 {total} 个模型',
  'metrics.models.trend.empty': '窗口内暂无模型收费数据。',
  'metrics.models.trend.error': '无法加载模型趋势。',
  'metrics.models.trend.rate_unavailable': '汇率不可用，暂无法按人民币折算模型趋势。',
  'metrics.models.cost_missing': '趋势窗口内 {count} 条用量成本未知，成本与利润或被低估',
  'metrics.models.table.title': '模型汇总',
  'metrics.models.table.sub': '窗口内合计 · 按 L2 利润降序',
  'metrics.models.table.cost_note': '上游成本按 revenue ledger 合计；缺失采购快照的行未计入，成本与利润可能被低估。',
  'metrics.models.table.empty': '窗口内暂无模型营收。',
  'metrics.models.table.error': '无法加载模型汇总。',
  'metrics.models.col.model': '模型',
  'metrics.models.col.calls': '调用数',
  'metrics.models.col.charged': '收费',
  'metrics.models.col.cost': '上游成本',
  'metrics.models.col.profit': 'L2 利润',
  'metrics.models.col.margin': '毛利率',
  'metrics.models.table.col.total': '合计',
  'metrics.group.kpi.calls': '总调用',
  'metrics.group.kpi.charged': '总收费（CNY）',
  'metrics.group.kpi.cost': '总上游成本',
  'metrics.group.kpi.profit': '总 L2 利润',
  'metrics.group.cost_missing': '窗口内 {count} 条用量成本未知，成本与利润或被低估',
  'metrics.group.hourly_note': '当前窗口不超过 24 小时，趋势按小时（UTC+8）聚合。',
  'metrics.group.clamp_note': '自定义窗口超过 90 天，本页仅统计截止时间前最近 90 天。',
  'metrics.group.rate_unavailable': '汇率不可用，收费金额暂无法按人民币折算。',
  'metrics.group.error': '无法加载趋势数据。',
  'metrics.group.empty': '窗口内暂无数据。',
  'metrics.group.metric_aria': '指标切换',
  'metrics.group.metric.charged': '收费（CNY）',
  'metrics.group.metric.calls': '调用数',
  'metrics.group.metric.profit': 'L2 利润（CNY）',
  'metrics.group.default_visible_note': '默认显示收费前 {count}，其余可在图例中点选。',
  'metrics.group.col.name': '名称',
  'metrics.group.col.calls': '调用数',
  'metrics.group.col.charged': '收费',
  'metrics.group.col.cost': '上游成本',
  'metrics.group.col.profit': 'L2 利润',
  'metrics.group.col.margin': '毛利率',
  'metrics.group.col.cost_missing': '成本未知',
  'metrics.platform_sku.trends.title': '平台 SKU 趋势对比',
  'metrics.platform_sku.table.title': '平台 SKU 汇总',
  'metrics.platform_sku.table.sub': '由趋势序列合计推导 · 按收费降序',
  'metrics.platform_sku.table.note':
    '收费与毛利率按当前展示汇率折算；上游成本与 L2 利润为账本人民币原值。缺失采购快照的行未计入成本，成本与利润可能被低估。',
  'metrics.upstream.trends.title': '上游 SKU 趋势对比',
  'metrics.upstream.table.title': '上游 SKU 汇总',
  'metrics.upstream.table.sub': '窗口内合计 · 账本逐行汇率折算',
  'metrics.upstream.table.error': '无法加载上游汇总。',
  'metrics.throughput.sku.title': '平台 SKU 吞吐（RPM / TPM）',
  'metrics.throughput.upstream.title': '上游 SKU 吞吐（RPM / TPM）',
  'metrics.throughput.metric_aria': '吞吐指标切换',
  'metrics.throughput.metric.rpm': 'RPM',
  'metrics.throughput.metric.tpm': 'TPM',
  'metrics.throughput.note':
    '数值为桶内平均每分钟：RPM = 请求数 ÷ 桶时长（分钟），TPM = token 数 ÷ 桶时长（分钟），TPM 含输入、输出与缓存读写 token；首尾桶按与查询窗口实际相交的时长折算。吞吐统计所有请求（含失败与未计价），与上方调用数（钱口径）口径不同。',
  'metrics.throughput.default_visible_note': '默认显示请求量前 {count}，其余可在图例中点选。',
  'metrics.throughput.error': '无法加载吞吐数据。',
  'metrics.throughput.window_unavailable':
    '该窗口早于保留的评级历史，无法得到完整曲线。请缩短窗口后重试。',
  'metrics.tenants.title': 'Top 租户',
  'metrics.tenants.sort.aria': '租户排序',
  'metrics.tenants.sort.spend': '消费榜',
  'metrics.tenants.sort.profit': '利润榜',
  'metrics.tenants.col.account': '租户',
  'metrics.tenants.col.requests': '请求数',
  'metrics.tenants.col.charged': '收费',
  'metrics.tenants.col.cost': '上游成本',
  'metrics.tenants.col.profit': 'L2 利润',
  'metrics.tenants.col.margin': '毛利率',
  'metrics.tenants.view_requests': '查看请求 →',
  'metrics.tenants.empty': '窗口内暂无租户营收。',
  'metrics.tenants.error': '无法加载租户榜单。',
  'metrics.usage.loading': '正在加载用量…',
  'metrics.usage.error': '无法加载用量：{message}',
  'metrics.usage.empty': '当前窗口没有用量。',
  'metrics.usage.metric.requests': '请求数',
  'metrics.usage.metric.requests.sub': '成功 {success} · 失败 {error}',
  'metrics.usage.metric.tokens': 'Tokens',
  'metrics.usage.metric.tokens.sub': '输入 {input} · 输出 {output}',
  'metrics.usage.metric.cache': '缓存 Tokens',
  'metrics.usage.metric.cache.sub': '命中 {read} · 写入 {write}',
  'metrics.usage.metric.charged': '用户收费',
  'metrics.usage.metric.charged.sub': 'Gateway {amount}',
  'metrics.usage.metric.upstream_cost': '上游成本',
  'metrics.usage.metric.upstream_cost.sub': 'revenue ledger',
  'metrics.usage.metric.l2_profit': 'L2 利润',
  'metrics.usage.metric.l2_profit.sub': '毛利率 {pct}',
  'metrics.usage.metric.gross_margin': '总毛利',
  'metrics.usage.metric.gross_margin.sub': 'L1 + L2',
  'metrics.usage.metric.window': '窗口',
  'metrics.usage.metric.window.sub': 'local time',
  'metrics.usage.table.tenants': 'Top 租户',
  'metrics.usage.table.models': '按模型',
  'metrics.usage.table.brands': '按品牌',
  'metrics.usage.table.upstreams': '按上游',
  'metrics.usage.table.keys': '按密钥',
  'metrics.usage.empty.tenants': '暂无租户用量',
  'metrics.usage.empty.models': '暂无模型用量',
  'metrics.usage.empty.brands': '暂无品牌用量',
  'metrics.usage.empty.upstreams': '暂无上游用量',
  'metrics.usage.empty.keys': '暂无密钥用量',
  'metrics.usage.col.object': '对象',
  'metrics.usage.col.requests': '请求',
  'metrics.usage.col.io': '输入/输出',
  'metrics.usage.col.charged': '用户收费',
  'metrics.usage.col.l2_profit': 'L2 利润',
  'metrics.usage.col.gateway_cost': 'Gateway 成本',
  'metrics.usage.col.errors': '错误',
  'metrics.usage.col.cache_rw': '缓存读/写',
  'metrics.usage.view_requests': '查看请求 →',
  'metrics.usage.unnamed_key': '未命名密钥',
  'metrics.usage.unknown_channel': '未知渠道',

  // ── Not found ───────────────────────────────────────────────────
  'notfound.title': '页面未找到',
  'notfound.body_prefix': '你访问的路由暂未定义。',
  'notfound.back': '返回仪表盘',

  // ── Tenant Routing ─────────────────────────────────────────────
  'tenant_routing.title': '系统路由',
  'tenant_routing.subtitle': '配置库 · SKU 目标池 · 执行策略',
  'tenant_routing.loading': '正在加载租户路由…',
  'tenant_routing.action.route_check': '路由检查',
  'tenant_routing.action.edit_default': '编辑',
  'tenant_routing.status.healthy': '健康',
  'tenant_routing.status.attention': '需要关注',
  'tenant_routing.status.checking': '路由检查中…',
  'tenant_routing.status.enabled': '启用',
  'tenant_routing.status.disabled': '停用',
  'tenant_routing.status.target_enabled': '目标启用',
  'tenant_routing.status.target_disabled': '目标停用',
  // Config library list + dialogs
  'tenant_routing.library.open': '打开',
  'tenant_routing.configs.title': '路由配置',
  'tenant_routing.configs.create': '新建配置',
  'tenant_routing.configs.default_new_name': '从全局默认复制的路由配置',
  'tenant_routing.configs.global_default_badge': '全局默认',
  'tenant_routing.configs.empty': '暂无可用路由配置。',
  'tenant_routing.configs.pool_targets': '目标池目标',
  'tenant_routing.configs.name': '配置',
  'tenant_routing.configs.type': '类型',
  'tenant_routing.configs.users': '使用客户数',
  'tenant_routing.configs.assigned_customers': '已分配客户',
  'tenant_routing.configs.no_assignments': '这个配置暂未分配给客户。',
  'tenant_routing.configs.assign': '分配',
  'tenant_routing.configs.make_global': '设为全局',
  'tenant_routing.configs.rename': '重命名',
  'tenant_routing.configs.make_global_title': '设为全局默认',
  'tenant_routing.configs.make_global_target': '将全局默认切换到',
  'tenant_routing.configs.make_global_save': '切换全局默认',
  'tenant_routing.configs.switch_reason': '本次切换原因',
  'tenant_routing.configs.switch_reason_hint': '会记录到切换事件上，下次故障复盘时会被读到。必填。',
  'tenant_routing.configs.switch_reason_placeholder': '例如：主上游持续 5xx，切到备用配置',
  'tenant_routing.configs.rename_title': '重命名路由配置',
  'tenant_routing.configs.rename_save': '保存名称',
  'tenant_routing.configs.create_title': '创建路由配置',
  'tenant_routing.configs.description': '描述',
  'tenant_routing.configs.assign_org_optional': '同时分配给 Org',
  'tenant_routing.configs.create_from_global_default': '从全局默认创建',
  'tenant_routing.configs.clone': '克隆',
  'tenant_routing.configs.clone_title': '克隆路由配置',
  'tenant_routing.configs.clone_source': '克隆来源',
  'tenant_routing.configs.clone_save': '克隆配置',
  'tenant_routing.configs.clone_default_name': '{name}（副本）',
  'tenant_routing.configs.assign_title': '分配路由配置',
  'tenant_routing.configs.selected_config': '路由配置',
  'tenant_routing.configs.assign_org': 'Org ID',
  'tenant_routing.configs.assign_replaces_stack':
    '这会替换该客户现有的全部专属路由绑定。',
  'tenant_routing.configs.assignment_position': '{position}',
  'tenant_routing.configs.assign_save': '分配配置',
  'tenant_routing.configs.type.system': '系统',
  'tenant_routing.configs.type.customer': '客户',
  'tenant_routing.configs.type.experiment': '实验',
  'tenant_routing.configs.status.draft': '草稿',
  'tenant_routing.configs.status.active': '启用',
  'tenant_routing.configs.status.archived': '归档',
  'tenant_routing.configs.toast.created': '路由配置已创建',
  'tenant_routing.configs.toast.cloned': '路由配置已克隆',
  'tenant_routing.configs.toast.cloned_incompatible_pins':
    '已复制，但其中 {count} 个固定目标与出口配置不再匹配，路由时会被跳过，请在配置中检查。',
  'tenant_routing.configs.toast.global_set': '全局路由配置已更新',
  'tenant_routing.configs.toast.assigned': '路由配置已分配',
  'tenant_routing.configs.toast.renamed': '路由配置已重命名',
  'tenant_routing.configs.error.name_required': '请输入路由配置名称。',
  'tenant_routing.configs.error.reason_required': '请填写本次切换的原因，它会记录到切换事件上。',
  'tenant_routing.configs.error.reason_too_long':
    '原因超过 1000 字符，请精简——切换事件只存摘要，不存完整的故障复盘。',
  'tenant_routing.configs.error.switch_head_moved':
    '全局路由配置在此期间已被改动，这次回滚会多退一步。请重新加载切换记录后再决定。',
  'tenant_routing.configs.error.preflight_stale':
    '预检结果与当前配置或当前全局默认已经不一致——有人改动了其中之一。请重新跑一次预检并重新确认后再切换。',
  'tenant_routing.configs.error.assign_required': '请选择路由配置并填写 Org ID。',

  // ── 容灾方案、预检报告与切换记录（#1421 P3b） ─────────────────────
  'routing_dr.plans.title': '容灾方案',
  'routing_dr.plans.mark': '标记容灾方案',
  'routing_dr.plans.mark_title': '把路由配置标记为容灾方案',
  'routing_dr.plans.edit_title': '编辑容灾方案',
  'routing_dr.plans.save_mark': '标记为容灾方案',
  'routing_dr.plans.save_edit': '保存方案',
  'routing_dr.plans.loading': '加载容灾方案中…',
  'routing_dr.plans.empty_title': '还没有标记任何容灾方案。',
  'routing_dr.plans.empty_hint': '把预备切换的路由配置标记出来，故障时不用现翻配置列表，也能一眼看到上次验证是什么时候。',
  'routing_dr.plans.column.plan': '方案',
  'routing_dr.plans.column.rank': '顺位',
  'routing_dr.plans.column.config_status': '配置状态',
  'routing_dr.plans.column.last_verified': '上次验证',
  'routing_dr.plans.column.result': '验证结果',
  'routing_dr.plans.column.config': '路由配置',
  'routing_dr.plans.config_hint': '标记只加一条记录，不会改动这个路由配置本身。',
  'routing_dr.plans.label': '方案名称',
  'routing_dr.plans.label_placeholder': '例如：主上游故障时的备用方案',
  'routing_dr.plans.rank': '顺位',
  'routing_dr.plans.rank_hint': '数字越小越先用，留空按 0 处理。',
  'routing_dr.plans.notes': '备注',
  'routing_dr.plans.notes_hint': '写清楚什么情况下切到这个方案，故障时读的就是这一句。',
  'routing_dr.plans.never_verified': '从未验证',
  'routing_dr.plans.unreadable_verified_at': '验证时间读不出来',
  'routing_dr.plans.verdict_undated': '有结果，但读不出是什么时候的',
  'routing_dr.plans.verdict_counts': '无路由 {uncovered} · 不可用目标 {unhealthy} · 触达不到 {unreachable}',
  'routing_dr.plans.unmark_title': '取消容灾方案标记',
  'routing_dr.plans.unmark_body': '取消「{label}」的标记后，它从容灾方案列表消失，路由配置本身、目标和分配都保持原样。',
  'routing_dr.plans.action.preflight': '预检',
  'routing_dr.plans.action.edit': '编辑',
  'routing_dr.plans.action.unmark': '取消标记',
  'routing_dr.plans.toast.marked': '已标记为容灾方案',
  'routing_dr.plans.toast.updated': '容灾方案已更新',
  'routing_dr.plans.toast.unmarked': '已取消容灾方案标记',
  'routing_dr.plans.error.config_required': '请选择要标记的路由配置。',
  'routing_dr.plans.error.label_required': '请填写方案名称，故障时靠它认人。',
  'routing_dr.plans.error.rank_invalid': '顺位必须是不小于 0 的整数。',
  'routing_dr.age.days_one': '{count} 天前',
  'routing_dr.age.days_many': '{count} 天前',
  'routing_dr.age.hours_one': '{count} 小时前',
  'routing_dr.age.hours_many': '{count} 小时前',
  'routing_dr.age.just_now': '刚刚',
  'routing_dr.verdict.clean': '通过',
  'routing_dr.verdict.caution': '有注意项',
  'routing_dr.verdict.blocking': '有阻断项',

  'routing_dr.preflight.title': '切换预检',
  'routing_dr.preflight.window': '流量窗口',
  'routing_dr.preflight.window_hours': '最近 {count} 小时',
  'routing_dr.preflight.run': '运行预检',
  'routing_dr.preflight.verify': '验证并记录到方案',
  'routing_dr.preflight.read_only_hint': '预检只读，不改任何配置，可以反复跑；「验证并记录」会把这次结果写到方案上，刷新上次验证时间。',
  'routing_dr.preflight.running': '预检运行中…',
  'routing_dr.preflight.empty_title': '还没有预检结果。',
  'routing_dr.preflight.empty_hint': '运行一次预检，看看现在切过去会变成什么样。',
  'routing_dr.preflight.toast.verified': '预检结果已记录到方案',
  'routing_dr.preflight.generated_at': '生成于 {at}',
  'routing_dr.preflight.window_summary': '按最近 {hours} 小时的流量评估了 {skus} 个 SKU。',
  'routing_dr.preflight.window_truncated': 'SKU 数超过评估上限，已截断。',
  'routing_dr.preflight.candidate_not_active': '这个配置不是「启用」状态，现在切不过去。',
  'routing_dr.preflight.show_all': '展开其余 {count} 行',
  'routing_dr.preflight.section_capped': '仅返回 {shown} 条，实际共 {total} 条（已达上限截断）',
  'routing_dr.preflight.all_skus': '全部 SKU',
  'routing_dr.preflight.unpinned': '未固定能力',
  'routing_dr.preflight.skus_outside_window': '这个配置还绑了 {count} 个 SKU 在窗口内没有流量，本次没被评估到。',
  'routing_dr.preflight.headline.uncovered': '有 SKU 完全没有路由',
  'routing_dr.preflight.headline.unhealthy': '有目标不可用',
  'routing_dr.preflight.headline.unreachable': '有客户切不过去',
  'routing_dr.preflight.headline.changed': '路由会发生变化',
  'routing_dr.preflight.headline.clean': '没有发现问题',
  'routing_dr.preflight.stat.uncovered': '无路由',
  'routing_dr.preflight.stat.fall_through': '回落下层',
  'routing_dr.preflight.stat.covered': '本配置覆盖',
  'routing_dr.preflight.stat.unhealthy_targets': '不可用目标',
  'routing_dr.preflight.stat.degraded_targets': '降级目标',
  'routing_dr.preflight.stat.changed_skus': '路由变化',
  'routing_dr.preflight.stat.unreachable_orgs': '触达不到的客户',
  'routing_dr.preflight.section.unreachable_orgs': '切换触达不到的客户',
  'routing_dr.preflight.section.unreachable_orgs_hint': '这些客户走的是更靠前的路由层，全局切换对他们不生效。',
  'routing_dr.preflight.section.unreachable_orgs_none': '所有有流量的客户都会跟着这次全局切换。',
  'routing_dr.preflight.section.coverage': '本配置未覆盖的 SKU',
  'routing_dr.preflight.section.coverage_hint': '另有 {covered} 个 SKU 由本配置直接供给，未逐行列出。',
  'routing_dr.preflight.section.coverage_none': '窗口内每个 SKU 都由本配置直接供给。',
  'routing_dr.preflight.section.health': '有问题的目标',
  'routing_dr.preflight.section.health_hint': '阻断项在前：阻断的目标选择器根本不会尝试，降级的仍会尝试。',
  'routing_dr.preflight.section.health_none': '本配置引用的目标都没有发现问题。',
  'routing_dr.preflight.section.diff': '逐 SKU 路由变化',
  'routing_dr.preflight.section.diff_hint': '只列出切换前后落点不同的 SKU，按流量排序。',
  'routing_dr.preflight.section.diff_none': '窗口内没有 SKU 的落点会变化。',
  'routing_dr.preflight.column.org': '客户',
  'routing_dr.preflight.column.reason': '原因',
  'routing_dr.preflight.column.skus': '影响 SKU',
  'routing_dr.preflight.column.requests': '请求数',
  'routing_dr.preflight.column.sku': 'SKU',
  'routing_dr.preflight.column.status': '状态',
  'routing_dr.preflight.column.detail': '说明',
  'routing_dr.preflight.column.target': '目标',
  'routing_dr.preflight.column.findings': '发现',
  'routing_dr.preflight.column.sku_count': 'SKU 数',
  'routing_dr.preflight.column.before': '切换前',
  'routing_dr.preflight.column.after': '切换后',
  'routing_dr.coverage_status.covered': '本配置覆盖',
  'routing_dr.coverage_status.fall_through': '回落下层',
  'routing_dr.coverage_status.uncovered': '无路由',
  'routing_dr.fallback_tier.sku_default': '回落到 SKU 默认路由',
  'routing_dr.fallback_tier.global_default': '回落到平台全局默认',
  'routing_dr.fallback_tier.legacy_fallback': '回落到遗留兜底层',
  'routing_dr.uncovered_reason.uncovered_by_exclusive_candidate': '配置是独占模式，且这个 SKU 既没有目标也没有策略行，请求直接无候选。',
  'routing_dr.uncovered_reason.candidate_bound_but_no_live_target': '配置绑了这个 SKU，但绑定后面没有存活的能力。',
  'routing_dr.uncovered_reason.no_candidate_anywhere': '整条链路（含默认与兜底层）都没有候选。',
  'routing_dr.unreachable_reason.exclusive_org_config': '客户挂在独占的专属配置上，全局层不会被查到。',
  'routing_dr.unreachable_reason.inherit_org_config_covers_sku': '客户的专属配置已覆盖这些 SKU，走自己的分支。',
  'routing_dr.unreachable_reason.org_sku_pool': '客户有冻结的组织级 SKU 路由，这一层对所有 SKU 都是终点。',
  'routing_dr.unreachable_reason.org_sku_override': '客户对这些 SKU 有生效中的组织级覆盖。',
  'routing_dr.health.degraded': '降级',
  'routing_dr.health.blocking': '阻断',
  'routing_dr.health.breaker': '熔断：{state}',
  'routing_dr.breaker_state.closed': '关闭',
  'routing_dr.breaker_state.open': '打开',
  'routing_dr.breaker_state.half_open': '半开',
  'routing_dr.breaker_state.forced': '强制熔断',
  'routing_dr.breaker_state.unknown': '未知',
  'routing_dr.finding.upstream_missing': '上游不存在',
  'routing_dr.finding.upstream_disabled': '上游已停用',
  'routing_dr.finding.upstream_not_forward': '上游不是转发类型',
  'routing_dr.finding.account_disabled': '账户已停用',
  'routing_dr.finding.key_bucket_disabled': '密钥桶已停用',
  'routing_dr.finding.capability_missing': '能力记录不存在',
  'routing_dr.finding.capability_not_live': '能力状态不在存活集合内',
  'routing_dr.finding.capability_binding_missing': '缺少密钥与渠道的绑定',
  'routing_dr.finding.egress_profile_unresolved': '出口配置解析不到可用值',
  'routing_dr.finding.capability_not_passed': '能力探测未通过（仍会尝试）',
  'routing_dr.finding.breaker_open': '熔断已打开',
  'routing_dr.finding.breaker_half_open': '熔断半开（仍会试探）',
  'routing_dr.finding.breaker_forced': '被强制熔断',
  'routing_dr.finding.capacity_depleted': '容量已耗尽',
  'routing_dr.caveat.openai_wire_api_not_modelled': '预检没有模拟 OpenAI 线路的 wire 过滤，openai 协议的 SKU 显示为已覆盖时仍可能没有兼容候选。',
  'routing_dr.caveat.unreachable_orgs_truncated': '触达不到的客户列表已达上限被截断，实际数量以汇总里的总数为准。',

  'routing_dr.events.title': '全局切换记录',
  'routing_dr.events.bound': '仅最近 {count} 条',
  'routing_dr.events.loading': '加载切换记录中…',
  'routing_dr.events.empty_title': '还没有全局切换记录。',
  'routing_dr.events.empty_hint': '每次切换全局默认都会在这里留下一条，回滚就是从这条记录往回走。',
  'routing_dr.events.column.when': '时间',
  'routing_dr.events.column.change': '变更',
  'routing_dr.events.column.actor': '操作人',
  'routing_dr.events.column.reason': '原因',
  'routing_dr.events.column.propagation': '生效情况',
  'routing_dr.events.no_previous': '（无上一个）',
  'routing_dr.events.preflighted': '带预检凭证',
  'routing_dr.events.rollback_to': '回滚到 {config}',
  'routing_dr.events.rollback_title': '回滚上一次全局切换',
  'routing_dr.events.rollback_save': '执行回滚',
  'routing_dr.events.rollback_explainer': '回滚的是最近一次全局切换，也就是把下面这次原路退回：',
  'routing_dr.events.rollback_syncing': '正在重新读取最新一次切换…',
  'routing_dr.events.rollback_head_changed': '这期间又有人切换过全局默认，上面的回滚目标已经变成新的这一次。请确认后再执行。',
  'routing_dr.events.rollback_reconfirm': '确认这个目标',
  'routing_dr.events.rollback_reread_failed': '没能重新读到最新一次切换，上面显示的目标可能已经不是当前的。恢复后重试，别在这个状态下直接回滚。',
  'routing_dr.events.rollback_retry_reread': '重新读取',
  'routing_dr.events.rollback_reason_placeholder': '例如：切过去后错误率反而更高，先回滚',
  'routing_dr.events.error.head_moved': '这期间有人改过全局默认，再回滚会连他们那次一起撤掉。请重新读一遍切换记录，别直接重试。',
  'routing_dr.events.error.no_target': '最近一次切换没有上一个配置可回退，回滚没有目标。',
  'routing_dr.events.error.target_gone': '要回滚到的配置已不存在或不再是「启用」状态，回滚没有执行。',
  'routing_dr.events.toast.rollback_unavailable': '最近一次切换已经不能回滚了（记录被新的切换顶掉，或者没有上一个配置），回滚入口已关闭。',
  'routing_dr.events.toast.rolled_back': '已回滚全局路由配置',
  'routing_dr.events.toast.rolled_back_degraded': '回滚已提交，但缓存只发布了一部分，最长约 {seconds} 秒后全网生效——这段时间内的流量还会走旧配置。',
  'routing_dr.events.toast.switched_override_in_force':
    '全局路由配置已切换并记录——但紧急覆盖仍在生效，它位于全局配置之上，因此没有任何租户会按这次切换路由。需要生效请先解除覆盖。',
  'routing_dr.events.toast.rolled_back_override_in_force':
    '回滚已记录——但紧急覆盖仍在生效，它位于全局配置之上，因此没有任何租户流量发生变化。需要生效请先解除覆盖。',
  'routing_dr.override.repoint_action': '切换覆盖目标',
  'routing_dr.override.repoint_title': '切换紧急覆盖的目标配置',
  'routing_dr.override.repoint_description':
    '当前全部租户路由到 {current}。切换目标是一次写入，中途不会把集群放回正常阶梯。',
  'routing_dr.override.repoint_confirm_title': '确认把全部租户换到另一个配置？',
  'routing_dr.override.repoint_confirm_body':
    '整个集群会在一次写入中从 {current} 切到 {next}。请输入新配置名称以确认。',
  'routing_dr.override.repoint_confirm_action': '对全部租户切换目标',
  'routing_dr.override.repointed_toast': '紧急覆盖目标已切换：全部租户现在路由到 {config}。',
  'routing_dr.events.toast.rolled_back_unknown': '回滚已提交，但这次响应没带缓存生效信息，无法确认是否已全网生效。请等约 {seconds} 秒后再据流量下判断。',
  'routing_dr.events.toast.switched_degraded': '切换已提交，但缓存只发布了一部分，最长约 {seconds} 秒后全网生效——这段时间内的流量还会走旧配置。',
  'routing_dr.events.toast.switched_unknown': '切换已提交，但这次响应没带缓存生效信息，无法确认是否已全网生效。请等约 {seconds} 秒后再据流量下判断。',
  'routing_dr.convergence.immediate': '已全网生效',
  'routing_dr.convergence.degraded': '延迟生效',
  'routing_dr.convergence.pending': '未记录',
  'routing_dr.convergence.unknown': '未知',
  'routing_dr.propagation.hint': '切换在数据库里立即生效，网关实例最长约 {seconds} 秒后全部跟上；这段时间内新旧配置会并存。',
  // Emergency override (#1422)
  'routing_dr.override.caption': '紧急路由覆盖',
  'routing_dr.override.status_off': '未启用',
  'routing_dr.override.status_in_force': '生效中 — 影响全部租户',
  'routing_dr.override.status_expired': '已启用但已过期',
  'routing_dr.override.status_archived_target': '已启用但目标配置已归档',
  'routing_dr.override.idle_body':
    '把全部租户强制路由到同一个配置，位于组织级分配之上。当全局切换不够用时使用：分配了 exclusive 配置的组织根本不会读取全局配置，全局切换对这些租户完全无效。',
  'routing_dr.override.in_force_body':
    '当前全部租户都被路由到该配置，包括拥有 exclusive 组织级分配的组织。组织分配、SKU 默认和全局配置全部被绕过。',
  'routing_dr.override.expired_body':
    '该覆盖已过期，请求路径已经不再采用它，流量已回到正常阶梯。但记录仍是启用状态——请执行解除，避免误导下一位响应者。',
  'routing_dr.override.archived_target_body':
    '该覆盖指向的配置已不是 active，请求路径会完全忽略这个覆盖，全部租户已经回落到正常阶梯——也就是当初启用覆盖想要绕开的那条阶梯。请改为指向一个 active 配置，或解除覆盖并改用其他手段。',
  'routing_dr.override.in_force_notice':
    '紧急覆盖生效中，全部租户都路由到 {config}。此时切换或回滚全局路由配置会被记录下来，但在解除覆盖之前不会产生任何实际效果。',
  'routing_dr.override.reason_label': '原因',
  'routing_dr.override.engaged_by_label': '启用人',
  'routing_dr.override.expires_label': '过期时间',
  'routing_dr.override.expires_never': '未设置过期',
  'routing_dr.override.engage_action': '启用覆盖',
  'routing_dr.override.disengage_action': '解除覆盖',
  'routing_dr.override.engage_title': '启用紧急路由覆盖',
  'routing_dr.override.engage_description': '这不是全局切换，它完全忽略组织级作用域。',
  'routing_dr.override.blast_radius_warning':
    '影响范围：全部租户。它会把整个集群路由到同一个配置，并绕过所有组织级分配。该控制没有按组织生效的形式。',
  'routing_dr.override.config_label': '路由到该配置',
  'routing_dr.override.config_placeholder': '选择一个 active 路由配置',
  'routing_dr.override.preflight_action': '运行预检',
  'routing_dr.override.preflight_hint':
    '必填。网关以预检令牌为准，重复运行没有代价。切换目标配置会清空令牌，因为它是针对上一个配置算出来的。',
  'routing_dr.override.preflight_missing': '尚未运行预检',
  'routing_dr.override.preflight_ready': '预检令牌已就绪',
  'routing_dr.override.preflight_ran': '预检完成。普通全局切换将有 {unreachable} 个组织无法覆盖。',
  'routing_dr.override.reason_hint': '会记录到切换事件日志。请写清事故编号——复盘时首先读的就是这一行。',
  'routing_dr.override.expiry_label': '自动过期时间',
  'routing_dr.override.expiry_hint': '强烈建议设置。这里要防的是「忘了解除」，不是「没启用」。',
  'routing_dr.override.expiry_hours': '{hours} 小时',
  'routing_dr.override.expiry_none': '不过期（不推荐）',
  'routing_dr.override.confirm_title': '确认把全部租户路由到该配置？',
  'routing_dr.override.confirm_body':
    '确认后整个集群立即切换，包括全局切换无法触达的租户。请输入配置名称以确认。',
  'routing_dr.override.confirm_type_label': '配置名称',
  'routing_dr.override.confirm_action': '对全部租户启用',
  'routing_dr.override.engaged_toast': '紧急覆盖已启用：全部租户现在路由到 {config}。',
  'routing_dr.override.disengage_title': '解除紧急路由覆盖',
  'routing_dr.override.disengage_description': '全部租户的流量回到正常阶梯。',
  'routing_dr.override.disengage_hint':
    '流量会回到阶梯当前解析出的结果，未必等于启用覆盖之前的位置。解除后请复查一个代表性组织。',
  'routing_dr.override.disengaged_toast': '紧急覆盖已解除。',
  'routing_dr.override.error_config_required': '请选择要覆盖到的路由配置。',
  'routing_dr.override.error_reason_required': '必须填写原因。',
  'routing_dr.override.error_preflight_required': '启用前请先运行预检。',
  // Detail sheet + SKU target pools
  'tenant_routing.sheet.error': '路由配置详情加载失败。',
  'tenant_routing.ladder.title': '路由解析阶梯',
  'tenant_routing.ladder.tier_override': '紧急覆盖',
  'tenant_routing.ladder.tier_org': '客户指派',
  'tenant_routing.ladder.tier_global': '全局默认配置',
  'tenant_routing.ladder.tier_default_tables': '平台默认表',
  'tenant_routing.ladder.body':
    '按此顺序取第一个命中的。客户自己的指派优先于全局默认，所以切换全局默认不会移动已有自己指派的客户；紧急覆盖压过一切，包括客户指派。继承模式的配置在某个 SKU 上没有目标也没有策略时，回落到的是「全局默认配置」，不是「平台默认表」——后者只有在全局默认本身为继承模式时才够得到。',
  'tenant_routing.parity.open': '与平台默认表对比',
  'tenant_routing.parity.title': '全局默认配置 vs 平台默认表',
  'tenant_routing.parity.description': '两者是各自独立的两张表，没有任何同步机制。',
  'tenant_routing.parity.loading': '正在比对…',
  'tenant_routing.parity.failed': '比对失败，请稍后重试。',
  'tenant_routing.parity.no_global_default': '当前没有任何配置被指派为全局默认，无从比对。',
  'tenant_routing.parity.in_sync': '两者一致',
  'tenant_routing.parity.in_sync_body': '已比对 {configSkus} 个 SKU 的目标与策略，未发现差异。',
  'tenant_routing.parity.drift': '目标差异 {targets} 处 / 策略差异 {policies} 处',
  'tenant_routing.parity.drift_body_live':
    '其中 {count} 个 SKU 现在就有问题：全局默认配置是「独占」模式，解析在第三层无条件终止，而这些 SKU 只存在于平台默认表里、配置本身没有覆盖。对所有走全局默认的客户，它们拿不到任何候选，请求直接失败，也不会出现在 /v1/models 里。要么把它们补进全局默认配置，要么从平台默认表移除。',
  'tenant_routing.parity.drift_body_latent':
    '这些差异当前不影响任何客户：涉及的 SKU 都已被全局默认配置覆盖（当前覆盖模式 {mode}），平台默认表那一层够不到。它们会在全局默认被替换或清空时变成真实路由行为——而那正是没人会回头核对这两张表的时候。',
  'tenant_routing.parity.section_targets': '目标差异',
  'tenant_routing.parity.section_policies': '策略差异',
  'tenant_routing.parity.column_sku': 'SKU',
  'tenant_routing.parity.column_kind': '差异类型',
  'tenant_routing.parity.column_config': '全局默认配置',
  'tenant_routing.parity.column_default': '平台默认表',
  'tenant_routing.parity.kind_missing_in_config': '仅平台默认表有',
  'tenant_routing.parity.kind_missing_in_default': '仅全局默认配置有',
  'tenant_routing.parity.kind_mismatched': '两侧内容不同',
  'tenant_routing.deprecation.group_toggle': '已标记过时 {count} 个',
  'tenant_routing.deprecation.badge': '已过时',
  'tenant_routing.deprecation.mark': '标记为过时',
  'tenant_routing.deprecation.unmark': '取消过时标记',
  'tenant_routing.deprecation.mark_title': '把这条配置标记为过时？',
  'tenant_routing.deprecation.mark_body':
    '标记后它不能再被指派给新的客户，并会折叠到「已标记过时」分组。已经绑定这条配置的 {count} 个客户完全不受影响，路由行为保持不变。随时可以取消标记。',
  'tenant_routing.deprecation.unmark_title': '取消这条配置的过时标记？',
  'tenant_routing.deprecation.unmark_body': '取消后它会回到配置库，并重新可以被指派给新客户。',
  'tenant_routing.deprecation.toast_marked': '已标记为过时',
  'tenant_routing.deprecation.toast_unmarked': '已取消过时标记',
  'tenant_routing.deprecation.error.blocked':
    '该配置已被标记为过时，不能再指派给新客户。已经在用它的客户不受影响；如果确实要继续用，先取消它的过时标记。',
  'tenant_routing.configs.coverage': '覆盖模式',
  'tenant_routing.coverage.short_exclusive': '独占',
  'tenant_routing.coverage.short_inherit': '继承',
  'tenant_routing.coverage.short_unknown': '未知',
  'tenant_routing.coverage.exclusive_warning': '此配置对已分配客户是独占层。某个 SKU 缺目标时，请求会直接无候选，不会回退到平台默认池。',
  'tenant_routing.coverage.inherit_hint': '缺少目标和策略的 SKU 会继续使用当前平台全局/默认路由。',
  'tenant_routing.coverage.mode_exclusive': '独占（缺失即失败）',
  'tenant_routing.coverage.mode_inherit': '继承平台默认',
  'tenant_routing.coverage.mode_label': '覆盖模式',
  'tenant_routing.coverage.namespace': '覆盖 {covered}/{total} SKU · 缺 {missing} 个新 SKU',
  'tenant_routing.coverage.preview_fill': '预览补齐',
  'tenant_routing.coverage.preview_title': '预览补齐 SKU 覆盖',
  'tenant_routing.coverage.preview': '生成预览',
  'tenant_routing.coverage.apply': '应用补齐',
  'tenant_routing.coverage.select_upstream': '选择明确的上游身份',
  'tenant_routing.coverage.identity_label': '目标上游身份',
  'tenant_routing.coverage.affected_customers': '影响已分配客户：{count}',
  'tenant_routing.coverage.missing_list': '精确缺失 SKU：{skus}',
  'tenant_routing.coverage.non_overwrite': '仅新增缺失目标，不覆盖现有权重、重试顺序或停用状态。',
  'tenant_routing.coverage.add': '新增',
  'tenant_routing.coverage.conflict': '冲突',
  'tenant_routing.coverage.skipped': '跳过',
  'tenant_routing.coverage.column_sku': 'SKU',
  'tenant_routing.coverage.column_outcome': '结果',
  'tenant_routing.coverage.column_reason': '原因',
  'tenant_routing.coverage.column_identity': '所选上游身份',
  'tenant_routing.coverage.column_mode': '覆盖模式',
  'tenant_routing.coverage.reason_missing': '缺少覆盖',
  'tenant_routing.coverage.reason_already_exists': '相同目标已存在',
  'tenant_routing.coverage.reason_explicit_coverage_exists': '该 SKU 已有明确目标或策略',
  'tenant_routing.coverage.reason_capability_not_live': '所选 canonical capability 已不再可用',
  'tenant_routing.coverage.applied': '已新增 {count} 个缺失目标',
  'tenant_routing.sheet.shared_warning': '此配置已分配给 {count} 个客户，保存修改会立即影响这些客户的路由。',
  'tenant_routing.pool.add_target': '新增目标',
  'tenant_routing.pool.write_blocked_targets_unknown': '该 SKU 的目标池读取失败，无法确认这次保存会落到哪一行；继续保存可能会改掉甚至删掉已有的绑定。请先刷新。',
  'tenant_routing.pool.write_hits_unpinned_row': '该配置下这条四元组上已有一条未固定能力的目标（{channel}，{id}）。保存会落到那一行上：把它改到你选的能力，并覆盖它的权重、重试顺序和启用状态，而不是写入你这次要保存的目标。已取消保存。请用那一行自己的编辑按钮去固定能力。',
  'tenant_routing.pool.write_hits_bound_row': '该配置下这条四元组上已经绑定了该能力（{channel}，{id}）。保存会落到那一行上，覆盖它的权重、重试顺序和启用状态，而不是写入你这次要保存的目标。已取消保存。请直接编辑下方那一行。',
  'tenant_routing.pool.reserve': '纯兜底',
  'tenant_routing.pool.retry_order': '重试顺序',
  'tenant_routing.pool.retry_order_auto': '自动',
  'tenant_routing.pool.retry_order_hint': '可选。固定该目标在重试链中的位置（正整数，越小越先试）；留空则在显式顺序之后按权重排列。',
  'tenant_routing.pool.weight_hint': '按会话安置：同一会话恒定路由到同一目标（保 prompt cache）；权重决定新会话在目标间的分配比例，不是按请求的百分比分流。0 = 纯兜底，不接首发流量。',
  'tenant_routing.pool.share_title': '首发流量分配',
  'tenant_routing.pool.share_hint': '按会话安置的分配比例，不是按请求的百分比分流。',
  'tenant_routing.pool.share_none': '当前没有目标承接首发流量。',
  'tenant_routing.pool.share_label': '{channel} 承接 {percent}% 首发流量',
  'tenant_routing.pool.share_reserve_count': '另有 {count} 个纯兜底目标，不接首发流量。',
  // ── 路由时段窗（0044）──────────────────────────────────────────────
  'tenant_routing.time_windows.title': '时段安排',
  'tenant_routing.time_windows.now': '现在 {time} → {schedule}',
  'tenant_routing.time_windows.standard': '标准',
  'tenant_routing.time_windows.in_force': '此刻生效',
  'tenant_routing.time_windows.add': '新增时段',
  'tenant_routing.time_windows.none': '该 SKU 没有任何时段安排，全天使用标准配置。',
  'tenant_routing.time_windows.unloaded': '时段安排读取失败，下面的目标池权重是标准配置，可能不是此刻真正生效的值。请刷新。',
  'tenant_routing.time_windows.write_blocked': '时段安排读取失败，无法确认新时段是否与已有时段重叠。请先刷新。',
  'tenant_routing.time_windows.no_legs': '该 SKU 目标池为空',
  'tenant_routing.time_windows.fallback_note': '未被任何时段覆盖的时间使用标准配置。时段只改权重和重试顺序，不会增减可用上游。',
  'tenant_routing.time_windows.dialog_title_new': '新增时段',
  'tenant_routing.time_windows.dialog_title_edit': '编辑时段',
  'tenant_routing.time_windows.dialog_description': '时段只能覆盖权重和重试顺序：它改不了上游、渠道、能力，也停不掉任何一条腿。',
  'tenant_routing.time_windows.label': '名称',
  'tenant_routing.time_windows.start': '开始（本地）',
  'tenant_routing.time_windows.end': '结束（本地）',
  'tenant_routing.time_windows.timezone': '时区（IANA）',
  'tenant_routing.time_windows.range_note': '按该时区的本地时间计算；开始含、结束不含。',
  'tenant_routing.time_windows.wrap_note': '开始晚于结束＝跨本地零点，归属开始那一天（如周五 22:00–08:00 覆盖周六凌晨）。',
  'tenant_routing.time_windows.enabled': '启用',
  'tenant_routing.time_windows.legs': '该时段的权重覆盖',
  'tenant_routing.time_windows.legs_hint': '留空＝该时段不改这条腿，沿用标准配置的值。两列都留空则这条腿不进这个时段。',
  'tenant_routing.time_windows.base': '标准 {weight} / {retry}',
  'tenant_routing.time_windows.save': '保存时段',
  'tenant_routing.time_windows.delete': '删除时段',
  'tenant_routing.time_windows.saved': '时段已保存。',
  'tenant_routing.time_windows.deleted': '时段已删除，相关腿恢复标准配置。',
  'tenant_routing.time_windows.day_mon': '一',
  'tenant_routing.time_windows.day_tue': '二',
  'tenant_routing.time_windows.day_wed': '三',
  'tenant_routing.time_windows.day_thu': '四',
  'tenant_routing.time_windows.day_fri': '五',
  'tenant_routing.time_windows.day_sat': '六',
  'tenant_routing.time_windows.day_sun': '日',
  'tenant_routing.pool.dialog_title_new': '新增目标池目标',
  'tenant_routing.pool.dialog_title_edit': '编辑目标池目标',
  'tenant_routing.pool.save': '保存目标',
  'tenant_routing.pool.remove_target': '移除目标',
  'tenant_routing.pool.remove_confirm_title': '移除此 SKU 目标？',
  'tenant_routing.pool.remove_confirm_body':
    '移除成功后，此目标将立即停止承接新流量；同一 SKU 的其他目标不会改变。',
  'tenant_routing.pool.remove_last_enabled_title':
    '{protocols} 的最后一个启用目标',
  'tenant_routing.pool.remove_last_enabled_body':
    '移除后，此配置在 {sku} / {protocols} 下将没有任何候选。所有在该 SKU 与协议上解析到此配置的组织都会 fail-closed，对应请求不可路由。当前已分配组织数：{count}。',
  'tenant_routing.pool.toast_saved': '目标池目标已保存',
  'tenant_routing.pool.toast_deleted': '目标已从目标池移除',
  // SKU target pools (epic #898 P5)
  'tenant_routing.sku_pools.title': 'SKU 目标池',
  'tenant_routing.sku_pools.hint': '每个 SKU（namespace/model）一个统一目标池：权重 > 0 的目标按会话安置接首发流量，权重 0 为纯兜底；重试链按「重试顺序 → 权重 → 优先级」排列。',
  'tenant_routing.sku_pools.namespace_empty': '该命名空间暂无 SKU。',
  'tenant_routing.sku_pools.empty': '该 SKU 目标池暂无目标。',
  'tenant_routing.sku_pools.empty_pool': '无启用目标',
  'tenant_routing.sku_pools.coverage_ready': '有启用目标',
  'tenant_routing.sku_pools.coverage_disabled': '目标全部禁用',
  'tenant_routing.sku_pools.coverage_missing': '无目标',
  'tenant_routing.sku_pools.coverage_unknown': '目标池未读到',
  'tenant_routing.sku_pools.loading_pools': '正在读取各 SKU 的目标池…',
  'tenant_routing.sku_pools.no_selection': '左侧暂无可选 SKU；先在命名空间里添加模型，再回来配置目标池。',
  'tenant_routing.sku_pools.missing_routes': '缺路由 SKU',
  'tenant_routing.sku_pools.apply_namespace': '应用到该命名空间的全部 {count} 个 SKU',
  'tenant_routing.sku_pools.capability_single_sku': '物理能力按 SKU 逐条固定；请分别配置同命名空间中的其他模型。',
  'tenant_routing.sku_pools.toast_saved_many': '目标已保存到 {count} 个 SKU',
  'tenant_routing.sku_pools.save_failed': '{sku} 保存失败：{message}',
  'tenant_routing.sku_pools.stale_row_warning': '新身份的目标已保存，但旧目标行 {id} 仍然存活并承接流量——再次保存可重试清理，或手动移除 {id}。',
  'tenant_routing.sku_pools.exclusive_impact_title': '独占配置不会回退到默认池——{count} 个已分配的独占配置暂无法路由该 SKU。',
  'tenant_routing.sku_pools.exclusive_impact_row': '{name}（{id}）· {scope} · 已分配 {orgs} 个组织',
  'tenant_routing.sku_pools.exclusive_scope_global': '全局',
  'tenant_routing.sku_pools.exclusive_scope_org': '组织',
  'tenant_routing.sku_pools.exclusive_fix': '复制默认目标',
  'tenant_routing.sku_pools.exclusive_fix_unpinned': '首个默认目标未固定物理能力，请手动复制该身份。',
  'tenant_routing.default.platform_channel': '平台渠道',
  'tenant_routing.default.upstream': '上游',
  'tenant_routing.default.account': '账户',
  'tenant_routing.default.key_bucket': 'API 密钥',
  'tenant_routing.default.upstream_channel': '上游渠道',
  'tenant_routing.default.capability': '物理能力',
  'tenant_routing.default.capability_hint': '必选。系统会精确提交所选 capability ID 与上游模型，不会猜测物理模型。',
  'tenant_routing.default.capability_legacy_warning': '这条旧目标尚未固定物理能力；保存任何变更前必须先选择一个 capability。',
  'tenant_routing.default.capability_stale_warning': '原先选择的 capability 已不再匹配该身份或不再存活；请重新选择后保存。',
  'tenant_routing.default.capability_last_passed': '最近通过：{time}',
  'tenant_routing.default.capability_status.passed': '已通过',
  'tenant_routing.default.capability_status.needs_probe': '待探测',
  'tenant_routing.default.capability_status.probe_failed': '探测失败',
  'tenant_routing.default.capability_status.missing_from_list': '列表缺失',
  'tenant_routing.default.capability_status.mismatch': '不匹配',
  'tenant_routing.default.capability_status.ignored': '已忽略',
  'tenant_routing.target.capability_legacy': '旧目标未固定',
  'tenant_routing.target.capability_not_live': '固定能力已不再存活',
  'tenant_routing.default.weight': '权重',
  'tenant_routing.default.enabled': '启用此目标',
  'tenant_routing.default.routability_hint': '只能保存网关判定可为该 SKU 绑定的上游渠道。',
  'tenant_routing.default.routability_loading': '正在检查已验证模型目标...',
  'tenant_routing.default.not_routable_option': '不可路由',
  'tenant_routing.default.not_routable_unpriced': '未定价',
  'tenant_routing.default.no_routable_channels': '这个 API 密钥与上游渠道没有匹配该 SKU 的已验证模型目标；请先发现并归类能力。',
  'tenant_routing.default.routability_unavailable': '读取供给候选失败，无法判断哪些渠道可绑定；这不代表没有供给。请重试，仍失败则检查网关。',
  'tenant_routing.default.missing_key_channel': '这个 API 密钥还没有绑定 {channel} 对应的上游渠道；请先在上游账户里配置渠道与 egress。',
  'tenant_routing.default.open_upstream': '打开上游账户',
  'tenant_routing.default_channels.platform_channel': '平台渠道',
  'tenant_routing.default_channels.upstream_channel': '上游渠道',
  'tenant_routing.default_channels.weight': '权重',
  'tenant_routing.default_channels.policy_mode': '策略',
  'tenant_routing.default_channels.save_policy': '保存策略',
  'tenant_routing.default_channels.target_count_one': '{count} 个目标',
  'tenant_routing.default_channels.target_count_many': '{count} 个目标',
  'tenant_routing.policy_mode.full_chain': '完整链路',
  'tenant_routing.policy_mode.network_only': '仅网络失败',
  'tenant_routing.policy_mode.same_cache_domain': '同缓存域',
  'tenant_routing.policy_mode.fail_fast': '快速失败',
  'tenant_routing.policy_mode.full_chain_hint': '当前行为：可重试的上游失败沿目标池重试链继续尝试。',
  'tenant_routing.policy_mode.network_only_hint': '只在网络异常或可重试上游 5xx 响应时继续尝试。',
  'tenant_routing.policy_mode.same_cache_domain_hint': '只重试厂商、协议、egress、上游、账户和 key bucket 都一致的目标。',
  'tenant_routing.policy_mode.fail_fast_hint': '不自动重试目标池中的其他目标。',
  'tenant_routing.table.title': '已启用专属路由的租户',
  'tenant_routing.table.count': '{count} 个租户',
  'tenant_routing.table.org': '租户',
  'tenant_routing.table.custom_count': '专属路由',
  'tenant_routing.table.custom_count_one': '{count} 条专属路由',
  'tenant_routing.table.custom_count_many': '{count} 条专属路由',
  'tenant_routing.table.sku_examples': 'SKU 示例',
  'tenant_routing.table.target': '目标',
  'tenant_routing.supply.title': '供给',
  'tenant_routing.supply.model': '模型',
  'tenant_routing.supply.key_bucket': 'key bucket',
  'tenant_routing.supply.channel': '渠道',
  'tenant_routing.supply.egress': '出口',
  'tenant_routing.supply.upstream_model': '上游模型',
  'tenant_routing.supply.protocol': '协议',
  'tenant_routing.supply.unnamed': '未命名',
  'tenant_routing.supply.unbound': '未绑定',
  'tenant_routing.supply.override': '有覆盖',
  'tenant_routing.supply.disabled': '已停用',
  'tenant_routing.supply.details': '详情（ID）',
  'tenant_routing.supply.target': 'target',
  'tenant_routing.supply.copy_id': '复制{label} ID',
  'tenant_routing.supply.copy_failed': '复制失败，请手动复制',
  'tenant_routing.supply.legend': '上游：供应商；账户：上游下的充值和余额账户；key bucket：实际使用的一把或一组 key；渠道：上游下按协议划分的入口；出口：key bucket 与渠道的 binding 所使用的 egress profile。',
  'tenant_routing.table.status': '状态',
  'tenant_routing.table.updated': '更新时间',
  'tenant_routing.table.actions': '操作',
  'tenant_routing.table.open_detail': '打开详情',
  'tenant_routing.table.empty': '没有租户启用专属路由',
  'tenant_routing.override.org': 'Org ID',
  'tenant_routing.override.filter': '按租户筛选',
  // Routing circuit breaker (epic #886 PR-6)
  'tenant_routing.probe.settings.title': '目标探针',
  'tenant_routing.probe.card_title': '探针',
  'tenant_routing.probe.settings.hint': '关闭后每个 isolate 在 30 秒内收敛，外加 CN 副本延迟。',
  'tenant_routing.probe.summary': '{eligible} 条合格物理腿 · {tripped} 个已熔断（探针 {probe} / 质量 {quality} / 链内 {chain} / Jev {jev}） · {backoff} 条退避中 · 下次探针 {at}',
  'tenant_routing.probe.summary_hint': '在下方 SKU 表编辑探针开关和间隔。熔断后的腿退役首发，仍留在重试链末尾。',
  'tenant_routing.probe.settings.enabled': '探针',
  'tenant_routing.probe.settings.interval': '间隔（分钟）',
  'tenant_routing.probe.settings.interval_invalid': '间隔须为 1 到 1440 的整数',
  'tenant_routing.probe.settings.save': '保存间隔',
  'tenant_routing.probe.settings.saved': '探针设置已保存',
  'tenant_routing.probe.tripped': '退役首发',
  'tenant_routing.probe.ok': '上次探针 {at}',
  'tenant_routing.probe.next': '下次探针 {at}',
  'tenant_routing.probe.backoff': '退避档 {level}',
  'tenant_routing.probe.tripped_hint': '退役首发的腿（含探针已关闭的 SKU）。它们不参与加权首发，仍按原顺序留在重试链末尾；连续 5 次干净的链内成功，或一轮探针通过，会自己回到首发。清除后立即恢复首发。',
  'tenant_routing.probe.col_sku': 'SKU',
  'tenant_routing.probe.col_leg': '腿（上游 · 通道）',
  'tenant_routing.probe.col_reason': '摘除原因',
  'tenant_routing.probe.col_last_error': '最近错误',
  'tenant_routing.probe.col_tripped_at': '摘除时间',
  'tenant_routing.probe.col_next': '下次探针',
  'tenant_routing.probe.col_actions': '操作',
  'tenant_routing.probe.reason.probe': '探针',
  'tenant_routing.probe.reason.chain': '链内证据',
  'tenant_routing.probe.reason.jev': 'Jev',
  'tenant_routing.probe.reason.quality': '探针质量',
  'tenant_routing.probe.reason.silent': '静默摘腿',
  'tenant_routing.probe.reset': '清除摘除',
  'tenant_routing.probe.reset_done': '已清除，该腿重新进入候选；这条物理腿上所有 SKU 的警戒 / 战争和战后冷却也一并结束',
  'tenant_routing.probe.probe_now': '立即探测',
  'tenant_routing.probe.probe_now_recovered': '探测通过，已恢复',
  'tenant_routing.probe.probe_now_still_tripped': '探测仍失败，保持退役首发',
  'tenant_routing.probe.disable_target': '禁用目标',
  'tenant_routing.probe.disable_title': '禁用该目标？',
  'tenant_routing.probe.disable_body': '禁用后，这条腿会离开首发，也会离开重试链，直到重新启用。退役首发是另一件事，干净的链内成功或探针通过后会自己恢复。',
  'tenant_routing.probe.disable_confirm': '禁用目标',
  'tenant_routing.probe.disable_done': '目标已禁用',
  'tenant_routing.probe.disable_missing': '找不到这条腿的路由目标，没有改动',
  'tenant_routing.disabled_note.label': '摘除备注（可选）',
  'tenant_routing.disabled_note.placeholder': '例如：没有余额了，等待充值',
  'tenant_routing.disabled_note.count': '{count}/{max} 字',
  'tenant_routing.disabled_note.none': '无备注',
  'tenant_routing.disabled_note.tip': '手动摘除 · {detail}',
  'tenant_routing.disabled_note.edit': '编辑摘除备注（打开目标编辑）',
  'tenant_routing.ago.just_now': '刚刚',
  'tenant_routing.ago.minutes': '{n} 分钟前',
  'tenant_routing.ago.hours': '{n} 小时前',
  'tenant_routing.ago.days': '{n} 天前',
  'probe_try.button': '试打探针（无副作用）',
  'probe_try.tip': '试打探针（无副作用）：选探针类型打一发，只看能不能打通。不计入探针状态、不摘除不恢复、不动路由；会产生一次探针用量。',
  'probe_try.title': '试打探针',
  'probe_try.description': '只打一发看能不能打通：不写探针状态、不摘除不恢复、不影响路由；这一发照常记一条探针用量。',
  'probe_try.send': '发送',
  'probe_try.result': 'HTTP {code} · {ms} ms · 输出 {tokens} tokens',
  'tenant_routing.breaker.strip_label': '熔断',
  'tenant_routing.breaker.chip_open': '熔断中 · 剩余 {seconds}s',
  'tenant_routing.breaker.chip_half_open': '试探中',
  'tenant_routing.breaker.chip_forced': '手动摘除 · 至 {until}',
  'tenant_routing.breaker.state.open': '熔断中',
  'tenant_routing.breaker.state.half_open': '试探中',
  'tenant_routing.breaker.state.forced': '手动摘除',
  'tenant_routing.breaker.reset': '立即恢复',
  'tenant_routing.breaker.reset_confirm_title': '立即恢复该目标？',
  'tenant_routing.breaker.reset_confirm_body': '恢复后，该目标立即回到配置的重试链位置，重新接收流量；若上游仍不稳定，被动熔断可能再次触发。',
  'tenant_routing.breaker.trip': '手动摘除',
  'tenant_routing.breaker.trip_title': '手动摘除目标',
  'tenant_routing.breaker.trip_body': '摘除后，该目标在所有引用它的路由重试链中移至末位，不再接收首发流量；到期自动恢复，期间可随时「立即恢复」。对该密钥的所有渠道与配置全局生效。',
  'tenant_routing.breaker.trip_duration': '摘除时长',
  'tenant_routing.breaker.trip_duration.30m': '30 分钟',
  'tenant_routing.breaker.trip_duration.1h': '1 小时',
  'tenant_routing.breaker.trip_duration.6h': '6 小时',
  'tenant_routing.breaker.trip_duration.24h': '24 小时',
  'tenant_routing.breaker.trip_until_preview': '恢复时间：{until}',
  'tenant_routing.breaker.trip_save': '确认摘除',
  'tenant_routing.breaker.drawer_title': '路由熔断',
  'tenant_routing.breaker.drawer_hint': '被动熔断 + 半开恢复：开路目标被降级到重试链末位，不会被移除。',
  'tenant_routing.breaker.table.identity': '目标',
  'tenant_routing.breaker.table.state': '状态',
  'tenant_routing.breaker.table.reason': '最近错误',
  'tenant_routing.breaker.table.opened': '触发时间',
  'tenant_routing.breaker.table.actions': '操作',
  'tenant_routing.breaker.empty': '当前没有熔断中的目标。',
  'tenant_routing.breaker.disabled': '熔断器未启用（未配置存储绑定）。',
  'tenant_routing.breaker.toast.reset': '目标已恢复',
  'tenant_routing.breaker.toast.tripped': '目标已手动摘除',
  'tenant_routing.breaker.reason_none': '—',
  'tenant_routing.breaker.sheet_summary_one': '熔断 · {count} 个目标受影响',
  'tenant_routing.breaker.sheet_summary_many': '熔断 · {count} 个目标受影响',
  'tenant_routing.breaker.open_panel': '打开熔断面板',
  // Routing breaker threshold settings (epic #886 settings PR)
  'tenant_routing.breaker.settings.open': '阈值设置',
  'tenant_routing.breaker.settings.back': '返回列表',
  'tenant_routing.breaker.settings.title': '熔断阈值设置',
  'tenant_routing.breaker.settings.hint': '留空的字段跟随环境变量或内置默认值；填写后成为自定义值，对所有网关实例生效。',
  'tenant_routing.breaker.settings.loading': '加载设置中…',
  'tenant_routing.breaker.settings.load_failed': '设置加载失败，请稍后重试。',
  'tenant_routing.breaker.settings.kill_switch': '环境变量已强制关闭熔断（BREAKER_ENABLED=false），此处设置不生效；解除环境变量后自动恢复生效。',
  'tenant_routing.breaker.settings.enabled_label': '启用熔断',
  'tenant_routing.breaker.settings.enabled_desc': '关闭后不再自动降级持续失败的目标，所有目标始终按配置顺序尝试。',
  'tenant_routing.breaker.settings.source.default': '默认',
  'tenant_routing.breaker.settings.source.env': '环境变量',
  'tenant_routing.breaker.settings.source.custom': '已自定义',
  'tenant_routing.breaker.settings.effective': '当前生效：{value}',
  'tenant_routing.breaker.settings.restore': '恢复默认',
  'tenant_routing.breaker.settings.save': '保存设置',
  'tenant_routing.breaker.settings.save_hint': '保存后约 30 秒内对所有实例生效。',
  'tenant_routing.breaker.settings.toast.saved': '阈值设置已保存',
  'tenant_routing.breaker.settings.toast.restored': '已恢复默认，约 30 秒内生效',
  'tenant_routing.breaker.settings.invalid_int': '请输入正整数',
  'tenant_routing.breaker.settings.invalid_rate': '请输入大于 0、不超过 1 的数值',
  'tenant_routing.breaker.settings.field.consecutive_failures': '连续失败阈值',
  'tenant_routing.breaker.settings.field.consecutive_failures_desc': '同一目标连续 N 次可重试失败即熔断。',
  'tenant_routing.breaker.settings.field.window_seconds': '统计窗口（秒）',
  'tenant_routing.breaker.settings.field.window_seconds_desc': '滑动窗口长度：统计最近 X 秒内的成功与失败，用于失败率判断。',
  'tenant_routing.breaker.settings.field.window_min_failures': '窗口最少失败次数',
  'tenant_routing.breaker.settings.field.window_min_failures_desc': '窗口内失败达到 Z 次后，失败率条件才可能触发熔断，避免低流量误判。',
  'tenant_routing.breaker.settings.field.window_failure_rate': '窗口失败率阈值',
  'tenant_routing.breaker.settings.field.window_failure_rate_desc': '窗口内失败率超过 Y（0–1，如 0.5 即 50%）且满足最少失败次数时熔断。',
  'tenant_routing.breaker.settings.field.cooldown_base_seconds': '冷却基础时长（秒）',
  'tenant_routing.breaker.settings.field.cooldown_base_seconds_desc': '熔断后先冷却 B 秒再放行试探；反复失败每次翻倍。',
  'tenant_routing.breaker.settings.field.cooldown_cap_seconds': '冷却时长上限（秒）',
  'tenant_routing.breaker.settings.field.cooldown_cap_seconds_desc': '冷却翻倍的最大值，最长不超过 C 秒；需不小于冷却基础时长。',
  'tenant_routing.breaker.settings.field.entry_ttl_seconds': '自动恢复时长（秒）',
  'tenant_routing.breaker.settings.field.entry_ttl_seconds_desc': '无新失败 N 秒后，熔断记录自动过期，目标视为恢复。',
  'tenant_routing.breaker.settings.field.half_open_trial_ttl_seconds': '半开试探有效期（秒）',
  'tenant_routing.breaker.settings.field.half_open_trial_ttl_seconds_desc': '冷却结束后放行 1 个真实请求试探；这是该试探名额的抢占有效期。',
  // Load-aware routing (#2310)
  'tenant_routing.load.strip_label': '负载感知路由',
  'tenant_routing.load.strip.events': '观测',
  'tenant_routing.load.strip.l1': 'L1',
  'tenant_routing.load.strip.l2': 'L2',
  'tenant_routing.load.strip.l3': 'L3',
  'tenant_routing.load.strip.l4': 'L4',
  'tenant_routing.load.strip.warn': '需要关注',
  'tenant_routing.load.drawer_hint': '按层、车道或 SKU 开关负载感知路由；运行态只确认开了之后有没有在动。',
  'tenant_routing.load.view.settings': '设置',
  'tenant_routing.load.view.scope': '按车道 / SKU',
  'tenant_routing.load.view.runtime': '运行态',
  'tenant_routing.load.settings.title': '负载感知路由设置',
  'tenant_routing.load.settings.hint': '保存后约 30 秒内在所有网关实例生效。',
  'tenant_routing.load.settings.order_hint': '推荐开启顺序：观测 → L1 租户限额 → L2 与 L3（含自学习上限）→ L4 与首字节超时。',
  'tenant_routing.load.settings.loading': '加载设置中…',
  'tenant_routing.load.settings.load_failed': '设置加载失败，请稍后重试。',
  'tenant_routing.load.settings.save': '保存设置',
  'tenant_routing.load.settings.save_hint': '保存后约 30 秒内对所有实例生效。',
  'tenant_routing.load.settings.toast.saved': '负载感知设置已保存',
  'tenant_routing.load.settings.toast.restored': '已恢复默认，约 30 秒内生效',
  'tenant_routing.load.settings.restore': '恢复默认',
  'tenant_routing.load.settings.source.default': '默认',
  'tenant_routing.load.settings.source.env': '环境变量',
  'tenant_routing.load.settings.source.custom': '已自定义',
  'tenant_routing.load.settings.effective': '当前生效：{value}',
  'tenant_routing.load.settings.invalid_int': '请输入正整数',
  'tenant_routing.load.settings.invalid_decimal': '请输入大于 0 且不超过上限的数值',
  'tenant_routing.load.settings.advanced': '高级',
  'tenant_routing.load.settings.enable_l2_with_l3': '一起开 L2',
  'tenant_routing.load.settings.warning.l3_without_l2': 'L3 已开启但 L2 未开，自适应分流没有目标用量可跟踪。',
  'tenant_routing.load.settings.kill_switch.l1': '环境变量已强制关闭 L1（LOAD_L1_ENABLED=false），此处设置不生效；解除环境变量后自动恢复生效。',
  'tenant_routing.load.settings.kill_switch.l2': '环境变量已强制关闭 L2（LOAD_L2_ENABLED=false），此处设置不生效；解除环境变量后自动恢复生效。',
  'tenant_routing.load.settings.kill_switch.l3': '环境变量已强制关闭 L3（LOAD_L3_ENABLED=false），此处设置不生效；解除环境变量后自动恢复生效。',
  'tenant_routing.load.settings.kill_switch.l4': '环境变量已强制关闭 L4（LOAD_L4_ENABLED=false），此处设置不生效；解除环境变量后自动恢复生效。',
  'tenant_routing.load.settings.group.events': '观测',
  'tenant_routing.load.settings.group.l1': 'L1 租户限额',
  'tenant_routing.load.settings.group.l2': 'L2 目标用量',
  'tenant_routing.load.settings.group.l3': 'L3 自适应分流',
  'tenant_routing.load.settings.group.l4': 'L4 缓存粘性',
  'tenant_routing.load.settings.group.timeout': '首字节超时',
  'tenant_routing.load.settings.l1_limits_hint': '限额在 SKU 详情页与客户详情页配置。',
  'tenant_routing.load.settings.timeout_enable': '启用',
  'tenant_routing.load.settings.timeout_enable_desc': '开启后按基值限制每次尝试的首字节等待；关闭则不限制。',
  'tenant_routing.load.settings.field.events_enabled': '启用观测',
  'tenant_routing.load.settings.field.events_enabled_desc': '记录负载感知事件，供巡检与运行态确认。',
  'tenant_routing.load.settings.field.l1_enabled': '启用 L1',
  'tenant_routing.load.settings.field.l1_enabled_desc': '按 SKU 默认限额与租户覆盖拒绝超额请求。',
  'tenant_routing.load.settings.field.l2_enabled': '启用 L2',
  'tenant_routing.load.settings.field.l2_enabled_desc': '跟踪每个上游目标的用量，并对照已配置或观测到的上限。',
  'tenant_routing.load.settings.field.l3_enabled': '启用 L3',
  'tenant_routing.load.settings.field.l3_enabled_desc': '按目标用量自适应分流；需要 L2 同时开启。',
  'tenant_routing.load.settings.field.l4_enabled': '启用 L4',
  'tenant_routing.load.settings.field.l4_enabled_desc': '会话粘性：尽量把同一会话钉在同一目标上。',
  'tenant_routing.load.settings.field.l2_observed_caps_enabled': '读取上游限流头',
  'tenant_routing.load.settings.field.l2_observed_caps_enabled_desc': '把上游返回的限流头当作观测上限。',
  'tenant_routing.load.settings.field.l3_inferred_caps_enabled': '号池自学习上限',
  'tenant_routing.load.settings.field.l3_inferred_caps_enabled_desc': '没有配置或观测上限时，使用自学习的 RPM/TPM 上限。',
  'tenant_routing.load.settings.field.session_rehome_enabled': '会话级分流',
  'tenant_routing.load.settings.field.session_rehome_enabled_desc': '账号池上游上，连续 429 的会话改走备用目标；其他会话不受影响。',
  'tenant_routing.load.settings.field.session_rehome_after_429': '会话改挂 429 次数',
  'tenant_routing.load.settings.field.session_rehome_after_429_desc': '同一会话在池目标上连续 429 几次后改挂备用。留空=2。',
  'tenant_routing.load.settings.field.session_rehome_ttl_seconds': '会话改挂 TTL（秒）',
  'tenant_routing.load.settings.field.session_rehome_ttl_seconds_desc': '改挂持续秒数，到期后恢复普通粘性。留空=120。',
  'tenant_routing.load.settings.kill_switch.session_rehome': '进程已关闭会话级分流（LOAD_SESSION_REHOME_ENABLED=false）。',
  'tenant_routing.load.settings.field.affinity_conversation_enabled': '按会话前缀粘性',
  'tenant_routing.load.settings.field.affinity_conversation_enabled_desc': '无显式会话提示时，按对话前缀而不是账户默认会话粘性。',
  'tenant_routing.load.settings.field.shed_step': '分流步进',
  'tenant_routing.load.settings.field.shed_step_desc': 'L3 每次上调分流的加性步长。留空=恢复内置默认。',
  'tenant_routing.load.settings.field.shed_quiet_seconds': '静默窗口（秒）',
  'tenant_routing.load.settings.field.shed_quiet_seconds_desc': 'L3 开始衰减前需要安静的秒数。留空=恢复内置默认。',
  'tenant_routing.load.settings.field.shed_decay': '分流衰减',
  'tenant_routing.load.settings.field.shed_decay_desc': 'L3 静默后的乘性衰减因子。留空=恢复内置默认。',
  'tenant_routing.load.settings.field.half_open_trials': '半开试探次数',
  'tenant_routing.load.settings.field.half_open_trials_desc': 'L3 半开阶段放行的试探名额。留空=恢复内置默认。',
  'tenant_routing.load.settings.field.shed_target_ema_alpha': '目标跟踪平滑',
  'tenant_routing.load.settings.field.shed_target_ema_alpha_desc': 'L3 每个控制周期的目标跟踪平滑系数。留空=恢复内置默认。',
  'tenant_routing.load.settings.field.shed_tick_seconds': '控制周期（秒）',
  'tenant_routing.load.settings.field.shed_tick_seconds_desc': 'L3 控制律的节拍间隔。留空=恢复内置默认。',
  'tenant_routing.load.settings.field.shed_warm_threshold': '热会话分流门槛',
  'tenant_routing.load.settings.field.shed_warm_threshold_desc': 'L4 热会话开始让出流量的分流门槛。留空=恢复内置默认。',
  'tenant_routing.load.settings.field.shed_warm_429_ratio': '热会话 429 比例',
  'tenant_routing.load.settings.field.shed_warm_429_ratio_desc': 'L4 热会话让出时要求达到的近期 429 比例。留空=恢复内置默认。',
  'tenant_routing.load.settings.field.session_bucket_rpm': '会话分桶 RPM',
  'tenant_routing.load.settings.field.session_bucket_rpm_desc': 'L4 拆分粘性键的批量会话 RPM。留空=恢复内置默认。',
  'tenant_routing.load.settings.field.pool_key_rpm_ceiling': '号池键 RPM 上限',
  'tenant_routing.load.settings.field.pool_key_rpm_ceiling_desc': 'session_pool 目标上每个亲和键每 60 秒最多拨号次数；超出后轮转子键并改写发给号池的 session id。留空=恢复内置默认（120）。',
  'tenant_routing.load.settings.group.lifecycle_sampling': 'Lifecycle 时间线采样',
  'tenant_routing.load.settings.group.lifecycle_sampling_hint':
    '采样只影响时间线（event batches），每请求汇总（metrics）不采样；设为 1 或留空即今天的行为。',
  'tenant_routing.load.settings.empty.keep_all': '全保留（=1）',
  'tenant_routing.load.settings.empty.off': '不启用',
  'tenant_routing.load.settings.field.lifecycle_sample_p_jp': 'JP 网关采样比例',
  'tenant_routing.load.settings.field.lifecycle_sample_p_jp_desc': 'JP 网关保留时间线的比例。留空=全保留（=1）。',
  'tenant_routing.load.settings.field.lifecycle_sample_p_cn': 'CN 网关采样比例',
  'tenant_routing.load.settings.field.lifecycle_sample_p_cn_desc': 'CN 网关保留时间线的比例。留空=全保留（=1）。',
  'tenant_routing.load.settings.field.lifecycle_keep_slow_ms': '慢请求保留阈值 (ms)',
  'tenant_routing.load.settings.field.lifecycle_keep_slow_ms_desc': '总时长 ≥ 阈值的成功请求 100% 保留时间线。',
  'tenant_routing.load.settings.field.lifecycle_keep_org_ids': '100% 保留时间线的 org',
  'tenant_routing.load.settings.field.lifecycle_keep_org_ids_desc':
    '失败、探针、熔断重排/多次上游尝试的请求已经无条件保留，这里只需要填排错中的 org。',
  'tenant_routing.load.settings.group.lifecycle_sampling_superseded':
    '下方「采样策略」表里某区域一旦有 lifecycle 行，该区域此处的设置就不再生效（按区域切换，见各区域的生效来源）。',
  'tenant_routing.load.sampling.title': '采样策略',
  'tenant_routing.load.sampling.hint':
    '一张表同时管 trace 正文与 lifecycle 时间线的采样。每个值逐项取最具体的非空值，空 = 沿用上一层；某流某区域没有任何行时，lifecycle 回落旧配置，trace 全量保留。车道 = SKU 所属的命名空间。',
  'tenant_routing.load.sampling.ladder': '更具体的层优先；空字段继承更宽的层',
  'tenant_routing.load.sampling.rows_count': '{count} 行',
  'tenant_routing.load.sampling.col.layer': '层级',
  'tenant_routing.load.sampling.col.target': '作用对象',
  'tenant_routing.load.sampling.target.region': '该区域的全部请求',
  'tenant_routing.load.sampling.target.lane': '车道',
  'tenant_routing.load.sampling.detail.slow': '慢请求 ≥ {value} ms 必留',
  'tenant_routing.load.sampling.detail.size': '大 trace ≥ {value} B',
  'tenant_routing.load.sampling.detail.full': '大 trace 全量 {value}',
  'tenant_routing.load.sampling.loading': '正在加载采样策略…',
  'tenant_routing.load.sampling.load_failed': '采样策略加载失败，刷新页面重试。',
  'tenant_routing.load.sampling.conflict': '采样策略已被他人修改，本次未写入。请刷新后重新编辑。',
  'tenant_routing.load.sampling.refresh': '刷新并放弃本地修改',
  'tenant_routing.load.sampling.warnings_title': '已保存，但有以下提示：',
  'tenant_routing.load.sampling.dirty': '有未保存的修改（保存后共 {count} 行，整表覆盖）',
  'tenant_routing.load.sampling.discard': '放弃修改',
  'tenant_routing.load.sampling.save': '保存采样策略',
  'tenant_routing.load.sampling.toast.saved': '已保存采样策略',
  'tenant_routing.load.sampling.add': '新增一行',
  'tenant_routing.load.sampling.edit': '编辑',
  'tenant_routing.load.sampling.remove': '删除',
  'tenant_routing.load.sampling.actions': '操作',
  'tenant_routing.load.sampling.inherit': '沿用',
  'tenant_routing.load.sampling.empty.trace': '没有行：该区域 trace 全量保留，不分大小档。',
  'tenant_routing.load.sampling.empty.lifecycle': '没有行：该区域 lifecycle 按上面的旧配置采样。',
  'tenant_routing.load.sampling.source.table': '生效：采样策略表',
  'tenant_routing.load.sampling.source.legacy': '生效：旧配置',
  'tenant_routing.load.sampling.source.keep_all': '生效：全量保留',
  'tenant_routing.load.sampling.stream.trace': 'Trace 正文',
  'tenant_routing.load.sampling.stream.lifecycle': 'Lifecycle 时间线',
  'tenant_routing.load.sampling.scope.region': '区域默认',
  'tenant_routing.load.sampling.scope.sku': 'SKU',
  'tenant_routing.load.sampling.scope.org': 'org',
  'tenant_routing.load.sampling.scope.org_sku': 'org × SKU',
  'tenant_routing.load.sampling.scope.lane': '车道',
  'tenant_routing.load.sampling.scope.org_lane': 'org × 车道',
  'tenant_routing.load.sampling.field.stream': '流',
  'tenant_routing.load.sampling.field.region': '区域',
  'tenant_routing.load.sampling.field.scope': '作用域',
  'tenant_routing.load.sampling.field.scope_hint': '区域默认不填对象；SKU 填 SKU；车道选命名空间；org 填 org；org × SKU、org × 车道各填两项。',
  'tenant_routing.load.sampling.field.lanes': '车道（SKU 命名空间）',
  'tenant_routing.load.sampling.field.lanes_hint': '可多选：每选一个车道，生成一行取值相同的策略。',
  'tenant_routing.load.sampling.field.lanes_free': '车道目录读取失败，请手动输入命名空间，多个用逗号分隔。',
  'tenant_routing.load.sampling.field.values_title': '采样取值',
  'tenant_routing.load.sampling.field.org_id': 'org ID',
  'tenant_routing.load.sampling.field.sku_id': 'SKU ID',
  'tenant_routing.load.sampling.field.note': '备注',
  'tenant_routing.load.sampling.field.keep_ratio': '保留比例',
  'tenant_routing.load.sampling.field.keep_ratio_hint': '0 到 1；0 = 只留必留请求（失败、重试、计费异常、慢请求等）。留空 = 沿用。',
  'tenant_routing.load.sampling.field.keep_slow_ms': '慢请求必留阈值 (ms)',
  'tenant_routing.load.sampling.field.keep_slow_ms_hint': '整数 ≥ 1；总时长达到阈值的请求一律保留。留空 = 沿用。',
  'tenant_routing.load.sampling.field.size_threshold_bytes': '大 trace 阈值 (bytes)',
  'tenant_routing.load.sampling.field.size_threshold_bytes_hint': '整数 ≥ 1；正文合计达到该字节数的 trace 算大 trace。留空 = 沿用。',
  'tenant_routing.load.sampling.field.large_full_keep_ratio': '大 trace 全量比例',
  'tenant_routing.load.sampling.field.large_full_keep_ratio_hint': '0 到 1；保留下来的大 trace 中带完整媒体的比例，其余去掉媒体。留空 = 沿用。',
  'tenant_routing.load.sampling.field.trace_only': '仅 trace 流可用。',
  'tenant_routing.load.sampling.error.org_required': '该作用域必须填 org ID。',
  'tenant_routing.load.sampling.error.sku_required': '该作用域必须填 SKU ID。',
  'tenant_routing.load.sampling.error.lane_required': '该作用域至少要选一个车道。',
  'tenant_routing.load.sampling.error.invalid_lane': '车道名只能含小写字母、数字和连字符，最长 64 个字符。',
  'tenant_routing.load.sampling.error.invalid_ratio': '请输入 0 到 1 之间的数，或留空沿用。',
  'tenant_routing.load.sampling.error.invalid_int': '请输入 ≥ 1 的整数，或留空沿用。',
  'tenant_routing.load.sampling.error.duplicate': '同一流 × 区域 × 作用域 × 对象已有一行，请直接编辑那一行。',
  'tenant_routing.load.sampling.dialog.add_title': '新增采样策略行',
  'tenant_routing.load.sampling.dialog.edit_title': '编辑采样策略行',
  'tenant_routing.load.sampling.dialog.hint': '应用后只改本地草稿，点「保存采样策略」才整表写入。',
  'tenant_routing.load.sampling.dialog.apply': '应用到草稿',
  'tenant_routing.load.sampling.legacy.active': '当前生效的旧配置：',
  'tenant_routing.load.sampling.legacy.superseded': '旧配置（采样策略表已生效；表里未填写的字段仍沿用这些值）：',
  'tenant_routing.load.sampling.legacy.values': '采样比例 {p} · 慢请求阈值 {slow} ms · 必留 org {orgs}',
  'tenant_routing.load.sampling.legacy.unset': '未设',
  'tenant_routing.load.sampling.write_gate.title': '暂不能保存：CN 下行复制尚未就绪，此时写入会卡住 CN 复制。原因与核对方法：',
  'tenant_routing.load.sampling.confirm.title': '确认带着这些提示保存？',
  'tenant_routing.load.sampling.confirm.body': '本次保存的预检（dry run）发现以下情况，目前尚未写入任何内容。',
  'tenant_routing.load.sampling.confirm.save': '仍然保存',
  'tenant_routing.load.sampling.confirm.back': '返回修改',
  'request_lifecycle.timeline.unsampled': '未采样（p={p}）',
  'request_lifecycle.timeline.kept': '已保留（p={p}）',
  'request_lifecycle.not_recorded': '未记录生命周期：成功请求按比例采样写入，或已过保留期。用量与计费以请求行为准。',
  'request_lifecycle.column_unsampled': '无生命周期行（未采样或已过保留期）',
  'request_lifecycle.filter_scope_note': '在途（started）只覆盖有生命周期行的请求（成功请求按比例采样）',
  'tenant_routing.load.settings.field.sticky_escape_ttft_ms': '粘性逃逸首字节（毫秒）',
  'tenant_routing.load.settings.field.sticky_escape_ttft_ms_desc': 'L4 允许逃离粘性目标的首字节门槛。留空=关闭。',
  'tenant_routing.load.settings.field.sticky_escape_error_rate': '粘性逃逸错误率',
  'tenant_routing.load.settings.field.sticky_escape_error_rate_desc': 'L4 允许逃离粘性目标的错误率门槛。留空=关闭。',
  'tenant_routing.load.settings.field.attempt_first_byte_timeout_ms': '首字节超时基值（毫秒）',
  'tenant_routing.load.settings.field.attempt_first_byte_timeout_ms_desc': '每次尝试等待首字节的基值。留空=关闭。',
  'tenant_routing.load.settings.field.attempt_timeout_ttft_multiplier': '超时相对平滑倍数',
  'tenant_routing.load.settings.field.attempt_timeout_ttft_multiplier_desc': '自适应首字节超时 = 该倍数 × 目标首字节平滑值。留空=恢复内置默认。',
  'tenant_routing.load.settings.field.attempt_timeout_max_multiplier': '超时上限倍数',
  'tenant_routing.load.settings.field.attempt_timeout_max_multiplier_desc': '自适应首字节超时相对基值的上限倍数。留空=恢复内置默认。',
  'tenant_routing.load.runtime.title': '负载感知运行态',
  'tenant_routing.load.runtime.loading': '加载运行态…',
  'tenant_routing.load.runtime.empty': '当前没有目标用量。打开层之后这里会动。',
  'tenant_routing.load.runtime.table.identity': '目标',
  'tenant_routing.load.runtime.table.rpm': 'RPM',
  'tenant_routing.load.runtime.table.tpm': 'TPM',
  'tenant_routing.load.runtime.table.cap': '上限',
  'tenant_routing.load.runtime.table.shed': '分流',
  'tenant_routing.load.runtime.table.breaker': '熔断',
  'tenant_routing.load.runtime.table.ttft': '首字节平滑',
  'tenant_routing.load.runtime.table.rehomed': '改挂会话',
  'tenant_routing.load.runtime.table.r429_rehomed': '改挂 429',
  'tenant_routing.load.runtime.cap_source.configured': '已配置',
  'tenant_routing.load.runtime.cap_source.observed': '观测',
  'tenant_routing.load.runtime.cap_source.inferred': '自学习',
  'tenant_routing.load.runtime.cap_source.min': '取最小',
  'tenant_routing.load.runtime.confidence': '置信 {value}',
  'tenant_routing.load.runtime.unlimited': '不限',
  'tenant_routing.load.runtime.breaker.closed': '闭合',
  'tenant_routing.load.runtime.breaker.open': '打开',
  'tenant_routing.load.runtime.breaker.half_open': '半开',
  'tenant_routing.load.runtime.breaker.forced': '强制摘除',
  'tenant_routing.load.runtime.table.layers': '生效层',
  'tenant_routing.load.runtime.layers.sku': 'SKU',
  'tenant_routing.load.runtime.layers.namespace': '车道',
  'tenant_routing.load.runtime.layers.global': '全局',
  'tenant_routing.load.runtime.layers.unknown': '未知（旧快照）',
  'tenant_routing.load.scope.title': '按车道 / SKU 开关',
  'tenant_routing.load.scope.hint': '先按车道打开，再用 SKU 例外收窄或关掉。空着的字段跟随上一层：SKU → 车道 → 全局。环境变量强制关闭仍优先。',
  'tenant_routing.load.scope.loading': '加载作用域设置…',
  'tenant_routing.load.scope.load_failed': '作用域设置加载失败，请稍后重试。',
  'tenant_routing.load.scope.lanes_title': '车道快捷开关',
  'tenant_routing.load.scope.lanes_hint': '一行一个平台车道。一键开启会打开该车道的负载感知层；也可按字段三态细调。',
  'tenant_routing.load.scope.lanes_empty': '还没有车道。',
  'tenant_routing.load.scope.skus_title': 'SKU 例外',
  'tenant_routing.load.scope.skus_hint': '只列出已经写下的 SKU 作用域行。未列出的 SKU 跟随其车道或全局。',
  'tenant_routing.load.scope.skus_empty': '还没有 SKU 例外。',
  'tenant_routing.load.scope.add_sku': '添加 SKU 例外',
  'tenant_routing.load.scope.add_sku_title': '添加 SKU 例外',
  'tenant_routing.load.scope.add_sku_pick': '选择 SKU',
  'tenant_routing.load.scope.add_sku_search': '按名称或 ID 筛选',
  'tenant_routing.load.scope.add_sku_confirm': '添加',
  'tenant_routing.load.scope.add_sku_none': '没有可添加的 SKU。',
  'tenant_routing.load.scope.enable_lane': '一键开负载感知',
  'tenant_routing.load.scope.enable_lane_title': '为该车道开启负载感知？',
  'tenant_routing.load.scope.enable_lane_body': '将写入以下字段：',
  'tenant_routing.load.scope.enable_lane_confirm': '开启',
  'tenant_routing.load.scope.restore': '恢复跟随全局',
  'tenant_routing.load.scope.restore_title': '恢复跟随全局？',
  'tenant_routing.load.scope.restore_body': '删除这一行作用域覆盖，之后跟随上一层。',
  'tenant_routing.load.scope.restore_confirm': '恢复',
  'tenant_routing.load.scope.tri.follow': '跟随',
  'tenant_routing.load.scope.tri.on': '开',
  'tenant_routing.load.scope.tri.off': '关',
  'tenant_routing.load.scope.effective': '生效',
  'tenant_routing.load.scope.source.sku': 'SKU',
  'tenant_routing.load.scope.source.namespace': '车道',
  'tenant_routing.load.scope.source.global': '全局',
  'tenant_routing.load.scope.toast.saved': '作用域设置已保存',
  'tenant_routing.load.scope.toast.enabled': '已为该车道开启负载感知',
  'tenant_routing.load.scope.toast.restored': '已恢复跟随全局',
  'tenant_routing.load.scope.toast.sku_added': '已添加 SKU 例外',
  'tenant_routing.target.rpm_cap': 'RPM 上限',
  'tenant_routing.target.tpm_cap': 'TPM 上限',
  'tenant_routing.target.rpm_cap_hint': '可选。留空则交给观测或自学习上限。',
  'tenant_routing.target.tpm_cap_hint': '可选。留空则交给观测或自学习上限。',
  'tenant_routing.error.cap': '上限须为正整数，或留空表示不配。',
  'tenant_routing.smart.mode': '路由模式',
  'tenant_routing.smart.mode.manual': '手动',
  'tenant_routing.smart.mode.smart': '智能',
  'tenant_routing.smart.role': '角色',
  'tenant_routing.smart.role.self_pool': '自有号池',
  'tenant_routing.smart.role.external': '外部',
  'tenant_routing.smart.role.reserve': '预备',
  'tenant_routing.smart.role.unknown': '—',
  'tenant_routing.smart.cost_multiplier': '采购倍率',
  'tenant_routing.smart.missing_cost': '缺成本行',
  'tenant_routing.smart.no_runtime': '未学习',
  'tenant_routing.smart.initial_score': '初始评分',
  'tenant_routing.smart.initial_score.empty': '—',
  'tenant_routing.smart.initial_score_hint': '0–1，可空。空表示无先验。',
  'tenant_routing.smart.readonly': '只读',
  'tenant_routing.smart.settings_loading': '正在读取路由模式…',
  'tenant_routing.smart.settings_error': '读取路由模式失败。',
  'tenant_routing.smart.restore_default': '清空回默认',
  'tenant_routing.smart.error.score': '初始评分须为 0–1，或留空。',
  'tenant_routing.smart.enabled': '启用',
  'tenant_routing.smart.exploration_budget': '探索预算',
  'tenant_routing.smart.exploration_budget_hint': '美元/天。',
  'tenant_routing.smart.refire_budget': '补拨预算',
  'tenant_routing.smart.refire_budget_hint': '每目标每分钟补拨占拨号量上限（%）。',
  'tenant_routing.smart.retire_after_days': '退役天数',
  'tenant_routing.smart.retire_after_days_hint': '后验持续低于阈值的天数。',
  'tenant_routing.smart.retire_below': '后验阈值',
  'tenant_routing.smart.retire_below_hint': '后验均值低于该值且达到退役天数后自动退役（0–1）。',
  'tenant_routing.smart.save_settings': '保存智能设置',
  'tenant_routing.smart.stability': '稳定',
  'tenant_routing.smart.stability_hint': '成功率权重。100 为满权。',
  'tenant_routing.smart.economy': '省钱',
  'tenant_routing.smart.economy_hint': '采购成本权重。0 表示成本不参与。',
  'tenant_routing.smart.speed': '速度',
  'tenant_routing.smart.speed_hint': '过慢 TTFT（超过 2 秒）的权重。0 表示延迟不参与。',
  'tenant_routing.smart.slider_default': '默认',
  'tenant_routing.smart.error.slider': '须为 0–100 的整数，留空则用默认。',
  'tenant_routing.smart.toast_mode': '路由模式已更新',
  'tenant_routing.smart.toast_settings': '智能路由设置已保存',
  'tenant_routing.smart.error.number': '须为非负数。',
  'tenant_routing.smart.error.percent': '须为 0–100 的百分比。',
  'tenant_routing.smart.error.days': '须为正整数天数。',
  'tenant_routing.smart.error.threshold': '须为 0–1 的后验阈值。',
  'tenant_routing.smart.overview.title': '学习状态',
  'tenant_routing.smart.overview.dials': '接客数 1m/1h/24h',
  'tenant_routing.smart.overview.share': '份额 1m/1h/24h',
  'tenant_routing.smart.overview.first_dial': '首发成功率 1m/1h/24h',
  'tenant_routing.smart.overview.refire_saved': '补拨救回率',
  'tenant_routing.smart.overview.ttft': 'TTFT p50/p95',
  'tenant_routing.smart.overview.cost_per_1k': '每千 token 成本',
  'tenant_routing.smart.overview.posterior': '后验',
  'tenant_routing.smart.overview.probe': '探针',
  'tenant_routing.smart.overview.state': '状态',
  'tenant_routing.smart.state.warming': '热身',
  'tenant_routing.smart.state.serving': '服役',
  'tenant_routing.smart.state.quarantined': '隔离',
  'tenant_routing.smart.state.half_open': '半开',
  'tenant_routing.smart.state.retired': '退役',
  'tenant_routing.smart.warning.self_pool_unprobeable':
    '号池目标无法探针，恢复靠真实流量涓流。',
  'tenant_routing.smart.overview.empty': '尚无学习样本。',
  'tenant_routing.smart.overview.loading': '正在读取学习状态…',
  'tenant_routing.smart.overview.error': '读取学习状态失败。',
  'tenant_routing.smart.overview.stale': '数据可能过期',
  'tenant_routing.smart.overview.host': '主机 {host}',
  'tenant_routing.smart.overview.default_pool': '默认池',
  'tenant_routing.smart.overview.config': '{name} ({id})',
  'tenant_routing.smart.overview.folded':
    '另有 {configs} 个配置（{targets} 条目标）已折叠，仅列出系统默认、默认池和已分配给组织的配置。',
  'tenant_routing.smart.chart.title': '份额随时间',
  'tenant_routing.smart.chart.empty': '还没有按分钟的决策样本。',
  'tenant_routing.smart.reset': '重置学习',
  'tenant_routing.smart.reset_title': '重置该 SKU 的学习状态？',
  'tenant_routing.smart.reset_body': '将清除该 SKU 全部运行态后验与份额记忆，冷启动重新开始。开关、预算和目标配置保留。',
  'tenant_routing.smart.reset_confirm': '重置',
  'tenant_routing.smart.reset_type_label': '输入该 SKU 名称以确认',
  'tenant_routing.smart.toast_reset': '学习状态已重置',
  'tenant_routing.smart.probe.ok': '成功',
  'tenant_routing.smart.probe.fail': '失败',
  // Route check drawer
  'tenant_routing.effective.title': '有效路由检查',
  'tenant_routing.effective.body': '辅助检查单个 org + SKU 的最终命中来源。',
  'tenant_routing.effective.check': '检查',
  'tenant_routing.effective.sku_chain_hint': '仅检查 SKU 分区：组织指派配置 → SKU 目标池，失败重试按重试顺序、权重、优先级继续。',
  'tenant_routing.effective.no_sku_target': '该 SKU 分区没有可用目标',
  'tenant_routing.effective.route_config_ref': '{name}（{id}）',
  'tenant_routing.field.sku': 'SKU',
  'tenant_routing.field.protocol': '协议',
  'tenant_routing.source.emergency_override': '紧急覆盖 — 影响全部租户',
  'tenant_routing.source.global_default': '全局默认单例',
  'tenant_routing.source.legacy_fallback': '历史回退绑定',
  'tenant_routing.effective.gap_uncovered_by_emergency_override':
    '生效中的紧急覆盖不包含 {sku}。第 0 层是 fail-closed，因此该 SKU 目前对全部租户都在失败——请扩充 {config} 或解除覆盖。',
  'tenant_routing.effective.gap_uncovered_by_assigned_config':
    '已分配的配置 {config} 不包含 {sku}，且该配置是 exclusive，因此相关客户的该 SKU 没有任何候选。',
  'tenant_routing.source.route_config': '路由配置',
  'tenant_routing.source.org_sku': '组织 SKU 路由',
  'tenant_routing.source.org_override': '组织 SKU 覆盖',
  'tenant_routing.source.sku_default': 'SKU 默认路由',
  'tenant_routing.source.none': '无可用路由',
  // Toasts + errors
  'tenant_routing.toast.action.view_upstream': '查看上游',
  'tenant_routing.toast.policy_saved': '渠道策略已保存',
  'tenant_routing.error.default_required': '请选择平台渠道、上游、账户、API 密钥和上游渠道。',
  'tenant_routing.error.default_weight': '权重必须是非负整数。',
  'tenant_routing.error.retry_order': '重试顺序必须是正整数或留空。',
  'tenant_routing.error.default_routability_pending': '请等待已验证目标检查完成。',
  'tenant_routing.error.default_not_routable': '请选择对当前 SKU 具有可用能力的上游渠道。',
  'tenant_routing.error.effective_required': '请填写 org，并选择 SKU 和协议。',

  // ── Dashboard (Mission Control) ─────────────────────────────────
  'dashboard.title': '任务控制台',
  'dashboard.subtitle': '按上游分组的路由流量',
  'dashboard.window.label': '时间窗口',
  'dashboard.window.7d': '最近 7 天',
  'dashboard.window.30d': '最近 30 天',
  'dashboard.window.90d': '最近 90 天',
  'dashboard.stat.upstreams.label': '活跃上游',
  'dashboard.stat.upstreams.delta': '最近 {days} 天内出现',
  'dashboard.stat.requests.label': '总请求数',
  'dashboard.stat.requests.delta': '{days} 天窗口累计',
  'dashboard.stat.failure_rate.label': '失败率',
  'dashboard.stat.failure_rate.delta': 'HTTP 4xx/5xx 占比',
  'dashboard.stat.cost.label': '开销',
  'dashboard.stat.cost.delta': '网关累计费用',
  'dashboard.loading_aria': '正在加载路由统计…',
  'dashboard.error.title': '无法加载路由统计',
  'dashboard.empty.title': '当前窗口暂无流量',
  'dashboard.empty.body_prefix': '最近 {days} 天 ',
  'dashboard.empty.body_suffix': ' 中无记录。请放宽窗口或确认网关正在接收请求。',
  'dashboard.column.upstream': '上游',
  'dashboard.column.requests': '请求数',
  'dashboard.column.failure_rate': '失败率',
  'dashboard.column.latency': 'p95(均值)',
  'dashboard.column.cost': '费用',
  'dashboard.row.deleted_upstream': '已删除上游',
  'dashboard.latency_note': '延迟列目前为网关中 p95 的 AVG 近似(参见 router-stats 契约)。',

  // ── Upstreams list ──────────────────────────────────────────────
  'upstreams.title': '上游',
  'upstreams.subtitle': '管理从网关路由出去的 forward + pool + BYOK 供应源。',
  'upstreams.search.placeholder': '搜索名称、端点…',
  'upstreams.search.aria_label': '搜索上游',
  'upstreams.new': '新建上游',
  'upstreams.new.title': '启动 4 步上游接入向导:基础信息 → 挂模型 → 登记资金 → 探针自检',
  'upstreams.error.title': '无法加载上游',
  'upstreams.loading_aria': '正在加载上游…',
  'upstreams.empty.title': '暂无已配置的上游',
  'upstreams.empty.search_body': '没有匹配此搜索的上游。清空筛选或创建新条目。',
  'upstreams.empty.initial_body':
    '通过 admin bootstrap 流程(网关 /internal/upstreams)创建第一个上游,方可开始路由流量。',
  'upstreams.stats.total': '总上游',
  'upstreams.stats.enabled': '已启用',
  'upstreams.stats.accounts': '账户总数',
  'upstreams.stats.funding': '活跃资金',
  'upstreams.column.name': '名称',
  'upstreams.column.endpoint': '端点',
  'upstreams.column.source': '来源码',
  'upstreams.column.status': '状态',
  'upstreams.column.accounts': '账户',
  'upstreams.column.models': '模型数',
  'upstreams.column.funding': '资金',
  'upstreams.column.priority': '优先级',
  'upstreams.column.actions': '操作',
  'upstreams.status.enabled': 'enabled',
  'upstreams.status.disabled': 'disabled',
  'upstreams.row.copy_endpoint': '复制端点 URL',
  'upstreams.row.probe': '探针',
  'upstreams.row.edit': '编辑',
  'upstreams.row.delete': '删除',
  'upstreams.delete_modal.title': '软删除上游?',
  'upstreams.delete_modal.body_suffix':
    ' 将被标记为已删除(网关停止向其路由)。需通过后续迁移修改 `deleted_at` 才可恢复。',

  // ── Upstream detail ─────────────────────────────────────────────
  'upstream_detail.error.title': '无法加载上游',
  'upstream_detail.back': '返回上游列表',
  'upstream_detail.breadcrumb.upstreams': '上游',
  'upstream_detail.meta.kind': '类型 · {kind}',
  'upstream_detail.chip.enabled': '已启用',
  'upstream_detail.chip.disabled': '已禁用',
  'upstream_detail.config.title': '配置',
  'upstream_detail.config.display_name': '显示名称',
  'upstream_detail.config.endpoint_url': '端点 URL',
  'upstream_detail.config.priority': '优先级',
  'upstream_detail.config.enabled': '启用',
  'upstream_detail.config.auth_secret': '已存储密钥',
  'upstream_detail.config.auth_secret.write_only': '密钥已写入(只写不可回显)',
  'upstream_detail.config.auth_secret.empty': '尚未写入密钥',
  'upstream_detail.config.auth_secret.hint':
    '网关会将此密钥加密存储。运维可轮换，但不能从控制台读回原值。异步任务链路轮询上游任务状态时使用的就是这份凭据。',
  'upstream_detail.config.auth_secret.rotate': '轮换密钥',
  'upstream_detail.config.auth_secret.rotate_placeholder': '粘贴新的上游凭据',
  'upstream_detail.config.session_pool': '账号池（按会话选号）',
  'upstream_detail.config.session_pool.state.enabled': '该上游按客户端会话选号；会话级分流只在这里生效。',
  'upstream_detail.config.session_pool.state.disabled': '不是账号池。会话级分流不会改写这个上游的流量。',
  'upstream_detail.config.session_pool.hint': '标记后，负载感知可以把连续 429 的会话改挂到备用目标，而不抬高整个池的分流。',
  'upstream_detail.config.cookie_passthrough': 'Cookie 透传',
  'upstream_detail.config.cookie_passthrough.state.enabled': '该上游会收到租户原始 Cookie。',
  'upstream_detail.config.cookie_passthrough.state.disabled': '网关会在出站前剥离租户 Cookie。',
  'upstream_detail.config.cookie_passthrough.hint':
    '除非上游明确依赖租户 Cookie 维持鉴权或会话，否则保持关闭。',
  'upstream_detail.config.request_gzip_ratio': '出站请求 gzip 比例 (0–1)',
  'upstream_detail.config.request_gzip_ratio.hint':
    '对该上游的生成请求，按这个比例把合格的 JSON 请求体 gzip。0 表示完全不压缩。',
  'upstream_detail.config.state.enabled': '上游处于启用状态',
  'upstream_detail.config.state.disabled': '上游处于禁用状态',
  'upstream_detail.config.model_discovery': '模型发现',
  'upstream_detail.config.model_discovery.hint':
    '决定“拉取模型”如何获得该上游的模型清单。所有字段作为一个整体保存。',
  'upstream_detail.config.model_discovery.save': '保存模型发现配置',
  'upstream_detail.config.model_discovery.saved': '模型发现配置已保存',
  // ── Model discovery (shared: wizard step 1 + detail config card, #1658) ──
  'model_discovery.mode.label': '模型发现方式',
  'model_discovery.mode.compatible': 'compatible · 协议默认列表',
  'model_discovery.mode.list': 'list · 自定义列表接口',
  'model_discovery.mode.manual': 'manual · 手动导入',
  'model_discovery.mode.compatible.hint':
    '沿用协议推导的 /v1/models 或 /v1beta/models 列表接口。',
  'model_discovery.mode.manual.hint':
    '不请求远端列表。在“拉取模型”里手动粘贴模型 ID 清单。',
  'model_discovery.path.label': '列表路径',
  'model_discovery.path.hint':
    '相对端点源 (origin) 的绝对路径，可带查询串，如 /v1/models?type=text',
  'model_discovery.response_path.label': '响应字段路径',
  'model_discovery.response_path.hint':
    '指向模型数组的 JSON 键，多级用 . 分隔（1 到 8 段），如 data 或 result.models',
  'model_discovery.auth.label': '鉴权携带方式',
  'model_discovery.error.path_required': '请填写列表路径（以 / 开头的同源路径）',
  'model_discovery.error.path_format':
    '列表路径必须以单个 / 开头、不含 # 片段、长度不超过 2048 字符',
  'model_discovery.error.path_origin': '列表路径必须解析到该上游端点的同一 origin',
  'model_discovery.error.endpoint_absolute': 'list 模式要求端点 URL 是绝对 URL',
  'model_discovery.error.response_path':
    '响应字段路径需要 1 到 8 段非空 JSON 键，每段不超过 128 字符',
  'model_discovery.error.auth': '鉴权携带方式必须是 anthropic、bearer 或 google-api-key',
  'model_discovery.error.mode': '模型发现模式必须是 compatible、list 或 manual',
  'model_discovery.error.mode_fields': '当前模式不接受 list 配置字段，请先切换到 list 模式',
  'model_discovery.error.manual_models_required': '手动模式需要提供非空的模型 ID 清单',
  'model_discovery.error.manual_protocol_required':
    '该渠道尚未归类协议，手动导入必须显式选择协议',
  'model_discovery.error.models_limit':
    '单批最多提交 200 个模型 ID，且每个 ID 为 1 到 512 字符',
  'model_discovery.error.limit_range': 'limit 必须是 1 到 200 的整数',
  'model_discovery.error.list_exceeds': '上游返回的模型清单超过 200 条上限',
  'upstream_detail.probe.title': '探针',
  'upstream_detail.probe.hint': '结果仅在本次会话内保留',
  'upstream_detail.probe.vendor': '厂商',
  'upstream_detail.probe.vendor_placeholder': '选择厂商',
  'upstream_detail.probe.channel': '渠道',
  'upstream_detail.probe.channel_placeholder': '选择渠道',
  'upstream_detail.probe.model': '模型',
  'upstream_detail.probe.select.placeholder': '选择模型',
  'upstream_detail.probe.submit': '探测 {name}',
  'upstream_detail.probe.submitting': '探测中…',
  'upstream_detail.probe.error.select_model': '请先选择厂商、渠道和模型',
  'upstream_detail.probe_history.title': '探测历史',
  'upstream_detail.probe_history.body': '当前上游的已持久化探测结果。新探测完成后自动刷新。',
  'upstream_detail.probe_history.count': '最近 {count} 条',
  'upstream_detail.probe_history.loading': '正在加载探测历史…',
  'upstream_detail.probe_history.error': '加载探测历史失败。',
  'upstream_detail.probe_history.empty': '暂无探测记录。',
  'upstream_detail.probe_history.column.time': '时间 (本地)',
  'upstream_detail.probe_history.column.model': '厂商 / 模型',
  'upstream_detail.probe_history.column.outcome': '结果',
  'upstream_detail.probe_history.column.latency': '延迟',
  'upstream_detail.probe_history.column.cost': '成本 (USD)',
  'upstream_detail.probe_history.column.status': 'HTTP',
  // ── Upstream detail · model SKU table (epic #1327 PR-5) ─────────
  'upstream_detail.routing.title': '上游模型 SKU',
  'upstream_detail.routing.subtitle':
    '按上游命名空间管理已发现的模型：协议归类、探测、启用状态与采购倍率。平台 SKU 的绑定与路由在“模型市场”的 SKU 详情页完成。',
  'upstream_detail.routing.channels.empty': '该上游暂无可用命名空间。先到“渠道”页创建并启用一个渠道。',
  'upstream_detail.routing.load_error': '无法加载上游模型：{message}',
  'upstream_detail.routing.namespace.disabled': '已停用',
  'upstream_detail.routing.namespace.stale': '历史证据',
  'upstream_detail.routing.namespace.count': '{count} 个模型',
  'upstream_detail.routing.namespace.empty': '该命名空间下暂无模型。点击“拉取模型”开始发现。',
  'upstream_detail.routing.discover': '拉取模型',
  'upstream_detail.routing.discovering': '正在拉取…',
  'upstream_detail.routing.discover.blocked': '命名空间已停用，无法拉取模型。',
  'upstream_detail.routing.discover.failed': '拉取模型失败：{message}。下方保留上一次发现证据。',
  'upstream_detail.routing.manual.title': '手动导入模型 · {channel}',
  'upstream_detail.routing.manual.body':
    '该上游为手动发现模式：粘贴模型 ID（一行一个或用逗号分隔）。超过 200 个会自动分批串行提交。',
  'upstream_detail.routing.manual.models.label': '模型 ID 清单',
  'upstream_detail.routing.manual.protocol.label': '协议',
  'upstream_detail.routing.manual.protocol.placeholder': '选择协议',
  'upstream_detail.routing.manual.parsed': '已解析 {count} 个模型 ID。',
  'upstream_detail.routing.manual.batches': '将分 {count} 批提交（每批最多 200 个）。',
  'upstream_detail.routing.manual.progress': '正在提交第 {current}/{total} 批…',
  'upstream_detail.routing.manual.submit': '导入模型',
  'upstream_detail.routing.manual.submitting': '导入中…',
  'upstream_detail.routing.manual.error.empty': '请先粘贴至少一个模型 ID',
  'upstream_detail.routing.manual.error.too_long': '存在超过 512 字符的模型 ID，请修正后重试',
  'upstream_detail.routing.manual.error.protocol': '请选择协议',
  'upstream_detail.routing.manual.partial':
    '第 {failed}/{total} 批提交失败：{message} 前 {succeeded} 批（{models} 个模型）已成功导入并保留。修正后可直接重新导入：重复的模型会被覆盖写入而非重复创建，全量重发是安全的。',
  'upstream_detail.routing.col.model': '模型',
  'upstream_detail.routing.col.protocol': '协议归类',
  'upstream_detail.routing.col.probe': '探测状态',
  'upstream_detail.routing.col.enabled': '启用',
  'upstream_detail.routing.col.cost': '采购倍率',
  'upstream_detail.routing.col.serving': '服务中的平台 SKU',
  'upstream_detail.routing.model.canonical': 'canonical {vendor} / {model}',
  'upstream_detail.routing.model.key_bucket': '密钥 {key}',
  'upstream_detail.routing.protocol.unclassified': '未归类',
  'upstream_detail.routing.protocol.label': '{model} 的协议归类',
  'upstream_detail.routing.probe.run': '探测',
  'upstream_detail.routing.probe.rerun': '重新探测',
  'upstream_detail.routing.probe.running': '探测中…',
  'upstream_detail.routing.probe.waiting': '等待结果…',
  'upstream_detail.routing.probe.classify_first': '先完成协议归类',
  'upstream_detail.routing.probe.last_passed': '最近通过 {time}',
  'upstream_detail.routing.enable.label': '启用模型 {model}',
  'upstream_detail.routing.enable.on': '已启用',
  'upstream_detail.routing.enable.off': '已停用',
  'upstream_detail.routing.cost.label': '{model} 的采购倍率',
  'upstream_detail.routing.cost.save': '保存',
  'upstream_detail.routing.cost.saving': '保存中…',
  'upstream_detail.routing.cost.explicit': '×{value}',
  'upstream_detail.routing.cost.missing': '未定价',
  'upstream_detail.routing.cost.unavailable': '倍率不可用',
  'upstream_detail.routing.cost.invalid': '倍率必须大于 0 且不超过 {max}',
  'upstream_detail.routing.cost.load_error': '无法读取采购倍率：{message}',
  'upstream_detail.routing.cost.save_error': '保存采购倍率失败：{message}',
  'upstream_detail.routing.cost.saved': '已保存 {model} 的采购倍率',
  'upstream_detail.routing.cost.unconfirmed': '上游未回执该模型的倍率，请重试。',
  'upstream_detail.routing.cost.windows.configure': '窗口',
  'upstream_detail.routing.cost.windows.none': '无窗口 / 全天基础倍率',
  'upstream_detail.routing.cost.windows.active': '生效中：{label}',
  'upstream_detail.routing.cost.windows.base': '基础 ×{value}',
  'upstream_detail.routing.cost.windows.effective': '此刻 ×{value}',
  'upstream_detail.routing.cost.windows.dialog_title': '{model} 的峰谷窗口',
  'upstream_detail.routing.cost.windows.dialog_description': '保存会整组替换该模型的窗口。空列表会清空全部窗口，基础倍率全天生效。',
  'upstream_detail.routing.cost.windows.add': '添加窗口',
  'upstream_detail.routing.cost.windows.remove': '删除窗口',
  'upstream_detail.routing.cost.windows.label': '标签',
  'upstream_detail.routing.cost.windows.timezone': '时区',
  'upstream_detail.routing.cost.windows.days': '星期',
  'upstream_detail.routing.cost.windows.day.mon': '一',
  'upstream_detail.routing.cost.windows.day.tue': '二',
  'upstream_detail.routing.cost.windows.day.wed': '三',
  'upstream_detail.routing.cost.windows.day.thu': '四',
  'upstream_detail.routing.cost.windows.day.fri': '五',
  'upstream_detail.routing.cost.windows.day.sat': '六',
  'upstream_detail.routing.cost.windows.day.sun': '日',
  'upstream_detail.routing.cost.windows.start': '开始',
  'upstream_detail.routing.cost.windows.end': '结束',
  'upstream_detail.routing.cost.windows.cross_midnight': '结束时刻早于开始时刻 = 跨到次日。',
  'upstream_detail.routing.cost.windows.multiplier': '窗口倍率',
  'upstream_detail.routing.cost.windows.advanced': '高级：分项倍率',
  'upstream_detail.routing.cost.windows.input': '输入',
  'upstream_detail.routing.cost.windows.output': '输出',
  'upstream_detail.routing.cost.windows.cache_read': '缓存读',
  'upstream_detail.routing.cost.windows.cache_write': '缓存写',
  'upstream_detail.routing.cost.windows.reasoning': '推理',
  'upstream_detail.routing.cost.windows.enabled': '启用',
  'upstream_detail.routing.cost.windows.save': '保存窗口',
  'upstream_detail.routing.cost.windows.cancel': '取消',
  'upstream_detail.routing.cost.windows.empty': '还没有窗口。基础倍率全天生效。',
  'upstream_detail.routing.cost.windows.need_price': '先保存基础倍率，再配置窗口。',
  'upstream_detail.routing.cost.windows.close': '关闭',
  'upstream_detail.routing.cost.windows.days_required': '至少选择一天',
  'upstream_detail.routing.cost.windows.label_required': '填写名称',
  'upstream_detail.routing.cost.windows.timezone_required': '填写时区',
  'upstream_detail.routing.cost.windows.clock_invalid': '时刻须为 HH:MM（结束可为 24:00）',
  'upstream_detail.routing.cost.windows.start_end_equal': '开始与结束不能是同一时刻',
  'upstream_detail.routing.cost.windows.start_not_end_of_day': '24:00 只能作为结束时刻',
  'upstream_detail.routing.cost.windows.multiplier_invalid': '须为大于 0 且不超过 {max} 的数字',
  'upstream_detail.routing.serving.count': '服务 {count} 个平台 SKU',
  'upstream_detail.routing.serving.unknown': '未知',
  'upstream_detail.routing.serving.open_market': '前往模型市场',
  'upstream_detail.routing.serving.error': '无法读取服务中的平台 SKU：{message}',
  'upstream_detail.routing.serving.truncated': '路由摘要已限量（{loaded}/{total}），服务数量暂不可信。',
  'upstream_detail.routing.bulk.selected': '已选 {count} 个模型',
  'upstream_detail.routing.bulk.select_all': '全选可见模型',
  'upstream_detail.routing.bulk.select_row': '选择模型 {model}',
  'upstream_detail.routing.bulk.probe': '批量探测',
  'upstream_detail.routing.bulk.probing': '探测中…',
  'upstream_detail.routing.bulk.protocol': '归类为 {protocol}',
  'upstream_detail.routing.bulk.protocol_setting': '归类中…',
  'upstream_detail.routing.bulk.enable': '批量启用',
  'upstream_detail.routing.bulk.enabling': '启用中…',
  'upstream_detail.routing.bulk.disable': '批量停用',
  'upstream_detail.routing.bulk.disabling': '停用中…',
  'upstream_detail.routing.bulk.clear': '清除选择',
  'upstream_detail.routing.toast.models_loaded': '已拉取 {count} 个模型',
  'upstream_detail.routing.toast.probe_queued': '探测任务已排队',
  'upstream_detail.routing.toast.bulk_probe_queued': '已为 {count} 个模型排队探测',
  'upstream_detail.routing.toast.protocol_set': '已将 {count} 个模型归类为 {protocol}',
  'upstream_detail.routing.toast.enabled': '模型已启用',
  'upstream_detail.routing.toast.disabled': '模型已停用',
  'upstream_detail.routing.toast.bulk_enabled': '已启用 {count} 个模型',
  'upstream_detail.routing.toast.bulk_disabled': '已停用 {count} 个模型',
  'upstream_detail.routing.impact.ignore.title': '确认停用上游模型',
  'upstream_detail.routing.impact.unignore.title': '确认启用上游模型',
  'upstream_detail.routing.impact.subtitle': '预览与实际写入使用同一组 exact capability IDs；实际写入仍会在锁内重新校验。',
  'upstream_detail.routing.impact.exact_ids': 'Exact capability IDs',
  'upstream_detail.routing.impact.protected': '该操作会影响受保护的 SKU 目标，已禁用确认。先处理下列精确引用；后台仍会返回 409 防止竞态。',
  'upstream_detail.routing.impact.truncated_protected': '影响预览已限量（SKU 目标展示 {skuShown}/{skuTotal}）；完整审阅所有受保护引用前禁止停用。',
  'upstream_detail.routing.impact.safe': '当前预览未发现受保护引用。',
  'upstream_detail.routing.impact.examples': '展示 {shown}/{total} 条精确引用',
  'upstream_detail.routing.impact.confirm_ignore': '确认停用所选模型',
  'upstream_detail.routing.impact.confirm_unignore': '确认启用所选模型',
  'upstream_detail.routing.impact.scope.default': 'Default',
  'upstream_detail.routing.impact.scope.config': 'Config',
  'upstream_detail.routing.impact.scope.org': 'Org',
  'upstream_detail.routing.impact.scope.org_override': 'Org override',
  'upstream_detail.funding.new': '+ 新建批次',
  'upstream_detail.funding.loading_aria': '正在加载资金批次…',
  'upstream_detail.funding.error.load': '无法加载资金批次',
  'upstream_detail.funding.empty': '尚未登记资金批次。按需新增以记录当前余额或预付额度。',
  'upstream_detail.funding.account.unassigned': '未关联账户',
  'upstream_detail.funding.form.account.label': '关联账户',
  'upstream_detail.funding.form.account.none': '无账户',
  'upstream_detail.funding.register.title': '登记资金批次',
  'upstream_detail.funding.register.submit': '登记批次',
  'upstream_detail.funding.register.submitting': '保存中…',
  'upstream_detail.funding.batch.mode_rate': '模式 · {mode} · 汇率 {rate}',
  'upstream_detail.funding.batch.chip.committed': '已入库',
  'upstream_detail.funding.batch.chip.draft': '未入库',
  'upstream_detail.funding.batch.chip.active': '活跃',
  'upstream_detail.funding.batch.chip.depleted': '已耗尽',
  'upstream_detail.funding.batch.chip.correction': '对账修正',
  'upstream_detail.funding.batch.chip.alert_95': '警告 95%',
  'upstream_detail.funding.batch.chip.alert_80': '警告 80%',
  'upstream_detail.funding.batch.meta.paid': '已付',
  'upstream_detail.funding.batch.meta.capacity': '额度',
  'upstream_detail.funding.batch.meta.consumed': '已消耗',
  'upstream_detail.funding.batch.meta.funded_at': '入款时间',
  'upstream_detail.funding.batch.pct_consumed': '{pct}% 已消耗',
  'upstream_detail.funding.batch.invoice': '发票',
  'upstream_detail.funding.batch.note': '备注',
  'upstream_detail.funding.form.cny_paid_yuan.label': '已付人民币（元）',
  'upstream_detail.funding.form.correction.label': '对账修正',
  'upstream_detail.funding.form.usd_delta.label': 'USD 差额（负数，美元）',
  'upstream_detail.funding.form.exchange_rate.label': '汇率（CNY/USD）',
  'upstream_detail.funding.form.exchange_rate.loading': '正在获取汇率…',
  'upstream_detail.funding.form.computed_usd.label': '折算 USD 额度',
  'upstream_detail.funding.form.computed_cny.label': '反推已付人民币',
  'upstream_detail.funding.form.invoice_ref.label': '上游发票号（可选）',
  'upstream_detail.funding.form.note.label': '备注（可选）',
  'upstream_detail.funding.form.note.required_label': '备注（必填）',
  'upstream_detail.funding.error.cny_paid_required': '请输入已付人民币金额',
  'upstream_detail.funding.error.usd_delta_required': '请输入负的 USD 差额',
  'upstream_detail.funding.error.correction_note_required': '对账修正批次必须填写备注',
  'upstream_detail.funding.error.account_required': '请选择一个上游账户',
  'upstream_detail.funding.error.rate_unavailable': '汇率不可用，请先在汇率页设定',
  'upstream_detail.funding.toast.registered': '资金批次 {id} 已登记',
  'upstream_detail.funding.toast.committed': '资金批次已入库',
  'upstream_detail.funding.toast.updated': '资金批次已更新',
  'upstream_detail.funding.batch.action.edit': '编辑',
  'upstream_detail.funding.batch.action.commit': '入库',
  'upstream_detail.credit.title': '计费方式与授信额度',
  'upstream_detail.credit.description': '预付账号的批次用完后会被摘下。账期账号用完批次后继续靠上游授信服务，只有欠款（扣除仍可用的批次余额）超过人民币授信额度时才摘下。付款即登记一个资金批次。',
  'upstream_detail.credit.error.load': '授信条款加载失败',
  'upstream_detail.credit.mode.prepaid': '预付',
  'upstream_detail.credit.mode.postpaid': '账期',
  'upstream_detail.credit.chip.over_limit': '超出授信',
  'upstream_detail.credit.action.edit': '编辑条款',
  'upstream_detail.credit.meta.payable': '应付上游',
  'upstream_detail.credit.meta.used': '已用授信',
  'upstream_detail.credit.meta.limit': '授信额度',
  'upstream_detail.credit.meta.batch_remaining': '可用批次余额',
  'upstream_detail.credit.unlimited': '无上限',
  'upstream_detail.credit.utilisation': '已用 {pct}% · 剩余 {remaining}',
  'upstream_detail.credit.form.postpaid': '账期（批次用完后继续靠授信服务）',
  'upstream_detail.credit.form.limit': '授信额度（人民币，元）',
  'upstream_detail.credit.form.limit_placeholder': '例如 50000',
  'upstream_detail.credit.form.unlimited': '无上限授信（永不因额度被摘下）',
  'upstream_detail.credit.error.limit_invalid': '请输入不小于 0、最多两位小数的金额，或勾选「无上限授信」',
  'upstream_detail.credit.toast.saved': '授信条款已保存',
  'finance.pnl.flow.upstream_postpaid_net_payable': '应付账期上游净额（欠款 − 未抵扣付款）',
  'cockpit.upstream.postpaid.title': '账期上游',
  'cockpit.upstream.postpaid.payable': '净应付',
  'cockpit.upstream.postpaid.limit': '授信额度 · 已用',
  'cockpit.upstream.postpaid.unlimited': '无上限',
  'cockpit.upstream.postpaid.unreadable': '读取失败',
  'upstream_detail.credit.error.position': '暂时无法读取该账号的授信头寸；上面的条款仍然有效。',
  'finance.pnl.flow.upstream_postpaid_unread': '（不完整：{count} 个账号读取失败）',
  'upstream_detail.danger.title': '危险操作',
  'upstream_detail.danger.body':
    '软删除此上游。网关将停止向其路由，现有 SKU 目标影响会在操作前展示。',
  'upstream_detail.danger.already_deleted': '已软删除',
  'upstream_detail.danger.delete': '软删除上游',
  'upstream_detail.delete_modal.title': '软删除 {name}?',
  'upstream_detail.delete_modal.body': '网关会立即停止向此上游路由，并保留相关 SKU 目标的审计记录。',

  // ── Upstream retirement impact dialog ───────────────────────────
  // Rendered by DangerZone before soft-deleting an upstream (Epic
  // #109 / P0.1). Tells the operator what will break — uniquely
  // served SKUs, active funding batches, last-24h traffic share —
  // and gates the Delete button behind an explicit acknowledgement.
  'upstream_detail.retirement.loading': '分析退役影响中…',
  'upstream_detail.retirement.load_error': '无法加载退役影响。请重试,或联系平台管理员。',
  'upstream_detail.retirement.retry': '重试',
  'upstream_detail.retirement.uniquely_served.title': '🚨 唯一承接的模型',
  'upstream_detail.retirement.uniquely_served.body':
    '退役后以下 {count} 个模型将没有任何上游可路由,tenant 调用会立刻返回 404:',
  'upstream_detail.retirement.uniquely_served.none':
    '无唯一承接。所有模型路由映射都有其他上游可路由。',
  'upstream_detail.retirement.total_attached_suffix':
    '(共映射 {total} 个模型路由,其中 {unique} 个仅此上游承接)',
  'upstream_detail.retirement.funding.title': '💰 活跃资金批次',
  'upstream_detail.retirement.funding.body':
    '{count} 批尚未耗尽,剩余额度 {remaining}。退役后这些批次不再被消耗,审计追溯中断。',
  'upstream_detail.retirement.funding.none': '无活跃资金批次。',
  'upstream_detail.retirement.traffic.title': '📊 近 24 小时流量份额',
  'upstream_detail.retirement.traffic.body':
    '此上游承接了近 24 小时网关流量的 {percentage}%({handled}/{total} 次请求)。',
  'upstream_detail.retirement.traffic.no_traffic': '近 24 小时无流量。流量份额无法估算。',
  'upstream_detail.retirement.ack': '我已了解退役此上游的影响,并希望继续软删除。',
  'upstream_detail.retirement.ack_required': '请勾选上方确认框后继续。',

  // ── Models list ─────────────────────────────────────────────────
  'models.title': '模型',
  'models.subtitle': '已注册的 (厂商, 模型) 目录。定价历史与协议绑定在单模型详情页管理。',
  'models.search.placeholder': '搜索模型、显示名称…',
  'models.search.aria_label': '搜索模型',
  'models.filter.vendor': '厂商',
  'models.filter.vendor_all': '全部厂商',
  'models.new': '发现模型',
  'models.create.open': '创建模型',
  'models.create.title': '创建模型',
  'models.create.subtitle': '当厂商发现无法提供候选行时，直接注册一个平台模型。',
  'models.create.vendor.label': '厂商',
  'models.create.vendor.placeholder': 'anthropic',
  'models.create.model.label': '模型',
  'models.create.model.placeholder': 'claude-opus-4-7',
  'models.create.display_name.label': '显示名称',
  'models.create.display_name.placeholder': 'Claude Opus 4.7',
  'models.create.error.vendor_model_required': '厂商和模型不能为空。',
  'models.create.submit': '创建模型',
  'models.create.toast.created': '已创建 {model}',
  'models.error.title': '无法加载模型',
  'models.loading_aria': '正在加载模型…',
  'models.empty.search.title': '没有匹配此搜索的模型',
  'models.empty.search.body': '清空筛选，或先到厂商页发现模型。',
  'models.empty.initial.title': '尚未发现任何模型',
  'models.empty.initial.body': '先到厂商页执行官方发现，发现结果会以「下架」候选进入这里。',
  'models.column.vendor': '厂商',
  'models.column.model': '模型',
  'models.column.display_name': '显示名称',
  'models.column.price': '价格',
  'models.column.family': '家族',
  'models.column.context_window': '上下文窗口',
  'models.column.actions': '操作',
  'models.price.unit': '输入 / 输出 · USD/MTok',
  'models.price.cache_line': '缓存读 {read} / 写 {write}',
  'models.price.input_output': '输入 / 输出 · USD/MTok',
  'models.price.input': '输入',
  'models.price.output': '输出',
  'models.price.mode.anthropic': 'Anthropic · 缓存写分 5m / 1h',
  'models.price.mode.openai_hit_only': 'OpenAI · 仅缓存命中',
  'models.price.mode.openai_write_billed': 'OpenAI · 缓存命中 + 写入',
  'models.price.mode.legacy': 'Legacy · 缓存写聚合',
  'models.price.cache_read': '缓存读',
  'models.price.cache_hit': '缓存命中',
  'models.price.cache_write': '缓存写',
  'models.price.cache_write_5m': '写 5m',
  'models.price.cache_write_1h': '写 1h',
  'models.price.reasoning': 'Reasoning',
  'models.price.not_configured': '未配置',
  'models.price.not_billed': '不计费',
  'models.price.unpriced': '未定价',
  'models.row.empty_value': '—',
  'models.row.manage': '管理',
  'models.row.delete': '删除',
  'models.delete_modal.title': '软删除模型?',
  'models.delete_modal.body_suffix':
    ' 将被标记为已删除。定价和协议行会保留以便于历史重建;如需恢复,请后续通过迁移脚本修改 ',
  'models.delete_modal.body_deleted_at_code': 'deleted_at',
  'models.delete_modal.body_tail': ' 字段。',

  // ── Model detail ────────────────────────────────────────────────
  'model_detail.loading_aria': '正在加载模型…',
  'model_detail.error.title': '无法加载模型',
  'model_detail.back': '返回模型列表',
  'model_detail.breadcrumb.models': '模型',
  'model_detail.meta.family': '家族 · {family}',
  'model_detail.meta.soft_deleted': '已软删除 · {date}',
  'model_detail.meta.deprecated': '已弃用 · {date}',
  'model_detail.basic.title': '基本信息',
  'model_detail.basic.display_name': '显示名称',
  'model_detail.basic.family': '家族',
  'model_detail.basic.context_window': '上下文窗口',
  'model_detail.basic.max_output_tokens': '最大输出 tokens',
  'model_detail.aliases.title': '模型别名',
  'model_detail.aliases.subtitle': '允许租户请求使用别名模型名，并解析到这个 canonical 模型。',
  'model_detail.aliases.empty': '暂无别名。',
  'model_detail.aliases.add.label': '新增别名',
  'model_detail.aliases.add.placeholder': 'claude-latest',
  'model_detail.aliases.add.submit': '新增',
  'model_detail.aliases.add.adding': '新增中…',
  'model_detail.aliases.remove_aria': '移除 {alias}',
  'model_detail.aliases.error.required': '别名不能为空',
  'model_detail.aliases.error.duplicate': '别名已存在或与 canonical 模型相同',
  'model_detail.pricing.title': '定价',
  'model_detail.pricing.lane.token': '按 token 计价',
  'model_detail.pricing.loading_aria': '正在加载定价…',
  'model_detail.pricing.error.load': '加载定价失败',
  'model_detail.pricing.empty': '尚无定价行,请在下方新增第一代。',
  'model_detail.pricing.column.effective_from': '生效时间',
  'model_detail.pricing.column.input': '输入 $/Mtok',
  'model_detail.pricing.column.output': '输出 $/Mtok',
  'model_detail.pricing.column.cache_read': '缓存读 $/Mtok',
  'model_detail.pricing.column.cache_hit': '缓存命中 $/Mtok',
  'model_detail.pricing.column.cache_write': '缓存写 $/Mtok',
  'model_detail.pricing.column.cache_write_5m': '缓存写 5m $/Mtok',
  'model_detail.pricing.column.cache_write_1h': '缓存写 1h $/Mtok',
  'model_detail.pricing.column.cache_mode': '缓存计费方式',
  'model_detail.pricing.cache_mode.hit_only': '仅缓存命中',
  'model_detail.pricing.cache_mode.write_billed': '缓存命中 + 写入',
  'model_detail.pricing.column.reasoning': 'Reasoning $/Mtok',
  'model_detail.pricing.column.image_input': '图像输入 $/Mtok',
  'model_detail.pricing.column.single_rate': '单价 $/Mtok',
  'model_detail.pricing.column.status': '状态',
  'model_detail.pricing.row.current': '当前',
  'model_detail.pricing.row.archived': '已归档',
  'model_detail.pricing.row.anchor': '锚定行（不参与分档计费）',
  'model_detail.pricing.row.anchor_unit': '锚定行（该模型按 unit 计费，此行不参与计费）',
  'model_detail.pricing.add.title': '新增定价行',
  'model_detail.pricing.add.effective_from.label': '生效时间',
  'model_detail.pricing_effective.immediate': '立即生效',
  'model_detail.pricing_effective.scheduled_date': '指定日期',
  'model_detail.pricing_effective.scheduled_datetime': '指定时间',
  'model_detail.pricing.add.input_rate.label': '输入 $/Mtok',
  'model_detail.pricing.add.input_rate.placeholder': '3.000000',
  'model_detail.pricing.add.output_rate.label': '输出 $/Mtok',
  'model_detail.pricing.add.output_rate.placeholder': '15.000000',
  'model_detail.pricing.add.cache_read.label': '缓存读 $/Mtok',
  'model_detail.pricing.add.cache_read.placeholder': '0.300000',
  'model_detail.pricing.add.cache_hit.label': '缓存命中 $/Mtok',
  'model_detail.pricing.add.cache_hit.placeholder': '0.200000',
  'model_detail.pricing.add.cache_write.label': '缓存写 $/Mtok',
  'model_detail.pricing.add.cache_write.placeholder': '3.750000',
  'model_detail.pricing.add.cache_write_5m.label': '缓存写 5m $/Mtok',
  'model_detail.pricing.add.cache_write_5m.placeholder': '3.750000',
  'model_detail.pricing.add.cache_write_1h.label': '缓存写 1h $/Mtok',
  'model_detail.pricing.add.cache_write_1h.placeholder': '5.000000',
  'model_detail.pricing.add.cache_mode.label': '缓存计费方式',
  'model_detail.pricing.add.reasoning.label': 'Reasoning $/Mtok',
  'model_detail.pricing.add.reasoning.placeholder': '4.000000',
  'model_detail.pricing.add.image_input.label': '图像输入 $/Mtok',
  'model_detail.pricing.add.image_input.placeholder': '8.000000',
  'model_detail.pricing.add.single_rate.label': '单价 $/Mtok',
  'model_detail.pricing.add.single_rate.placeholder': '6.400000',
  'model_detail.pricing.add.submit': '新增定价行',
  'model_detail.pricing.add.submitting': '新增中…',
  'model_detail.pricing.add.base_anchor_note':
    '该模型按档位销售：计费按档位费率精确匹配，基准费率不参与档位计费，仅用于上架锚定。已有当前价目时会自动带入其基准费率，可按需修改。',
  'model_detail.pricing.unit_anchor.note':
    '上架门槛要求每个模型存在一条 token 锚定价目代；该模型按 unit 计费，token 费率不参与本模型计费。一键以零费率创建锚定行即可。',
  'model_detail.pricing.unit_anchor.create': '创建锚定行（零费率）',
  'model_detail.pricing.error.effective_from_required': '生效时间不能为空',
  'model_detail.pricing.error.effective_from_invalid': '生效时间必须是有效日期',
  'model_detail.pricing.error.rates_required': '输入与输出费率必须填写',
  'model_detail.pricing.error.input_rate_invalid': '输入费率必须为非负数',
  'model_detail.pricing.error.output_rate_invalid': '输出费率必须为非负数',
  'model_detail.pricing.error.cache_read_invalid': '缓存读费率必须为非负数',
  'model_detail.pricing.error.cache_write_invalid': '缓存写费率必须为非负数',
  'model_detail.pricing.error.cache_write_5m_invalid': '缓存写 5m 费率必须为非负数',
  'model_detail.pricing.error.cache_write_1h_invalid': '缓存写 1h 费率必须为非负数',
  'model_detail.pricing.error.cache_write_ttl_required': 'Anthropic 模型必须同时填写缓存写 5m 和 1h 费率',
  'model_detail.pricing.error.cache_write_positive_required': '缓存命中 + 写入模式要求缓存写入费率大于 0',
  'model_detail.pricing.error.metering_unknown': '无法加载该模型的计量 profile，已暂停定价提交，以免漏填可计费维度',
  'model_detail.pricing.error.reasoning_invalid': 'Reasoning 费率必须为非负数',
  'model_detail.pricing.error.reasoning_required': '该模型会计量 reasoning token，留空会发布一份把思考按零计费的价目。确实免费请填 0。',
  'model_detail.pricing.error.image_input_invalid': '图像输入费率必须为非负数',
  'model_detail.pricing.error.image_input_required':
    '该模型会计量图像输入 token，留空会发布一份把图像输入按文本输入费率计费的价目，已配置的图像输入价会被打回。确实免费请填 0。',
  'model_detail.pricing.error.invalid': '费率无效',
  'model_detail.pricing.error.single_rate_invalid': '单价必须为非负数',
  'model_detail.pricing.error.single_rate_required': '单价必须填写',
  'model_detail.pricing.error.rated_anchor_conflict':
    '该模型在所选生效时间之后已有计费记录（最近一次计费在 {latest}）。新价目必须晚于该时刻生效——选择「立即生效」或更晚的日期即可。',
  'model_detail.pricing.error.rated_anchor_conflict_generic':
    '该模型在所选生效时间之后已有计费记录。新价目必须晚于最近一次计费的时刻生效——选择「立即生效」或更晚的日期即可。',
  'model_detail.pricing.error.rated_anchor_unverifiable':
    '更早的计费记录已不在保留的评级窗口内，无法校验该生效时间。请选择保留窗口内的时间，或选择「立即生效」。',
  'model_detail.pricing.variants.title': '分档价目（按请求形态）',
  'model_detail.pricing.variants.generation_current': '当前生效',
  'model_detail.pricing.variants.column.variant': '档位',
  'model_detail.pricing.variants.add.title': '新一代价目的分档费率',
  'model_detail.pricing.variants.add.hint':
    '该模型按请求形态分档计价，且分档不会自动继承——新一代价目从零开始。请在此重新填写每一档，否则新价目一档都没有价。',
  'model_detail.pricing.variants.add.key.label': '档位 key',
  'model_detail.pricing.variants.add.add_row': '添加档位',
  'model_detail.pricing.variants.add.row_count': '{count} / {max} 档',
  'model_detail.pricing.variants.add.remove_aria': '删除第 {row} 档',
  'model_detail.pricing.variants.error.too_many': '每一代价目最多 {max} 个档位',
  'model_detail.pricing.variants.error.key_invalid':
    '第 {row} 档：key 必须匹配 ^[a-z0-9][a-z0-9-]{0,62}$（空 key 归基准费率所有）',
  'model_detail.pricing.variants.error.duplicate': '同一代价目内档位 key 重复：{variant}',
  'model_detail.pricing.variants.error.rates_required': '第 {row} 档：input 和 output 费率必填',
  'model_detail.pricing.variants.error.single_rate_required': '第 {row} 档：单价必填',
  'model_detail.pricing.variants.error.rate_invalid': '第 {row} 档：费率必须为非负数',
  'model_detail.pricing.variants.error.reasoning_required':
    '第 {row} 档：该模型会计量 reasoning token，而分档会整体替换基准费率——留空会把这一档的思考按零计费。确实免费请填 0。',
  'model_detail.pricing.variants.error.image_input_required':
    '第 {row} 档：该模型会计量图像输入 token，而分档会整体替换基准费率——留空会把这一档的图像输入按文本输入费率计费。确实免费请填 0。',
  'model_detail.pricing.variants.error.fields_required':
    '第 {row} 档：请把每个档位字段填全（仅允许小写字母和数字）',
  'model_detail.pricing.variants.add.hint_derived':
    '该模型按请求形态分档计价，档位 key 由下方字段自动生成，不需要手拼。分档不会自动继承——新一代价目须在此重新给出每一档的费率。',
  'metering.variant_field.size': METERING_VARIANT_FIELD_LABELS.size.zh,
  'metering.variant_field.quality': METERING_VARIANT_FIELD_LABELS.quality.zh,
  'metering.variant_field.resolution': METERING_VARIANT_FIELD_LABELS.resolution.zh,
  'metering.variant_field.has_video_input': METERING_VARIANT_FIELD_LABELS.has_video_input.zh,
  'metering.variant_field.select_placeholder': '请选择',
  'metering.variant_field.value_placeholder': '如 480p',
  'metering.video_input.absent': VIDEO_INPUT_VARIANT_LABELS.absent.zh,
  'metering.video_input.present': VIDEO_INPUT_VARIANT_LABELS.present.zh,
  'metering.variant_key.preview': '生成的档位 key：',
  'metering.variant_key.preview_incomplete': '档位 key 待生成：请把每个字段填全（仅小写字母和数字）',
  'metering.variant_key.preview_default': '所有字段留空 = 默认行（key 为空）',
  'metering.shape.lane_token': '按 token 计费',
  'metering.shape.lane_unit': '按量（unit）计费',
  'metering.shape.no_tiers': '不分档',
  'metering.shape.tiered_by': '按 {fields} 分档',
  'metering.shape.summary': '计费形态：{lane}，{tiers}',
  'metering.shape.fixed.ark_video_token_v1': '计费形态：按 token 计费 · 按是否含视频输入分两档（火山官方口径）',
  'metering.shape.fixed.ark_video_token_tiered_v1':
    '计费形态：按 token 计费 · 按是否含视频输入×分辨率分档（火山官方逐分辨率口径）',
  'metering.shape.fixed.duration_seconds_v1': '计费形态：按秒计费 · 按分辨率分档（火山官方口径）',
  'metering.shape.fixed.ark_video_seconds_v1':
    '计费形态：按秒计费 · 按是否含视频输入×分辨率分档（火山任务口径）',
  'metering.shape.fixed.openai_images_unit_v1':
    '计费形态：按张计费 · 按尺寸×质量分档（OpenAI images 口径）',
  'metering.shape.fixed.ark_video_seconds_ref_v1':
    '计费形态：按秒计费 · 按分辨率分档，另按参考图超额张数加收（MiniMax A）',
  'metering.shape.fixed.grok_video_seconds_v1':
    '计费形态：按秒计费 · 按分辨率分档（Grok 视频口径）',
  'metering.shape.fixed.grok_image_unit_v1':
    '计费形态：按交付张数计费 · 无分档（Grok 静图口径）',
  'model_detail.unit_pricing.title': '按量单价（unit 价目）',
  'model_detail.unit_pricing.loading': '正在加载 unit 价目…',
  'model_detail.unit_pricing.error.load': '加载 unit 价目失败',
  'model_detail.unit_pricing.empty': '尚无 unit 价目，请在下方新增第一代。',
  'model_detail.unit_pricing.generation.current': '当前生效',
  'model_detail.unit_pricing.generation.scheduled': '已排期',
  'model_detail.unit_pricing.generation.archived': '已归档',
  'model_detail.unit_pricing.column.unit': '计量单位',
  'model_detail.unit_pricing.column.variant': '规格（variant）',
  'model_detail.unit_pricing.column.rate_micros': '单价（micro-USD）',
  'model_detail.unit_pricing.column.rate_human': '换算',
  'model_detail.unit_pricing.variant.default': '默认',
  'model_detail.unit_pricing.unit.call': UNIT_USAGE_UNIT_LABELS.call.zh,
  'model_detail.unit_pricing.unit.second': UNIT_USAGE_UNIT_LABELS.second.zh,
  'model_detail.unit_pricing.unit.character': UNIT_USAGE_UNIT_LABELS.character.zh,
  'model_detail.unit_pricing.unit.image': UNIT_USAGE_UNIT_LABELS.image.zh,
  'model_detail.unit_pricing.add.title': '新增一代价目',
  'model_detail.unit_pricing.add.effective_from.label': '生效时间',
  'model_detail.unit_pricing.add.row.unit.label': '计量单位',
  'model_detail.unit_pricing.add.row.variant.label': '规格（可留空为默认行）',
  'model_detail.unit_pricing.add.row.variant.placeholder': '如 1080p，留空为默认',
  'model_detail.unit_pricing.add.row.rate.label': '单价（micro-USD / 单位）',
  'model_detail.unit_pricing.add.row.rate.placeholder': '40000',
  'model_detail.unit_pricing.add.row.remove_aria': '移除第 {row} 行',
  'model_detail.unit_pricing.add.add_row': '添加一行',
  'model_detail.unit_pricing.add.row_count': '{count} / {max} 行',
  'model_detail.unit_pricing.add.submit': '新增价目代',
  'model_detail.unit_pricing.add.submitting': '提交中…',
  'model_detail.unit_pricing.error.effective_from_invalid': '生效时间必须是有效时间',
  'model_detail.unit_pricing.error.rows_required': '至少需要一行价目',
  'model_detail.unit_pricing.error.too_many_rows': '一代最多 {max} 行价目',
  'model_detail.unit_pricing.error.unit_invalid': '第 {row} 行：计量单位不在允许集合内',
  'model_detail.unit_pricing.error.variant_invalid': '第 {row} 行：规格须留空或匹配 ^[a-z0-9][a-z0-9-]{0,62}$',
  'model_detail.unit_pricing.error.rate_invalid': '第 {row} 行：单价必须是正整数（micro-USD / 单位）',
  'model_detail.unit_pricing.error.duplicate_row': '同一代内 (unit, variant) 重复：{unit} / {variant}',
  'model_detail.unit_pricing.error.variant_fields_invalid':
    '第 {row} 行：规格字段未填全或含非法字符（仅小写字母和数字；全部留空为默认行）',
  'model_detail.protocols.title': '协议',
  'model_detail.protocols.loading_aria': '正在加载协议…',
  'model_detail.protocols.error.load': '加载协议失败',
  'model_detail.protocols.empty': '尚未登记协议。Pool 选择器会跳过此模型。',
  'model_detail.protocols.add.label': '新增协议',
  'model_detail.protocols.add.submit': '新增',
  'model_detail.protocols.add.adding': '新增中…',
  'model_detail.protocols.error.already_registered': '{protocol} 已登记',
  'model_detail.protocols.remove_aria': '移除 {protocol}',
  'model_detail.protocols.all_registered': '三个协议都已登记；要改计量口径请先移除其中一个。',
  'model_detail.protocols.profile.label': '计量口径',
  'model_detail.protocols.profile.default': '按协议默认（{profile}）',
  'model_detail.protocols.profile.unset': '无口径',
  'model_detail.protocols.profile.pick': '选择计量口径',
  'model_detail.protocols.profile.required': '协议 {protocol} 没有默认计量口径，请显式选择一个。',
  'model_detail.protocols.spec.present': '规格',
  'model_detail.protocols.spec.absent': '—',
  'model_detail.protocols.edit': '编辑',
  'model_detail.protocols.edit_title': '重新登记 {protocol}（会整份覆盖：表单里没有的文档会被清空，已为你回填现存配置）',
  'model_detail.protocols.edit_submit': '重新登记',
  'model_detail.protocols.docs.section.accepts.title': '接受什么',
  'model_detail.protocols.docs.section.accepts.hint':
    '参数词汇表随协议版本进代码，这里只拦类型错误和明显垃圾；值域由上游模型自己把关，档位能否计费由价目矩阵在产生任何上游花费之前裁决。',
  'model_detail.protocols.docs.section.billing.title': '怎么计费',
  'model_detail.protocols.docs.section.billing.hint': '计费形态由计量口径决定，登记时自动生成，无需填写。',
  'model_detail.protocols.docs.spec_json.label': '计费规格（metering_spec，JSON，可留空）',
  'model_detail.protocols.docs.spec_json.hint':
    '留空则沿用该口径的内置规格；duration_seconds_v1 / ark_video_seconds_v1 / ark_video_seconds_ref_v1 / grok_video_seconds_v1 没有内置规格，不填则该 SKU 无法计费。提交前会用与写面完全相同的校验器检查。',
  'model_detail.protocols.docs.spec_json.placeholder':
    '{"spec_version":1,"lane":"unit","unit":"second","quantity":{"field":"duration_seconds","a":1,"b":0},"quantity_by_variant":{"480p":{"field":"duration_seconds","a":1,"b":0}},"variant_key_template":"{resolution}","delivery_gate":"positive_quantity"}',
  'model_detail.protocols.docs.vocabulary.label': '协议参数词汇表（只读）',
  'model_detail.protocols.docs.vocabulary.hint':
    '随协议版本进代码。上游加参数是新的协议版本，不是表单编辑；值域由上游自己把关。',
  'model_detail.protocols.docs.vocabulary.col_parameter': '参数',
  'model_detail.protocols.docs.vocabulary.col_type': '类型',
  'model_detail.protocols.docs.vocabulary.col_commands': '提示词命令',
  'model_detail.protocols.docs.admission.label': '并发准入（task_admission）',
  'model_detail.protocols.docs.admission.hint':
    '按档位类别限制单账号在途任务数。档位类别就是这张映射自身的键：写下键即上架该档位，请求的档位值匹配不到任何键会在产生花费之前被拒绝。',
  'model_detail.protocols.docs.admission.tier_class': '档位类别参数',
  'model_detail.protocols.docs.admission.tier_class_placeholder': '选择词汇表参数',
  'model_detail.protocols.docs.admission.tier_class_value': '档位类别',
  'model_detail.protocols.docs.admission.tier_class_value_placeholder': '如 480P',
  'model_detail.protocols.docs.admission.allowance': '{tier} 在途上限',
  'model_detail.protocols.docs.admission.add_tier': '添加档位类别',
  'model_detail.protocols.docs.admission.remove_aria': '删除第 {row} 条档位类别',
  'model_detail.protocols.docs.error.spec_invalid': '计费规格未通过校验：{violations}',
  'model_detail.protocols.docs.error.spec_json_invalid': '计费规格不是合法 JSON',
  'model_detail.protocols.docs.error.spec_lane_mismatch':
    '这份计费规格与 {profile} 的计费通道不符（token 口径的分档规格由固定模板自动生成，不在 JSON 里配）',
  'model_detail.protocols.docs.error.spec_template_fixed':
    '{profile} 是固定销售形态：variant_key_template 必须逐字等于 {fixed}，收到的是 {template}',
  'model_detail.protocols.docs.error.spec_template_fixed_none': '无（该口径不分档）',
  'model_detail.protocols.docs.error.spec_descriptor_not_produced':
    '{profile} 不产出计量字段 {field}，写面会拒绝这份规格',
  'model_detail.protocols.docs.error.spec_protocol_excluded':
    '计费规格的 protocols 不包含正在登记的 {protocol}，登记后将永远无法计费',
  'model_detail.protocols.docs.error.spec_surcharge_not_sold':
    '{profile} 不出售附加计量；附加计量由口径枚举，不可按登记配置',
  'model_detail.protocols.docs.error.spec_surcharge_unit':
    '{profile} 的附加计量单位是 {admitted}，收到的是 {unit}',
  'model_detail.protocols.docs.error.spec_surcharge_descriptor':
    '{profile} 不出售附加计量字段 {field}',
  'model_detail.protocols.docs.error.admission_invalid': '并发准入未通过校验：{violations}',
  'model_detail.deprecation.title': '弃用状态',
  'model_detail.deprecation.body':
    '标记此模型为弃用后,仍可列表 / 计费 / 路由;目的仅为提示新用户使用替代模型。弃用理由会出现在运营面板的模型详情页顶部,并通过审计日志追溯。',
  'model_detail.deprecation.chip.deprecated': '已弃用',
  'model_detail.deprecation.notice.label': '弃用理由(必填)',
  'model_detail.deprecation.notice.placeholder':
    '例如:厂商于 2026-06-01 起停止此模型,请迁移至 claude-opus-4-8。',
  'model_detail.deprecation.notice.counter': '{count} / {limit}',
  'model_detail.deprecation.deprecate.submit': '标记为弃用',
  'model_detail.deprecation.deprecate.pending': '标记中…',
  'model_detail.deprecation.undeprecate.submit': '恢复为可用',
  'model_detail.deprecation.undeprecate.pending': '恢复中…',
  'model_detail.deprecation.error.notice_required': '请填写弃用理由',
  'model_detail.deprecation.error.notice_too_long': '弃用理由不得超过 {limit} 字符',
  'model_detail.deprecation.toast.deprecated': '模型已标记为弃用',
  'model_detail.deprecation.toast.undeprecated': '模型弃用状态已清除',
  'model_detail.danger.title': '危险操作',
  'model_detail.danger.body':
    '软删除此模型。定价行与协议绑定会保留在 SQL 中以供历史成本重建;网关 pool 选择器会停止提供该模型。',
  'model_detail.danger.already_deleted': '已软删除',
  'model_detail.danger.delete': '软删除模型',
  'model_detail.delete_modal.title': '软删除 {vendor}/{model}?',
  'model_detail.delete_modal.body':
    '网关 pool 选择器会立即停止提供该模型。历史用量日志仍通过保留的定价行解析。',

  // ── Gift cards ──────────────────────────────────────────────────
  'gift_cards.title': '礼品卡',
  'gift_cards.subtitle':
    '按批次发行、查询和追踪兑换状态；租户自助兑换与 ops 代兑都会回到同一张清单。',
  'gift_cards.batch.unbatched': '未分批',
  'gift_cards.batches.title': '批次',
  'gift_cards.batches.empty': '暂无批次',
  'gift_cards.batches.status.active': '启用',
  'gift_cards.batches.status.closed': '已关闭',
  'gift_cards.batches.close': '关闭',
  'gift_cards.batches.reopen': '恢复',
  'gift_cards.batches.metric.issued': '已发行',
  'gift_cards.batches.metric.active': '可用',
  'gift_cards.batches.metric.redeemed': '已兑',
  'gift_cards.batches.metric.value': '总面值',
  'gift_cards.batches.create.name': '批次名称',
  'gift_cards.batches.create.description': '描述(可选)',
  'gift_cards.batches.create.submit': '创建批次',
  'gift_cards.batches.create.submitting': '创建中…',
  'gift_cards.batches.error.name_required': '批次名称不能为空',
  'gift_cards.chip.revoked': '已吊销',
  'gift_cards.chip.redeemed': '已兑换',
  'gift_cards.chip.expired': '已过期',
  'gift_cards.chip.active': '可用',
  'gift_cards.issue.title': '发行',
  'gift_cards.issue.subtitle': '核心路径 · 创建一张余额凭证',
  'gift_cards.issue.amount.label': '金额(RMB)',
  'gift_cards.issue.amount.placeholder': '50',
  'gift_cards.issue.code_hint.label': '码前缀(可选)',
  'gift_cards.issue.code_hint.placeholder': '自动生成 32 位十六进制',
  'gift_cards.issue.batch_id.label': '批次 ID(可选)',
  'gift_cards.issue.batch_id.placeholder': '2026-q2-promo',
  'gift_cards.issue.expires_at.label': '过期时间(可选)',
  'gift_cards.issue.submit': '发行礼品卡',
  'gift_cards.issue.submitting': '发行中…',
  'gift_cards.issued.title': '已发行新礼品卡',
  'gift_cards.issued.code_label': '兑换码(点击复制)',
  'gift_cards.issued.code_aria_label': '复制礼品卡兑换码',
  'gift_cards.issued.copy_status.copied': '已复制到剪贴板',
  'gift_cards.issued.copy_status.idle': '点击整块以复制',
  'gift_cards.issued.face_value': '面值',
  'gift_cards.issued.batch': '批次',
  'gift_cards.issued.expires_at': '过期时间',
  'gift_cards.issued.never': '永不过期',
  'gift_cards.issued.credit_value': '入账金额(USD)',
  'gift_cards.issued.applied_rate': '锁定汇率',
  'gift_cards.issued.issue_another': '再发一张',
  'gift_cards.lookup.title': '查询',
  'gift_cards.lookup.clear': '清空',
  'gift_cards.lookup.code.label': '礼品卡兑换码',
  'gift_cards.lookup.code.placeholder': '32 位十六进制兑换码',
  'gift_cards.lookup.submit': '查询',
  'gift_cards.lookup.submitting': '查询中…',
  'gift_cards.lookup.error.network': '网络错误',
  'gift_cards.list.title': '清单',
  'gift_cards.list.batch.all': '全部批次',
  'gift_cards.list.status.all': '全部状态',
  'gift_cards.list.account.placeholder': '兑换账户',
  'gift_cards.list.column.code': '兑换码',
  'gift_cards.list.column.status': '状态',
  'gift_cards.list.column.amount': '面值',
  'gift_cards.list.column.batch': '批次',
  'gift_cards.list.column.redeemed_by': '兑换账户',
  'gift_cards.list.column.updated': '最近状态时间',
  'gift_cards.list.column.actions': '操作',
  'gift_cards.list.column.source': '来源',
  'gift_cards.list.show_code': '显示',
  'gift_cards.list.hide_code': '隐藏',
  'gift_cards.list.copy_code': '复制',
  'gift_cards.list.empty': '没有符合条件的礼品卡',
  'gift_cards.list.total': '共 {total} 张',
  'gift_cards.result.code': '兑换码',
  'gift_cards.result.face_value': '面值',
  'gift_cards.result.issued_at': '发行时间',
  'gift_cards.result.expires_at': '过期时间',
  'gift_cards.result.never': '永不过期',
  'gift_cards.result.batch_id': '批次 ID',
  'gift_cards.result.batch_id_empty': '—',
  'gift_cards.result.redeemed_at': '兑换时间',
  'gift_cards.result.redeemed_by': '兑换账户',
  'gift_cards.result.redeemed_by_empty': '—',
  'gift_cards.result.credit_value': '入账金额(USD)',
  'gift_cards.result.applied_rate': '锁定汇率',
  'gift_cards.result.revoked_at': '吊销时间',
  'gift_cards.result.revoke': '吊销',
  'gift_cards.result.redeem': '代为兑换',
  'gift_cards.revoke_modal.title': '吊销礼品卡?',
  'gift_cards.revoke_modal.body_prefix': '这将永久停用 ',
  'gift_cards.revoke_modal.body_separator': '(面值 ',
  'gift_cards.revoke_modal.body_suffix': ')。操作不可撤销。',
  'gift_cards.revoke_modal.reason.label': '原因(可选,写入审计日志)',
  'gift_cards.revoke_modal.reason.placeholder': '重复发行 / 误发 / 欺诈',
  'gift_cards.revoke_modal.submit': '吊销',
  'gift_cards.revoke_modal.submitting': '吊销中…',
  'gift_cards.revoke_modal.toast': '礼品卡 {code} 已吊销',
  'gift_cards.redeem_modal.title': '代为兑换',
  'gift_cards.redeem_modal.body_prefix': '该操作将把 ',
  'gift_cards.redeem_modal.body_middle': ' 入账到目标账户，礼品卡 ',
  'gift_cards.redeem_modal.body_suffix':
    ' 的人民币面值为 {faceValue}。资金立即在平台账本结算,只能通过余额调整反向操作。',
  'gift_cards.redeem_modal.account.label': '目标账户 ID(UUID)',
  'gift_cards.redeem_modal.account.placeholder': 'acct_…',
  'gift_cards.redeem_modal.confirm.label': '确认金额(请键入 {amount})',
  'gift_cards.redeem_modal.reason.label': '原因(可选)',
  'gift_cards.redeem_modal.reason.placeholder': '客服补偿 / 促销兑换',
  'gift_cards.redeem_modal.idempotency': '幂等 key:',
  'gift_cards.redeem_modal.submit': '入账 {amount}',
  'gift_cards.redeem_modal.submitting': '入账中…',
  'gift_cards.redeem_modal.toast': '已向 {account} 充值 {amount}——新余额 {balance}',
  'gift_cards.redeem_modal.error.account_required': '账户 ID 不能为空',
  'gift_cards.redeem_modal.error.amount_mismatch': '确认金额必须等于 {amount}(资金操作的二次确认)',
  'gift_cards.bulk.title': '批量操作记录',
  'gift_cards.bulk.body':
    '阶段 1 暂不跟踪批量操作。发行 / 吊销 / 兑换事件记录在平台账本和 gateway 审计日志中,批量视图待后续阶段。',
  'gift_cards.issue.error.amount_positive': '金额必须为正的 RMB 值',
  'gift_cards.issue.error.amount_whole_yuan': 'RMB 礼品卡金额必须为整元',
  'gift_cards.issue.error.expires_at_invalid': '过期时间必须是有效日期',
  'gift_cards.issue.error.expires_at_future': '过期时间必须在将来',

  // ── Accounts list ───────────────────────────────────────────────
  'accounts.title': '账户',
  'accounts.subtitle':
    '带有组织归属、当前余额、最近账本访问记录的租户账户。点击邮箱打开详情 / 余额调整面板。',
  'accounts.search.placeholder': '搜索邮箱…',
  'accounts.search.aria_label': '搜索账户邮箱',
  'accounts.has_balance': '有余额',
  'accounts.low_balance': '余额告急',
  'accounts.sort.aria_label': '账户排序',
  'accounts.sort.created': '最新注册优先',
  'accounts.sort.balance': '余额最低优先',
  'accounts.search.submit': '搜索',
  'accounts.search.clear': '清空',
  'accounts.error.title': '无法加载账户',
  'accounts.loading_aria': '正在加载账户…',
  'accounts.empty.filtered.title': '没有账户匹配当前筛选',
  'accounts.empty.filtered.body': '清空筛选或调整搜索条件。',
  'accounts.empty.initial.title': '暂无已注册账户',
  'accounts.empty.initial.body': '当租户通过公开 API 注册后,账户会出现在这里。',
  'accounts.column.email': '邮箱',
  'accounts.column.org_id': '组织 ID',
  'accounts.column.discount': '折扣',
  'accounts.column.sku_rates': 'SKU 费率',
  'accounts.column.balance': '余额',
  'accounts.column.brand': '品牌',
  'accounts.column.kind': '类型',
  'accounts.column.created': '创建时间',
  'accounts.chip.system': '系统',
  'accounts.chip.tenant': '租户',
  'accounts.discount.no_discount': '无折扣',
  'accounts.discount.discounted': '折后价',
  'accounts.sku_rates.default': '默认',
  'accounts.sku_rates.overrides': '覆盖',
  'accounts.pagination.aria_label': '分页',
  'accounts.pagination.range': '显示第 {start}–{end} 条,共 {total} 条',

  // ── Brand authority (epic #1030 E1-S4, #1040) ──────────────────
  'brand_scope.cross_brand': '跨品牌视图',
  'brand_scope.self_scoped': '品牌 {brand} · 我的客户',
  'brand_scope.brand_wide': '品牌 {brand} · 全品牌客户',
  'brand_scope.no_profile': '未配置销售档案,请联系管理员分配品牌',
  'brand_scope.filter.aria_label': '按品牌筛选',
  'brand_scope.filter.all': '全部品牌',
  'brand_edit.action_aria': '修改客户品牌',
  'brand_edit.kicker': '品牌归属',
  'brand_edit.title': '修正客户品牌',
  'brand_edit.customer': '客户',
  'brand_edit.brand': '品牌',
  'brand_edit.guard_hint': '若该客户正由其他品牌的客户经理持有,修改会被拒绝。',
  'brand_edit.error.conflict': '该客户正由其他品牌的客户经理持有;请先改派或释放客户,再修正品牌。',
  'brand_edit.toast.saved': '客户品牌已更新。',

  // ── Customer ownership ─────────────────────────────────────────
  'customer_ownership.kicker': '客户经理工作域',
  'customer_ownership.admin.title': '客户归属',
  'customer_ownership.admin.subtitle': '查看客户负责人状态,并维护组织级客户经理归属。',
  'customer_ownership.manager.title': '我的客户',
  'customer_ownership.manager.subtitle': '只显示当前客户经理负责的客户组织。',
  'customer_ownership.manager.readonly': '当前视图按客户经理身份收敛,只能查看自己的客户组合。',
  'customer_ownership.metric.total': '总数',
  'customer_ownership.metric.assigned_page': '本页已分配',
  'customer_ownership.metric.unassigned_page': '本页未分配',
  'customer_ownership.search.placeholder': '搜索客户邮箱 / 企业名称…',
  'customer_ownership.search.aria_label': '搜索客户邮箱或企业名称',
  'customer_ownership.search.submit': '筛选',
  'customer_ownership.search.clear': '清空',
  'customer_ownership.error.title': '无法加载客户归属',
  'customer_ownership.loading_aria': '正在加载客户归属…',
  'customer_ownership.empty.filtered.title': '没有客户匹配当前筛选',
  'customer_ownership.empty.filtered.body': '清空筛选或调整搜索条件。',
  'customer_ownership.empty.admin.title': '暂无客户',
  'customer_ownership.empty.admin.body': '当租户账户注册后,客户归属队列会出现在这里。',
  'customer_ownership.empty.manager.title': '暂无负责客户',
  'customer_ownership.empty.manager.body': '当前客户经理还没有被分配客户。',
  'customer_ownership.column.customer': '客户',
  'customer_ownership.column.org': '组织',
  'customer_ownership.column.balance': '余额',
  'customer_ownership.column.brand': '品牌',
  'customer_ownership.column.owner': '负责人',
  'customer_ownership.column.assigned_at': '分配时间',
  'customer_ownership.column.created': '创建时间',
  'customer_ownership.column.actions': '操作',
  'customer_ownership.owner.assigned': '已分配',
  'customer_ownership.owner.unassigned': '未分配',
  'customer_ownership.action.assign': '分配',
  'customer_ownership.action.reassign': '改派',
  'customer_ownership.action.revoke': '撤销',
  'customer_ownership.action.readonly': '只读',
  'customer_ownership.pagination.aria_label': '客户归属分页',
  'customer_ownership.pagination.range': '显示第 {start}–{end} 条,共 {total} 条',
  'customer_ownership.assignment.kicker': '归属操作',
  'customer_ownership.assignment.title': '分配客户经理',
  'customer_ownership.assignment.customer': '客户',
  'customer_ownership.assignment.manager': '客户经理',
  'customer_ownership.assignment.select_placeholder': '选择客户经理',
  'customer_ownership.assignment.current_out_of_band': '当前负责人: {owner}',
  'customer_ownership.assignment.duplicate_name_warning':
    '有多个客户经理叫「{name}」,请按登录方式和邮箱确认选中的是正确账号。',
  'customer_ownership.toast.assigned': '客户经理归属已更新',
  'customer_ownership.toast.revoked': '客户经理归属已撤销',

  // ── Customer manager workspace ─────────────────────────────────
  'customer_manager_workspace.kicker': 'Ops v2 · 真实工作台',
  'customer_manager_workspace.title': '客户经理工作台',
  'customer_manager_workspace.subtitle.admin': '查看真实客户经理组合,并复用客户经理可见的风险和客户详情信号。',
  'customer_manager_workspace.subtitle.manager': '跟进负责客户、风险信号、用量状态、API key 状态和生命周期证据。',
  'customer_manager_workspace.generated_at': '生成于 {time}',
  'customer_manager_workspace.generated_pending': '等待真实数据',
  'customer_manager_workspace.tabs_aria': '工作台分区',
  'customer_manager_workspace.tab.overview': '概览',
  'customer_manager_workspace.tab.customers': '客户列表',
  'customer_manager_workspace.tab.top_spend': '消费 Top',
  'customer_manager_workspace.tab.top_up_reminders': '催充值',
  'customer_manager_workspace.tab.quotation': '报价单',
  'customer_manager_workspace.quotation.brand': '品牌',
  'customer_manager_workspace.quotation.brand_placeholder': '选择品牌',
  'customer_manager_workspace.quotation.pick_brand': '先选择品牌，再填写报价',
  'customer_manager_workspace.quotation.customer_name': '客户名称',
  'customer_manager_workspace.quotation.quote_date': '报价日期',
  'customer_manager_workspace.quotation.valid_days': '有效天数',
  'customer_manager_workspace.quotation.note': '备注',
  'customer_manager_workspace.quotation.currency': '币种',
  'customer_manager_workspace.quotation.currency_cny': '人民币 CNY',
  'customer_manager_workspace.quotation.currency_usd': '美元 USD',
  'customer_manager_workspace.quotation.expand': '展开模型',
  'customer_manager_workspace.quotation.collapse': '收起模型',
  'customer_manager_workspace.quotation.namespaces': '命名空间',
  'customer_manager_workspace.quotation.empty': '该品牌暂无在售命名空间',
  'customer_manager_workspace.quotation.error': '报价目录加载失败',
  'customer_manager_workspace.quotation.col.include': '纳入',
  'customer_manager_workspace.quotation.col.namespace': '命名空间',
  'customer_manager_workspace.quotation.col.model': '模型',
  'customer_manager_workspace.quotation.col.sku_count': 'SKU 数',
  'customer_manager_workspace.quotation.col.floor': '销售底价',
  'customer_manager_workspace.quotation.floor_hint_excel':
    '销售底价只在本页提示，不会写入导出的 Excel',
  'customer_manager_workspace.quotation.col.discount': '折扣',
  'customer_manager_workspace.quotation.col.sku_discount': '单模型折扣',
  'customer_manager_workspace.quotation.col.status': '状态',
  'customer_manager_workspace.quotation.include': '纳入',
  'customer_manager_workspace.quotation.floor_unset': '未配置',
  'customer_manager_workspace.quotation.floor_hint': '最低 {zhe}折',
  'customer_manager_workspace.quotation.floor_partial': '部分 SKU 未配置底价',
  'customer_manager_workspace.quotation.below_floor': '低于销售底价',
  'customer_manager_workspace.quotation.discount_invalid': '折扣需为 0.1–10，最多一位小数',
  'customer_manager_workspace.quotation.overrides_count': '{count} 个模型单独折扣',
  'customer_manager_workspace.quotation.inherit_placeholder': '继承 {zhe}折',
  'customer_manager_workspace.quotation.category.text': '文本',
  'customer_manager_workspace.quotation.category.image': '图片',
  'customer_manager_workspace.quotation.category.video': '视频',
  'customer_manager_workspace.quotation.export': '导出 Excel',
  'customer_manager_workspace.quotation.export_failed': '导出失败，请重试',
  'customer_manager_workspace.quotation.clear': '清空草稿',
  'customer_manager_workspace.quotation.saved_status': '已保存到服务器：{customer}（第 {count} 次导出）',
  'customer_manager_workspace.quotation.save_failed': 'Excel 已下载，但保存到服务器失败，请重试导出',
  'customer_manager_workspace.quotation.save_skipped':
    '已下载。填写客户名称和报价日期后导出，报价单才会保存到服务器。',
  'customer_manager_workspace.quotation.saved_label': '载入已保存的报价单',
  'customer_manager_workspace.quotation.saved_placeholder': '选择客户…',
  'customer_manager_workspace.quotation.saved_load_failed': '载入失败，请重试',
  'customer_manager_workspace.overview.filtered': '概览已按客户搜索筛选：{email}',
  'customer_manager_workspace.metric.customers': '负责客户',
  'customer_manager_workspace.metric.balance': '账面余额合计(人民币)',
  'customer_manager_workspace.metric.active_keys': '活跃 API key',
  'customer_manager_workspace.metric.requests': '30 天请求',
  'customer_manager_workspace.metric.errors': '30 天失败',
  'customer_manager_workspace.search.placeholder': '搜索客户邮箱 / 企业名称…',
  'customer_manager_workspace.search.aria_label': '搜索客户邮箱或企业名称',
  'customer_manager_workspace.search.submit': '筛选',
  'customer_manager_workspace.search.clear': '清空',
  'customer_manager_workspace.error.title': '无法加载客户经理工作台',
  'customer_manager_workspace.empty.title': '暂无负责客户',
  'customer_manager_workspace.empty.body': '组织级客户经理归属生效后,客户组合会出现在这里。',
  'customer_manager_workspace.empty.filtered.title': '没有客户匹配当前筛选',
  'customer_manager_workspace.empty.filtered.body': '清空筛选或调整搜索条件。',
  'customer_manager_workspace.risk_queue.title': '风险队列',
  'customer_manager_workspace.risk_queue.count': '{count} 个客户',
  'customer_manager_workspace.risk_queue.item_detail': '{count} 个信号',
  'customer_manager_workspace.risk_queue.show_more': '展开下 {count} 条',
  'customer_manager_workspace.risk_queue.show_all': '展开全部（剩余 {count} 条）',
  'customer_manager_workspace.risk_queue.collapse': '收起',
  'customer_manager_workspace.column.customer': '客户',
  'customer_manager_workspace.column.org': '组织',
  'customer_manager_workspace.column.balance': '账面余额(人民币)',
  'customer_manager_workspace.column.usage': '用量',
  'customer_manager_workspace.column.keys': 'API key',
  'customer_manager_workspace.column.owner': '负责人',
  'customer_manager_workspace.column.risks': '风险',
  'customer_manager_workspace.column.actions': '操作',
  'customer_manager_workspace.usage.errors': '{count} 个失败',
  'customer_manager_workspace.usage.no_errors': '无失败',
  'customer_manager_workspace.keys.active': '{active}/{total} 活跃',
  'customer_manager_workspace.keys.last_used': '最近使用 {time}',
  'customer_manager_workspace.risk.none': '稳定',
  'customer_manager_workspace.action.customer_detail': '客户详情',
  'customer_manager_workspace.action.record_receipt': '记录收款',
  'customer_manager_workspace.action.sku_rates': 'SKU 费率',
  'customer_manager_workspace.pagination.aria_label': '客户经理工作台分页',
  'customer_manager_workspace.pagination.range': '显示第 {start}–{end} 条,共 {total} 条',
  'customer_manager_workspace.drawer.kicker': '客户详情',
  'customer_manager_workspace.drawer.error': '无法加载客户详情',
  'customer_manager_workspace.drawer.balance': '账面余额(人民币)',
  'customer_manager_workspace.drawer.requests': '请求',
  'customer_manager_workspace.drawer.errors': '失败',
  'customer_manager_workspace.drawer.active_keys': '活跃 key',
  'customer_manager_workspace.drawer.owner': '负责人和状态',
  'customer_manager_workspace.drawer.owner_name': '负责人',
  'customer_manager_workspace.drawer.assigned_at': '分配时间',
  'customer_manager_workspace.drawer.created_at': '创建时间',
  'customer_manager_workspace.drawer.last_request': '最近请求',
  'customer_manager_workspace.drawer.recent_failures': '近期失败',
  'customer_manager_workspace.drawer.latest_failure': '最近失败',
  'customer_manager_workspace.drawer.open_account': '打开账户详情',
  'customer_manager_workspace.drawer.api_keys': 'API key 状态',
  'customer_manager_workspace.drawer.key_name': '名称',
  'customer_manager_workspace.drawer.key_prefix': '前缀',
  'customer_manager_workspace.drawer.key_channel': '渠道',
  'customer_manager_workspace.drawer.key_state': '状态',
  'customer_manager_workspace.drawer.key_active': '活跃',
  'customer_manager_workspace.drawer.key_revoked': '已撤销',
  'customer_manager_workspace.drawer.lifecycle': '生命周期证据',
  'customer_manager_workspace.drawer.no_requests': '暂无近期请求证据。',
  'customer_manager_workspace.column.spend_30d': '30 天消费(人民币)',
  'customer_manager_workspace.money.error.title': '无法加载客户组合消费数据',
  'customer_manager_workspace.money.consumption_note': '消费口径:按客户计费(usd_charged)折算人民币展示,不是现金收入。',
  'customer_manager_workspace.money.funnel.owned': '负责客户',
  'customer_manager_workspace.money.funnel.active_30d': '30 天活跃客户',
  'customer_manager_workspace.money.funnel.paying_30d': '30 天充值客户',
  'customer_manager_workspace.money.funnel.conversion': '活跃→充值转化率',
  'customer_manager_workspace.money.funnel.conversion_pending': '暂无活跃客户',
  'customer_manager_workspace.money.trend.title': '客户组合消费趋势',
  'customer_manager_workspace.money.trend.days_30': '30 天',
  'customer_manager_workspace.money.trend.days_90': '90 天',
  'customer_manager_workspace.money.trend.aria': '客户组合每日消费(人民币)',
  'customer_manager_workspace.money.trend.empty': '窗口内暂无消费。',
  'customer_manager_workspace.money.trend.tooltip': '{date} · 消费 {amount} · {count} 次请求',
  'customer_manager_workspace.money.top.title': '消费 Top 客户(30 天)',
  'customer_manager_workspace.money.top.column.customer': '客户',
  'customer_manager_workspace.money.top.column.requests': '30 天请求',
  'customer_manager_workspace.money.top.column.spend': '30 天消费(人民币)',
  'customer_manager_workspace.money.top.column.actions': '操作',
  'customer_manager_workspace.money.top.empty': '30 天内暂无客户消费。',
  'customer_manager_workspace.money.top.open_360': '客户详情',
  'customer_manager_workspace.money.top.unlinked': '无客户档案',
  'customer_manager_workspace.runway.title': '催充值(Runway)',
  'customer_manager_workspace.runway.error.title': '无法加载催充值列表',
  'customer_manager_workspace.runway.note':
    '剩余天数 = 剩余额度 ÷ 近 {days} 天消耗速率;消费口径,非现金收入。',
  'customer_manager_workspace.runway.threshold': '≤ {days} 天',
  'customer_manager_workspace.runway.threshold_aria': '剩余天数阈值',
  'customer_manager_workspace.runway.column.customer': '客户',
  'customer_manager_workspace.runway.column.days_left': '剩余天数',
  'customer_manager_workspace.runway.column.remaining': '剩余额度(人民币)',
  'customer_manager_workspace.runway.column.burn_per_day': '日均消耗(人民币)',
  'customer_manager_workspace.runway.column.actions': '操作',
  'customer_manager_workspace.runway.days_value': '{days} 天',
  'customer_manager_workspace.runway.empty': '{days} 天内没有客户余额耗尽。',
  'customer_manager_workspace.runway.open_360': '客户详情',
  // Scoped usage overview (epic #1035 E6-D4, #1068)
  'crm_usage.title': '客户用量',
  'crm_usage.sub': '名下客户的请求、Token 与消费金额,按模型和 SKU 拆分。',
  'crm_usage.window_aria': '用量窗口',
  'crm_usage.window.today': '今日',
  'crm_usage.window.custom': '自定义',
  'crm_usage.from_local': 'From local',
  'crm_usage.to_local': 'To local',
  'crm_usage.account_filter': '仅看客户',
  'crm_usage.clear_filter': '清除筛选',
  'crm_usage.export_csv': '导出 CSV',
  'crm_usage.loading': '正在加载用量…',
  'crm_usage.error': '无法加载用量:{message}',
  'crm_usage.metric.requests': '请求数',
  'crm_usage.metric.requests_sub': '成功 {success} · 失败 {error}',
  'crm_usage.metric.tokens': 'Tokens',
  'crm_usage.metric.tokens_sub': '输入 {input} · 输出 {output}',
  'crm_usage.metric.cache': '缓存 Tokens',
  'crm_usage.metric.cache_sub': '命中 {read} · 写入 {write}',
  'crm_usage.metric.charged': '消费金额',
  'crm_usage.metric.charged_sub': '{usd} USD',
  'crm_usage.metric.window': '窗口',
  'crm_usage.metric.window_book_sub': '覆盖名下 {count} 个客户',
  'crm_usage.metric.window_customer_sub': '单客户视图',
  'crm_usage.metric.window_all_sub': '当前范围',
  'crm_usage.table.models': '按模型',
  'crm_usage.table.skus': '按 SKU',
  'crm_usage.table.empty_models': '暂无模型用量',
  'crm_usage.table.empty_skus': '暂无 SKU 用量',
  'crm_usage.col.object': '对象',
  'crm_usage.col.requests': '请求',
  'crm_usage.col.io_tokens': '输入/输出',
  'crm_usage.col.cache_tokens': '缓存读/写',
  'crm_usage.col.charged': '消费',
  'crm_usage.legacy_sku': '未归因历史用量',
  // Lead pricing panel (epic #1032 E3-P1, #1102)
  'lead_pricing.title': '销售定价',
  'lead_pricing.sub': '本品牌各 SKU 的销售成本。全站销售底价均由这些值按 USD_CNY 政策汇率推导;销售成本不得低于采购成本。',
  'lead_pricing.brand_aria': '品牌',
  'lead_pricing.policy_rate': 'USD_CNY 政策汇率 {rate}',
  'lead_pricing.error.load': '加载销售成本失败',
  'lead_pricing.empty.title': '目录中暂无 SKU',
  'lead_pricing.empty.hint': '销售成本挂在市场 SKU 上;请先添加渠道模型。',
  'lead_pricing.bulk.label': '批量销售成本(CNY/USD)',
  'lead_pricing.bulk.apply': '应用到全部',
  'lead_pricing.toast.saved': '销售成本已保存',
  'lead_pricing.toast.cleared': '销售成本已清除',
  'lead_pricing.toast.bulk_applied': '已原子化应用 {count} 个 SKU 的销售成本',
  'lead_pricing.table.model': '模型',
  'lead_pricing.table.procurement': '采购成本(CNY/USD)',
  'lead_pricing.table.procurement_floor': '采购底价 ×',
  'lead_pricing.table.sales_cost': '销售成本(CNY/USD)',
  'lead_pricing.table.sales_floor': '销售底价 ×',
  'lead_pricing.table.note': '备注',
  'lead_pricing.table.actions': '操作',
  'lead_pricing.row.save': '保存',
  'lead_pricing.row.clear': '清除',
  'lead_pricing.row.not_set': '未设置',
  'lead_pricing.alerts.title': '采购成本已超过销售成本',
  'lead_pricing.alerts.sub': '这些 SKU 的采购成本已高于你设置的销售成本——请提高销售成本以恢复利差。',
  'lead_pricing.alerts.table.model': 'SKU',
  'lead_pricing.alerts.table.procurement': '采购成本(CNY/USD)',
  'lead_pricing.alerts.table.sales_cost': '销售成本(CNY/USD)',
  'lead_pricing.alerts.table.since': '起始时间',
  // Salesperson invite codes (epic #1475). One page, three scopes — the
  // copy avoids promising a total quota the session cannot see: a CM
  // reads today's count, not a headroom the server computes per code.
  'invite_codes.title': '邀请码',
  'invite_codes.sub': '每位销售可以持有多张长期有效的邀请码,每张码可以各自绑定模型方案。新客户凭「邀请码 + 邮箱」自助开户并领取试用金。每张码自带试用金金额和有效期；当日开户数受该码的每日名额限制(按 UTC+8 自然日切,零点重置不结转)。',
  'invite_codes.readonly': '只读',
  'invite_codes.brand_aria': '品牌',
  'invite_codes.filter.brand_all': '所有品牌',
  'invite_codes.filter.aria': '状态筛选',
  'invite_codes.filter.all': '全部状态',
  'invite_codes.filter.active': '仅启用',
  'invite_codes.filter.disabled': '仅停用',
  'invite_codes.owner_filter.all': '全部销售',
  'invite_codes.owner_filter.label': '归属人',
  'invite_codes.empty': '暂无邀请码',
  'invite_codes.error.load': '加载邀请码失败',
  'invite_codes.no_profile.title': '尚未配置销售档案',
  'invite_codes.no_profile.hint': '邀请码按品牌分区,而当前账号还没有销售档案,无法确定可见范围。请让管理员在「运营用户」里为该账号指定品牌后重试。',
  'invite_codes.column.code': '邀请码',
  'invite_codes.column.owner': '归属销售',
  'invite_codes.column.brand': '品牌',
  'invite_codes.column.status': '状态',
  'invite_codes.column.today': '今日已用',
  'invite_codes.column.quota': '每日名额',
  'invite_codes.column.created': '创建时间',
  'invite_codes.column.plan': '模型方案',
  'invite_codes.column.actions': '操作',
  'invite_codes.plan.none': '—',
  'invite_codes.status.active': '启用',
  'invite_codes.status.disabled': '停用',
  'invite_codes.action.quota': '改配置',
  'invite_codes.action.rename': '改码',
  'invite_codes.action.disable': '停用',
  'invite_codes.action.enable': '启用',
  'invite_codes.action.activations': '邀请记录',
  'invite_codes.action.rebind': '换绑方案',
  // 邀请记录来自每次激活发放的那笔试用金,不是单独的流水表——文案只讲
  // 运营看得见的事实,不解释这一层。
  'invite_codes.activations.title': '{code} 的邀请记录',
  'invite_codes.activations.summary': '累计邀请 {total} 人,今日 {today} 人,每日名额 {quota}。',
  'invite_codes.activations.column.email': '客户邮箱',
  'invite_codes.activations.column.org': '组织',
  'invite_codes.activations.column.activated_at': '激活时间',
  'invite_codes.activations.column.grant': '试用金',
  'invite_codes.activations.column.grant_expires': '试用金到期',
  'invite_codes.activations.empty': '还没有人用这个码开过户',
  'invite_codes.activations.error': '加载邀请记录失败',
  'invite_codes.activations.truncated': '这里只列最近 {shown} 条,累计 {total} 条。',
  'invite_codes.toast.disabled': '邀请码已停用',
  'invite_codes.toast.enabled': '邀请码已启用',
  'invite_codes.disable.title': '停用这个邀请码?',
  'invite_codes.disable.body': '停用后 {code} 将无法再用于激活,已激活的客户不受影响。',
  'invite_codes.enable.title': '重新启用这个邀请码?',
  'invite_codes.enable.body': '{code} 将恢复可用。',
  'invite_codes.mint.open': '新建邀请码',
  'invite_codes.mint.title': '新建邀请码',
  'invite_codes.mint.description': '可以自定义邀请码,也可以留空由系统生成。一位销售可以同时持有多张启用中的码,每张码可以各自绑定不同的模型方案。',
  'invite_codes.mint.description_admin':
    '可以自定义邀请码,也可以留空由系统生成。一位销售可以同时持有多张启用中的码,每张码可以各自绑定不同的模型方案。先在下方选择品牌,再从该品牌的销售中选择归属人。',
  'invite_codes.mint.submit': '生成',
  'invite_codes.mint.owner_label': '归属销售',
  'invite_codes.mint.owner_hint': '凭此码激活的客户会自动挂到该销售名下。',
  'invite_codes.mint.owner_placeholder': '选择一名销售',
  'invite_codes.mint.owner_empty': '该品牌下暂无可选销售。',
  'invite_codes.mint.owner_load_error': '销售列表加载失败,请重新打开此弹窗重试。',
  'invite_codes.mint.quota_label': '每日名额',
  'invite_codes.mint.quota_hint': '已用推荐值预填。框里是什么,就会写到这张码上。',
  'invite_codes.mint.grant_label': '试用金金额(元)',
  'invite_codes.mint.grant_hint': '已用推荐值预填。框里是什么,就会写到这张码上。',
  'invite_codes.mint.expiry_label': '试用金有效期(天)',
  'invite_codes.mint.expiry_hint': '已用推荐值预填。框里是什么,就会写到这张码上。',
  'invite_codes.mint.plan_label': '模型方案',
  'invite_codes.mint.plan_hint': '可选。绑定后该码激活的客户按方案落地模型与折扣;与码上的一次性折扣互斥。',
  'invite_codes.mint.plan_none': '不绑定方案',
  'invite_codes.mint.plan_placeholder': '选择一个启用中的方案',
  'invite_codes.mint.plan_empty': '该品牌下暂无启用中的模型方案。',
  'invite_codes.mint.error.owner': '请选择归属销售',
  'invite_codes.mint.error.quota': '每日名额需为正整数',
  'invite_codes.mint.error.grant': '试用金金额需为大于 0 的数字,最多两位小数',
  'invite_codes.mint.error.grant_cap': '试用金金额超过上限 {max}',
  'invite_codes.mint.error.expiry': '试用金有效期需为正整数天',
  'invite_codes.mint.error.defaults': '无法加载全局激活默认值,请关闭后重试。',
  'invite_codes.mint.toast': '已生成邀请码 {code}',
  'invite_codes.custom_code.label': '自定义邀请码（选填）',
  'invite_codes.custom_code.hint':
    '留空则由系统生成。只能用大写字母、数字和连字符，后缀 {min}–{max} 位；与已有邀请码、推广码或礼品卡重复时不能创建。',
  'invite_codes.custom_code.error.format':
    '邀请码格式不正确：前缀固定为 {prefix}-，后缀 {min}–{max} 位，只能用大写字母、数字和连字符，且不能以连字符开头或结尾、不能连续两个连字符。',
  'invite_codes.custom_code.error.taken': '邀请码 {code} 已被占用，不能创建，请换一个',
  'invite_codes.rename.label': '新邀请码',
  'invite_codes.rename.hint':
    '只能用大写字母、数字和连字符，后缀 {min}–{max} 位；与已有邀请码、推广码或礼品卡重复时不能使用。',
  'invite_codes.rename.title': '修改邀请码 {code}',
  'invite_codes.rename.warning':
    '改码后旧码 {code} 立即失效，已发出的海报或链接需要更换；已激活的客户不受影响。',
  'invite_codes.rename.submit': '保存',
  'invite_codes.rename.toast': '邀请码已改为 {code}',
  'invite_codes.rename.error.taken': '邀请码 {code} 已被占用，不能修改，请换一个',
  'invite_codes.rebind.title': '换绑 {code} 的模型方案',
  'invite_codes.rebind.description': '客户挂在方案上;这张码只决定「以后激活的新客户」挂到哪个方案。这次换绑要不要连老客户一起搬,请在下方明确选择。',
  'invite_codes.rebind.current_plan': '当前绑定方案:{name}',
  'invite_codes.rebind.current_plan_none': '当前未绑定方案——激活走旧流程。',
  'invite_codes.rebind.plan_label': '模型方案',
  'invite_codes.rebind.unbind': '解绑(不再绑定方案)',
  'invite_codes.rebind.scope_label': '这次换绑影响谁?',
  'invite_codes.rebind.scope_future': '只影响以后的新客户',
  'invite_codes.rebind.scope_future_hint': '这张码已激活的客户保持现有方案与价格,完全不动。',
  'invite_codes.rebind.scope_restate': '连同这张码的存量客户一起搬到新方案',
  'invite_codes.rebind.scope_restate_hint': '仍归属这张码的客户会迁到所选方案,价格随之改变——确认前请看下方预览。这相当于一次批量改价。',
  'invite_codes.rebind.scope_restate_admin_only': '仅管理员可选——这是对在用客户的批量改价。',
  'invite_codes.rebind.scope_required': '请先选择这次换绑影响谁,再确认。',
  'invite_codes.rebind.unbind_note': '解绑只影响以后的新客户:此后扫这张码激活的客户不再绑定方案。已激活客户保持现有方案不变——解绑没有可迁入的目标方案,所以不存在「连同存量一起」的选项。',
  'invite_codes.rebind.preview_loading': '正在预览受影响的客户…',
  'invite_codes.rebind.preview_error': '无法加载重放预览',
  'invite_codes.rebind.preview_empty': '没有仍归属这张码、需要重放的客户。',
  'invite_codes.rebind.preview_orgs': '{count} 个客户会换到所选方案',
  'invite_codes.rebind.legacy_discounts':
    '这张码还带着一次性折扣。可用下方「从此码折扣生成方案」,或先在「改配置」里清空折扣再绑定。',
  'invite_codes.rebind.from_discounts.name': '方案名称',
  'invite_codes.rebind.from_discounts.hint': '品牌跟随这张码。写入前会做销售底价校验,不通过则什么都不写。',
  'invite_codes.rebind.from_discounts.name_required': '请填写方案名称',
  'invite_codes.rebind.from_discounts.submit': '从此码折扣生成方案',
  'invite_codes.rebind.from_discounts.created': '已生成方案 {name}',
  'invite_codes.rebind.from_discounts.open': '打开模型方案',
  'invite_codes.rebind.from_discounts.toast': '已生成并绑定方案 {name}',
  'invite_codes.rebind.submit': '确认换绑',
  'invite_codes.rebind.toast': '模型方案已更新',
  'invite_codes.rebind.toast_unbound': '已解绑模型方案',
  'invite_codes.quota.title': '调整 {code} 的激活配置',
  'invite_codes.quota.label': '每日名额',
  'invite_codes.quota.hint': '会作为显式值写到这张码上。点「恢复赠金、有效期与名额的推荐值」会带回这三项。已发放的试用金金额与到期时间不变;今日已用数不受影响。',
  'invite_codes.quota.grant_label': '试用金金额(元)',
  'invite_codes.quota.grant_hint': '会作为显式值写到这张码上。已发放的试用金不追溯。',
  'invite_codes.quota.expiry_label': '试用金有效期(天)',
  'invite_codes.quota.expiry_hint': '会作为显式值写到这张码上。已发放的试用金不追溯。',
  'invite_codes.quota.restore': '恢复赠金、有效期与名额的推荐值',
  'invite_codes.quota.submit': '保存',
  'invite_codes.quota.error.quota': '每日名额需为正整数',
  'invite_codes.quota.error.grant': '试用金金额需为大于 0 的数字,最多两位小数',
  'invite_codes.quota.error.grant_cap': '试用金金额超过上限 {max}',
  'invite_codes.quota.error.expiry': '试用金有效期需为正整数天',
  'invite_codes.quota.toast': '激活配置已更新',
  // Admin-only activation settings. Snapshot semantics are the load-
  // bearing part of this copy: changing the grant does not retroactively
  // top up or claw back balances already issued.
  'invite_codes.settings.panel': '激活配置(全局)',
  'invite_codes.settings.load_error': '加载激活配置失败',
  'invite_codes.settings.snapshot_note': '当前推荐试用金 {current}。改三项推荐值只影响下次建码弹窗带出来的数(以及仍为 NULL 的存量码)。赠金上限和登录验证码限制立即生效。已发放的试用金金额与到期时间不变。',
  'invite_codes.settings.save': '保存配置',
  'invite_codes.settings.toast.saved': '激活配置已保存',
  'invite_codes.settings.grant_amount_label': '推荐试用金金额(元)',
  'invite_codes.settings.grant_amount_hint': '建码弹窗的预填值。已经建好的码保持写在码上的数。按赠送余额规则消费:先扣赠送、先扣快到期的。',
  'invite_codes.settings.grant_expiry_label': '推荐试用金有效期(天)',
  'invite_codes.settings.grant_expiry_hint': '建码弹窗的预填值。自发放时刻起算。到期后剩余额度由每小时清扫任务自动扣除,不结转。',
  'invite_codes.settings.default_quota_label': '推荐每日名额',
  'invite_codes.settings.default_quota_hint': '建码弹窗的预填值。按 UTC+8 自然日切。已经建好的码保持写在码上的名额。',
  'invite_codes.settings.max_grant_label': '每码试用金上限(元)',
  'invite_codes.settings.max_grant_hint': '硬上限,立即生效。建码或改赠金时不得超过此值。上面的推荐试用金也不得高于此上限。',
  'invite_codes.settings.code_ttl_label': '验证码有效期(秒)',
  'invite_codes.settings.code_ttl_hint': '登录验证码从发送到失效的时长。',
  'invite_codes.settings.resend_interval_label': '重发间隔(秒)',
  'invite_codes.settings.resend_interval_hint': '同一邮箱两次索要验证码之间的最短间隔。',
  'invite_codes.settings.send_limit_label': '每邮箱每日发送上限',
  'invite_codes.settings.send_limit_hint': '单个邮箱一天最多能收到几封验证码邮件。',
  'invite_codes.settings.max_attempts_label': '单码尝试次数上限',
  'invite_codes.settings.max_attempts_hint': '同一个验证码最多可以试错几次,超出即作废需重新索要。',
  'invite_codes.settings.error.grant_amount': '试用金金额需为大于 0 的数字,最多两位小数',
  'invite_codes.settings.error.grant_expiry': '试用金有效期需为正整数天',
  'invite_codes.settings.error.default_quota': '推荐每日名额需为正整数',
  'invite_codes.settings.error.max_grant': '每码试用金上限需为大于 0 的数字,最多两位小数',
  'invite_codes.settings.error.grant_above_cap': '推荐试用金不能高于每码赠金上限',
  'invite_codes.settings.error.code_ttl': '验证码有效期需为正整数秒',
  'invite_codes.settings.error.resend_interval': '重发间隔需为正整数秒',
  'invite_codes.settings.error.send_limit': '每邮箱每日发送上限需为正整数',
  'invite_codes.settings.error.max_attempts': '单码尝试次数上限需为正整数',
  // Model plans (epic #2292 / #2297). A plan is a live catalog template
  // bound to invite codes: allowed model groups, per-group default
  // discount, per-SKU exceptions and visibility.
  'model_plans.title': '模型方案',
  'model_plans.sub': '邀请码绑定的模型与折扣模板。改方案会同步到已托管的客户;一次性赠金与配额仍按建码时快照。',
  'model_plans.readonly': '只读',
  'model_plans.brand_aria': '品牌',
  'model_plans.filter.brand_all': '所有品牌',
  'model_plans.filter.aria': '状态筛选',
  'model_plans.filter.all': '全部状态',
  'model_plans.filter.active': '仅启用',
  'model_plans.filter.archived': '仅归档',
  'model_plans.empty': '暂无模型方案',
  'model_plans.error.load': '加载模型方案失败',
  'model_plans.no_profile.title': '尚未配置销售档案',
  'model_plans.no_profile.hint': '模型方案按品牌分区,而当前账号还没有销售档案,无法确定可见范围。请让管理员在「运营用户」里为该账号指定品牌后重试。',
  'model_plans.column.name': '名称',
  'model_plans.column.brand': '品牌',
  'model_plans.column.status': '状态',
  'model_plans.column.entries': '分组 / SKU',
  'model_plans.column.bound_codes': '绑定邀请码',
  'model_plans.column.assigned_orgs': '托管客户',
  'model_plans.column.updated': '更新时间',
  'model_plans.column.actions': '操作',
  'model_plans.status.active': '启用',
  'model_plans.status.archived': '已归档',
  'model_plans.unsynced_badge': '有上新 {count}',
  'model_plans.action.create': '新建方案',
  'model_plans.action.edit': '编辑/详情',
  'model_plans.action.clone': '克隆',
  'model_plans.action.archive': '归档',
  'model_plans.action.unarchive': '取消归档',
  'model_plans.action.detail': '同步历史',
  'model_plans.action.sync': '同步新模型',
  'model_plans.create.title': '新建模型方案',
  'model_plans.edit.title': '编辑 {name}',
  'model_plans.editor.brand': '品牌',
  'model_plans.editor.name': '名称',
  'model_plans.editor.name_placeholder': '例如：标准套餐',
  'model_plans.editor.description': '说明',
  'model_plans.editor.description_placeholder': '可选',
  'model_plans.editor.add_namespace': '添加模型分组',
  'model_plans.editor.namespace_placeholder': '选择一个分组',
  'model_plans.editor.namespace_empty': '该品牌下暂无已上架分组,或已全部加入方案。',
  'model_plans.editor.default_rate': '分组默认倍率',
  'model_plans.editor.sales_floor': '销售底价 {floor}',
  'model_plans.editor.sales_floor_unset': '销售底价未配置',
  'model_plans.editor.sales_floor_partial': '部分 SKU 未配置销售底价',
  'model_plans.editor.remove_namespace': '移除分组',
  'model_plans.editor.sku_visible': '可见',
  'model_plans.editor.sku_hidden': '隐藏',
  'model_plans.editor.sku_rate': 'SKU 倍率(空则继承分组默认)',
  'model_plans.editor.sku_inherit': '继承',
  'model_plans.editor.sku_floor_header': '销售底价',
  'model_plans.editor.no_skus': '该分组暂无已上架 SKU。',
  'model_plans.editor.new_arrival_group':
    '有上新:{count} 个模型未纳入(当前对客户隐藏、按原价)',
  'model_plans.editor.new_arrival_sku': '新',
  'model_plans.editor.submit_create': '创建',
  'model_plans.editor.submit_edit': '预览影响范围',
  'model_plans.editor.error.name': '请填写方案名称',
  'model_plans.editor.error.brand': '请选择品牌',
  'model_plans.editor.error.entries': '至少添加一个模型分组',
  'model_plans.editor.error.rate': '倍率需为正数',
  'model_plans.floor.breaches_title': '以下 SKU 的解析价低于销售底价',
  'model_plans.floor.row': '{sku} · 解析价 {rate} < 销售底价 {floor}（来自{source}）',
  'model_plans.floor.row_no_rate': '{sku} · 销售底价 {floor}',
  'model_plans.floor.row_no_source': '{sku} · 解析价 {rate} < 销售底价 {floor}',
  'model_plans.floor.row_unknown_floor': '{sku} · 低于价格底线（该 SKU 未配置销售底价，按采购保底校验）',
  'model_plans.floor.row_unknown_floor_rate':
    '{sku} · 解析价 {rate} 低于价格底线（该 SKU 未配置销售底价，按采购保底校验）',
  'model_plans.floor.unconfigured_hint':
    '提示：未配置销售底价的 SKU 按采购保底地板拦截，具体数值不在本页展示。可先到「销售定价」页为这些 SKU 配置销售底价，或提高倍率后重试。',
  'model_plans.floor.source.group_default': '组默认',
  'model_plans.floor.source.sku_override': '本行覆盖',
  'model_plans.floor.hint': '解析价 {rate} < 销售底价 {floor}（来自{source}）',
  'model_plans.floor.group_default_error': '该默认倍率使部分 SKU 低于销售底价',
  'model_plans.floor.block_title': '倍率低于销售底价，无法保存',
  'model_plans.floor.block_body': '请改分组默认或对应 SKU 的覆盖倍率后再提交。',
  'model_plans.floor.block_back': '回去修改',
  'model_plans.floor.unparsable': '倍率低于销售底价，无法保存；明细获取失败，请刷新后重试。',
  'model_plans.dry_run.title': '确认改动影响范围',
  'model_plans.dry_run.body': '保存后会把方案重放到已托管客户。一次性赠金、配额和码上折扣不会被改写。',
  'model_plans.dry_run.bound_codes': '绑定中的邀请码 {count}',
  'model_plans.dry_run.assigned_orgs': '托管客户 {count}',
  'model_plans.dry_run.skipped_manual': '预计跳过手工行 {count}',
  'model_plans.dry_run.added': '新增 SKU {count}',
  'model_plans.dry_run.removed': '移除 SKU {count}',
  'model_plans.dry_run.rate_changed': '倍率变更 {count}',
  'model_plans.dry_run.visibility_changed': '可见性变更 {count}',
  'model_plans.dry_run.no_sku_deltas': 'SKU 集合与倍率、可见性没有变化。',
  'model_plans.dry_run.confirm': '确认保存并同步',
  'model_plans.dry_run.blocked': '存在低于底价的倍率,无法保存。',
  'model_plans.clone.title': '克隆方案',
  'model_plans.clone.body': '复制为同品牌的新启用方案。从归档方案克隆是恢复模板的方式。',
  'model_plans.clone.name': '新方案名称',
  'model_plans.clone.submit': '克隆',
  'model_plans.clone.error.name': '请填写新方案名称',
  'model_plans.archive.title': '归档这个方案?',
  'model_plans.archive.body': '归档后不能再编辑或绑定新邀请码。仍有启用中的邀请码绑定时无法归档。已托管客户不会自动脱管。',
  'model_plans.archive.active_codes': '仍有启用中的邀请码绑定此方案,请先换绑或停用这些码。',
  'model_plans.unarchive.title': '取消归档?',
  'model_plans.unarchive.body': '{name} 将重新启用,可以再绑定邀请码。',
  'model_plans.detail.title': '{name}',
  'model_plans.detail.runs': '同步记录',
  'model_plans.detail.runs_empty': '还没有同步记录',
  'model_plans.detail.runs_error': '加载同步记录失败',
  'model_plans.detail.trigger.items_update': '方案编辑',
  'model_plans.detail.trigger.rebind_restate': '换绑重放',
  'model_plans.detail.trigger.new_model_sync': '新模型同步',
  'model_plans.detail.trigger.manual_propagate': '手动同步',
  'model_plans.detail.trigger.rollback': '回滚',
  'model_plans.detail.status.running': '进行中',
  'model_plans.detail.status.done': '完成',
  'model_plans.detail.status.partial_failed': '部分失败',
  'model_plans.detail.stats': '{orgs} 个客户 · 更新 {updated} · 写入 {upserted} · 删除 {deleted}',
  'model_plans.detail.skipped': '跳过 {reason}: {count}',
  'model_plans.detail.rollback': '回滚这次同步',
  'model_plans.detail.rollback_title': '回滚这次同步?',
  'model_plans.detail.rollback_body': '只恢复仍由本方案托管的行。手工改过的行不会被覆盖。此操作会再记一条同步记录。',
  'model_plans.detail.rollback_confirm': '确认回滚',
  'model_plans.sync.title': '同步新模型 · {name}',
  'model_plans.sync.empty': '没有未纳入方案的已上架模型。',
  'model_plans.sync.admit': '纳入方案',
  'model_plans.sync.hint': '未勾选的模型保持缺席(隐藏且原价)。勾选后默认为隐藏 + 原价,可改可见性或倍率。',
  'model_plans.sync.submit': '预览影响范围',
  'model_plans.toast.created': '方案已创建',
  'model_plans.toast.updated': '方案已保存并同步',
  'model_plans.toast.cloned': '已克隆为 {name}',
  'model_plans.toast.archived': '方案已归档',
  'model_plans.toast.unarchived': '方案已恢复启用',
  'model_plans.toast.rolled_back': '已回滚',
  'model_plans.toast.propagated': '已同步到托管客户',
  'model_plans.run.orgs_total': '客户总数 {count}',
  'model_plans.run.orgs_updated': '已更新 {count}',
  'model_plans.run.rows_upserted': '写入 {count}',
  'model_plans.run.rows_deleted': '删除 {count}',
  'model_plans.select.none': '不绑定方案',
  'model_plans.select.placeholder': '选择一个启用中的方案',
  'model_plans.select.empty': '暂无启用中的方案',
  'model_plans.select.load_error': '方案列表加载失败',
  // Commission + book-performance report (epic #1034 E5-M5, #1147). The
  // copy keeps the two attributions visibly apart — commission is
  // receipt-based, performance is lot-based, and they are never summed.
  'commission_report.title': '佣金与业绩',
  'commission_report.sub': '佣金按回款结算,业绩按份额归属 — 两者口径不同,不作汇总',
  'commission_report.period_aria': '选择结算周期',
  'commission_report.kpi.commission': '佣金合计(按回款)',
  'commission_report.kpi.book_margin': '业绩毛利(按份额)',
  'commission_report.commission.title': '佣金结算单',
  'commission_report.commission.attribution': '按回款归属(回款核销时刻)',
  'commission_report.commission.table.period': '周期',
  'commission_report.commission.table.brand': '品牌',
  'commission_report.commission.table.status': '状态',
  'commission_report.commission.table.total': '佣金合计',
  'commission_report.commission.table.updated': '更新时间',
  'commission_report.commission.empty': '本周期暂无佣金结算单',
  'commission_report.status.draft': '草稿',
  'commission_report.status.confirmed': '已确认',
  'commission_report.detail.title': '结算单明细',
  'commission_report.detail.table.source_type': '来源类型',
  'commission_report.detail.table.source_id': '来源单据',
  'commission_report.detail.table.net': '净额',
  'commission_report.detail.table.rate': '费率',
  'commission_report.detail.table.commission': '佣金',
  'commission_report.detail.empty': '暂无明细行',
  'commission_report.source.receipt': '回款',
  'commission_report.source.payment_order': '在线订单',
  'commission_report.source.clawback': '退款冲回',
  'commission_report.source.adjustment': '调整',
  'commission_report.book.title': '业绩(消费口径)',
  'commission_report.book.attribution': '按份额归属(份额创建时刻)',
  'commission_report.book.table.customer': '客户',
  'commission_report.book.table.revenue': '消费额(CNY)',
  'commission_report.book.table.gift_cost': '赠送成本(CNY)',
  'commission_report.book.table.margin': '毛利(CNY)',
  'commission_report.book.table.charges': '计费次数',
  'commission_report.book.house': '公摊(无归属)',
  'commission_report.book.empty': '本周期暂无业绩',
  'commission_report.book.lag_caveat': '业绩为按份额归属的消费口径,当计费的份额分摊尚未回放时会滞后偏低;佣金按回款结算,不受影响。',
  'commission_report.error.load': '加载失败',
  // Lead brand leaderboard (epic #1034 E5-M5, #1147). Its own page —
  // finance-reachable, off the procurement-bearing pricing page.
  'lead_leaderboard.title': '品牌销售榜',
  'lead_leaderboard.sub': '每位销售的佣金(按回款)与业绩毛利(按份额)并列 — 两者口径不同,不作汇总',
  'lead_leaderboard.attribution': '佣金按回款,业绩按份额 — 两列口径不同',
  'lead_leaderboard.period_aria': '选择周期',
  'lead_leaderboard.brand_aria': '选择品牌',
  'lead_leaderboard.all_brands': '全部品牌',
  'lead_leaderboard.table.manager': '销售',
  'lead_leaderboard.table.commission': '佣金(按回款)',
  'lead_leaderboard.table.book_margin': '业绩毛利(按份额)',
  'lead_leaderboard.table.customers': '客户数',
  'lead_leaderboard.house': '公摊',
  'lead_leaderboard.empty': '本周期暂无数据',
  'lead_leaderboard.error': '加载销售榜失败',
  'lead_leaderboard.lag_caveat': '业绩毛利为按份额归属的消费口径,当计费的份额分摊尚未回放时会滞后偏低;佣金按回款结算,不受影响。',
  // SKU procurement-vs-sales spread (epic #1034 E5-M5, #1147).
  'sku_spread.title': 'SKU 利差',
  'sku_spread.sub': '采购成本 vs 销售成本(采购成本仅 lead/admin 可见)',
  'sku_spread.table.model': '模型',
  'sku_spread.table.procurement': '采购成本(CNY/USD)',
  'sku_spread.table.sales_cost': '销售成本(CNY/USD)',
  'sku_spread.table.spread': '利差(CNY/USD)',
  'sku_spread.empty': '暂无 SKU',
  'sku_spread.error': '加载利差失败',
  'commission_rate.title': '佣金费率',
  'commission_rate.sub': '本品牌的销售佣金费率,按结算月生效、只增不改。',
  'commission_rate.current.unknown': '正在加载当前费率…',
  'commission_rate.current.none': '{period} 结算月尚未设置生效费率。',
  'commission_rate.current.value': '{period} 结算月当前费率:{rate}(自 {effective} 起生效)。',
  'commission_rate.form.percent_label': '费率(%)',
  'commission_rate.form.percent_hint': '按百分比填写,例如 5 表示 5%。',
  'commission_rate.form.percent_preview': '= {bps} 个基点',
  'commission_rate.form.effective_label': '生效月份',
  'commission_rate.form.effective_hint': '选择该费率开始生效的结算月(不可追溯到过去)。',
  'commission_rate.form.effective_min': '最早可选 {period}(不可追溯)。',
  'commission_rate.form.append': '追加费率',
  'commission_rate.error.percent': '费率必须是 0 到 100 之间、且可换算为整数基点的百分比。',
  'commission_rate.error.period': '生效月份格式无效(应为 YYYY-MM)。',
  'commission_rate.error.retroactive': '生效月份不得早于当前结算月 {period};佣金费率不可追溯。',
  'commission_rate.toast.appended': '佣金费率已追加',
  'commission_rate.empty.title': '暂无佣金费率',
  'commission_rate.empty.hint': '追加一条按结算月生效的费率即可开始。',
  'commission_rate.table.effective_from': '生效月份',
  'commission_rate.table.rate': '费率',
  'commission_rate.table.created_by': '创建人',
  'commission_rate.table.created_at': '创建时间',
  'commission_rate.table.current_badge': '当前',
  'customer_manager_360.back': '返回客户经理工作台',
  'customer_manager_360.kicker': '客户详情',
  'customer_manager_360.usage_link': '客户用量',
  'customer_manager_360.error': '无法加载客户详情',
  'customer_manager_360.missing_org': '缺少客户组织 ID',
  'customer_manager_360.metric.balance': '账面余额(人民币)',
  'customer_manager_360.metric.requests': '30 天请求',
  'customer_manager_360.metric.cost': '30 天人民币消费',
  // #1958: 汇率读不到时不画长度为 0 的条 —— 那看起来像"金额是 0"。
  'customer_manager_360.metric.rate_unavailable': '汇率不可读，人民币金额无法换算。',
  'customer_manager_360.metric.active_keys': '活跃 API key',
  'customer_manager_360.metric.errors': '30 天失败',
  'customer_manager_360.tabs_aria': '客户详情分区',
  'customer_manager_360.tab.overview': '概览',
  'customer_manager_360.tab.credit': '授信',
  'customer_manager_360.tab.usage': '用量',
  'customer_manager_360.usage.window_aria': '用量时间窗口',
  'customer_manager_360.usage.window.today': '今天',
  'customer_manager_360.usage.window.week': '本周',
  'customer_manager_360.usage.window.month': '本月',
  'customer_manager_360.usage.window.7d': '近 7 天',
  'customer_manager_360.usage.window.30d': '近 30 天',
  'customer_manager_360.usage.window.custom': '自定义',
  'customer_manager_360.usage.from_utc8': '开始（北京时间）',
  'customer_manager_360.usage.to_utc8': '结束（北京时间）',
  'customer_manager_360.usage.tz_hint': '按 北京时间 (UTC+8) 切天',
  'customer_manager_360.usage.export_csv': '导出 CSV',
  'customer_manager_360.usage.loading': '正在加载用量…',
  'customer_manager_360.usage.error': '无法加载用量：{message}',
  'customer_manager_360.usage.empty.title': '该窗口内没有请求',
  'customer_manager_360.usage.empty.hint': '换一个时间窗口，或等该客户产生用量后再看。',
  'customer_manager_360.usage.kpi.spend': '消费金额（{window}至今）',
  'customer_manager_360.usage.kpi.spend_sub': '{usd} · 汇率 {rate}{delta} 对比{baseline}',
  'customer_manager_360.usage.kpi.avg': '日均消费',
  'customer_manager_360.usage.kpi.avg_hour': '时均消费',
  'customer_manager_360.usage.kpi.avg_sub': '上期全期 {amount}',
  'customer_manager_360.usage.kpi.requests': '请求次数',
  'customer_manager_360.usage.kpi.requests_sub': '{baseline} {amount}',
  'customer_manager_360.usage.kpi.success': '成功率',
  'customer_manager_360.usage.kpi.success_sub': '失败 {error} · 限流 {limited}',
  'customer_manager_360.usage.kpi.tokens': 'Token 量（入 / 出）',
  'customer_manager_360.usage.kpi.tokens_sub': '综合缓存命中率 {pct}',
  'customer_manager_360.usage.compare.at_point': '上期同一时点',
  'customer_manager_360.usage.compare.same_span': '上期同段',
  'customer_manager_360.usage.runway.title': '余额跑道',
  'customer_manager_360.usage.runway.burn_aria': '燃烧速率基准',
  'customer_manager_360.usage.runway.burn_caption': '燃烧速率基准',
  'customer_manager_360.usage.runway.burn.1': '近 1 天',
  'customer_manager_360.usage.runway.burn.3': '近 3 天',
  'customer_manager_360.usage.runway.burn.7': '近 7 天',
  'customer_manager_360.usage.runway.burn.30': '近 30 天',
  'customer_manager_360.usage.runway.days_label': '余额可用天数',
  'customer_manager_360.usage.runway.days_unit': '天',
  'customer_manager_360.usage.runway.infinite': '按当前速率不会耗尽',
  'customer_manager_360.usage.runway.depletes_on': '预计 {date}（{weekday}）耗尽',
  'customer_manager_360.usage.runway.remaining': '当前余额',
  'customer_manager_360.usage.runway.burn_rate': '燃烧速率',
  'customer_manager_360.usage.runway.burn_rate_value': '{amount} / 天 · {baseline}',
  'customer_manager_360.usage.runway.alert_threshold': '告警阈值',
  'customer_manager_360.usage.runway.alert_days': '{days} 天',
  'customer_manager_360.usage.runway.alert_window': '告警窗口',
  'customer_manager_360.usage.runway.depleted': '余额耗尽',
  'customer_manager_360.usage.runway.today': '今天',
  'customer_manager_360.usage.runway.plus_days': '+{days}d',
  'customer_manager_360.usage.runway.note':
    '算法与催充值清单相同：余额 ÷ 燃烧速率。默认近 7 天，保证两个面给同一个数。近 1 天和近 3 天会因周内节律差出一周，所以主数字下面给出其他口径的区间。',
  'customer_manager_360.usage.runway.empty': '没有可用的余额跑道。',
  'customer_manager_360.usage.runway.sensitivity': '其他口径：{min} ~ {max} 天',
  'customer_manager_360.usage.runway.sensitivity_infinite': '其他口径：{min} ~ {max} 天（含不会耗尽）',
  'customer_manager_360.usage.runway.sensitivity_only_infinite': '其他口径含不会耗尽',
  'customer_manager_360.usage.runway.sensitivity_pending': '正在加载其他口径…',
  'customer_manager_360.usage.runway.sensitivity_error': '部分口径取数失败',
  'customer_manager_360.usage.trend.title': '用量趋势',
  'customer_manager_360.usage.trend.metric.usd': '消费 ¥',
  'customer_manager_360.usage.trend.metric.reqs': '请求次数',
  'customer_manager_360.usage.trend.metric.tok': 'Token',
  'customer_manager_360.usage.trend.mode.point': '逐点',
  'customer_manager_360.usage.trend.mode.cum': '累计',
  'customer_manager_360.usage.trend.compare.on': '对比上期',
  'customer_manager_360.usage.trend.compare.off': '关闭',
  'customer_manager_360.usage.trend.legend.current': '本期 · {window}',
  'customer_manager_360.usage.trend.legend.previous': '上期',
  'customer_manager_360.usage.trend.legend.now': '竖线 = 现在；右侧暗区尚未发生',
  'customer_manager_360.usage.trend.cmp.current': '{window}至此刻 · {metric}',
  'customer_manager_360.usage.trend.cmp.previous': '{baseline}',
  'customer_manager_360.usage.trend.cmp.delta': '差值',
  'customer_manager_360.usage.trend.cmp.previous_full': '上期全期',
  'customer_manager_360.usage.trend.cmp.projection': '按上期形状外推本期全期',
  'customer_manager_360.usage.trend.cmp.projection_estimate': '预估',
  'customer_manager_360.usage.trend.cmp.no_projection': '滚动窗口不外推',
  'customer_manager_360.usage.trend.cmp.no_projection_hint': '本期已走完，只有末日进行中',
  'customer_manager_360.usage.trend.note.rolling':
    '对比基线按 7 的整数倍向前平移，让每一天对上同一个星期几。滚动窗口不外推。',
  'customer_manager_360.usage.trend.note.calendar':
    '「外推」= 本期至此刻 × (上期全期 ÷ 上期同一时点)，借上期形状补完剩下的时间 — 是预估，不是事实。',
  'customer_manager_360.usage.trend.hover.mode.point': '当桶',
  'customer_manager_360.usage.trend.hover.mode.cum': '累计至此',
  'customer_manager_360.usage.trend.series.current': '本期',
  'customer_manager_360.usage.trend.series.previous': '上期',
  'customer_manager_360.usage.trend.empty': '该窗口没有可画的用量点。',
  'customer_manager_360.usage.trend.aria': '客户用量趋势',
  'customer_manager_360.usage.models.title': '模型构成',
  'customer_manager_360.usage.skus.title': 'SKU 占比',
  'customer_manager_360.usage.share.by_spend': '按消费',
  'customer_manager_360.usage.share.by_requests': '按请求',
  'customer_manager_360.usage.share.by_tokens': '按 Token',
  'customer_manager_360.usage.share.model': '模型',
  'customer_manager_360.usage.share.sku': 'SKU',
  'customer_manager_360.usage.share.requests': '请求',
  'customer_manager_360.usage.share.tokens': 'Token 入 / 出',
  'customer_manager_360.usage.share.spend': '消费 ¥',
  'customer_manager_360.usage.share.share': '占比',
  'customer_manager_360.usage.share.other': '其余 {count} 项',
  'customer_manager_360.usage.share.empty': '该窗口没有构成数据。',
  'customer_manager_360.usage.daily.title': '每日明细',
  'customer_manager_360.usage.daily.title_hour': '小时明细',
  'customer_manager_360.usage.daily.compare': '对比上期同日',
  'customer_manager_360.usage.daily.compare_hour': '对比上期同时',
  'customer_manager_360.usage.daily.nonzero': '仅看有消费的天',
  'customer_manager_360.usage.daily.nonzero_hour': '仅看有消费的小时',
  'customer_manager_360.usage.daily.date': '日期',
  'customer_manager_360.usage.daily.date_hour': '小时',
  'customer_manager_360.usage.daily.requests': '请求',
  'customer_manager_360.usage.daily.input': 'Token 入',
  'customer_manager_360.usage.daily.output': 'Token 出',
  'customer_manager_360.usage.daily.spend': '消费 ¥',
  'customer_manager_360.usage.daily.prev_spend': '上期同日 ¥',
  'customer_manager_360.usage.daily.prev_spend_hour': '上期同时 ¥',
  'customer_manager_360.usage.daily.delta': '差值',
  'customer_manager_360.usage.daily.lead_model': '主力模型',
  'customer_manager_360.usage.daily.in_progress': '进行中',
  'customer_manager_360.usage.daily.empty': '该窗口没有每日明细。',
  'customer_manager_360.usage.daily.empty_hour': '该窗口没有小时明细。',
  'customer_manager_360.usage.daily.expand': '… 展开其余 {count} 天 …',
  'customer_manager_360.usage.daily.expand_hour': '… 展开其余 {count} 小时 …',
  'customer_manager_360.usage.weekday.0': '周日',
  'customer_manager_360.usage.weekday.1': '周一',
  'customer_manager_360.usage.weekday.2': '周二',
  'customer_manager_360.usage.weekday.3': '周三',
  'customer_manager_360.usage.weekday.4': '周四',
  'customer_manager_360.usage.weekday.5': '周五',
  'customer_manager_360.usage.weekday.6': '周六',
  'metrics.chart.now': '现在',
  'metrics.chart.hover.pending': '尚未发生',
  'metrics.chart.hover.delta': '差值',
  // Per-customer service kill switch (epic #1033 E4-C7, #1131).
  'customer_manager_360.service_suspension.title': '服务开关',
  'customer_manager_360.service_suspension.status.active': '服务正常',
  'customer_manager_360.service_suspension.status.suspended': '已停用',
  'customer_manager_360.service_suspension.description':
    '停用后该客户的所有模型请求都会被网关拦截（返回 SERVICE_SUSPENDED），与余额、授信无关；可随时恢复。',
  'customer_manager_360.service_suspension.action.suspend': '停用服务',
  'customer_manager_360.service_suspension.action.resume': '恢复服务',
  'customer_manager_360.service_suspension.confirm.title_suspend': '确认停用服务？',
  'customer_manager_360.service_suspension.confirm.title_resume': '确认恢复服务？',
  'customer_manager_360.service_suspension.confirm.body_suspend':
    '停用后该客户将立即无法调用任何模型，直到再次恢复。此操作会记入审计日志。',
  'customer_manager_360.service_suspension.confirm.body_resume':
    '恢复后该客户可以重新正常调用模型。此操作会记入审计日志。',
  'customer_manager_360.service_suspension.reason_label': '原因（选填）',
  'customer_manager_360.service_suspension.reason_placeholder': '记录本次操作的原因，便于审计追溯。',
  'customer_manager_360.service_suspension.toast.suspended': '已停用该客户的服务。',
  'customer_manager_360.service_suspension.toast.resumed': '已恢复该客户的服务。',
  'customer_manager_360.tab.requests': '请求日志',
  'customer_manager_360.tab.receipts': '收款单',
  'customer_manager_360.tab.api_keys': 'API Keys',
  'customer_manager_360.tab.gift_cards': '礼品卡',
  'customer_manager_360.tab.sku_rates': 'SKU 费率',
  'customer_manager_360.tab.rate_limits': '限额',
  'customer_manager_360.tab.admin_actions': '管理员操作',
  'customer_manager_360.tab.task_admission': '任务并发',
  'customer_manager_360.rate_limits.title': '租户限额',
  'customer_manager_360.rate_limits.hint': '覆盖优先于 SKU 默认；都空则不限。请在配额中心编辑。',
  'customer_manager_360.rate_limits.l1_off': 'L1 未开启，限额不生效。',
  'customer_manager_360.rate_limits.sku': 'SKU',
  'customer_manager_360.rate_limits.sku_default': 'SKU 默认',
  'customer_manager_360.rate_limits.org_override': '租户覆盖',
  'customer_manager_360.rate_limits.effective': '生效',
  'customer_manager_360.rate_limits.rpm': 'RPM',
  'customer_manager_360.rate_limits.tpm': 'TPM（含缓存）',
  'customer_manager_360.rate_limits.tpm_uncached': '非缓存 TPM',
  'customer_manager_360.rate_limits.save': '保存',
  'customer_manager_360.rate_limits.clear': '清除',
  'customer_manager_360.rate_limits.use_clear': '三维（RPM / TPM（含缓存）/ 非缓存 TPM）都空时请用清除，而不是保存。',
  'customer_manager_360.rate_limits.effective_hint': '生效值 = 覆盖 ?? 默认 ?? 不限',
  'customer_manager_360.rate_limits.unlimited': '不限',
  'customer_manager_360.rate_limits.invalid': '须为正整数，或留空表示不覆盖。',
  'customer_manager_360.rate_limits.toast_saved': '租户限额已保存',
  'customer_manager_360.rate_limits.toast_cleared': '租户限额已清除',
  'customer_manager_360.rate_limits.empty': '该租户没有可见 SKU。',
  'customer_manager_360.rate_limits.loading': '加载限额…',
  'customer_manager_360.admin.hint':
    '仅管理员可见的账户管控操作。以下每个动作都要求 ops-admin 角色、需输入确认,并写入审计日志。',
  'customer_manager_360.overview.account': '账户状态',
  'customer_manager_360.overview.risks': '风险信号',
  'customer_manager_360.overview.transactions': '最近交易',
  'customer_manager_360.overview.lifecycle': '近期生命周期',
  'customer_manager_360.overview.money_dashboard': '人民币余额与消费',
  'customer_manager_360.overview.money_dashboard_hint': '账面余额和用量消费都按人民币展示。',
  'customer_manager_360.overview.usage_dashboard': '请求结果',
  'customer_manager_360.overview.usage_dashboard_hint': '30 天成功率、失败与 API key 状态。',
  'customer_manager_360.overview.account_dashboard_hint': '账户状态与风险信号。',
  'customer_manager_360.overview.transaction_dashboard_hint': '最近账务流动保留原始额度账本金额。',
  'customer_manager_360.overview.lifecycle_dashboard_hint': '仅展示请求证据摘要,不暴露原始 Trace。',
  'customer_manager_360.overview.success_rate': '成功率',
  'customer_manager_360.overview.errors': '失败',
  'customer_manager_360.overview.successful_requests': '成功请求',
  'customer_manager_360.overview.key_posture': 'API key 状态',
  'customer_manager_360.risk.none': '稳定',
  'customer_manager_360.empty.transactions': '暂无最近交易。',
  'customer_manager_360.empty.lifecycle': '暂无近期请求证据。',
  'customer_manager_360.money.orders': '充值订单',
  'customer_manager_360.money.orders_hint': '客户的充值订单状态,回答"充了钱怎么没到账"。只读事实,对账操作由平台侧处理。',
  'customer_manager_360.money.anomalies': '计费异常',
  'customer_manager_360.money.anomalies_hint': '客户当前未解决的计费异常。只读事实,处理动作由平台侧完成。',
  'customer_manager_360.money.loading': '加载中…',
  'customer_manager_360.money.error': '加载失败,请稍后重试。',
  'customer_manager_360.money.empty.orders': '暂无充值订单。',
  'customer_manager_360.money.empty.anomalies': '暂无未解决的计费异常。',
  'customer_manager_360.money.col.created': '创建时间',
  'customer_manager_360.money.col.order_no': '订单号',
  'customer_manager_360.money.col.pay_type': '支付方式',
  'customer_manager_360.money.col.amount': '金额',
  'customer_manager_360.money.col.status': '状态',
  'customer_manager_360.money.col.paid_at': '到账时间',
  'customer_manager_360.money.status.pending': '待支付',
  'customer_manager_360.money.status.paid': '已到账',
  'customer_manager_360.money.status.expired': '已过期',
  'customer_manager_360.money.status.failed': '支付失败',
  'customer_manager_360.money.status.refunded': '已退款',
  'customer_manager_360.money.pay_type.alipay': '支付宝',
  'customer_manager_360.money.pay_type.wxpay': '微信支付',
  'customer_manager_360.money.anomaly.open': '未解决',
  'customer_manager_360.money.anomaly.resolved': '已解决',
  'customer_manager_360.empty.requests': '没有请求日志匹配当前筛选。',
  'customer_manager_360.empty.api_keys': '暂无 API key。',
  'customer_manager_360.empty.sku_rates': '暂无 SKU。',
  'customer_manager_360.sku_rates.title': 'SKU 费率',
  'customer_manager_360.sku_rates.sku_count': '{count} 个 SKU',
  'customer_manager_360.sku_rates.bulk_rate_label': '批量倍率',
  'customer_manager_360.sku_rates.bulk_apply': '应用',
  'customer_manager_360.sku_rates.state_unpriced': '待定价',
  'customer_manager_360.sku_rates.state_discount_pending': '折扣待审批 / 未开放',
  'customer_manager_360.sku_rates.state_priced': '已定价未开放',
  'customer_manager_360.sku_rates.state_open': '已开放',
  'customer_manager_360.sku_rates.state_converging': '待缓存收敛',
  'customer_manager_360.sku_rates.state_degraded': '缓存收敛降级',
  'customer_manager_360.sku_rates.state_unknown': '收敛状态未知',
  'customer_manager_360.sku_rates.convergence_refresh_failed':
    '状态刷新失败，实际生效状态仍未确认；请重试刷新。',
  'customer_manager_360.sku_rates.convergence_unknown_hint':
    '服务未返回有效生效时间，状态保持未知；请刷新确认。',
  'customer_manager_360.sku_rates.retry_convergence': '重试状态刷新',
  'customer_manager_360.sku_rates.open_blocked_unpriced': '请先保存已应用的客户价格，再开放该 SKU。',
  'customer_manager_360.sku_rates.open_blocked_pending_discount':
    '折扣仍在审批中；请先完成应用或关闭审批，再开放该 SKU。',
  'customer_manager_360.sku_rates.bulk_preview_title': '预览批量开放',
  'customer_manager_360.sku_rates.bulk_preview_hint': '逐项校验已应用价格。路由覆盖属于独立的 Activation readiness，本次可见性保存不会校验。',
  'customer_manager_360.sku_rates.bulk_confirm_show': '确认批量开放',
  'customer_manager_360.sku_rates.bulk_ready': '可提交',
  'customer_manager_360.sku_rates.bulk_blocked_unpriced': '待定价',
  'customer_manager_360.sku_rates.bulk_blocked_pending_discount': '折扣待审批',
  'customer_manager_360.sku_rates.bulk_preview_row_ready':
    '已应用价格 {price} · 路由 readiness 需单独检查',
  'customer_manager_360.sku_rates.bulk_preview_row_unpriced':
    '尚无已应用客户价格，本次将跳过该 SKU。',
  'customer_manager_360.sku_rates.bulk_preview_row_pending_discount':
    '审批尚未应用折扣价格，本次将跳过该 SKU。',

  // ── 线路（namespace）级协议价 (2026-07-31 design §5.2) ──
  //
  // 商务协议签在「客户 × 线路」这一层，不是逐个模型签。线路是表达协议的地方，
  // 下面的模型行只展示各自解析出来的结果。
  'customer_manager_360.namespace_rate.line_rate': '线路协议价',
  'customer_manager_360.namespace_rate.state_agreed': '已签协议',
  'customer_manager_360.namespace_rate.state_platform': '平台价',
  'customer_manager_360.namespace_rate.save': '保存线路价',
  'customer_manager_360.namespace_rate.save_for': '保存 {namespace} 的线路协议价',
  'customer_manager_360.namespace_rate.clear': '清除线路价',
  'customer_manager_360.namespace_rate.clear_for': '清除 {namespace} 的线路协议价',
  'customer_manager_360.namespace_rate.platform_rate': '平台价 {rate}',
  'customer_manager_360.namespace_rate.cost_floor': '成本底线 {floor}',
  'customer_manager_360.namespace_rate.sales_floor': '销售底价 {floor}',
  'customer_manager_360.namespace_rate.floor_unset': '未设底价',
  'customer_manager_360.namespace_rate.show_line': '开放整条线路',
  'customer_manager_360.namespace_rate.hide_line': '隐藏整条线路',
  'customer_manager_360.namespace_rate.show_blocked':
    '先为这条线路签定协议价，才能整条开放。',
  'customer_manager_360.namespace_rate.breaches_title':
    '以下模型的成本底线高于该线路价，整条协议未保存：',
  'customer_manager_360.namespace_rate.breach_row': '{sku}（底线 {floor}）',
  'customer_manager_360.namespace_rate.breach_row_unknown_floor': '{sku}',
  'customer_manager_360.namespace_rate.breaches_hint':
    '给这些模型单独定价，或提高线路价后重试。',
  'customer_manager_360.toast.namespace_rate_updated': '线路协议价已更新',
  'customer_manager_360.toast.namespace_rate_cleared': '线路协议价已清除',
  'customer_manager_360.toast.namespace_visibility_updated': '线路开放状态已更新',

  // 模型行上的来源标记：同一个数字既可能来自线路继承，也可能是这个模型自己的
  // 例外价，两者在界面上长得一模一样 —— 只有 rate_source 能区分。
  'customer_manager_360.rate_source.inherited': '继承自线路',
  'customer_manager_360.rate_source.sku_override': '本模型单独定价',
  'customer_manager_360.rate_source.platform': '平台价',
  'customer_manager_360.rate_source.optout': '不跟随线路折扣',
  'customer_manager_360.rate_source.optout_hint':
    '此 SKU 被配置为不继承产品线级费率协议。线路协议仍然存在，仍作用于这条线上的其他模型。',
  'customer_manager_360.rate_source.inherit_blocked':
    '该价格继承自线路，请在上方线路协议价处清除。',
  'customer_manager_360.rate_source.exception_hint':
    '保存后将只为该模型单独定价，线路协议价保持不变。',
  'customer_manager_360.field.owner': '负责人',
  'customer_manager_360.field.assigned_at': '分配时间',
  'customer_manager_360.field.created_at': '创建时间',
  'customer_manager_360.field.verified': '邮箱已验证',
  'customer_manager_360.field.suspended': '冻结时间',
  'customer_manager_360.field.deleted': '删除时间',
  'customer_manager_360.field.account_id': '账户 ID',
  'customer_manager_360.filter.from': '开始时间',
  'customer_manager_360.filter.to': '结束时间',
  'customer_manager_360.filter.request_id': 'Request ID',
  'customer_manager_360.filter.status': '状态',
  'customer_manager_360.filter.lifecycle_status': 'Lifecycle 状态',
  'customer_manager_360.filter.any': '全部',
  'customer_manager_360.filter.success': '成功',
  'customer_manager_360.filter.error': '失败',
  'customer_manager_360.filter.api_key': 'API Key',
  'customer_manager_360.filter.channel_slug': '渠道 slug',
  'customer_manager_360.filter.apply': '筛选',
  'customer_manager_360.filter.reset_30d': '最近 30 天',
  'customer_manager_360.requests.error': '无法加载请求日志',
  'customer_manager_360.pagination.aria_label': '请求记录分页',
  'customer_manager_360.pagination.range': '显示第 {start}–{end} 条,共 {total} 条',
  'customer_manager_360.column.type': '类型',
  'customer_manager_360.column.amount': '金额',
  'customer_manager_360.column.reason': '原因',
  'customer_manager_360.column.created_at': '时间',
  'customer_manager_360.column.request': '请求',
  'customer_manager_360.column.api_key': 'API Key',
  'customer_manager_360.column.channel': '渠道',
  'customer_manager_360.column.visibility': '可见性',
  'customer_manager_360.column.model': '模型',
  'customer_manager_360.column.status': '状态',
  'customer_manager_360.column.cost': '费用',
  'customer_manager_360.cost.pending': '计费中',
  'customer_manager_360.column.logs': '日志',
  'customer_manager_360.column.name': '名称',
  'customer_manager_360.column.prefix': '前缀',
  'customer_manager_360.column.last_used': '最近使用',
  'customer_manager_360.column.state': '状态',
  'customer_manager_360.column.action': '操作',
  'customer_manager_360.column.vendor': '厂商',
  'customer_manager_360.column.platform_rate': '平台倍率',
  'customer_manager_360.column.cost_floor': '成本底线',
  'customer_manager_360.column.sales_floor': '销售底价',
  'customer_manager_360.column.effective_rate': '客户倍率',
  'customer_manager_360.column.note': '备注',
  'customer_manager_360.state.active': '活跃',
  'customer_manager_360.state.revoked': '已吊销',
  'customer_manager_360.revoke.reason': '吊销原因',
  'customer_manager_360.action.revoke': '吊销',
  'customer_manager_360.action.issue': '申请',
  'customer_manager_360.action.redeem': '代充',
  'customer_manager_360.action.inherit': '继承',
  'customer_manager_360.action.save_rate_for': '保存 {sku} 的客户倍率',
  'customer_manager_360.action.inherit_rate_for': '清除 {sku} 的客户倍率并恢复继承',
  'customer_manager_360.action.hide_sku': '隐藏',
  'customer_manager_360.action.show_sku': '显示',
  'customer_manager_360.action.hide_sku_for': '隐藏 {sku}',
  'customer_manager_360.action.show_sku_for': '显示 {sku}',
  'customer_manager_360.action.confirm_show_sku': '再次确认开放',
  'customer_manager_360.action.hide_namespace': '隐藏全部',
  'customer_manager_360.action.show_namespace': '显示全部',
  'customer_manager_360.visibility.visible': '已开放',
  'customer_manager_360.visibility.hidden': '未开放',
  'customer_manager_360.visibility.pricing_required': '待定价',
  'customer_manager_360.visibility.rate_applied': '客户倍率已应用',
  'customer_manager_360.visibility.show_requires_rate': '先保存并应用客户倍率，再开放此 SKU。',
  'customer_manager_360.visibility.show_waits_for_approval':
    '折扣审批通过并刷新客户资料后，才能开放此 SKU。',
  'customer_manager_360.visibility.clear_requires_hidden':
    '此 SKU 已开放；请先隐藏，再清除客户倍率。',
  'customer_manager_360.rate.clear_below_line_floor':
    '清除后此 SKU 将回落到线路倍率，低于它自身的成本下限。请保留单独倍率，或调高线路倍率。',
  'customer_manager_360.visibility.bulk_show_partial':
    '{count} 个 SKU 尚未应用客户倍率；批量显示只处理已就绪的 SKU。',
  'customer_manager_360.visibility.bulk_show_blocked':
    '全部 SKU 都尚未应用客户倍率，请先完成定价。',
  'customer_manager_360.visibility.bulk_result':
    '批量结果：已应用 {applied} 个，审批中 {pending} 个，失败 {failed} 个',
  'customer_manager_360.rate.cny_per_credit': '{amount}/额度',
  'customer_manager_360.rate.floor_unset': '未设底价',
  'customer_manager_360.logs.view_lifecycle': 'Lifecycle',
  'customer_manager_360.logs.hide_lifecycle': '收起',
  'customer_manager_360.logs.open_global': '打开日志',
  'customer_manager_360.logs.trace_available': '有 Trace',
  'customer_manager_360.logs.trace_unavailable': '无 Trace',
  'customer_manager_360.logs.controls.title': '日志控件',
  'customer_manager_360.logs.controls.hint': '仅作用于当前客户的请求日志；Trace 正文仍留在全局日志工作流。',
  'customer_manager_360.logs.controls.visible': '当前页 {count} 条 · Trace {trace} 条',
  'customer_manager_360.logs.controls.refresh': '刷新日志',
  'customer_manager_360.logs.controls.errors_only': '只看失败',
  'customer_manager_360.logs.controls.expand': '展开 lifecycle',
  'customer_manager_360.logs.controls.collapse': '收起',
  'customer_manager_360.logs.lifecycle_loading': '正在加载 lifecycle…',
  'customer_manager_360.logs.lifecycle_error': '无法加载 lifecycle',
  'customer_manager_360.logs.lifecycle_empty': '打开 lifecycle 后加载请求事件。',
  'customer_manager_360.logs.metric.total': '总耗时',
  'customer_manager_360.logs.metric.upstream': '上游',
  'customer_manager_360.logs.metric.client': '客户端',
  'customer_manager_360.logs.metric.billing': '计费',
  'customer_manager_360.logs.metric.trace': 'Trace',
  'customer_manager_360.logs.phase': '阶段',
  'customer_manager_360.logs.event_time': '本地时间',
  'customer_manager_360.logs.elapsed': '已过',
  'customer_manager_360.logs.duration': '耗时',
  'customer_manager_360.logs.outcome': '结果',
  // Service level catalogue (#1723/#1725) — admin page.
  'service_levels.title': '服务等级',
  'service_levels.subtitle': '把路由配置包装成面向客户的等级;客户经理只按名称指派,看不到锚定的路由配置。',
  'service_levels.create_btn': '新建等级',
  'service_levels.loading': '正在加载服务等级…',
  'service_levels.error.title': '服务等级列表加载失败',
  'service_levels.error.hint': '请稍后重试;若持续失败,请检查 ops-api 与网关的连通性。',
  'service_levels.error.retry': '重试',
  'service_levels.error.retrying': '重试中…',
  'service_levels.empty.title': '还没有服务等级',
  'service_levels.empty.hint': '新建一个等级后,客户经理即可在客户 360 按名称指派。',
  'service_levels.empty.action': '新建等级',
  'service_levels.col.name': '名称',
  'service_levels.col.description': '客户可见说明',
  'service_levels.col.anchor': '锚定路由配置',
  'service_levels.col.customers': '客户数',
  'service_levels.col.status': '状态',
  'service_levels.col.actions': '操作',
  'service_levels.status.active': '启用',
  'service_levels.status.archived': '已归档',
  'service_levels.description.empty': '未填写',
  'service_levels.anchor.none': '不改变路由',
  'service_levels.anchor.unavailable': '已停用配置({id})',
  'service_levels.anchor.deprecated': '{name}（已过时，仅保留现有成员）',
  'service_levels.validation.anchor_deprecated':
    '该路由配置已被标记为过时，不能再被新的服务等级锚定。已锚定它的等级和已在其中的客户不受影响。',
  'service_levels.row.manage': '编辑',
  'service_levels.row.archive': '归档',
  'service_levels.row.restore': '恢复启用',
  'service_levels.field.name': '名称',
  'service_levels.field.name_placeholder': '例如:标准版',
  'service_levels.field.description': '客户可见说明',
  'service_levels.field.description_hint': '客户经理会在客户 360 看到这段文字;留空则不显示说明。',
  'service_levels.field.description_placeholder': '这个等级对客户意味着什么',
  'service_levels.field.anchor': '锚定路由配置',
  'service_levels.field.anchor_hint': '留空表示该等级不改变任何路由,仅作为对外命名。',
  'service_levels.field.anchor_error': '路由配置列表暂不可用,当前无法修改锚定配置。',
  'service_levels.create_modal.title': '新建服务等级',
  'service_levels.create_modal.description': '名称与说明会展示给客户经理;锚定的路由配置只有管理员可见。',
  'service_levels.create_modal.submit': '创建',
  'service_levels.manage_modal.title': '编辑等级:{name}',
  'service_levels.manage_modal.description': '修改会立即影响所有已指派该等级的客户。',
  'service_levels.manage_modal.submit': '保存',
  'service_levels.archive_modal.title': '归档服务等级',
  'service_levels.archive_modal.body': '归档「{name}」后,客户经理无法再把客户指派到该等级;已在该等级上的客户保持不变。',
  'service_levels.archive_modal.confirm': '归档',
  'service_levels.validation.name_required': '请填写等级名称',
  'service_levels.validation.name_taken': '该名称已被占用,请换一个',
  'service_levels.validation.anchor_missing': '所选路由配置不存在或已停用,请重新选择',
  'service_levels.toast.created': '服务等级已创建',
  'service_levels.toast.updated': '服务等级已更新',
  'service_levels.toast.archived': '服务等级已归档',
  'service_levels.toast.restored': '服务等级已恢复启用',
  // Customer 360 service-level assignment (#1723/#1725) — the customer
  // manager's whole routing surface, deliberately free of any routing vocabulary.
  'customer_manager_360.tab.service_level': '服务等级',
  'customer_manager_360.service_level.caption': '服务等级',
  'customer_manager_360.service_level.default_option': '默认',
  'customer_manager_360.service_level.default_description': '该客户使用平台默认服务,未套用任何等级。',
  'customer_manager_360.service_level.no_description': '该等级暂无说明。',
  'customer_manager_360.service_level.archived_option': '{name}(已归档)',
  'customer_manager_360.service_level.save': '保存',
  'customer_manager_360.service_level.saved': '服务等级已更新',
  'customer_manager_360.service_level.state_default': '当前:默认',
  'customer_manager_360.service_level.state_level': '当前:{name}',
  'customer_manager_360.service_level.state_custom': '当前:单独配置',
  'customer_manager_360.service_level.custom_label': '自定义(管理员配置)',
  'customer_manager_360.service_level.custom_hint': '该客户由管理员单独配置,不属于任何服务等级;如需调整请联系管理员。',
  'customer_manager_360.service_level.custom_admin_hint': '该客户由管理员单独配置,未套用服务等级。在下方选择一个等级会覆盖这份配置,选「默认」则清除它。',
  'customer_manager_360.service_level.save_error.archived': '该等级已归档,无法再指派;请另选一个。',
  'customer_manager_360.service_level.save_error.missing': '该等级已不存在;请刷新页面后另选一个。',
  'customer_manager_360.service_level.save_error.forbidden': '你没有修改该客户服务等级的权限。',
  'customer_manager_360.service_level.save_error.admin_managed': '本页面打开后,管理员已将该客户改为单独配置,服务等级对其不再生效。请刷新页面查看当前设置。',
  'customer_manager_360.service_level.save_error.generic': '服务等级保存失败;请刷新页面确认当前设置后再重试。',
  'customer_manager_360.service_level.clear_modal.title': '确认清除该客户的单独配置?',
  'customer_manager_360.service_level.clear_modal.body': '该客户由管理员单独配置,未套用服务等级。改为「默认」会丢弃这份配置——本页面无法撤销,要恢复需到审计日志中查回最初的指派记录。',
  'customer_manager_360.service_level.clear_modal.confirm': '改为默认',
  'customer_manager_360.service_level.error': '服务等级信息加载失败。',
  'customer_manager_360.service_level.retry': '重试',
  'customer_manager_360.egress_policy.caption': '出口策略',
  'customer_manager_360.egress_policy.global': '全局默认',
  'customer_manager_360.egress_policy.global_badge': '兜底',
  'customer_manager_360.egress_policy.overrides': '覆盖 {count} 条规则',
  'customer_manager_360.egress_policy.change': '更换',
  'customer_manager_360.egress_policy.unassign': '解除指派',
  'customer_manager_360.egress_policy.unassign_body': '将 {customer} 从策略「{policy}」上解除，改回全局默认。',
  'customer_manager_360.egress_policy.no_email': '无邮箱',
  'customer_manager_360.egress_policy.assign_confirm_title': '确认更换出口策略？',
  'customer_manager_360.egress_policy.assign_confirm_body': '将 {customer} 指派到策略「{policy}」。',
  'customer_manager_360.egress_policy.link': '打开出口策略页',
  'customer_manager_360.egress_policy.saved': '出口策略指派已更新',
  'customer_manager_360.egress_policy.error': '出口策略信息加载失败。',
  // Customer 360 custom-routes tab (#1658, restored admin-only by #1785) — the
  // admin's direct routing-config assignment, beside the service-level tab
  // above rather than instead of it. Routing vocabulary is fine here: the tab
  // is gated on TENANT_ROUTING_ASSIGN_ROLES and never reaches a customer
  // manager.
  'customer_manager_360.tab.custom_routes': '专属路由',
  'customer_manager_360.task_admission.caption': '任务并发覆盖',
  'customer_manager_360.task_admission.sku': 'SKU',
  'customer_manager_360.task_admission.default': 'SKU 默认',
  'customer_manager_360.task_admission.overlay': '客户覆盖',
  'customer_manager_360.task_admission.actions': '操作',
  'customer_manager_360.task_admission.save': '保存',
  'customer_manager_360.task_admission.clear': '清除',
  'customer_manager_360.task_admission.saved': '任务并发覆盖已保存',
  'customer_manager_360.task_admission.cleared': '任务并发覆盖已清除',
  'customer_manager_360.task_admission.no_overlay': '使用 SKU 默认',
  'customer_manager_360.task_admission.empty': '没有 ark 视频 SKU',
  'customer_manager_360.task_admission.empty_hint': '只能给已经发布任务并发文档的 SKU 设置覆盖。',
  'customer_manager_360.task_admission.error': '无法加载该客户的任务并发覆盖。',
  'customer_manager_360.task_admission.retry': '重试',
  'customer_manager_360.task_admission.invalid': '每项覆盖必须是 1 到 64 的整数。',
  'customer_manager_360.task_admission.save_error': '覆盖保存失败。',
  'customer_manager_360.task_admission.clear_error': '覆盖清除失败。',
  'customer_manager_360.session_policy.caption': '会话策略',
  'customer_manager_360.session_policy.content': '内容播种',
  'customer_manager_360.session_policy.random': '随机',
  'customer_manager_360.session_policy.hint':
    '随机：每次请求新会话，不命中前缀缓存，用于不传会话标识且内容分不开的客户。',
  'customer_manager_360.session_policy.saved': '会话策略已保存',
  'customer_manager_360.session_policy.save_error': '会话策略保存失败。',
  'customer_manager_360.session_policy.error': '无法加载该客户的会话策略。',
  'customer_manager_360.session_policy.retry': '重试',
  'customer_manager_360.custom_routes.custom_title': '客户路由配置',
  'customer_manager_360.custom_routes.route_config': '路由配置',
  'customer_manager_360.custom_routes.config_users': '使用客户数',
  'customer_manager_360.custom_routes.switch_hint':
    '为该客户绑定一组有序的路由配置。每个 SKU 从上往下找第一个覆盖它的配置生效；inherit 未覆盖则看下一条，exclusive 排到即终止。空栈跟随全局默认。SKU 目标池与执行策略统一在「租户路由 → 路由配置」管理面维护，此处仅可查看。',
  'customer_manager_360.custom_routes.following_global': '跟随全局默认',
  'customer_manager_360.custom_routes.inherited_config_line': '当前生效的全局默认配置：{name}',
  'customer_manager_360.custom_routes.manage_configs': '打开路由配置管理',
  'customer_manager_360.custom_routes.status.assigned_config': '已分配',
  'customer_manager_360.custom_routes.summary.title_named': '当前生效配置：{name}',
  'customer_manager_360.custom_routes.summary.view_full': '查看完整配置',
  'customer_manager_360.custom_routes.summary.following_hint':
    '该客户跟随全局默认配置，目标池详情请打开完整配置查看。',
  'customer_manager_360.custom_routes.summary.loading': '正在加载该配置的 SKU 路由摘要…',
  'customer_manager_360.custom_routes.summary.load_error':
    'SKU 路由目标与策略暂不可用；做路由判断前请打开完整配置核对。',
  'customer_manager_360.custom_routes.summary.coverage': '启用 SKU 覆盖',
  'customer_manager_360.custom_routes.summary.coverage_value': '{covered}/{total}',
  'customer_manager_360.custom_routes.summary.coverage_complete': '已覆盖全部目录 SKU',
  'customer_manager_360.custom_routes.summary.coverage_missing_count': '缺少 {count} 个 SKU',
  'customer_manager_360.custom_routes.summary.missing_skus': '缺失或仅含停用目标的 SKU',
  'customer_manager_360.custom_routes.summary.missing_more': '另 {count} 个',
  'customer_manager_360.custom_routes.summary.sku_routes': '配置级 SKU 路由',
  'customer_manager_360.custom_routes.summary.sku_routes_hint':
    '这些配置级 SKU 目标池就是已配置 SKU 使用的完整路由集合。',
  'customer_manager_360.custom_routes.summary.empty_sku':
    '该配置没有 SKU 目标行，请打开完整配置补充 SKU 覆盖。',
  'customer_manager_360.custom_routes.summary.column.sku': '命名空间 / SKU',
  'customer_manager_360.custom_routes.summary.column.upstream': '上游路径',
  'customer_manager_360.custom_routes.summary.column.dispatch': '调度参数',
  'customer_manager_360.custom_routes.summary.column.priority': '优先级',
  'customer_manager_360.custom_routes.toast.config_assigned': '路由配置已分配',
  'customer_manager_360.custom_routes.add_config': '添加配置',
  'customer_manager_360.custom_routes.add_placeholder': '选择要追加的配置',
  'customer_manager_360.custom_routes.save': '保存栈',
  'customer_manager_360.custom_routes.dirty': '未保存',
  'customer_manager_360.custom_routes.move_up': '上移',
  'customer_manager_360.custom_routes.move_down': '下移',
  'customer_manager_360.custom_routes.remove': '移除',
  'customer_manager_360.custom_routes.exclusive_not_last':
    '独占配置不在栈底时，其后的配置不会生效。',
  'customer_manager_360.custom_routes.toast.stack_saved': '路由栈已保存',
  'customer_manager_requests.kicker': 'Ops v2 · 客户经理',
  'customer_manager_requests.title': '组合请求日志',
  'customer_manager_requests.subtitle': '查询所有隶属于当前客户经理的客户请求,不暴露未分配客户。',
  'customer_manager_requests.generated_at': '生成于 {time}',
  'customer_manager_requests.generated_pending': '等待真实数据',
  'customer_manager_requests.error': '无法加载请求日志',
  'customer_manager_requests.controls.customers': '{count} 个客户',
  'customer_manager_requests.controls.hint': '范围限定为当前客户经理名下客户;Lifecycle 展开仍按单个客户校验。',
  'customer_manager_requests.column.customer': '客户',
  'customer_manager_360.gift.issue': '发放赠金（直接入账）',
  'customer_manager_360.gift.issue_hint':
    '直接为客户入账，无兑换码。月度赠金额度内立即到账；超出额度将自动提交销售主管审批，审批通过前不动账。',
  'customer_manager_360.gift.amount_yuan': '金额（元，整数）',
  'customer_manager_360.gift.redeem': '代客户充值',
  'customer_manager_360.gift.code': '礼品卡码',
  'customer_manager_360.gift.reason': '原因',
  'customer_manager_360.validation.reason': '需要填写原因',
  'customer_manager_360.validation.amount': '金额必须大于 0',
  'customer_manager_360.validation.gift_code': '需要填写礼品卡码',
  'customer_manager_360.validation.rate_floor': '不能低于成本底线 {floor}',
  'customer_manager_360.toast.key_revoked': 'API key 已吊销',
  'customer_manager_360.gift.credited_result': '已入账 {amount}',
  'customer_manager_360.gift.over_quota_result':
    '超出额度：申请 {amount}，本月剩余 {remaining}（额度 {quota}）——已提交销售主管审批。',
  'customer_manager_360.toast.gift_credited': '赠金已入账',
  'customer_manager_360.toast.gift_over_quota_submitted': '已提交超额审批申请',
  'customer_manager_360.toast.gift_card_redeemed': '已完成代充',
  // ── 余额批次（赠送可见性）─────────────────────────────────
  'customer_manager_360.lots.title': '余额批次（按消耗顺序）',
  'customer_manager_360.lots.hint': '仍有余额的批次，自上而下即扣费顺序：先扣即将过期的赠金，再扣现金。已用尽的批次不再列出。',
  'customer_manager_360.lots.loading': '加载中…',
  'customer_manager_360.lots.error': '余额批次加载失败',
  'customer_manager_360.lots.empty': '该账户当前没有未用尽的余额批次',
  'customer_manager_360.lots.truncated': '仅显示前 {count} 条，账户上还有更多批次。',
  'customer_manager_360.lots.column.kind': '类型',
  'customer_manager_360.lots.column.source': '来源',
  'customer_manager_360.lots.column.remaining': '剩余',
  'customer_manager_360.lots.column.amount': '面额',
  'customer_manager_360.lots.column.expires_at': '到期',
  'customer_manager_360.lots.column.owner': '归属销售',
  'customer_manager_360.lots.kind.promo': '赠送',
  'customer_manager_360.lots.kind.cash': '现金',
  'customer_manager_360.lots.never_expires': '不过期',
  'customer_manager_360.lots.expires_in_days': '{days} 天后',
  'customer_manager_360.lots.expires_today': '今天',
  'customer_manager_360.lots.expired': '已过期',
  'customer_manager_360.toast.sku_rate_updated': 'SKU 费率已更新',
  'customer_manager_360.toast.sku_rate_saved_still_hidden': '客户倍率已保存；SKU 仍未开放，请确认后手动开放',
  'customer_manager_360.toast.sku_visibility_updated': 'SKU 可见性已更新',
  'customer_manager_360.toast.bulk_applied': '已批量应用 {count} 个 SKU',
  'customer_manager_360.toast.bulk_partial': '已应用 {ok} 个，{failed} 个失败',
  // ── 低于销售底价折扣审批（epic #1032 E3-P3，#1106）─────────────────
  'customer_manager_360.sku_rates.pending_discount': '折扣审批中',
  'customer_manager_360.sku_rates.below_floor_hint': '低于销售底价 {floor}，保存后将提交折扣审批',
  'customer_manager_360.model_plan.banner':
    '该客户的模型与折扣由模型方案「{name}」托管',
  'customer_manager_360.model_plan.banner_hint':
    '改方案会影响所有绑定该方案的客户,不只这一家。',
  'customer_manager_360.model_plan.edit_plan': '修改方案',
  'customer_manager_360.model_plan.clone_rebind': '克隆方案并单独换绑',
  'customer_manager_360.model_plan.lock_tooltip': '该客户由模型方案托管,请到上方横幅操作。',
  'customer_manager_360.model_plan.managed_toast':
    '该客户的模型与折扣由模型方案「{name}」托管,请修改方案而不是改这一家。',
  'customer_manager_360.model_plan.assign': '指定方案',
  'customer_manager_360.model_plan.assign_title': '为该客户指定模型方案',
  'customer_manager_360.model_plan.assign_submit': '指定',
  'customer_manager_360.model_plan.detach': '脱管转手工',
  'customer_manager_360.model_plan.detach_title': '脱管转手工?',
  'customer_manager_360.model_plan.detach_body':
    '方案不再托管这家客户。现有费率与可见性行会保留,只是放开手工编辑,不会删除。',
  'customer_manager_360.model_plan.detach_confirm': '确认脱管',
  'customer_manager_360.model_plan.toast_assigned': '已指定模型方案',
  'customer_manager_360.model_plan.toast_detached': '已脱管,行已转为手工',
  'customer_manager_360.model_plan.clone_name': '新方案名称',
  'customer_manager_360.toast.discount_approval_submitted': '折扣审批已提交，等待销售负责人审批',
  'customer_manager_360.toast.bulk_discount_pending': '{count} 个 SKU 低于销售底价，已转为折扣审批',

  // ── 客户转移/释放审批（epic #1030 E1-S5，#1041）───────────────────
  'customer_transfer.action': '申请转移/释放',
  'customer_transfer.pending_chip': '转移审批中',
  'customer_transfer.dialog.title': '申请客户转移/释放',
  'customer_transfer.dialog.description': '提交后由同品牌销售负责人审批，审批通过前客户归属不变。',
  'customer_transfer.dialog.customer': '客户',
  'customer_transfer.mode.label': '方式',
  'customer_transfer.mode.release': '释放回公海',
  'customer_transfer.mode.release_hint': '审批通过后客户回到无归属公海。',
  'customer_transfer.mode.transfer': '转移给同品牌客户经理',
  'customer_transfer.mode.transfer_hint': '审批通过后客户归属原子转移给指定客户经理。',
  'customer_transfer.target.label': '接收客户经理',
  'customer_transfer.target.placeholder': '选择同品牌客户经理',
  'customer_transfer.target.empty': '没有可选的同品牌客户经理',
  'customer_transfer.target.required': '请选择接收客户经理',
  'customer_transfer.target.duplicate_name_warning':
    '有多个客户经理叫「{name}」,请按登录方式和邮箱确认选中的是正确账号。',
  'customer_transfer.reason.label': '原因',
  'customer_transfer.reason.placeholder': '给审批人的说明（可选）',
  'customer_transfer.reason.hint': '最多 500 字，随申请一并展示给审批人。',
  'customer_transfer.submit': '提交申请',
  'customer_transfer.toast.submitted': '转移/释放申请已提交，等待审批',
  'customer_transfer.error.duplicate': '该客户已有进行中的转移/释放申请，请先等待审批或撤销原申请。',

  // ── Customer 360 · CRM 联系方式与跟进记录（epic #1035 E6-D5, #1069）──
  'customer_crm.contact.caption': '联系方式',
  'customer_crm.contact.edit': '编辑',
  'customer_crm.contact.empty': '还没有联系方式，点击「编辑」补全联系卡。',
  'customer_crm.contact.field.contact_name': '联系人',
  'customer_crm.contact.field.wechat': '微信',
  'customer_crm.contact.field.phone': '电话',
  'customer_crm.contact.field.contact_email': '邮箱',
  'customer_crm.contact.field.company': '企业名称',
  'customer_crm.contact.field.remark': '备注',
  'customer_crm.contact.updated': '{time} 由 {user} 更新',
  'customer_crm.contact.dialog.title': '编辑联系方式',
  'customer_crm.contact.dialog.description': '每个客户组织一张联系卡，保存即整卡覆盖并记录审计。',
  'customer_crm.contact.toast.saved': '联系方式已保存',
  'customer_crm.error.title': '无法加载 CRM 信息',
  'customer_crm.notes.caption': '跟进记录',
  'customer_crm.notes.composer.placeholder': '记录一次跟进……',
  'customer_crm.notes.composer.aria': '新跟进记录',
  'customer_crm.notes.composer.submit': '添加记录',
  'customer_crm.notes.append_only_hint': '记录只增不删：提交后不可编辑或删除。',
  'customer_crm.notes.toast.posted': '跟进记录已添加',
  'customer_crm.notes.empty': '还没有跟进记录。',
  'customer_crm.notes.pagination_aria': '跟进记录分页',

  'customer_manager_pool.kicker': 'Ops v2 · 客户池',
  'customer_manager_pool.title': '客户池',
  'customer_manager_pool.subtitle': '未被认领的活跃客户先进入客户池,客户经理可以从这里认领并开始跟进。',
  'customer_manager_pool.admin_subtitle': '未分配的活跃客户先进入客户池,管理员可以从这里指定客户经理。',
  'customer_manager_pool.generated_at': '生成于 {time}',
  'customer_manager_pool.generated_pending': '等待真实数据',
  'customer_manager_pool.metric.available': '可认领客户',
  'customer_manager_pool.metric.balance': '账面余额合计(人民币)',
  'customer_manager_pool.metric.active_keys': '活跃 API key',
  'customer_manager_pool.metric.requests': '30 天请求',
  'customer_manager_pool.metric.risks': '有风险客户',
  'customer_manager_pool.search.placeholder': '搜索客户池邮箱 / 企业名称...',
  'customer_manager_pool.search.aria_label': '搜索客户池邮箱或企业名称',
  'customer_manager_pool.search.submit': '筛选',
  'customer_manager_pool.search.clear': '清空',
  'customer_manager_pool.error.title': '无法加载客户池',
  'customer_manager_pool.empty.title': '暂无未认领客户',
  'customer_manager_pool.empty.body': '当前活跃客户都有负责人,或还没有客户进入可认领状态。',
  'customer_manager_pool.empty.filtered.title': '没有客户匹配当前筛选',
  'customer_manager_pool.empty.filtered.body': '清空筛选或调整搜索条件。',
  'customer_manager_pool.column.customer': '客户',
  'customer_manager_pool.column.org': '组织',
  'customer_manager_pool.column.created_at': '创建时间',
  'customer_manager_pool.column.balance': '账面余额(人民币)',
  'customer_manager_pool.column.usage': '用量',
  'customer_manager_pool.column.keys': 'API key',
  'customer_manager_pool.column.risks': '风险',
  'customer_manager_pool.column.actions': '操作',
  'customer_manager_pool.usage.errors': '{count} 个失败',
  'customer_manager_pool.usage.no_errors': '无失败',
  'customer_manager_pool.keys.active': '{active}/{total} 活跃',
  'customer_manager_pool.keys.last_used': '最近使用 {time}',
  'customer_manager_pool.risk.none': '稳定',
  'customer_manager_pool.claim.action': '认领',
  'customer_manager_pool.claim.claiming': '认领中',
  'customer_manager_pool.claim.claimed': '已认领 {email}',
  'customer_manager_pool.claim.conflict': '这个客户刚刚已被其他操作员认领,客户池已刷新。',
  'customer_manager_pool.assign.select_placeholder': '选择客户经理',
  'customer_manager_pool.assign.loading_managers': '正在加载客户经理',
  'customer_manager_pool.assign.no_managers': '暂无可用客户经理',
  'customer_manager_pool.assign.action': '分配',
  'customer_manager_pool.assign.assigning': '分配中',
  'customer_manager_pool.assign.assigned': '已将 {email} 分配给 {manager}',
  'customer_manager_pool.assign.managers_error': '无法加载客户经理列表。',
  'customer_manager_pool.pagination.aria_label': '客户池分页',
  'customer_manager_pool.pagination.range': '显示第 {start}-{end} 条,共 {total} 条',

  // ── Account detail ──────────────────────────────────────────────
  'account_detail.loading_aria': '正在加载账户…',
  'account_detail.resolving': '正在解析客户组织…',
  'account_detail.no_org.notice':
    '该账户没有所属客户组织,因此没有客户 360 视图;以下为只读账户档案。',
  'account_detail.error.title': '无法加载账户',
  'account_detail.back': '返回账户列表',
  'account_detail.breadcrumb.accounts': '账户',
  'account_detail.meta.created': '创建于 {iso}',
  'account_detail.summary.title': '概要',
  'account_detail.summary.account_id': '账户 ID',
  'account_detail.summary.org_id': '组织 ID',
  'account_detail.summary.org_name': '组织名称',
  'account_detail.summary.member_role': '成员角色',
  'account_detail.summary.current_balance': '当前余额',
  'account_detail.tx.title': '最近交易',
  'account_detail.tx.last_n': '最近 {n} 条',
  'account_detail.tx.empty': '暂无账本记录。',
  'ledger.earlier_records_unreadable': '更早记录暂不可读',
  'account_detail.tx.column.type': '类型',
  'account_detail.tx.column.amount': '金额',
  'account_detail.tx.column.actor': '操作者',
  'account_detail.tx.column.reason': '原因',
  'account_detail.tx.column.created': '创建时间',
  'account_detail.adjust.title': '余额调整',
  'account_detail.adjust.subtitle': '签名账本 · 负号代表扣款(允许)',
  'account_detail.adjust.amount.label': '金额(USD)',
  'account_detail.adjust.amount.placeholder': '25 或 -12.50',
  'account_detail.adjust.reason.label': '原因(必填,写入审计日志)',
  'account_detail.adjust.reason.placeholder': '客服 / 退款 / 更正…',
  'account_detail.adjust.reason.placeholder_refund':
    '例:订单 ABC-123 退款(备注会自动加 refund: 前缀)',
  'account_detail.adjust.reason.placeholder_sla_credit':
    '例:2026-Q2 故障补偿(备注会自动加 sla_credit: 前缀)',
  'account_detail.adjust.mode.aria_label': '调账模式',
  'account_detail.adjust.mode.custom': '自定义',
  'account_detail.adjust.mode.refund': '退款',
  'account_detail.adjust.mode.sla_credit': 'SLA 补偿',
  'account_detail.adjust.mode.custom.hint': '任意有符号金额 + 自由备注;允许负数(借记)。',
  'account_detail.adjust.mode.refund.hint':
    '仅正数金额;备注自动加 `refund:` 前缀,财务月结按此归类。',
  'account_detail.adjust.mode.sla_credit.hint':
    '仅正数金额;备注自动加 `sla_credit:` 前缀,供 SLA 月度复核。',
  'account_detail.adjust.error.positive_required':
    '退款 / SLA 补偿必须是正数;负数请切回自定义模式。',
  'account_detail.adjust.submit': '调整余额',
  'account_detail.adjust.error.amount_required': '金额不能为空',
  'account_detail.adjust.error.amount_non_zero': '金额必须是非零的 USD 值(扣款请加 `-` 前缀)',
  'account_detail.adjust.error.reason_required': '原因不能为空——会写入账本审计日志',
  'account_detail.confirm.title': '确认余额调整',
  'account_detail.confirm.target_account': '目标账户',
  'account_detail.confirm.current_balance': '当前余额',
  'account_detail.confirm.adjustment': '调整金额',
  'account_detail.confirm.resulting_balance': '调整后余额',
  'account_detail.confirm.type_to_confirm': '请原样键入金额以确认({raw})',
  'account_detail.confirm.idempotency_key': '幂等 key:',
  'account_detail.confirm.submitting': '提交中…',
  'account_detail.confirm.submit': '应用 {signed}',
  'account_detail.email_verification.title': '邮箱验证',
  'account_detail.email_verification.body':
    '客服动作会写入审计。优先重发验证邮件;只有人工核验后才标记已验证。',
  'account_detail.email_verification.status.verified': '已验证',
  'account_detail.email_verification.status.unverified': '未验证',
  'account_detail.email_verification.resend': '重发验证邮件',
  'account_detail.email_verification.resending': '重发中…',
  'account_detail.email_verification.reason_label': '人工验证原因',
  'account_detail.email_verification.reason_placeholder': '工单 / 用户证明',
  'account_detail.email_verification.mark_verified': '标记已验证',
  'account_detail.email_verification.marking': '标记中…',
  'account_detail.email_verification.support_link': '支持请求',
  'account_detail.email_verification.toast.resent': '验证邮件已重发',
  'account_detail.email_verification.toast.marked': '邮箱已标记为验证',
  'account_detail.email_verification.error.reason_required': '标记已验证前必须填写原因',

  // ── Account detail / API keys card (Epic #109 / P0.2) ───────────
  // ops 侧吊销 tenant API key。card 列出该账户所属 org 的全部 key
  // (含已吊销),对活跃 key 提供吊销操作,附强制 reason 输入。

  // ── Billing anomalies ───────────────────────────────────────────
  'billing_anomalies.title': '账务异常',
  'billing_anomalies.subtitle': '租户已经收到成功响应，但账务结算还需要自动补偿或人工跟进的请求。',
  'billing_anomalies.filter.unresolved': '未处理',
  'billing_anomalies.filter.unresolved.hint': '仍待结算',
  'billing_anomalies.filter.resolved': '已处理',
  'billing_anomalies.filter.resolved.hint': '已补偿或已升级',
  'billing_anomalies.filter.all': '全部',
  'billing_anomalies.filter.all.hint': '完整异常历史',
  'billing_anomalies.error.title': '无法加载账务异常',
  'billing_anomalies.empty.unresolved': '当前没有未处理账务异常',
  'billing_anomalies.empty.resolved': '暂无已处理账务异常',
  'billing_anomalies.empty.all': '尚未记录账务异常',
  'billing_anomalies.empty.hint': '健康请求应直接结算为已扣费或显式零成本，而不是进入这个队列。',
  'billing_anomalies.column.account': '账户',
  'billing_anomalies.column.request_id': '请求 ID',
  'billing_anomalies.column.model': '模型',
  'billing_anomalies.column.amount': '金额',
  'billing_anomalies.column.reason': '原因',
  'billing_anomalies.column.replay': '重放状态',
  'billing_anomalies.column.created': '发生时间',
  'billing_anomalies.column.resolved': '处理时间',
  'billing_anomalies.pagination.summary': '显示 {shown} / 共 {total} 条',

  // ── Payments orders ─────────────────────────────────────────────
  'payments_orders.title': '支付订单',
  'payments_orders.subtitle': '查阅租户充值并对未收到 ZPay 回调的订单进行对账。',
  'payments_orders.status.all': '全部',
  'payments_orders.status.pending': '待支付',
  'payments_orders.status.paid': '已支付',
  'payments_orders.status.expired': '已过期',
  'payments_orders.status.failed': '失败',
  'payments_orders.status.refunded': '已退款',
  'payments_orders.filter.account_id': '账户 ID',
  'payments_orders.filter.from': '起始时间(ISO-8601)',
  'payments_orders.filter.to': '结束时间(ISO-8601)',
  'payments_orders.loading_aria': '正在加载支付订单…',
  'payments_orders.error.title': '无法加载支付订单',
  'payments_orders.empty': '没有订单匹配当前筛选。',
  'payments_orders.column.created': '创建时间',
  'payments_orders.column.account': '账户',
  'payments_orders.column.status': '状态',
  'payments_orders.column.usd': 'USD',
  'payments_orders.column.cny': 'CNY',
  'payments_orders.column.rate': '汇率',
  'payments_orders.column.pay': '支付方式',
  'payments_orders.column.paid_at': '支付时间',
  'payments_orders.column.order_id': '订单 ID',
  'payments_orders.column.actions': '操作',
  'payments_orders.row.details': '详情',
  'payments_orders.row.collapse': '收起',
  'payments_orders.row.reconcile': '对账',
  'payments_orders.showing': '共 {n} 条订单',
  'payments_orders.load_more': '加载更多',
  'payments_orders.loading_more': '加载中…',
  'payments_orders.reconcile_modal.title': '对账支付订单',
  'payments_orders.filter.status_label': '状态',
  'payments_orders.filter.status_aria_label': '状态筛选',
  'payments_orders.filter.account_id.placeholder': 'acc_...',
  'payments_orders.filter.to.placeholder': '(无结束时间)',
  'payments_orders.row.reconcile_tooltip.available': '强制向 ZPay 对账,如成功则立即结算',
  'payments_orders.row.reconcile_tooltip.disabled': '仅对待支付订单可发起对账',
  'payments_orders.reconcile_modal.body':
    '我们会向 ZPay 询问订单 {orderId} 是否已支付。若是,则将 {amount} 原子结算至该账户;若否,则不作变更。',
  'payments_orders.reconcile_modal.account': '账户',
  'payments_orders.reconcile_modal.usd': 'USD',
  'payments_orders.reconcile_modal.cny': 'CNY',
  'payments_orders.reconcile_modal.applied_rate': '已应用汇率',
  'payments_orders.reconcile_modal.note.label': '备注(可选,计入审计)',
  'payments_orders.reconcile_modal.note.placeholder':
    '例如:客户自述已通过 ZPay 支付但未收到 notify',
  'payments_orders.reconcile_modal.type_to_confirm': '键入订单号以确认',
  'payments_orders.reconcile_modal.type_hint_prefix': '精确粘贴或原样键入 ',
  'payments_orders.reconcile_modal.type_hint_suffix': ' 方可激活「对账」按钮。',
  'payments_orders.reconcile_modal.submitting': '对账中…',
  'payments_orders.reconcile_modal.submit': '立即对账',
  'payments_orders.reconcile_modal.detail.order_id': '订单 ID',
  'payments_orders.reconcile_modal.detail.out_trade_no': '内部交易号',
  'payments_orders.reconcile_modal.detail.upstream_trade_no': '上游交易号',
  'payments_orders.reconcile_modal.detail.expired_at': '过期时间',
  'payments_orders.reconcile_modal.detail.empty': '—',
  'payments_orders.reconcile_modal.toast.credited': '订单 {orderId} 已结算(状态={status})',
  'payments_orders.reconcile_modal.toast.upstream': '上游报告状态={status};订单 {orderId} 未入账',
  'payments_orders.reconcile_modal.toast.already': '订单 {orderId} 已处于状态={status};无需操作',

  // ── Payment providers ───────────────────────────────────────────
  'payment_providers.title': '支付通道',
  'payment_providers.subtitle':
    '管理每家支付聚合的凭证与端点 URL。变更在 60 秒内生效——轮换密钥后 platform-api 会自动拉取最新版本。',
  'payment_providers.new': '新建通道',
  'payment_providers.banner':
    '变更在 60 秒内生效——轮换密钥后 platform-api 会自动拉取最新版本,无需重新部署。',
  'payment_providers.empty.title': '尚未配置任何支付通道。',
  'payment_providers.empty.body':
    '添加第一个通道以开启租户充值。在此之前 /v1/billing/topup 将返回 503。',
  'payment_providers.empty.cta': '新增通道',
  'payment_providers.row.rotate': '轮换',
  'payment_providers.row.pid': 'PID',
  'payment_providers.row.reveal': '查看',
  'payment_providers.row.revealing': '查看中…',
  'payment_providers.row.revealed': '已显示',
  'payment_providers.chip.active': '活跃',
  'payment_providers.chip.retired': '停用',
  'payment_providers.create.title': '新增通道',
  'payment_providers.create.provider': '通道',
  'payment_providers.create.pid': '商户 ID(PID)',
  'payment_providers.create.secret': '密钥',
  'payment_providers.create.submit_url': '提交 URL',
  'payment_providers.create.mapi_url': 'MAPI URL',
  'payment_providers.create.query_url': '查询 URL',
  'payment_providers.create.note': '备注(可选,计入审计)',
  'payment_providers.create.active_checkbox': '将此条凭证设为 {provider} 的活跃版本',
  'payment_providers.create.rotation_warning': '已有此通道的活跃凭证——新建将轮换并停用旧版。',
  'payment_providers.create.error.pid_required': 'PID 不能为空',
  'payment_providers.create.error.secret_required': '密钥不能为空',
  'payment_providers.loading_aria': '正在加载支付通道…',
  'payment_providers.error.title': '无法加载支付通道',
  'payment_providers.create.modal.title.new': '新增 {provider} 通道',
  'payment_providers.create.modal.title.rotate': '轮换 {provider} 凭证',
  'payment_providers.create.secret.hint':
    '用于 MD5 签名。SQL 明文存储是有意为之——运维可无发布自行轮换。',
  'payment_providers.create.mapi_url.optional_suffix': '(可选)',
  'payment_providers.create.active_hint.rotation': '这将停用当前活跃记录。需键入确认。',
  'payment_providers.create.active_hint.retired': '不影响当前活跃记录——此条凭证保存为停用状态。',
  'payment_providers.create.active_hint.first': '当前无活跃记录,此条将成为首个活跃凭证。',
  'payment_providers.create.error.submit_url_required': '提交 URL 不能为空',
  'payment_providers.create.error.query_url_required': '查询 URL 不能为空',
  'payment_providers.create.error.mapi_url_required_zpay': 'zpay 必须配置 MAPI URL',
  'payment_providers.create.submit.save': '保存',
  'payment_providers.create.submit.continue': '继续',
  'payment_providers.confirm.title.rotation': '确认轮换 {provider} 凭证',
  'payment_providers.confirm.title.save': '保存 {provider} 凭证',
  'payment_providers.confirm.body.rotation':
    '即将替换线上凭证,后续签名调用将在 60 秒内使用新的 PID + 密钥。',
  'payment_providers.confirm.body.save': '此记录将保存,不影响当前活跃凭证。',
  'payment_providers.confirm.type_prefix': '键入 ',
  'payment_providers.confirm.type_suffix': ' 以确认',
  'payment_providers.confirm.back': '返回',
  'payment_providers.confirm.submit.saving': '保存中…',
  'payment_providers.confirm.submit.rotate': '轮换凭证',
  'payment_providers.confirm.submit.save': '保存',
  'payment_providers.confirm.error.type_mismatch': '请原样键入 {provider} 以确认轮换',
  'payment_providers.toast.rotated': '{provider} 凭证已轮换(停用 {retiredId})',
  'payment_providers.toast.saved': '{provider} 凭证已保存({status})',
  'payment_providers.toast.status.active': '活跃',
  'payment_providers.toast.status.retired': '停用',

  // ── Pricing (exchange rate policy) ──────────────────────────────
  'pricing.title': '人民币展示汇率',
  'pricing.subtitle': '用于 UI 展示和充值折算的预配置汇率(R)。底层账本仍是 USD credit。',
  'pricing.policy.title': '当前人民币展示汇率',
  'pricing.policy.empty': '尚未设置策略。使用下方表单确立初始汇率。',
  'pricing.policy.pair': '货币对',
  'pricing.policy.current_r': '当前 R',
  'pricing.policy.updated_by': '更新人',
  'pricing.policy.updated_at': '更新时间',
  'pricing.policy.meaning.title': '含义',
  'pricing.policy.meaning.body': 'UI 将 $1.00 USD credit 显示为 ¥{rate}；充值也使用同一预配置折算率。',
  'pricing.market.title': '市场参考',
  'pricing.market.subtitle': '每 6 小时通过 cron 更新',
  'pricing.market.empty': '尚未采集到市场样本——cron 上线后会自动填充。',
  'pricing.market.latest_rate': '最新市场价',
  'pricing.market.source': '来源',
  'pricing.market.sampled_at': '采样时间',
  'pricing.market.spread': '与市场价差',
  'pricing.edit.title': '调整汇率',
  'pricing.edit.subtitle': '输入确认 · 计入审计',
  'pricing.edit.new_r': '新 R(每 USD CNY)',
  'pricing.edit.note': '备注(可选,计入审计)',
  'pricing.edit.submit': '保存',
  'pricing.confirm.title': '确认汇率调整',
  'pricing.confirm.previous': '旧汇率',
  'pricing.confirm.new': '新汇率',
  'pricing.confirm.market': '市场参考',
  'pricing.confirm.projected_spread': '预计价差',
  'pricing.confirm.note': '备注',
  'pricing.loading_aria': '正在加载汇率策略…',
  'pricing.error.title': '无法加载汇率策略',
  'pricing.policy.note_prefix': '备注:',
  'pricing.edit.rate_placeholder': '7.89',
  'pricing.edit.note_placeholder': '跟随市场波动调整',
  'pricing.edit.error.required': '新汇率不能为空',
  'pricing.edit.error.not_number': '新汇率必须是数值',
  'pricing.edit.error.out_of_range': '新汇率必须在 ({min}, {max}] 区间——请确认小数点位置',
  'pricing.confirm.type_hint': '原样键入新汇率以确认({rate})',
  'pricing.confirm.error.type_mismatch': '请原样键入新汇率({rate})以确认',
  'pricing.confirm.submit.saving': '保存中…',
  'pricing.confirm.submit.save': '保存 {rate}',
  'pricing.toast.updated': '汇率已更新为 {rate}',

  // ── Revenue ─────────────────────────────────────────────────────
  'revenue.title': '收入',
  'revenue.subtitle': '跨全部租户与模型的 L1(充值汇率溢价)+ L2(Token 销售毛利)。',
  'revenue.window.today': '今日',
  'revenue.window.7d': '最近 7 天',
  'revenue.window.30d': '最近 30 天',
  'revenue.summary.title': '概要',
  'revenue.summary.subtitle': '所选窗口的 L1 溢价 + L2 毛利。',
  'revenue.summary.topup_gmv': '充值 GMV',
  'revenue.summary.usage_gmv': '用量 GMV',
  'revenue.summary.l1_profit': 'L1 利润',
  'revenue.summary.channel_fees': '通道手续费',
  'revenue.summary.usd_credited': 'USD 入账',
  'revenue.summary.l2_profit': 'L2 利润',
  'revenue.summary.upstream_cost': '上游成本',
  'revenue.summary.total': '总毛利',
  'revenue.source.title': '余额来源拆分',
  'revenue.source.subtitle': '所选窗口内用量扣费按来源批次归因。',
  'revenue.source.cash': '现金充值',
  'revenue.source.gift_card': '礼品卡',
  'revenue.source.other': '其他信用',
  'revenue.source.unallocated': '未分配',
  'revenue.source.coverage': '已归因覆盖率',
  'revenue.health.title': '余额来源账本',
  'revenue.health.subtitle': '来源批次、账户余额与 signed ledger 总额。',
  'revenue.health.status.balanced': '已平账',
  'revenue.health.status.needs_attention': '需处理',
  'revenue.health.source_remaining': '来源剩余额',
  'revenue.health.account_balance': '账户余额',
  'revenue.health.ledger_sum': 'Signed ledger 合计',
  'revenue.health.missing_lots': '缺失入账批次',
  'revenue.health.unallocated_debits': '未归因扣费',
  'revenue.health.mismatch_accounts': '不一致账户',
  'revenue.health.generated_at': '生成时间 {time}',
  'revenue.health.error_label': '无法加载余额来源健康状态',
  'revenue.daily.title': '每日趋势',
  'revenue.daily.subtitle': '按天堆叠 L1(主)+ L2(三级)利润。',
  'revenue.l2.title': '按模型 L2',
  'revenue.l2.subtitle': '按模型统计用量毛利。亏损模型自动标记。',
  'revenue.l2.currency.label': '币种',
  'revenue.l2.currency.aria': '按模型 L2 展示币种',
  'revenue.l2.column.model': '模型',
  'revenue.l2.column.usage': '用量',
  'revenue.l2.column.usd_charged': 'USD 计费',
  'revenue.l2.column.cny_cost': 'CNY 成本',
  'revenue.l2.column.charged': '计费收入 ({currency})',
  'revenue.l2.column.cost': '上游成本 ({currency})',
  'revenue.l2.column.l2_profit': 'L2 利润',
  'revenue.l2.column.l2_pct': 'L2 %',
  'revenue.l2.flag.loss': 'LOSS',
  'revenue.l2.flag.thin': 'THIN',
  'revenue.top_tenants.title': 'Top 租户',
  'revenue.top_tenants.subtitle': '按租户的终身 L1 + L2 累计贡献。按 CNY 毛利总额排序,前 {n} 名。',
  'revenue.top_tenants.column.email': '邮箱',
  'revenue.top_tenants.column.topups': '充值',
  'revenue.top_tenants.column.l1_profit': 'L1 利润',
  'revenue.top_tenants.column.usage': '用量',
  'revenue.top_tenants.column.l2_profit': 'L2 利润',
  'revenue.top_tenants.column.total': '合计',
  'revenue.summary.window_aria': '概要窗口',
  'revenue.summary.error_label': '无法加载概要',
  'revenue.summary.topup.orders': '{n} 笔订单',
  'revenue.summary.usage.api_calls': '{n} 次 API 调用',
  'revenue.daily.subtitle_full': '按天堆叠 L1(主)+ L2(三级)利润。{days} 天。',
  'revenue.daily.error_label': '无法加载每日趋势',
  'revenue.daily.empty': '尚无收入数据',
  'revenue.daily.aria_label': '每日收入趋势——{days} 天',
  'revenue.daily.tooltip': '{date}——L1 {l1}({topupCount} 笔充值),L2 {l2}({usageCount} 次用量)',
  'revenue.l2.error_label': '无法加载模型毛利分布',
  'revenue.l2.empty': '所选窗口内无用量记录。',
  'revenue.top_tenants.error_label': '无法加载 Top 租户',
  'revenue.top_tenants.empty': '尚无租户产生毛利贡献。',
  'revenue.top_tenants.no_email': '(无邮箱)',

  // ── Audit log ───────────────────────────────────────────────────
  'audit.title': '审计日志',
  'audit.actor.owner': '网关记录的 owner',
  'audit.actor.verifier': '核实身份',
  'audit.subtitle':
    'ops-admin 所有变更的只追加审计链。服务端按 `when` 倒序(最新在前);筛选字段仅支持精确匹配。',
  'audit.filter.action': '动作',
  'audit.filter.target_type': '目标类型',
  'audit.filter.target_id': '目标 ID',
  'audit.filter.ops_user_id': 'ops 用户 ID',
  'audit.filter.target_type.any': '任意目标类型',
  'audit.filter.target_type.account': 'account',
  'audit.filter.target_type.gift_card': 'gift-card',
  'audit.filter.target_type.gift_card_batch': 'gift-card-batch',
  'audit.filter.target_type.upstream': 'upstream',
  'audit.filter.target_type.upstream_funding_batch': 'upstream-funding-batch',
  'audit.filter.target_type.model': 'model',
  'audit.filter.target_type.failed_deduction': 'failed-deduction',
  'audit.filter.target_type.exchange_rate_policy': 'exchange-rate-policy',
  'audit.filter.target_type.payment_order': 'payment-order',
  'audit.filter.target_type.payment_provider': 'payment-provider',
  'audit.filter.apply': '应用',
  'audit.filter.reset': '重置',
  'audit.filter.action.placeholder': '例如 balance.adjust',
  'audit.filter.ops_user_id.placeholder': 'ops_xxx',
  'audit.filter.target_id.placeholder': '精确匹配',
  'audit.error.title': '无法加载审计日志',
  'audit.loading_aria': '正在加载审计日志…',
  'audit.empty.title': '没有行匹配当前筛选',
  'audit.empty.body': '放宽或清空筛选条件再试。',
  'audit.column.when': '时间',
  'audit.column.action': '动作',
  'audit.column.target_type': '目标类型',
  'audit.column.target_id': '目标 ID',
  'audit.column.ops_user': 'ops 用户',
  'audit.pagination.range': '显示第 {start}–{end} 条,共 {total} 条',

  // ── Upstream detail summary stats ───────────────────────────────────
  'upstream_detail.summary.source': '客服来源码',
  'upstream_detail.summary.endpoint': '端点',
  'upstream_detail.summary.accounts': '账户',
  'upstream_detail.summary.accounts_unit': '个',
  'upstream_detail.summary.funding': '资金',
  'upstream_detail.summary.funding_remaining': '剩余',
  'upstream_detail.summary.funding_none': '无资金批次',

  // ── Supply access section (epic #1327 PR-6) ─────────────────────────
  'upstream_supply_access.title': '供给接入',
  'upstream_supply_access.subtitle':
    '按上游命名空间列出接入前提。任一项未就绪即点击处理:该命名空间无法承载流量。',
  'upstream_supply_access.namespace.disabled': '已停用',
  'upstream_supply_access.namespace.stale': '渠道行已不存在',
  'upstream_supply_access.empty': '该上游还没有命名空间。先建一个渠道,再配置账户与密钥。',
  'upstream_supply_access.load_error': '无法读取命名空间:{message}',
  'upstream_supply_access.fact.account': '账户',
  'upstream_supply_access.fact.key': 'API 密钥',
  'upstream_supply_access.fact.egress': '出入站配置',
  'upstream_supply_access.fact.probe': '探测协议',
  'upstream_supply_access.fact.account.ready': '已有启用的供应商账户',
  'upstream_supply_access.fact.account.blocked': '还没有启用的供应商账户',
  'upstream_supply_access.fact.key.ready': '已有启用密钥绑定到该命名空间',
  'upstream_supply_access.fact.key.blocked': '没有启用密钥绑定到该命名空间',
  'upstream_supply_access.fact.egress.ready': '绑定的出入站策略均可用',
  'upstream_supply_access.fact.egress.blocked': '绑定缺少可用的出入站策略',
  'upstream_supply_access.fact.probe.ready': '已有可探测的模型',
  'upstream_supply_access.fact.probe.blocked': '该命名空间下没有已分类的可探测模型',
  'upstream_supply_access.fact.unknown': '读取失败,状态未知',
  'upstream_supply_access.state.ready': '已就绪',
  'upstream_supply_access.state.blocked': '未就绪',
  'upstream_supply_access.state.unknown': '未知',
  'upstream_supply_access.action.accounts': '管理账户与密钥',
  'upstream_supply_access.action.channels': '管理渠道',
  'upstream_supply_access.action.funding': '管理资金',
  'upstream_supply_access.drawer.accounts.title': '账户与密钥',
  'upstream_supply_access.drawer.accounts.description':
    '供应商账户、API 密钥,以及密钥到命名空间的绑定与出入站策略。',
  'upstream_supply_access.drawer.channels.title': '上游渠道',
  'upstream_supply_access.drawer.channels.description': '该上游自有的命名空间:新建、改名、启停。',
  'upstream_supply_access.drawer.funding.title': '资金批次',
  'upstream_supply_access.drawer.funding.description': '该上游的充值批次与消耗进度。',

  // ── Upstream maintenance toggle (P2.11) ────────────────────────────
  'upstream_detail.maintenance.label': '运行状态',
  'upstream_detail.maintenance.state.enabled': '正在路由流量',
  'upstream_detail.maintenance.state.disabled': '维护中 · 网关已停止路由',
  'upstream_detail.maintenance.enter.button': '进入维护',
  'upstream_detail.maintenance.resume.button': '恢复路由',
  'upstream_detail.maintenance.modal.title_enter': '把 {name} 置入维护?',
  'upstream_detail.maintenance.modal.title_resume': '恢复 {name} 的路由?',
  'upstream_detail.maintenance.modal.body_enter':
    '立即生效:gateway 选择器会把该上游从候选集中剔除。已在途的请求不受影响;新请求将改走其他候选。若是单点 SKU,租户会直接 404(见仪表盘告警)。',
  'upstream_detail.maintenance.modal.body_resume':
    '恢复后该上游重新进入候选集,选择器按原 SLA × 成本规则分配流量。',
  'upstream_detail.maintenance.impact.label': '当前 SKU 目标引用 · {count}',
  'upstream_detail.maintenance.impact.none': '无启用 SKU 目标引用;停用不会立即影响任何 SKU。',
  'upstream_detail.maintenance.confirm.type_label': '输入 `maintenance` 确认',
  'upstream_detail.maintenance.confirm.type_mismatch': '请准确输入 maintenance',
  'upstream_detail.maintenance.confirm.submit_enter': '确认停用',
  'upstream_detail.maintenance.confirm.submit_resume': '确认恢复',

  // ── Account lifecycle: suspend / delete (P2.10) ────────────────────
  'account_lifecycle.title': '账号生命周期',
  'account_lifecycle.status.active': '活跃',
  'account_lifecycle.status.suspended': '已停用',
  'account_lifecycle.status.deleted': '已删除',
  'account_lifecycle.suspended.banner': '{iso} 起停用 · 原因:{reason}',
  'account_lifecycle.suspended.no_reason': '(未填写)',
  'account_lifecycle.deleted.banner': '{iso} 起软删除,租户 API 请求将返回 403 ACCOUNT_DELETED。',
  'account_lifecycle.suspend.reason_label': '停用原因(可选)',
  'account_lifecycle.suspend.reason_placeholder': '例:滥用举报 2026-04-20',
  'account_lifecycle.suspend.reason_hint':
    '会写入 accounts.suspension_reason,供日后追溯;同时落一条 ops_audit 记录。',
  'account_lifecycle.suspend.button': '停用',
  'account_lifecycle.unsuspend.button': '解除停用',
  'account_lifecycle.delete.button': '软删除',
  'account_lifecycle.confirm.title_suspend': '停用 {email}?',
  'account_lifecycle.confirm.body_suspend':
    '立即停用该租户:/internal/auth/verify-key 会返回 ACCOUNT_SUSPENDED,所有新请求被拒。历史 usage_logs / 账户余额保留不动。',
  'account_lifecycle.confirm.title_unsuspend': '解除 {email} 的停用?',
  'account_lifecycle.confirm.body_unsuspend':
    '清除 suspended_at + suspension_reason,租户立即恢复正常路由。',
  'account_lifecycle.confirm.title_delete': '软删除 {email}?',
  'account_lifecycle.confirm.body_delete':
    '标记 deleted_at;租户 API 请求从此 403 ACCOUNT_DELETED。历史 usage_logs / balance_transactions 保留(用于对账)。完整 GDPR 擦除(清邮箱 + 密码哈希)是后续任务。',
  'account_lifecycle.confirm.type_email': '输入 {email} 确认',
  'account_lifecycle.confirm.type_mismatch': '请准确输入 {email}',
  'account_lifecycle.confirm.reason_preview': '停用原因预览:{reason}',
  'account_lifecycle.confirm.submit_suspend': '确认停用',
  'account_lifecycle.confirm.submit_unsuspend': '确认解除停用',
  'account_lifecycle.confirm.submit_delete': '确认软删除',
  'account_lifecycle.toast.suspended': '{email} 已停用',
  'account_lifecycle.toast.unsuspended': '{email} 已解除停用',
  'account_lifecycle.toast.deleted': '{email} 已软删除',

  // ── Upstream onboarding wizard (P1.5) ─────────────────────────────
  'upstream_wizard.title': '新建上游接入',
  'upstream_wizard.subtitle':
    '三步创建上游和密钥，然后进入路由工作台做能力探测与 SKU 目标配置。',
  'upstream_wizard.breadcrumb.upstreams': '上游',
  'upstream_wizard.breadcrumb.new': '新建',
  'upstream_wizard.progress.aria_label': '接入向导进度',
  'upstream_wizard.back': '上一步',
  'upstream_wizard.next': '下一步',
  'upstream_wizard.step.basic.title': '基础信息',
  'upstream_wizard.step.basic.heading': '创建上游记录',
  'upstream_wizard.step.basic.body':
    '创建 `upstream_configs` 行,保存鉴权密钥。`enabled` 默认关闭,最后一步才打开。',
  'upstream_wizard.step.basic.name': '显示名称',
  'upstream_wizard.step.basic.name.placeholder': 'anthropic-direct-a',
  'upstream_wizard.step.basic.endpoint': '端点 URL',
  'upstream_wizard.step.basic.endpoint.placeholder': 'https://api.anthropic.com',
  'upstream_wizard.step.basic.priority': '优先级',
  'upstream_wizard.step.basic.cookie_passthrough': 'Cookie 透传',
  'upstream_wizard.step.basic.cookie_passthrough.body': '允许租户 Cookie 原样透传到这个上游。',
  'upstream_wizard.step.basic.cookie_passthrough.hint':
    '默认关闭。只有在该上游确实依赖租户 Cookie 时才开启。',
  'upstream_wizard.step.basic.model_discovery': '模型发现方式（可选）',
  'upstream_wizard.step.basic.model_discovery.hint':
    '默认 compatible。创建后也可以在上游详情页的“配置”卡修改。',
  'upstream_wizard.step.basic.submit': '创建上游',
  'upstream_wizard.step.basic.submit.pending': '创建中…',
  'upstream_wizard.step.basic.error.required': '名称和端点为必填',
  'upstream_wizard.step.basic.error.priority': '优先级必须是非负整数',
  'upstream_wizard.step.keys.title': '添加密钥',
  'upstream_wizard.step.keys.heading': '为 {name} 添加 API 密钥',
  'upstream_wizard.step.keys.body': '至少添加一个密钥桶,网关会在桶中随机选取密钥转发请求。',
  'upstream_wizard.step.keys.count': '已添加 {count} 个密钥桶',
  'upstream_wizard.step.keys.label': '标签',
  'upstream_wizard.step.keys.label.placeholder': '如: primary-key',
  'upstream_wizard.step.keys.add': '添加',
  'upstream_wizard.step.keys.toast.added': '密钥桶已添加',
  'upstream_wizard.step.keys.error.min_one': '至少添加一个密钥桶才能继续',
  'upstream_wizard.step.keys.error.secret_required': '请输入 API 密钥',
  'upstream_wizard.step.models.title': '路由配置',
  'upstream_wizard.step.models.heading': '打开 {name} 的路由配置',
  'upstream_wizard.step.models.body':
    '在上游详情页拉取模型清单、发起能力探测，并加入 SKU 目标池。没有启用 SKU 目标前，此上游不会进入热路径。',
  'upstream_wizard.step.models.open_detail': '打开路由配置',
  'upstream_wizard.step.probe.title': '探针自检',
  'upstream_wizard.step.probe.heading': '对 {name} 运行探针',
  'upstream_wizard.step.probe.body':
    '选一个模型路由映射运行一次真实请求。探针失败不会阻止启用——但强烈建议先排查。成功后点完成,会把 enabled 翻为 true 并跳转到详情页。',
  'upstream_wizard.step.probe.target.label': '探针目标',
  'upstream_wizard.step.probe.run': '运行探针',
  'upstream_wizard.step.probe.run.pending': '探针中…',
  'upstream_wizard.step.probe.finish': '完成并启用',
  'upstream_wizard.step.probe.finish.pending': '启用中…',
  'upstream_wizard.step.probe.warning.failed':
    '探针失败。确认后再启用——可在详情页修改配置,重新探针。',
  'upstream_wizard.toast.created': '上游 {name} 已创建(未启用)',
  'upstream_wizard.toast.attached': '{vendor}/{model} 已映射',
  'upstream_wizard.toast.funded': '资金批次已登记',
  'upstream_wizard.toast.enabled': '{name} 已启用——网关开始路由',

  // ── Support: per-tenant request query (P0.4) ─────────────────────
  'support.requests.title': '租户请求查询',
  'support.requests.subtitle':
    '按账户查询网关请求日志,用于账单争议排查。仅对 ops-admin 与 ops-support 角色开放。',
  'support.requests.filter.account_id': '账户 ID',
  'support.requests.filter.account_id.placeholder': 'acc_xxx',
  'support.requests.filter.from': '起始时间',
  'support.requests.filter.to': '结束时间',
  'support.requests.filter.request_id': '请求 ID(可选,精确匹配)',
  'support.requests.filter.request_id.placeholder': 'req_xxx',
  'support.requests.filter.apply': '应用',
  'support.requests.filter.reset': '重置',
  'support.requests.cta.title': '请输入账户 ID',
  'support.requests.cta.body':
    '在上方过滤器中填写目标账户 ID 并点击「应用」。时间范围可选,默认查询最近 24 小时;填入请求 ID 时将忽略时间范围并执行精确查找。',
  'support.requests.error.title': '无法加载请求日志',
  'support.requests.loading_aria': '正在加载请求日志…',
  'support.requests.account.account_id': '账户',
  'support.requests.account.email': '邮箱',
  'support.requests.account.org': '组织',
  'support.requests.account.org.none': '(无组织)',
  'support.requests.empty.title': '所选范围内无请求',
  'support.requests.empty.body': '放宽时间范围或清空请求 ID 再试。',
  'support.requests.column.when': '时间',
  'support.requests.column.request_id': '请求 ID',
  'support.requests.column.model': '模型',
  'support.requests.column.status': '状态',
  'support.requests.column.latency': '延迟',
  'support.requests.column.cost': '成本(CNY)',
  'support.requests.cost.pending': '计费中',
  'support.requests.column.api_key': 'API Key',
  'support.requests.column.upstream': '上游',
  'support.requests.row.api_key.unnamed': '(未命名)',
  'support.requests.row.upstream.deleted': '(已删除)',
  'support.requests.detail.request_id': '请求 ID',
  'support.requests.detail.upstream_id': '上游 ID',
  'support.requests.detail.protocol': '协议',
  'support.requests.detail.created_at': '创建时间',
  'support.requests.detail.input_tokens': '输入 tokens',
  'support.requests.detail.output_tokens': '输出 tokens',
  'support.requests.detail.cache_read': '缓存读',
  'support.requests.detail.cache_write': '缓存写',
  'support.requests.detail.unit_kind': '计费单位',
  'support.requests.detail.unit_quantity': '计费数量',
  'support.requests.detail.unit_rate': '单价',
  'support.requests.detail.unit_variant': '规格',
  'support.requests.detail.unit_amount': '按量金额',
  'support.requests.detail.unit_variant.none': '(默认)',
  'support.requests.detail.unknown_dimension': '其他计费项',
  'support.requests.detail.unknown_dimension.unnamed': '(未识别)',
  'support.requests.detail.unit_unpriced': '按量计费状态',
  'support.requests.unit_unpriced.unit_price_missing': '未配置单价，未计费',
  'support.requests.unit_unpriced.unit_not_delivered': '未交付，未计费',
  'support.requests.unit_unpriced.variant_price_missing': '该档位未配置价格，已交付但未计费',
  'support.requests.unit_unpriced.unit_surcharges_malformed': '附加项块畸形已剥离，本体仍计费',
  'support.requests.unit.call': '次',
  'support.requests.unit.second': '秒',
  'support.requests.unit.character': '字符',
  'support.requests.unit.image': '图像',
  'support.requests.pagination.aria_label': '请求日志分页',
  'support.requests.pagination.range': '显示第 {start}–{end} 条,共 {total} 条',

  // ── Tenant announcements (#1396 follow-up) ──────────────────────
  'announcements.title': '租户公告',
  'announcements.subtitle':
    '向所有租户组织推送一条 kind:"system" 通知，客户端通知中心可见。上新模型、维护窗口、政策变更走这里。',
  'announcements.form.caption': '新建公告',
  'announcements.field.slug': '公告标识（slug）',
  'announcements.field.slug.placeholder': 'claude-opus-5-listed',
  'announcements.field.slug.hint':
    '幂等键：小写字母、数字、点、下划线、短横线，3–64 位。用新 slug 发出一条新公告；沿用已存在的 slug 会就地改写那条公告的文案并向所有客户端重新投递。',
  'announcements.field.slug.invalid': 'slug 只能是 3–64 位小写字母、数字、点、下划线或短横线',
  'announcements.field.slug.generate': '生成一个新的 slug',
  'announcements.field.title': '标题',
  'announcements.field.title.too_long': '标题超出长度上限',
  'announcements.field.description': '正文',
  'announcements.field.description.too_long': '正文超出长度上限',
  'announcements.field.counter': '{count} / {max}',
  'announcements.field.severity': '级别',
  // 两个动作分述，不给一句笼统的「不可选」（#1965）。原来那句在试发按钮上线后
  // 就是假的，而它恰好挂在试发按钮上方——运维读到的断言和页面实际能做的事不一致，
  // 正是 #1945 的成因。
  'announcements.audience.note':
    '「发布公告」一律发给全部租户组织，没有任何筛选条件；「试发」只发给你逐个点名的组织。两者都无法撤回。',
  'announcements.severity.info': '普通（info）',
  'announcements.severity.warning': '警告（warning）',
  'announcements.severity.critical': '严重（critical）',
  'announcements.submit': '发布公告',
  'announcements.submit.pending': '发布中…',
  'announcements.submit.warning': '发出后无法撤回。',
  'announcements.confirm.title': '确认向全部租户发布？',
  'announcements.confirm.body':
    '这条公告会写入每一个租户组织，指纹为 {fingerprint}，发出后无法撤回。若该 slug 已存在，原有文案会被覆盖并重新投递。输入 slug 以确认。',
  'announcements.confirm.type_slug': '输入 slug 确认',
  'announcements.confirm.send': '确认发布',
  'announcements.toast.sent': '公告已发布',
  // #1965 试发。收件名单只存在于这个弹窗里，关掉即销毁，所以文案不需要提醒
  // 运维「记得清空」——那正是 #1945 的形状。
  'announcements.test.open': '先试发给指定组织…',
  'announcements.test.dialog.title': '试发：只发给名单里的组织',
  // 复用真实指纹是对称的：正向承诺（之后全量发布会原地升级）和反向后果（撞上
  // 别人的 slug 会当场覆盖真公告正文）出自同一条 ON CONFLICT，只讲正向那半边
  // 等于把不可撤回的那半边藏起来。
  'announcements.test.dialog.body':
    '用与正式发布完全相同的指纹 {fingerprint} 只写入名单内的组织，方便先在真实客户端里校对。之后用同一个 slug 全量发布时，名单内组织手里那一条会被原地改写成最终文案——他们不会先后收到两条。反过来也成立：如果这个 slug 已经被一条正式公告占用，名单内组织手里那条公告的正文会被这份草稿当场覆盖。没有撤回操作；唯一的补救是用同一个 slug 把正确文案再发给这批组织。若你最终放弃这份草稿，他们手里就停在草稿文案上。',
  'announcements.test.field.orgs': '收件人名单',
  // 明确写出这是「组织级」投递：填的是邮箱也一样——它解析成那个人所在的组织，
  // 该组织里每个成员都会看到这份还没校对的草稿，而不是只有填的那个人。
  'announcements.test.field.orgs.hint': '一行一个邮箱或组织 ID（也可用逗号分隔），最多 {max} 个。填邮箱会解析成这个人所在的组织，而公告是发给组织的：该组织的全部成员都会看到这份尚未校对的草稿，不只是填的那个人。有解析不出来的条目时整次试发会被拒绝，并逐条告诉你是哪几个。',
  'announcements.test.field.orgs.placeholder': 'dean@example.com\norg_xxxxxxxx',
  // 数的是「条目」不是「组织」：同一个组织的两个同事是两条、一个组织，只有服务端
  // 解析完才知道。所以这里说条目，实际覆盖的组织数由发送结果面板报。
  'announcements.test.field.orgs.count': '共 {count} 个收件人（去重后）；实际覆盖的组织数发送后由结果面板给出。',
  'announcements.test.field.orgs.empty': '名单为空：试发至少要填一个邮箱或组织 ID。',
  'announcements.test.field.orgs.over_limit': '名单最多 {max} 个收件人；再多就不是校对而是发公告了，请直接全量发布。',
  'announcements.test.send': '试发给这 {count} 个收件人',
  'announcements.test.send.pending': '试发中…',
  // 两半都只讲「本次发送」，不讲世界：运维给一条已经全量发过的 slug 再试发一版
  // 修正稿（弹窗自己描述的流程）时，「尚未全量发布」当场就是假的。仅在服务端
  // 确认了白名单发送时才弹——分叉见 <Announcements />。
  'announcements.test.toast.sent': '已试发给 {count} 个组织，本次发送不是全量发布',
  // 另一条分支，用 error 而不是沉默：表单填满后结果面板的红色告警在折叠线以下，
  // 刚点完按钮的运维需要一条会打断他的提示。措辞同时覆盖两种坏回答（mode 为
  // fleet、以及根本没有 mode），所以不替服务端断言它说了什么。
  'announcements.test.toast.unconfirmed':
    '本次未被确认为试发：服务端没有回报白名单发送，这条公告可能已经发给全部租户组织，且无法撤回。请立即查看下面的结果面板。',
  // 发送失败时全页最需要读懂的一句，不能把服务端那段英文长句原样弹出来。
  // 措辞与服务端一致地留有余地：几乎可以肯定没发出去，但连接在 COMMIT 之后
  // 断开这一种情况无法排除，而「重发同一个 slug 是安全的」在两种情况下都成立。
  'announcements.toast.failed':
    '公告几乎可以肯定没有发出：广播与审计记录一同回滚。重发同一个 slug 是安全的，重发结果里的「覆盖改写」数可以确认上一次是否已经落地。',
  'announcements.result.caption': '发布结果',
  'announcements.result.targeted': '覆盖组织数',
  'announcements.result.created': '新建',
  'announcements.result.resurfaced': '覆盖改写',
  'announcements.result.fingerprint': '指纹',
  'announcements.result.recycled.tag': '注意',
  // 不指认唯一成因（#1965）：先试发再全量发布，必然产生「覆盖改写」，把这条
  // 提示单一解释成「你回收了别人的 slug」会让它在正常流程里天天误报，最后被
  // 当成狼来了而忽略——而它同时也是「另一条公告刚被你覆盖掉」的唯一信号。
  // 两种成因都写出来，第二种的后果一个字都不减。
  'announcements.result.recycled':
    '该 slug 此前已存在：这些组织收到的是原地改写后的公告，而不是新增一条。两种情况会这样：一是此前做过试发、这次是全量发布，属于预期；二是本意发新公告却复用了别人的 slug——那条公告的文案已被本次发送覆盖。没有撤回操作，而且那些组织已经被重新提醒过一次；唯一的补救是用同一个 slug 把原来的文案再发一次。不确定的话，先在下面的发送记录里核对这个 slug 上一次发的是什么。',
  'announcements.result.test.caption': '试发结果（本次发送不是全量发布）',
  'announcements.result.test.scope.tag': '试发',
  // #1945 的根子是没人告诉运维覆盖面只有 9 个组织。所以覆盖面用整句话说在
  // 数字之前，而不是留给运维去解读一个小一点的「覆盖组织数」。
  //
  // 只讲「本次发送」，原来那句不是：它断言「全量发布尚未发生」「其余租户什么都
  // 没有收到」——这是拿一次响应去陈述全局状态。而「试发 → 全量发布 → 发现错字
  // → 对同一 slug 再试发」正是本弹窗自己描述的流程，走完之后全部租户都持有这个
  // slug，面板却还在说没有。resurfaced === 0 也救不了：第二次试发的 resurfaced
  // 等于名单长度，和「首次试发撞上别人的 slug」一模一样。
  'announcements.result.test.scope':
    '本次发送只写入了 {count} 个组织，没有写入其他任何组织，也不是全量发布。确认文案无误后，再点「发布公告」发给全部租户。',
  'announcements.result.test.shortfall':
    '名单里有 {requested} 个组织，实际写入 {targeted} 个。请核对名单后再发，不要按名单人数对外沟通。',
  // 试发结果面板里 resurfaced > 0 只有坏的那一种成因：本次就是试发，说明这个
  // slug 已经被别的公告占用。用全量面板那句「两种成因」会把它稀释成可忽略的提示。
  'announcements.result.test.recycled.tag': '严重',
  'announcements.result.test.recycled':
    '名单内有 {count} 个组织此前已持有这个 slug 的公告：他们手里那条公告的正文已被这份草稿当场覆盖。没有撤回操作。本次是试发，所以这不可能是「先试发再发布」的正常改写——请先在下面的发送记录里核对这个 slug 上一次发的是什么，再用同一个 slug 把那份文案发回给同一批组织。若改为放弃这份草稿，这些组织手里就停在草稿文案上。',
  'announcements.result.mode_mismatch.tag': '严重',
  'announcements.result.mode_mismatch':
    '本次请求要求只发给名单内组织，但服务端回报按【全量】发送——也就是说这条公告已经发给全部租户组织，且无法撤回。请立即确认服务端版本是否早于白名单功能上线。',
  // 反方向，也就是 #1945 的原样形态：以为发了全量，实际只写了几个组织。原来这一
  // 种落进中性面板，只留一个「覆盖组织数」让运维自己解读——而「自己解读一个数字」
  // 正是 #1945 里没人做的那一步。
  'announcements.result.mode_mismatch.narrowed':
    '本次请求要求发给全部租户组织，但服务端回报按【白名单】发送：本次只写入了 {count} 个组织，全量发布并未发生。不要按「已通知全部客户」对外沟通；请确认服务端状态后重新发布。',
  'announcements.history.caption': '发送记录',
  'announcements.history.column.sent_at': '发送时间',
  'announcements.history.column.slug': 'slug',
  'announcements.history.column.title': '标题',
  'announcements.history.column.severity': '级别',
  'announcements.history.column.scope': '范围',
  'announcements.history.scope.fleet': '全量',
  // 这个数字是「名单长度」，不是到达数——同一行右边的「覆盖组织数」才是实际写入
  // 的组织数，两者可以不等（名单里有组织在校验和写入之间消失）。所以计数带上
  // 「名单」二字：一周后回看，「试发 3」会被读成「3 个组织收到了」，而「名单 3」
  // 不会。审计元数据读不出名单时不写 0（那是对名单的假陈述），改写「未记录」。
  'announcements.history.scope.whitelist': '试发 · 名单 {count}',
  'announcements.history.scope.whitelist.unknown': '试发 · 名单未记录',
  'announcements.history.column.targeted': '覆盖组织数',
  'announcements.history.column.created': '新建',
  'announcements.history.column.resurfaced': '覆盖改写',
  // #1947。以下每一条说的都是「拉取」——公告写入之后调用过通知接口的组织。
  // 一律不得改写成「已读」「送达」「已接收」：服务端无从知道这三件事，而
  // #1945 已经演示过一个看着可信、实际不成立的数字要付什么代价。
  'announcements.history.column.polled': '公告后拉取过',
  'announcements.history.column.polled.title':
    '公告写入之后调用过通知接口的组织数 / 目前仍持有该公告的组织数。这是「有机会看到」的下界，不能证明有人读过。',
  // n/a 那一句刻意不指认原因。判据只是「当前没有该 fingerprint 的通知行」，
  // 而行数为 0 至少有四种成因（过保留期、这个 slug 根本没发过、发的时候就是
  // 0 个组织、目标组织全被删）。挑一种写成结论，就是 #1945 那类「界面说出
  // 自己无从知道的因果」——只能举例，不能断言。
  'announcements.history.polled.note':
    '「公告后拉取过」统计的是：公告写入之后，至少有一名成员的客户端调用过通知接口的组织数 —— 也就是有机会看到。它不是已读状态，也不是送达回执：多人组织里一个人拉过就算整个组织拉过，而且这个数字会随着离线客户端上线继续往上走。刚发完的几分钟内这个数会系统性偏低：拉取活动按 5 分钟窗口记一次，所以公告落地后立刻来拉的客户端往往要等下一个窗口才会计入 —— 看到接近 0 是正常现象，不是故障。它还是「按 slug、按当前」算出来的数，不是按行算的：同一个 slug 的每一次发送共享同一个值，所以试发那一行显示 n/a，而不是显示一个并不描述这次试发的数字。n/a 表示本列对这一行给不出数：要么这一行是试发，要么当前没有该公告的通知行可比对（例如已过 90 天保留期）。它从不表示公告没有到达任何人。',
  'announcements.history.polled.unmeasurable': 'n/a',
  'announcements.history.polled.whitelist.title':
    '试发没有属于自己的可达性数字：本列按 slug 统计，同一个 slug 的每一次发送（包括之后的全量发布）共享同一个当前值。在这一行显示它，会被读成「这次试发到达了这么多组织」。',
  // 读取失败必须区别于「加载中」和「0」：满列 — 看上去像永远在加载，运维
  // 会以为是慢，而不是这块数据根本没读到。
  'announcements.history.polled.unavailable': '?',
  'announcements.history.polled.unavailable.title': '可达性读取失败，本列数字不可用（与「没人拉取」无关）。',
  'announcements.history.polled.error': '可达性数据加载失败，本列无法显示（发送记录本身不受影响）。',
  'announcements.history.column.operator': '操作人',
  'announcements.history.empty': '还没有发过公告。',
  // 读取失败和「没有记录」必须分开说：把加载失败显示成「还没有发过公告」，
  // 等于对着一个刚发完公告的管理员断言相反的事实。
  'announcements.history.error': '发送记录加载失败，无法确认历史公告。',
  // 刻意写短：完整那句已经由表格上方的 alert 播报过一次。
  'announcements.history.error.cell': '加载失败。',
  'announcements.history.loading': '加载中…',
  'announcements.history.pagination.aria_label': '发送记录分页',
  'announcements.quiet.caption': '沉默租户',
  'announcements.quiet.note':
    '在所选时间窗内，没有任何成员的客户端调用过通知接口的组织。这是排查起点而不是故障清单：团队放假、只用 API 不装客户端、客户端真的坏了，在这里长得一模一样。它也完全不说明谁读过什么。',
  'announcements.quiet.threshold': '沉默时长至少',
  'announcements.quiet.threshold.days': '{days} 天',
  'announcements.quiet.total': '共 {total} 个组织',
  'announcements.quiet.column.org': '组织',
  'announcements.quiet.column.org_id': '组织 ID',
  // 「活跃成员」不是「在册成员」：查询条件是 org_members.status = 'active'，
  // 被停用和已删除的成员都不计。中文写松一点会让运维把这一列当成组织规模。
  'announcements.quiet.column.members': '活跃成员',
  'announcements.quiet.column.last_polled': '最近一次拉取',
  'announcements.quiet.never': '从未拉取',
  'announcements.quiet.empty': '窗口内所有租户组织都拉取过。',
  'announcements.quiet.loading': '加载中…',
  // 与发送记录同一条规则：读取失败不等于「没有沉默租户」，后者是这块面板
  // 在读取失败时根本没有依据下的断言。
  'announcements.quiet.error': '沉默租户清单加载失败，无法确认哪些租户在沉默。',
  'announcements.quiet.error.cell': '加载失败。',
  'announcements.quiet.pagination.aria_label': '沉默租户分页',

  // ── Support tickets (#1394) ─────────────────────────────────────
  // 工单系统的运维侧:队列、会话、附件、状态流转与客服回复。与上面的
  // 租户请求查询(请求日志排查工具)只共享角色门槛。
  'support.tickets.title': '工单',
  'support.tickets.subtitle': '租户提交的支持工单:查看会话、查看附件、回复并流转状态。',
  'support.tickets.filter.status': '状态',
  'support.tickets.status.all': '全部',
  'support.tickets.status.open': '待处理',
  'support.tickets.status.processing': '处理中',
  'support.tickets.status.resolved': '已解决',
  'support.tickets.status.closed': '已关闭',
  'support.tickets.type.billing': '计费',
  'support.tickets.type.usage': '用量',
  'support.tickets.type.api': 'API',
  'support.tickets.type.model': '模型',
  'support.tickets.type.other': '其他',
  'support.tickets.filter.org_id': '组织 ID',
  'support.tickets.filter.org_id.placeholder': 'org_xxx',
  'support.tickets.filter.q': '搜索',
  'support.tickets.filter.q.placeholder': '搜索描述 / 邮箱',
  'support.tickets.filter.apply': '应用',
  'support.tickets.filter.reset': '重置',
  'support.tickets.error.title': '无法加载工单',
  'support.tickets.loading_aria': '正在加载工单…',
  'support.tickets.empty.title': '没有匹配的工单',
  'support.tickets.empty.body': '调整状态筛选或搜索条件再试。',
  'support.tickets.column.id': '工单 ID',
  'support.tickets.column.org': '组织',
  'support.tickets.column.type': '类型',
  'support.tickets.column.status': '状态',
  'support.tickets.column.email': '邮箱',
  'support.tickets.column.attachments': '附件',
  'support.tickets.column.created_at': '创建时间',
  'support.tickets.column.updated_at': '更新时间',
  'support.tickets.detail.description': '问题描述',
  'support.tickets.detail.conversation': '会话记录',
  'support.tickets.detail.conversation.empty': '暂无回复。',
  'support.tickets.detail.attachments': '附件',
  'support.tickets.detail.attachments.none': '无附件。',
  'support.tickets.detail.attachment_expand': '放大附件 {name}',
  'support.tickets.detail.attachment_collapse': '还原附件 {name}',
  'support.tickets.detail.attachment_loading_aria': '正在加载附件图片…',
  'support.tickets.detail.download': '下载',
  'support.tickets.detail.status_label': '工单状态',
  'support.tickets.detail.loading_aria': '正在加载工单详情…',
  'support.tickets.reply.author.user': '用户',
  'support.tickets.reply.author.vendor': '客服',
  'support.tickets.reply.label': '回复工单',
  'support.tickets.reply.placeholder': '输入回复内容…',
  'support.tickets.reply.submit': '发送回复',
  'support.tickets.reply.submitting': '发送中…',
  'support.tickets.toast.status_updated': '工单状态已更新',
  'support.tickets.toast.reply_sent': '回复已发送',
  'support.tickets.pagination.aria_label': '工单分页',
  'support.tickets.pagination.range': '显示第 {start}–{end} 条,共 {total} 条',

  // ── 准入拦截 (router#1609 / #1689，渲染于 #1615) ──────────────────
  // 三个运维请求面共用一套键：/customers/requests、/support/requests
  // 与客户经理请求日志渲染的是同一份判定，用词不一致会让同一次拦截
  // 在三处读起来像三件事。
  'requests.admission.title': '准入拦截',
  'requests.admission.rule': '命中规则',
  'requests.admission.action': '处置动作',
  'requests.admission.client_kind': '判定客户端类型',
  'requests.admission.client_entrypoint': '客户端入口',
  'requests.admission.lane': '出栈车道',

  // ── Probe outcome labels ────────────────────────────────────────
  // `probe.outcome.*` mirrors the `ProbeOutcome` union from
  // @nadoutong/contracts.
  'probe.outcome.pass': '通过',
  'probe.outcome.fail_shape': '格式错误',
  'probe.outcome.fail_content': '内容不符',
  'probe.outcome.fail_upstream_error': '上游错误',
  'probe.outcome.fail_timeout': '超时',
  'probe.id_label': '探针 {id}…',
  'probe.latency': '延迟',
  'probe.cost': '成本',
  'probe.upstream_status': '上游状态',
  'probe.input_output_tokens': '输入 / 输出 tokens',
  'probe.cache_read_write': '缓存读 / 写',
  'probe.charge_skipped': '跳过扣费',
  'probe.error': '错误',
  'probe.modal.title': '探测上游',
  'probe.modal.close_aria': '关闭探针对话框',
  'probe.modal.error.vendor_model_required': '厂商与模型均为必填',
  'probe.modal.vendor.label': '厂商',
  'probe.modal.vendor.placeholder': '例如 anthropic',
  'probe.modal.model.label': '模型',
  'probe.modal.model.placeholder': '例如 claude-sonnet-4-5',
  'probe.modal.protocol.label': '协议',
  'probe.modal.submit': '运行探针',
  'probe.modal.submitting': '探测中…',

  // ── Reports ─────────────────────────────────────────────────────
  'reports.title': '报表',
  'reports.subtitle': '合规与财务报表。按月聚合，支持 CSV 导出。',
  'reports.month_label': '月份',
  'reports.regulatory.title': '监管数据提取',
  'reports.regulatory.body':
    '用于 PRC 生成式 AI 备案的按账户月度使用数据。仅列出当月有使用记录的账户。',
  'reports.regulatory.csv_download': '下载 CSV',
  'reports.regulatory.loading': '正在加载监管数据…',
  'reports.regulatory.error': '加载监管数据失败。',
  'reports.regulatory.empty': '本月无账户使用记录。',
  'reports.regulatory.row_count': '{count} 个账户',
  'reports.regulatory.truncated': '行数超出导出上限，部分账户被截断。请提高上限或分页。',
  'reports.regulatory.column.account_id': '账户 ID',
  'reports.regulatory.column.email': '邮箱',
  'reports.regulatory.column.registered_at': '注册时间',
  'reports.regulatory.column.input_tokens': '输入 tokens',
  'reports.regulatory.column.output_tokens': '输出 tokens',
  'reports.regulatory.column.cost_usd': '成本 (CNY)',
  'reports.monthly.title': '月度对账',
  'reports.monthly.body': '所选月份的收入台账 ↔ 支付订单。若有差额请排查。',
  'reports.monthly.csv_download': '下载 CSV',
  'reports.monthly.view_orders': '查看支付订单',
  'reports.monthly.loading': '正在加载月度报表…',
  'reports.monthly.error': '加载月度报表失败。',
  'reports.monthly.drift.title': '检测到差额。',
  'reports.monthly.drift.body': '实收金额与确认充值金额相差 {amount}。请排查缺失的回调或台账写入。',
  'reports.monthly.tile.paid_cny': '已支付订单 (CNY)',
  'reports.monthly.tile.paid_count': '{count} 笔订单',
  'reports.monthly.tile.topups_cny_gross': '充值 (CNY 毛额)',
  'reports.monthly.tile.topups_count': '{count} 条台账',
  'reports.monthly.tile.usage_usd': '使用扣费 (CNY)',
  'reports.monthly.tile.usage_rows': '{rows} 条使用记录',
  'reports.monthly.tile.l2_profit': 'L2 利润 (CNY)',
  'reports.monthly.tile.l1_profit': 'L1 利润 {amount}',
  'reports.monthly.detail.metric': '指标',
  'reports.monthly.detail.value': '数值',
  'reports.monthly.detail.period_start': '起始时间',
  'reports.monthly.detail.period_end': '结束时间',
  'reports.monthly.detail.paid_cny_net': '充值 (CNY 手续费后净额)',
  'reports.monthly.detail.usd_credited': '入账 USD',
  'reports.monthly.detail.ops_adjust_rows': '运营调整条数',
  'reports.monthly.detail.ops_adjust_usd': '运营调整 USD',
  'reports.monthly.detail.drift_cny': '差额 (已付 − 充值, CNY)',

  // ── Webhook events ──────────────────────────────────────────────
  'webhook_events.title': 'Webhook 事件',
  'webhook_events.subtitle':
    '各 provider 的 webhook 回调收件箱。支持筛选、展开原始负载、手动标记处理完成。',
  'webhook_events.filter.provider': '提供商',
  'webhook_events.filter.provider_placeholder': '例如 zpay',
  'webhook_events.filter.processed': '状态',
  'webhook_events.filter.processed.all': '全部',
  'webhook_events.filter.processed.pending': '待处理',
  'webhook_events.filter.processed.done': '已处理',
  'webhook_events.filter.since': '起始时间 (本地)',
  'webhook_events.loading': '正在加载 webhook 事件…',
  'webhook_events.error': '加载 webhook 事件失败。',
  'webhook_events.empty': '当前筛选条件下没有 webhook 事件。',
  'webhook_events.column.created_at': '创建时间',
  'webhook_events.column.provider': '提供商',
  'webhook_events.column.event_type': '事件类型',
  'webhook_events.column.status': '状态',
  'webhook_events.column.payload': '负载',
  'webhook_events.column.actions': '操作',
  'webhook_events.status.processed': '已处理',
  'webhook_events.status.pending': '待处理',
  'webhook_events.payload.expand': '展开负载',
  'webhook_events.action.mark_processed': '标记已处理',
  'webhook_events.action.marking': '正在标记…',
  'webhook_events.toast.marked': '事件已标记为已处理。',

  // ── VPS management ──────────────────────────────────────────────
  'vps.title': 'VPS 节点',
  'vps.subtitle': '只读查看 KiwiVM 套餐、流量、负载和节点风险。',
  'vps.loading': '正在加载 VPS 节点…',
  'vps.empty': '尚未配置 KiwiVM VPS 节点。',
  'vps.metric.nodes': '节点',
  'vps.metric.nodes_detail': 'KiwiVM 配置中的 VPS 数量',
  'vps.metric.unhealthy': '异常',
  'vps.metric.unhealthy_detail': 'warning 或 critical 节点',
  'vps.metric.lowest_traffic': '最低剩余流量',
  'vps.metric.lowest_traffic_detail': '所有节点中的最低剩余比例',
  'vps.metric.updated': '更新',
  'vps.metric.updated_detail': '最近一次 KiwiVM 读取时间',
  'vps.column.node': '节点',
  'vps.column.status': '状态',
  'vps.column.plan': '套餐',
  'vps.column.traffic': '流量',
  'vps.column.billing_cycle': '账期',
  'vps.column.load': '负载',
  'vps.column.disk': '磁盘',
  'vps.column.risks': '风险',
  'vps.column.lb': 'LB 池',
  'vps.billing_cycle.reset_hint': '流量重置',
  'vps.lb.empty': '无池',
  'vps.lb.purpose.platform': 'Platform',
  'vps.lb.purpose.router': 'Router',
  'vps.lb.purpose.sidecar': 'Sidecar',
  'vps.lb.state.not_in_pool': '不在池中',
  'vps.lb.state.enabled': '启用',
  'vps.lb.state.disabled_manual': '已禁用（手动）',
  'vps.lb.state.disabled_auto': '已禁用（自动 · 流量）',
  'vps.lb.action.join': '加入',
  'vps.lb.action.enable': '启用',
  'vps.lb.action.disable': '禁用',
  'vps.lb.action.remove': '移除',
  'vps.lb.outcome.joined': '已加入池',
  'vps.lb.outcome.updated': '已更新',
  'vps.lb.outcome.enabled': '源站已启用',
  'vps.lb.outcome.disabled': '源站已禁用（已排空）',
  'vps.lb.outcome.removed': '已从池中移除',
  'vps.lb.outcome.absent': '未在池中',
  'vps.lb.outcome.held': '已标记暂缓再平衡',
  'vps.lb.confirm.body': '对 {node} 执行 {action} 操作（{pool} 池）？',
  'vps.lb.confirm.floor_warning': '此池将仅剩 ≤1 个启用源站。',
  'vps.config.title': 'KiwiVM 配置',
  'vps.config.subtitle': '当前环境的 VEID 和 key 预览。',
  'vps.config.add': '添加节点',
  'vps.config.loading': '正在加载配置…',
  'vps.config.empty': '尚未添加 KiwiVM 配置。',
  'vps.config.column.node': '节点',
  'vps.config.column.veid': 'VEID',
  'vps.config.column.key': 'Key',
  'vps.config.column.state': '状态',
  'vps.config.column.actions': '操作',
  'vps.config.key_configured': '已配置',
  'vps.config.key_missing': '未配置',
  'vps.config.enabled': '启用',
  'vps.config.disabled': '停用',
  'vps.config.edit': '编辑',
  'vps.config.modal.add_title': '添加 KiwiVM VPS',
  'vps.config.modal.edit_title': '编辑 KiwiVM VPS',
  'vps.config.field.id': 'ID',
  'vps.config.field.environment': '环境',
  'vps.config.field.label': '名称',
  'vps.config.field.role': '角色',
  'vps.config.field.veid': 'VEID',
  'vps.config.field.public_host': '主机',
  'vps.config.field.api_key': 'API key',
  'vps.config.field.rotate_api_key': '轮换 API key',
  'vps.config.field.deploy_target': '参与 CD 部署',
  'vps.config.field.deploy_ip': '部署 IP（IPv4）',
  'vps.config.field.enabled': '启用监控',
  'vps.config.cancel': '取消',
  'vps.config.save': '保存',
  'vps.config.toast.saved': 'VPS 配置已保存',
  'vps.config.toast.veid_required': 'VEID 不能为空',
  'vps.config.toast.key_required': 'API key 不能为空',
  'vps.column.trend': '趋势',
  'vps.trend.sparkline_aria': '{label} 近 7 天剩余流量比例',
  'vps.trend.no_samples': '暂无采样',
  'vps.trend.burn_per_day': '{value}/天',
  'vps.trend.no_burn': '消耗速率暂不可用',
  'vps.trend.projected': '预计 {date} 耗尽',
  'vps.trend.no_projection': '按当前速率不会提前耗尽',
  'vps.trend.open_detail': '查看 {label} 的 30 天趋势',
  'vps.detail.subtitle': '近 30 天剩余流量比例与每日消耗（占月度配额）。',
  'vps.detail.loading': '正在加载节点趋势…',
  'vps.detail.empty': '尚无历史采样。采集任务每 5 分钟写入一次。',
  'vps.detail.legend.remaining': '剩余比例',
  'vps.detail.legend.daily_burn': '每日消耗',
  'vps.config.toast.deploy_ip_invalid': '开启部署目标时必须填写合法的 IPv4 地址',
  'vps.automation.title': '低流量告警阈值',
  'vps.automation.hint':
    '节点剩余流量低于该百分比时发送飞书告警(0.1%–50%);权重重算不受影响',
  'vps.automation.inputLabel': '阈值(%)',
  'vps.automation.save': '保存',
  'vps.automation.saved': '告警阈值已更新',
  'vps.automation.invalid': '阈值必须在 0.1% 到 50% 之间',
  'vps.automation.loadError': '告警阈值加载失败',

  // ── Cron runs ───────────────────────────────────────────────────
  'cron_runs.title': '定时任务运行',
  'cron_runs.subtitle': 'control-loops 定时触发的运行历史。每行对应一次 tick 中所有到期处理器。',
  'cron_runs.filter.handler': '处理器',
  'cron_runs.filter.handler_placeholder': '例如 usage_rollup',
  'cron_runs.filter.since': '起始时间 (本地)',
  'cron_runs.filter.since_placeholder': 'YYYY-MM-DD HH:MM',
  'cron_runs.loading': '正在加载定时任务运行…',
  'cron_runs.error': '加载定时任务运行失败。',
  'cron_runs.empty': '当前筛选条件下无运行记录。',
  'cron_runs.column.scheduled_at': '计划时间',
  'cron_runs.column.duration': '耗时',
  'cron_runs.column.due': '到期',
  'cron_runs.column.ok': '成功',
  'cron_runs.column.fail': '失败',
  'cron_runs.column.outcomes': '结果',
  'cron_runs.outcomes.expand': '展开原始结果',
  'cron_runs.manual.title': '手动触发',
  'cron_runs.manual.caveat':
    '手动触发直接调用各处理器背后的幂等 internal 端点。运行历史（含手动触发）为 best-effort 记录，可能缺失或延迟——手动触发结果以触发响应（toast）为准。',
  'cron_runs.manual.run_now': '立即运行',
  'cron_runs.manual.scheduler_only': '仅限定时',
  'cron_runs.manual.confirm_title': '立即运行 {handler}？',
  'cron_runs.manual.confirm_body':
    '将立即调用该处理器背后的幂等 internal 端点，与定时执行完全一致，不会与定时节奏冲突。本次触发会写入一条审计记录。',
  'cron_runs.manual.confirm_action': '立即运行',
  'cron_runs.manual.now_ms_label': '测试时钟（可选）',
  'cron_runs.manual.now_ms_hint':
    '仅非生产环境可用。生产会拒绝该字段。留空则使用真实时间。',
  'cron_runs.manual.toast_success': '{handler} 运行完成（{duration}ms）。',
  'cron_runs.manual.toast_failed': '{handler} 运行失败：{error}',
  'cron_runs.manual.unknown_error': '未知错误',
  'cron_runs.manual.reason.fx_rate_refresh':
    '汇率抓取阶段访问外部行情源，抓取与解析逻辑仅存在于 cron worker，无法经 internal 端点代理。',
  'cron_runs.manual.reason.gateway_public_canary':
    '需要仅配置在 cron worker 环境中的 canary 凭证与公网网关地址。',
  'cron_runs.manual.reason.supplier_concentration':
    '仅观测型处理器：集中度快照只写入 worker 日志，没有可重跑的状态变更。',
  'cron_runs.manual.reason.usage_logs_cn_cleanup':
    '三腿协议（东京 uplink 门闸、CN 选取、东京 confirm-present、CN 删除）不能由一次 POST 驱动；武装/解除武装走 USAGE_LOGS_CN_CLEANUP_ARMED。',
  'cron_runs.manual.reason.ledger_archive':
    '钱表归档导出只由 control-loops 驱动：仅在上海时间 02:00–07:00 窗口内、按每个 tick 的剩余预算运行，且 LEDGER_ARCHIVE_ARMED 与 LEDGER_ARCHIVE_ENABLED 两把钥匙都打开时才执行；手动触发会绕过窗口、跳过区间与刹车检查，因此不提供。',
  'cron_runs.manual.reason.generic': '该处理器仅支持定时执行。',
  'cron_runs.manual.side_effect.account_balance_alert':
    '会真的向该环境账号填写的真实邮箱发出余额告警邮件——dev 和 staging 同样真发，不只是生产。邮件一旦发出无法撤回。',
  'cron_runs.manual.side_effect.upstream_runway_alert':
    '会真的向该环境的 Lark 运维群推送续航告警卡片——dev 和 staging 同样真推，不只是生产。',
  'cron_runs.manual.side_effect.email_notice_reminder':
    '会真的向该环境的 Lark 运维群推送到期提醒卡片——dev 和 staging 同样真推，不只是生产。它会把这些通知标记为已提醒，下一轮就不再推；不会发任何客户邮件，也不会改任何模型。',
  'cron_runs.manual.side_effect.routing_default_parity_check':
    '只读比对，不写任何一张表，重跑和刷新页面一样安全。唯一的对外动作是：当全局默认配置与平台默认表确实不一致时，向该环境的 Lark 运维群推一张卡片——两边一致时完全静默。漂移未修复期间每次触发都会再推一张。',
  'cron_runs.manual.side_effect.deliver_credit_alerts':
    '会真的向该环境的授信飞书群推送告警卡片——dev 和 staging 同样真推，不只是生产。它推的是授信专用群，不是运维告警群。投递留痕只在确认送达之后才写，所以失败的那条会在下一轮重试，而已经推过的不会因为重跑再推一次；逾期客户每天一条汇总，直到结清。不发任何客户邮件。',
  'cron_runs.manual.side_effect.finance_pnl_materialize':
    '重算财务盈亏用量的 5 分钟片（派生表），并把过了 48 小时宽限的 Asia/Shanghai 日从片汇总封存进日表。幂等：每次只跑一段有界的工作，下一次接着跑。复核时片汇总与日表对不上的日子会被改写并上报漂移。',
  'cron_runs.manual.side_effect.revenue_funding_materialize':
    '会写入已封账 Asia/Shanghai 日的营收 funding 日汇总（派生表）。幂等：已经物化过的日子会跳过。一次调用只补一小批缺标日。',
  'cron_runs.manual.side_effect.rated_pair_exercise':
    '只读调用评级对拍读者，走两个冷窗口。平台未设 RATED_UNIFIED_PAIR_EXERCISE=1 时什么都不读。不写任何业务表。只有同时设了 RATED_UNIFIED_PAIR_RECORD=1 才会往对拍记录表插一行。',
  'cron_runs.manual.side_effect.rated_hour_fold':
    '会写入已封口上海小时的 usage_rated_hour。平台未设 RATED_HOUR_FOLD_ARMED=1 时什么都不读不写。幂等：已折的小时会被同一份结果替换。一次调用只折一小批小时。',
  'cron_runs.manual.side_effect.rated_rollup_fold':
    '会写入已封账 Asia/Shanghai 小时的评级事件日汇总（运营吞吐序列、邮件受众、定价锚点读它；派生表）。平台未设 RATED_ROLLUP_FOLD_ARMED=1 时什么都不读不写。幂等：已折叠的小时会被同一份折叠结果替换。一次调用只折一小批小时。',
  'cron_runs.manual.side_effect.revenue_hourly_fold':
    '会写入已封账 UTC 小时的营收小时汇总（summary 的 funding 拆分、by-model/daily 读它；派生表）。平台未设 REVENUE_HOURLY_FOLD_ARMED=1 时什么都不读不写。幂等：已封小时由同一折叠替换。一次调用只折一小批小时。',
  'cron_runs.manual.side_effect.promoter_grant_expiry_scan':
    '会真的向该环境已绑定的 C 端账号发出赠送额度到期提醒邮件——dev 和 staging 同样真发，不只是生产。邮件一旦发出无法撤回。它会盖上 grant_expiry_notified_at，重跑不会对同一批次再发。',
  'cron_runs.manual.side_effect.credit_draw_sweep':
    '会写入 credit_draw 授信批次及其扣费归属记录——它只为已经结算完成的消费补记归属，不动钱、不改余额。幂等：已归属过的扣费会跳过，批次总额每轮重新推导，重跑不会重复归属任何一分钱。手动点一次只处理滚动三天窗口内的一页；更早的数据它够不到，不能当回填工具用。剩余部分由定时任务下一轮继续排空。',
  'cron_runs.manual.side_effect.codex_capacity_alert':
    '会真的向该环境账号填写的真实邮箱发出 Codex 容量告警邮件——dev 和 staging 同样真发，不只是生产。邮件一旦发出无法撤回。',
  'cron_runs.manual.side_effect.codex_capacity_rollup':
    '会按回算窗口重算 Codex 容量归档桶，并按保留窗剪掉过期行。幂等覆盖：重跑是替换计数，不是累加。不发任何通知。',
  'cron_runs.manual.side_effect.official_status_sync':
    '会抓取 OpenAI 与 Anthropic 的公开状态页并镜像进 official_status_items。幂等：只写内容变化的行。不向租户发送任何东西。',
  'cron_runs.manual.side_effect.generic':
    '手动运行会产生真实的对外副作用，dev 和 staging 同样会发生，不只是生产。',

  // ── Ops users ───────────────────────────────────────────────────
  'ops_users.title': '运维账户',
  'ops_users.subtitle': '运维账户管理目录。禁用被盗账户、重新启用合法账户、强制重置密码。',
  'ops_users.loading': '正在加载运维账户…',
  'ops_users.error': '加载运维账户失败。',
  'ops_users.empty': '未找到运维账户。',
  'ops_users.metric.total': '总数',
  'ops_users.metric.active': '启用',
  'ops_users.metric.disabled': '已禁用',
  'ops_users.metric.locked_out': '无法登录',
  'ops_users.metric.unbound': '未绑定',
  'ops_users.matrix.title': '权限矩阵',
  'ops_users.matrix.subtitle': '按运维账户横向比较可分配角色，创建和编辑通过侧边 Sheet 完成。',
  'ops_users.matrix.roles': '角色',
  'ops_users.column.email': '邮箱',
  'ops_users.column.user': '用户',
  'ops_users.column.roles': '角色',
  'ops_users.column.github': 'GitHub',
  'ops_users.column.feishu': '飞书',
  'ops_user_identity.method.feishu': '飞书',
  'ops_user_identity.method.password': '密码',
  'ops_user_identity.method.github': 'GitHub',
  'ops_users.column.status': '状态',
  'ops_users.column.created_at': '创建时间',
  'ops_users.column.actions': '操作',
  'ops_users.form.email': '邮箱',
  'ops_users.form.roles': '角色',
  'ops_users.form.github_login': 'GitHub login',
  'ops_users.form.feishu_tenant': '飞书租户',
  'ops_users.form.feishu_open_id': '飞书 open ID',
  'ops_users.form.feishu_name': '飞书显示名',
  'ops_users.form.sales_profile': '销售档案品牌',
  'ops_users.form.sales_profile_hint': '每个销售归属一个品牌。名下仍有客户时不允许删除或改品牌。',
  'ops_users.form.sales_profile_none': '无销售档案',
  'ops_users.form.sales_profile_error': '销售档案加载失败——重试成功前不能修改品牌。',
  'ops_users.sheet.eyebrow': '访问控制',
  'ops_users.inspector.empty': '选择一个运维账户查看身份和安全状态。',
  'ops_users.inspector.roles': '角色权限',
  'ops_users.inspector.feishu': '飞书绑定',
  'ops_users.inspector.security': '会话与安全',
  'ops_users.inspector.password': '密码状态',
  'ops_users.inspector.password_set': '已设置',
  'ops_users.inspector.password_unset': '未设置',
  'ops_users.inspector.revoked_before': '会话撤销时间',
  'ops_users.github.unbound': '未绑定',
  'ops_users.feishu.unbound': '未绑定',
  'ops_users.action.create': '创建用户',
  'ops_users.action.provision': '新建销售账号',
  'ops_users.action.provisioning': '开通中…',
  'ops_users.form.display_name': '姓名',
  'ops_users.provision.intro':
    '一次性建号：创建运维账户、绑定销售品牌、生成登录密码、生成邀请码。四步在同一个事务里完成，失败不会留下半成品账号。',
  'ops_users.provision.display_name': '姓名',
  'ops_users.provision.email': '邮箱',
  'ops_users.provision.brand': '品牌',
  'ops_users.provision.invite_code_label': '自定义邀请码（选填）',
  'ops_users.provision.invite_code_hint':
    '留空则由系统生成。只能用大写字母、数字和连字符，后缀 {min}–{max} 位；与已有邀请码、推广码或礼品卡重复时不能创建。',
  'ops_users.provision.invite_code_taken': '邀请码 {code} 已被占用，账号未创建，请换一个',
  'ops_users.provision.grant_label': '试用金金额（元）',
  'ops_users.provision.grant_hint': '已用推荐值预填。框里是什么，就会写到这张邀请码上。',
  'ops_users.provision.expiry_label': '试用金有效期（天）',
  'ops_users.provision.expiry_hint': '已用推荐值预填。框里是什么，就会写到这张邀请码上。',
  'ops_users.provision.quota_label': '每日名额',
  'ops_users.provision.quota_hint': '已用推荐值预填。框里是什么，就会写到这张邀请码上。',
  'ops_users.provision.plan_label': '模型方案',
  'ops_users.provision.plan_hint': '可选。绑定后该码激活的客户按方案落地模型与折扣。',
  'ops_users.provision.error.display_name_required': '请填写姓名。',
  'ops_users.provision.duplicate_name_warning':
    '已有同名账号「{name}」,继续创建会出现重名,分配客户时请按登录方式和邮箱区分:',
  'ops_users.provision.error.grant': '试用金金额需为大于 0 的数字，最多两位小数',
  'ops_users.provision.error.grant_cap': '试用金金额超过上限 {max}',
  'ops_users.provision.error.expiry': '试用金有效期需为正整数天',
  'ops_users.provision.error.quota': '每日名额需为正整数',
  'ops_users.provision.error.defaults': '无法加载全局激活默认值，请关闭后重试。',
  'ops_users.provision.result.warning':
    '密码只显示这一次。关闭后无法再次查看，请先复制保存。',
  'ops_users.provision.result.name': '姓名',
  'ops_users.provision.result.email': '邮箱',
  'ops_users.provision.result.password': '密码',
  'ops_users.provision.result.invite_code': '邀请码',
  'ops_users.provision.result.brand': '品牌',
  'ops_users.provision.result.copy_label': '一键复制（邮箱/密码/邀请码）',
  'ops_users.provision.result.copy': '复制',
  'ops_users.provision.result.copied': '已复制',
  'ops_users.provision.result.copy_failed': '复制失败，请手动选中文本复制。',
  'ops_users.provision.result.close': '我已复制，关闭',
  'ops_users.provision.result.close_confirm': '关闭后密码将无法再次查看。确认已经复制？',
  'ops_users.toast.provisioned': '销售账号已开通。',
  'ops_users.action.permissions': '权限矩阵',
  'ops_users.action.creating': '创建中…',
  'ops_users.action.edit': '编辑',
  'ops_users.action.saving': '保存中…',
  'ops_users.action.revoke': '踢下线',
  'ops_users.action.revoking': '踢下线中…',
  'ops_users.status.active': '启用',
  'ops_users.status.disabled': '已禁用',
  'ops_users.status.locked_out': '无法登录',
  'ops_users.action.disable': '禁用',
  'ops_users.action.disabling': '禁用中…',
  'ops_users.action.enable': '重新启用',
  'ops_users.action.enabling': '启用中…',
  'ops_users.action.reset_password': '重置密码',
  'ops_users.action.resetting': '重置中…',
  'ops_users.action.reset_confirm': '确认重置',
  'ops_users.action.set_password': '设置密码',
  'ops_users.action.setting_password': '设置中…',
  'ops_users.action.invite_password': '发送设密邀请',
  'ops_users.action.inviting_password': '发送中…',
  'ops_users.action.cancel': '取消',
  'ops_users.reset.eyebrow': '危险确认',
  'ops_users.reset.confirm_prompt':
    '请输入邮箱 ({email}) 以确认。重置将删除凭证并使所有活跃会话失效。',
  'ops_users.set_password.eyebrow': '管理员代设凭据',
  'ops_users.set_password.prompt':
    '为 {email} 设置登录密码。不能为角色高于自己的用户设密。替换已有密码会让对方下线。',
  'ops_users.set_password.password': '新密码',
  'ops_users.set_password.confirm': '确认密码',
  'ops_users.set_password.error.length': '密码长度必须是 {min}–{max} 个字符。',
  'ops_users.set_password.error.mismatch': '两次输入的密码不一致。',
  'ops_users.invite_password.eyebrow': '邮件邀请',
  'ops_users.invite_password.prompt':
    '向账号邮箱 {email} 发送一次性设密链接。控制台不回显令牌。',
  'ops_users.toast.disabled': '已禁用运维账户。',
  'ops_users.toast.enabled': '已重新启用运维账户。',
  'ops_users.toast.created': '已创建运维账户。',
  'ops_users.toast.updated': '已更新运维账户。',
  'ops_users.toast.revoked': '会话撤销时间已更新。更早签发的运维 Cookie 将被拒绝。',
  'ops_users.toast.reset': '密码已重置。更早签发的运维 Cookie 将被拒绝。',
  'ops_users.toast.set_password': '密码已设置。',
  'ops_users.toast.invite_password': '设密邀请已发到该账号邮箱。',
  'ops_users.set_password_page.brand': '调度台',
  'ops_users.set_password_page.subtitle': '设置你的运维密码',
  'ops_users.set_password_page.password': '新密码',
  'ops_users.set_password_page.confirm': '确认密码',
  'ops_users.set_password_page.submit': '设置密码',
  'ops_users.set_password_page.submitting': '保存中…',
  'ops_users.set_password_page.missing_token': '邀请链接缺少令牌。',
  'ops_users.set_password_page.success': '密码已设置。现在可以登录。',
  'ops_users.set_password_page.to_login': '前往登录',

  // ── 修改密码 ───────────────────────────────────────────────────
  'shell.change_password': '修改密码',
  'change_password.eyebrow': '账号安全',
  'change_password.title': '修改密码',
  'change_password.intro':
    '需要填写原密码。修改成功后本账号的全部登录会话都会失效，需要用新密码重新登录。',
  'change_password.current': '原密码',
  'change_password.next': '新密码',
  'change_password.confirm': '确认新密码',
  'change_password.error.length': '新密码长度必须是 {min}–{max} 个字符。',
  'change_password.error.mismatch': '两次输入的新密码不一致。',
  'change_password.error.same': '新密码不能与原密码相同。',
  'change_password.submit': '修改密码',
  'change_password.submitting': '提交中…',
  'change_password.cancel': '取消',
  'change_password.toast.success': '密码已修改，请用新密码重新登录。',

  // ── DR check ───────────────────────────────────────────────────
  'dr_check.title': '灾备就绪演练',
  'dr_check.subtitle': '对两个数据库运行按需检测。探测 platform-db 读写路径、gateway-db 读路径。',
  'dr_check.runner.title': '演练执行器',
  'dr_check.runner.body': '点击"运行演练"执行三探针序列，结果显示在下方。',
  'dr_check.action.run': '运行演练',
  'dr_check.action.running': '执行中…',
  'dr_check.idle': '暂无演练结果。点击"运行演练"开始。',
  'dr_check.toast.pass': '灾备演练：所有探针通过。',
  'dr_check.toast.fail': '灾备演练：一个或多个探针失败。',
  'dr_check.overall.pass_title': '所有探针通过。',
  'dr_check.overall.fail_title': '一个或多个探针失败。',
  'dr_check.overall.body': '开始于 {started}，耗时 {duration}ms。',
  'dr_check.column.probe': '探针',
  'dr_check.column.outcome': '结果',
  'dr_check.column.latency': '延迟',
  'dr_check.column.detail': '详情',
  'dr_check.probe.pass': '通过',
  'dr_check.probe.fail': '失败',

  // ── Toast ───────────────────────────────────────────────────────
  'toast.region_aria': '通知',
  'toast.dismiss_aria': '关闭通知',
  'toast.action.view_audit': '查看审计日志',

  // ── Namespaces registry (#134 → #143 → Model Market #910) ──────
  // The `channels.*` keys that remain are shared form/brand-binding
  // strings reused by the namespace surfaces + ChannelBrandBindingDrawer.
  'namespaces.title': '命名空间',
  'namespaces.subtitle': '命名空间注册表：身份、品牌与供应商（定价在 SKU 粒度维护）',
  'namespaces.create_btn': '新建命名空间',
  'namespaces.search.placeholder': '搜索命名空间、slug、供应商…',
  'namespaces.empty': '暂无命名空间',
  'namespaces.brand_filter_aria': '品牌筛选',
  'namespaces.brand_filter.all': '全部',
  'namespaces.col.display_name': '命名空间',
  'namespaces.col.vendor': '厂商',
  'namespaces.col.brand_profiles': '品牌',
  'namespaces.col.skus': 'SKU 数',
  'namespaces.col.supplier': '供应商',
  'namespaces.col.official': '官方',
  'namespaces.col.created_at': '创建时间',
  'namespaces.row.delete': '删除命名空间',
  'namespaces.delete_modal.title': '删除命名空间？',
  'namespaces.delete_modal.body': '{name} 将被永久删除，其 SKU 与品牌绑定一并移除。此操作不可撤销。',
  'namespaces.create_modal.title': '新建命名空间',
  'namespaces.create_modal.vendor': '厂商',
  'namespaces.create_modal.select_vendor': '选择厂商…',
  'namespaces.create_modal.pricing_note': '定价在 SKU 粒度维护：新命名空间以 ×1.0 起步，上架模型后到模型市场为每个 SKU 设置明确的倍率与成本。',
  'namespaces.toast.created': '命名空间创建成功',
  'namespaces.toast.deleted': '命名空间已删除',
  'namespaces.toast.duplicate': '命名空间标识已存在',
  'namespaces.toast.alias_duplicate': '品牌公开标识已存在',
  'channels.create_modal.display_name': '显示名称',
  'channels.create_modal.display_name_placeholder': '输入命名空间展示名称',
  'channels.create_modal.description': '描述',
  'channels.create_modal.description_placeholder': '输入展示给 app 端的描述',
  'channels.create_modal.description_hint': '展示在租户端选择和定价信息中；品牌绑定可覆盖。',
  'channels.create_modal.supplier_name': '供应商',
  'channels.create_modal.supplier_placeholder': '供应商名称',
  'channels.create_modal.slug_hint': '唯一标识',
  'channels.create_modal.is_official': '官方命名空间',
  'channels.create_modal.is_official_hint': '启用后将标记为官方认证资源',
  'channels.create_modal.launch_brands': '上线品牌',
  'channels.create_modal.launch_brands_hint': '创建后可在品牌绑定中设置公开 Slug 和别名。',
  'channels.brand_profiles.title': '品牌可见性',
  'channels.brand_profiles.enabled': '可见',
  'channels.brand_profiles.public_slug': '公开 Slug',
  'channels.brand_profiles.display_name': '品牌别名',
  'channels.brand_profiles.description': '描述覆盖',
  'channels.brand_profiles.description_placeholder': '使用渠道全局描述',
  'channels.brand_profiles.description_fallback': '未填写时回退到全局描述',
  'channels.brand_bindings.eyebrow': '品牌绑定',
  'channels.brand_bindings.close': '关闭品牌绑定',
  'channels.brand_bindings.bound_title': '已绑定品牌',
  'channels.brand_bindings.empty_bound': '暂无已绑定品牌',
  'channels.brand_bindings.add_title': '添加品牌',
  'channels.brand_bindings.no_unbound': '没有可添加的品牌',
  'channels.brand_bindings.remove_brand': '解绑 {brand}',
  'channels.brand_bindings.save': '保存绑定',
  'channels.brand_bindings.toast_saved': '品牌绑定已保存',
  'channels.brand_bindings.summary_title': '品牌绑定',
  'channels.brand_bindings.summary_hint': '仅展示已绑定且启用的品牌。',
  'channels.brand_bindings.manage': '管理品牌',
  'channels.create_modal.cancel': '取消',
  'channels.create_modal.submit': '创建',
  'channels.create_modal.creating': '创建中…',

  // ── Namespace detail — SKU listing/delisting (#910) ────────────
  'namespace_detail.not_found': '命名空间不存在',
  'namespace_detail.skus_count': '个 SKU',
  'namespace_detail.edit_info': '编辑信息',
  'namespace_detail.empty': '暂无 SKU，请到「模型市场」新建 SKU',
  'namespace_detail.col.model': '模型',
  'namespace_detail.col.sku_name': 'SKU 名称',
  'namespace_detail.col.listed_at': '上架时间',
  'namespace_detail.row.open_sku': '打开 SKU 详情',
  'namespace_detail.row.delist': '下架 SKU',
  'namespace_detail.edit_modal.title': '编辑命名空间信息',
  'namespace_detail.edit_modal.display_name_required': '显示名称不能为空',
  'namespace_detail.harnesses.label': 'Harness',
  'namespace_detail.harnesses.hint': '客户端用来判断该命名空间能挂在哪些 harness 上。小写字母开头，仅含小写字母、数字和短横线，最多 {max} 个。',
  'namespace_detail.harnesses.placeholder': '例如 claude-code',
  'namespace_detail.harnesses.add': '添加',
  'namespace_detail.harnesses.remove': '移除 harness {harness}',
  'namespace_detail.harnesses.empty': '尚未配置 harness',
  'namespace_detail.harnesses.invalid': 'harness 须为小写字母开头，仅含小写字母、数字和短横线（最长 32 字符）',
  'namespace_detail.harnesses.too_many': '最多只能配置 {max} 个 harness',
  'namespace_detail.toast.model_removed': 'SKU 已下架',
  'namespace_detail.toast.model_remove_blocked': '该 SKU 仍被激活计划或组织权限引用，暂时无法删除。',
  'namespace_detail.toast.info_updated': '命名空间信息已更新',
  'namespace_detail.delist_modal.title': '下架 SKU？',
  'namespace_detail.delist_modal.body': '{sku} 将从市场下架并删除该 SKU 行。此操作不可撤销。',
  'namespace_detail.delist_modal.pool_loading': '正在统计路由池依赖…',
  'namespace_detail.delist_modal.pool_count': '默认路由池中有 {count} 个启用目标将失去挂载点',
  'namespace_detail.delist_modal.pool_none': '默认路由池中没有启用的目标',
  'namespace_detail.delist_modal.overrides_warning': '该 SKU 上的组织级倍率/可见性覆盖（如有）将随下架一并失效。',
  'namespace_detail.rename_notice': '命名空间 slug 已从 {from} 改为 {to}。网关与状态页存储的 sku_name 展示快照不会自动刷新——请运行以下脚本：',
  'namespace_detail.rename_slug': '重命名 slug',
  'namespace_detail.rename_modal.title': '重命名命名空间 slug',
  'namespace_detail.rename_modal.hint': 'slug 是租户 canonical 名（命名空间/模型）里的命名空间部分，全局唯一。裸模型名调用不受影响；旧 canonical 名会立即 404。',
  'namespace_detail.rename_modal.current_label': '当前 slug',
  'namespace_detail.rename_modal.new_slug_label': '新 slug',
  'namespace_detail.rename_modal.slug_invalid': 'slug 须为小写字母开头，仅含小写字母、数字和短横线（最长 63 字符）',
  'namespace_detail.rename_modal.slug_unchanged': '新 slug 与当前相同',
  'namespace_detail.rename_modal.preflight_loading': '正在统计旧 slug 的 canonical 调用量……',
  'namespace_detail.rename_modal.preflight_error_confirm': 'canonical 调用量统计失败——影响面未知。请再次输入新 slug 以强制确认。',
  'namespace_detail.rename_modal.preflight_zero': '近 10 天没有检测到使用 canonical 名（旧 slug）的调用。',
  'namespace_detail.rename_modal.preflight_usage': '近 10 天至少有 {count} 条请求（{keys} 个 key）使用 canonical 名调用——改名后这些调用会立即 404。请再次输入新 slug 以确认。',
  'namespace_detail.rename_modal.type_to_confirm_label': '输入新 slug 确认',
  'namespace_detail.rename_modal.confirm': '重命名',
  'namespace_detail.toast.renamed': 'slug 已从 {from} 改为 {to}，快照已刷新',
  'namespace_detail.toast.renamed_refresh_failed': 'slug 已从 {from} 改为 {to}，但快照刷新失败——请在页面横幅中重试',
  'namespace_detail.rename_refresh_warn': '改名已生效，但网关 sku_name 快照刷新失败：{error}',
  'namespace_detail.rename_refresh_retry': '重试刷新',
  'namespace_detail.rename_refresh_unknown_error': '未知错误',
  'namespace_detail.toast.refresh_retried': '快照已刷新（{count} 行）',

  // ── Model Market — SKU list (#910, #1327 PR-8) ─────────────────
  'sku_market.title': '模型市场',
  'sku_market.subtitle': '全部 SKU（命名空间/模型）：采购成本 → 销售成本 → 销售指导倍率，以及上架就绪状态',
  'sku_market.namespace_filter_aria': '命名空间筛选',
  'sku_market.tabs.all': '全部',
  'sku_market.empty': '暂无 SKU',
  'sku_market.col.sku': 'SKU',
  'sku_market.col.vendor': '厂商',
  'sku_market.col.rate': '销售指导倍率',
  'sku_market.col.sales_cost': '销售成本倍率',
  'sku_market.col.procurement_cost': '采购成本倍率',
  'sku_market.col.sales_cost_brand': '销售成本倍率（{brand}）',
  'sku_market.procurement.unknown': '未知',
  'sku_market.col.readiness': '就绪状态',
  'sku_market.readiness.ready': '已就绪',
  'sku_market.readiness.unknown': '就绪状态未知',
  'sku_market.readiness.no_rate': '未定价',
  'sku_market.readiness.not_bound': '未绑定供给',
  'sku_market.readiness.probe_failing': '探测未通过',
  'sku_market.readiness.no_procurement_cost': '缺采购成本',
  'sku_market.readiness.not_visible': '未对租户可见',
  'sku_market.readiness.truncated': 'SKU 数量超过单批上限，仅显示前 500 个 SKU 的就绪状态。',
  'sku_market.new_sku': '新建 SKU',

  // ── 新密钥默认命名空间组（design 2026-08-02）────────────────────
  'default_key_namespaces.title': '新密钥默认命名空间',
  'default_key_namespaces.subtitle': '新建的租户 API 密钥自动映射哪些命名空间',
  'default_key_namespaces.panel': '默认命名空间组',
  'default_key_namespaces.hint': '勾选的命名空间会成为新密钥的持续自动映射规则：命名空间上新 SKU 时，已发出的密钥会自动跟上。客户看不到自己无权访问的命名空间。留空即恢复旧行为（新密钥不带任何映射）。',
  'default_key_namespaces.sku_count': '{count} 个在售 SKU',
  'default_key_namespaces.save': '保存默认组',
  'default_key_namespaces.forbidden': '需要管理员权限才能配置新密钥的默认命名空间',
  'default_key_namespaces.load_error': '无法加载默认命名空间组',
  'default_key_namespaces.toast.saved': '默认命名空间组已保存',

  // ── New SKU dialog (#1327 PR-9) ─────────────────────────────────
  'new_sku.title': '新建 SKU',
  'new_sku.subtitle': '在平台命名空间下上架一个模型，并给出销售指导倍率。',
  'new_sku.namespace_label': '平台命名空间',
  'new_sku.namespace_hint': '命名空间决定 SKU 名称前缀与可选模型的厂商范围。',
  'new_sku.namespace_placeholder': '选择命名空间',
  'new_sku.model_label': '模型',
  'new_sku.model_hint': '只列出该命名空间厂商下、已上架且尚未在此命名空间售卖的模型。',
  'new_sku.model_hint_pick_namespace': '先选择命名空间。',
  'new_sku.model_placeholder': '选择模型',
  'new_sku.model_none': '该命名空间没有可上架的模型',
  'new_sku.model_loading': '正在加载模型目录…',
  'new_sku.model_unavailable': '模型目录不可用',
  'new_sku.models_unavailable': '无法读取模型目录（{error}）。在目录恢复前不能新建 SKU——此刻的空列表不代表没有可上架的模型。',
  'new_sku.rate_label': '销售指导倍率',
  'new_sku.rate_hint': '租户计费 = 官方牌价 × 该倍率。',
  'new_sku.rate_invalid': '销售指导倍率必须是大于 0 的数字。',
  'new_sku.pricing_gap': '{model} 还没有官方牌价，无法按倍率计费——先在模型详情页补录定价，再回来创建 SKU。',
  'new_sku.pricing_gap_link': '前往模型定价编辑',
  'new_sku.submit': '创建 SKU',
  'new_sku.toast.created': 'SKU {sku} 已创建',

  // ── SKU detail (#910) ───────────────────────────────────────────
  'sku_detail.not_found': 'SKU 不存在或已下架',
  'sku_detail.back': '返回模型市场',
  'sku_detail.namespace': '命名空间',
  'sku_detail.listed_at': '上架于',
  'sku_detail.pricing.title': 'SKU 定价',
  'sku_detail.pricing.rate_label': '倍率',
  'sku_detail.pricing.rate_hint': '租户计费 = 原厂价 × 此倍率',
  'sku_detail.pricing.cost_label': '上游成本（¥/USD）',
  'sku_detail.pricing.cost_hint': '每 1 USD 原厂价的上游采购成本（人民币）',
  'sku_detail.pricing.save': '保存定价',
  'sku_detail.pricing.saving': '保存中…',
  'sku_detail.pricing.invalid_rate': '倍率必须是大于 0 的数字',
  'sku_detail.pricing.invalid_cost': '成本必须是大于 0 的数字',
  'sku_detail.pricing.toast_saved': 'SKU 定价已更新',
  'sku_detail.namespace_rate.label': '跟随产品线级费率协议',
  'sku_detail.namespace_rate.hint':
    '此开关只决定本 SKU 是否继承各组织的产品线级费率协议，不会修改或删除那些协议本身。',
  'sku_detail.namespace_rate.state_follows':
    '本 SKU 跟随各组织已签订的产品线级费率协议。',
  'sku_detail.namespace_rate.state_own':
    '本 SKU 不跟随产品线级费率协议。已有的产品线协议不会被改动或删除。',
  'sku_detail.namespace_rate.toast_follows': '本 SKU 已跟随产品线级费率协议',
  'sku_detail.namespace_rate.toast_own': '本 SKU 已不再跟随产品线级费率协议',
  'sku_detail.rate_limit.title': 'SKU 默认限额',
  'sku_detail.rate_limit.hint': '租户未覆盖时使用这里的 RPM / TPM（含缓存）/ 非缓存 TPM。留空表示这一维不限。请在配额中心编辑。',
  'sku_detail.rate_limit.l1_off': 'L1 未开启，限额不生效。',
  'sku_detail.rate_limit.rpm': 'RPM',
  'sku_detail.rate_limit.tpm': 'TPM（含缓存）',
  'sku_detail.rate_limit.tpm_uncached': '非缓存 TPM',
  'sku_detail.rate_limit.save': '保存限额',
  'sku_detail.rate_limit.clear': '清除',
  'sku_detail.rate_limit.use_clear': '三维（RPM / TPM（含缓存）/ 非缓存 TPM）都空时请用清除，而不是保存。',
  'sku_detail.rate_limit.unlimited': '不限',
  'sku_detail.rate_limit.invalid': '须为正整数，或留空表示不限。',
  'sku_detail.rate_limit.toast_saved': 'SKU 限额已保存',
  'sku_detail.rate_limit.toast_cleared': 'SKU 限额已清除',
  'sku_detail.rate_limit.loading': '加载限额…',
  'sku_detail.load_scope.title': '负载感知（本 SKU）',
  'sku_detail.load_scope.hint': '本 SKU 的覆盖优先于车道与全局。跟随表示不写例外。',
  'sku_detail.load_scope.loading': '加载负载感知设置…',
  'sku_detail.load_scope.restore': '恢复跟随',
  'sku_detail.load_scope.toast_saved': '本 SKU 负载感知已保存',
  'sku_detail.load_scope.toast_cleared': '已清除本 SKU 例外',
  'sku_detail.pricing.reprice_warning': '以下方案的客户价将不低于新牌价',
  'sku_detail.pricing.reprice_plan': '{name} · {brand} · ×{rate} · {orgs} 个客户',
  'sku_detail.pricing.windows.configure': '排期',
  'sku_detail.pricing.windows.none': '无窗口 / 全天基础倍率',
  'sku_detail.pricing.windows.active': '生效中：{label}',
  'sku_detail.pricing.windows.base': '基础 ×{value}',
  'sku_detail.pricing.windows.effective': '此刻 ×{value}',
  'sku_detail.pricing.windows.dialog_title': '{model} 的售价排期',
  'sku_detail.pricing.windows.dialog_description': '保存会整组替换该 SKU 的售价窗口。空列表会清空全部窗口，基础倍率全天生效。',
  'sku_detail.pricing.windows.add': '添加窗口',
  'sku_detail.pricing.windows.remove': '删除窗口',
  'sku_detail.pricing.windows.label': '标签',
  'sku_detail.pricing.windows.timezone': '时区',
  'sku_detail.pricing.windows.days': '星期',
  'sku_detail.pricing.windows.day.mon': '一',
  'sku_detail.pricing.windows.day.tue': '二',
  'sku_detail.pricing.windows.day.wed': '三',
  'sku_detail.pricing.windows.day.thu': '四',
  'sku_detail.pricing.windows.day.fri': '五',
  'sku_detail.pricing.windows.day.sat': '六',
  'sku_detail.pricing.windows.day.sun': '日',
  'sku_detail.pricing.windows.start': '开始',
  'sku_detail.pricing.windows.end': '结束',
  'sku_detail.pricing.windows.cross_midnight': '结束时刻早于开始时刻 = 跨到次日。',
  'sku_detail.pricing.windows.multiplier': '窗口倍率',
  'sku_detail.pricing.windows.enabled': '启用',
  'sku_detail.pricing.windows.save': '保存排期',
  'sku_detail.pricing.windows.cancel': '取消',
  'sku_detail.pricing.windows.empty': '还没有窗口。基础倍率全天生效。',
  'sku_detail.pricing.windows.close': '关闭',
  'sku_detail.pricing.windows.days_required': '至少选择一天',
  'sku_detail.pricing.windows.label_required': '填写名称',
  'sku_detail.pricing.windows.timezone_required': '填写时区',
  'sku_detail.pricing.windows.clock_invalid': '时刻须为 HH:MM（结束可为 24:00）',
  'sku_detail.pricing.windows.start_end_equal': '开始与结束不能是同一时刻',
  'sku_detail.pricing.windows.start_not_end_of_day': '24:00 只能作为结束时刻',
  'sku_detail.pricing.windows.multiplier_invalid': '须为大于 0 且不超过 {max} 的数字',
  'sku_detail.pricing.windows.toast_saved': '售价排期已更新',
  'sku_detail.pool.open_tenant_routing': '打开租户路由',
  'sku_detail.status.title': '24 小时可用性',
  'sku_detail.status.availability': '可用性',
  'sku_detail.status.requests_24h': '24h 请求数',
  'sku_detail.status.updated_at': '更新于',
  'sku_detail.status.state.healthy': '正常',
  'sku_detail.status.state.busy': '繁忙',
  'sku_detail.status.state.unavailable': '不可用',
  'sku_detail.status.sparkline_aria': '5 分钟成功率趋势',
  'sku_detail.readiness.checking': '正在检查就绪状态',
  'sku_detail.readiness.unknown': '就绪状态不可用',
  'sku_detail.readiness.ready': '已上架 · 路由就绪',
  'sku_detail.key_preset_activation': '激活密钥预设映射',
  // ── 供给绑定（#1327 PR-4）───────────────────────────────────────
  'sku_detail.supply.title': '供给',
  'sku_detail.supply.hint': '这一个面板就是本 SKU 的全部供给：已绑定的目标在最上，其余是可绑定的候选能力。采购倍率在上游页按（上游渠道 × 上游模型）维护，本页只继承、不录入；未定价的候选无法绑定。',
  'sku_detail.supply.column.upstream': '上游',
  'sku_detail.supply.column.probe': '探测状态',
  'sku_detail.supply.column.multiplier': '采购倍率',
  'sku_detail.supply.column.action': '状态 / 操作',
  'sku_detail.supply.bind': '绑定',
  'sku_detail.supply.multiplier_missing': '未定价',
  'sku_detail.supply.unpriced_hint': '先在上游页设置采购倍率。',
  'sku_detail.supply.open_upstream': '打开上游页',
  'sku_detail.supply.reason.protocol_mismatch': '该能力行的协议值异常，无法绑定。',
  'sku_detail.supply.reason.egress_incompatible': '出口通道 / 准入 / 模型白名单不接受此绑定。',
  'sku_detail.supply.not_synced': '网关无法将此 SKU 解析为规范的市场身份。全新命名空间最多滞后 30 秒；若持续出现，请检查命名空间 slug 是否仍与 SKU 名称一致。',
  'sku_detail.supply.toast_bound': '供给已绑定到该 SKU',
  'sku_detail.supply.column.upstream_channel': '上游渠道',
  'sku_detail.supply.column.upstream_model': '上游模型',
  'sku_detail.supply.column.weight': '权重',
  'sku_detail.supply.column.enabled': '启用',
  'sku_detail.supply.column.scheduling': '调度',
  'sku_detail.supply.probe_unpinned': '—',
  'sku_detail.supply.multiplier_unknown': '未知',
  'sku_detail.supply.group.bound': '已绑定（{count}）',
  'sku_detail.supply.group.bound_empty': '该 SKU 还没有绑定任何供给；从下面的候选里选一条绑定。',
  'sku_detail.supply.group.recommended': '推荐（{count}）',
  'sku_detail.supply.group.recommended_empty': '没有与本 SKU 模型同名、且尚未绑定的候选。',
  'sku_detail.supply.group.nothing_at_all': '该 SKU 既没有已绑定的供给，也没有可绑定的候选能力。先在上游页配置并探测线路，或用右上角「添加目标」手动指定。',
  'sku_detail.supply.group.cross_model': '跨模型候选（{count}）',
  'sku_detail.supply.save': '保存',
  'sku_detail.supply.unsaved': '未保存',
  'sku_detail.supply.no_capability_pin': '未固定能力',
  'sku_detail.supply.issue.legacy_unpinned': '旧目标未固定',
  'sku_detail.supply.issue.not_in_candidates': '能力不在候选中',
  'sku_detail.supply.issue.probe_abnormal': '探测异常',
  'sku_detail.supply.pin': '固定能力',
  'sku_detail.supply.pin_title': '固定能力',
  'sku_detail.supply.pin_hint': '这条旧目标没有钉住具体能力，网关拒绝就地保存它的权重与重试序。四元组已经确定，只需从该四元组下的在线能力里选一条：保存时会写入固定后的绑定，并删除旧的未固定行。',
  'sku_detail.supply.pin_empty': '该四元组下没有在线能力可固定；先去上游页探测这条线路。',
  'sku_detail.supply.pin_confirm': '固定并保存',
  'sku_detail.supply.pin_select_placeholder': '请选择一条能力',
  'sku_detail.supply.pin_select_hint': '选中一条后才能保存；列表按「上游模型 · 能力 ID · 协议 · 探测状态」排列。',
  'sku_detail.supply.toast_pinned': '已固定能力',
  'sku_detail.supply.pin_before_tuning': '固定能力后才能调整',
  'sku_detail.supply.pin_load_failed': '读取该上游的能力列表失败，无法判断这条线有没有可固定的能力。',
  'sku_detail.supply.targets_load_failed': '读取该 SKU 的路由目标失败，下面看不到任何已绑定行；在恢复前已禁止绑定新供给，「新增目标」也不可用。刷新后重试。',
  'sku_detail.supply.bind_blocked_targets_unknown': '路由目标读取失败，无法确认这条四元组上有没有未固定的旧目标；直接绑定可能会改掉已有的绑定。请先刷新。',
  'sku_detail.supply.pin_all_taken': '该四元组上的在线能力已被本 SKU 的其它目标全部固定，这条未固定的旧行是多余的——删除它即可，不需要重新探测。',
  'sku_detail.supply.bind_hits_legacy_row': '该四元组上已有一条未固定的旧目标（{channel}）。直接绑定会把那条旧目标改到新能力上，而不是新增一条，所以这里改走「固定能力」。',
  'sku_detail.supply.add_hits_legacy_row': '该四元组上已有一条未固定的旧目标（{channel}），直接保存会改掉那条旧目标而不是新增一条。已改走「固定能力」，新增表单已关闭；你填的权重不会生效——固定时沿用该目标已保存的权重。',
  'sku_detail.supply.write_hits_bound_row': '本 SKU 在这条四元组上已经绑定了该能力（{channel}），继续保存不会新增一行，而是覆盖那一行的权重、重试顺序和启用状态。已取消保存。请直接在上面的已绑定列表里就地修改。',
  'sku_detail.supply.delete_confirm_title': '删除这条供给绑定？',
  'sku_detail.supply.delete_confirm_body': '删除后，该 SKU 的流量不再走这条线路。',
  'sku_detail.supply.delete_confirm': '删除绑定',
  'sku_detail.hint.label': '术语说明',
  'sku_detail.hint.sku': 'SKU：对外售卖的一个「命名空间 + 模型名」，例如 oauth/claude-sonnet-5。它卖什么，和它背后向谁采购是两件事。',
  'sku_detail.hint.namespace': '命名空间：卖方的一条产品线，例如 oauth、openai-pro、xai。SKU 名的斜杠前半段就是它。',
  'sku_detail.hint.upstream': '上游：供应商。一个上游下面挂账号，账号下面挂密钥桶。',
  'sku_detail.hint.upstream_channel': '上游渠道：这把密钥挂在哪条线路上。上游 + 账号 + 密钥桶 + 上游渠道构成四元组，回答「找谁买」。',
  'sku_detail.hint.capability': '能力：一个四元组下实测能出什么上游模型、走什么协议、探测通不通。一个四元组可以挂多条能力；绑定钉住的就是这一层。',
  'sku_detail.hint.probe_status': '探测状态：这条能力最近一次探测的结果。只有 passed / needs_probe / probe_failed 会被网关当作在线线路。',
  'sku_detail.hint.multiplier': '采购倍率：按（上游渠道 × 上游模型）维护的进价倍率，在上游页录入。没有它就不能绑定。',
  'sku_detail.hint.weight': '权重：同一重试档位内按权重分流。0 表示备用——没有加权兄弟时仍会走，有则待命。',
  'sku_detail.hint.retry_order': '重试序：转发失败后的尝试顺序，数字小的先试。留空表示按权重自动排。',
  'sku_detail.hint.scheduling': '调度：选路时怎么用这条供给。权重决定同一重试档位内的分流，0 表示备用；重试顺序决定转发失败后的尝试次序，数字小的先试，留空则按权重自动排。改动要点保存才生效。',
  'sku_detail.hint.policy_mode': '执行策略：这个 SKU 失败后重试到什么程度（整条链 / 只重试网络错误 / 同缓存域 / 直接失败）。它是 SKU 级设置，对所有目标生效。',
  'sku_detail.hint.legacy_unpinned': '旧目标未固定：这条绑定写在能力钉住机制之前，capability_id 为空。网关拒绝就地保存它，需要先「固定能力」。',
  // ── 独占覆盖修复 ────────────────────────────────────────────────
  'sku_detail.exclusive.hint': '独占（exclusive）路由方案不继承默认池，缺少本 SKU 的方案会直接对其客户不可用；此处一键把默认目标复制过去。',
  // ── 密钥预设 ────────────────────────────────────────────────────
  'sku_detail.keys.hint': '把该 SKU 作为托管别名写入指定组织的某一把密钥；作用域固定为单组织 + 单密钥，无全局操作。',
  // ── 就绪清单（#1327 PR-4）───────────────────────────────────────
  'sku_detail.checklist.title': '上架就绪清单',
  'sku_detail.checklist.ready': '就绪',
  'sku_detail.checklist.not_ready': '未就绪',
  'sku_detail.checklist.blocked_count': '{count} 项未就绪',
  'sku_detail.checklist.fix': '去处理',
  'sku_detail.checklist.pricing': '销售定价',
  'sku_detail.checklist.pricing_hint': '该 SKU 有大于 0 的租户倍率。',
  'sku_detail.checklist.binding': '供给绑定',
  'sku_detail.checklist.binding_hint': '默认路由池中至少有一个启用的目标。',
  'sku_detail.checklist.probe': '链路存活',
  'sku_detail.checklist.probe_hint': '已绑定的每个目标都仍能走通在线能力链。',
  'sku_detail.checklist.cost': '采购成本',
  'sku_detail.checklist.cost_hint': '已绑定的每个目标都有明确的采购倍率。',
  'sku_detail.checklist.visibility': '租户可见性',
  'sku_detail.checklist.visibility_hint': '该 SKU 已上公开货架：所有组织都按标价看到它，除非有覆盖把它藏起来。',
  'sku_detail.checklist.visibility_hint_private': '未上架：只有被逐个开通的组织能用到该 SKU。可在下方上公开货架。',
  'sku_detail.shelf.label': '公开货架',
  'sku_detail.shelf.state_listed': '已上架：所有组织按标价可见。单独的组织覆盖仍然优先。',
  'sku_detail.shelf.state_private': '未上架：只有被逐个开通的组织才能看到。',
  'sku_detail.shelf.blocked_pricing': '先定价——该模型没有生效中的标价，货架没有可售的价格。',
  'sku_detail.shelf.blocked_checking': '正在检查路由就绪状态……',
  'sku_detail.shelf.blocked_unknown': '读不到路由就绪状态，货架保持关闭。等它恢复后重试。',
  'sku_detail.shelf.blocked_binding': '先给供给——默认路由池里没有启用的目标。',
  'sku_detail.shelf.blocked_probe': '先修路由——已绑定的目标走不通在线能力链。',
  'sku_detail.shelf.blocked_cost': '先录采购成本——已绑定的目标没有明确的成本记录。',
  'sku_detail.shelf.toast_listed': '该 SKU 已上公开货架',
  'sku_detail.shelf.toast_delisted': '该 SKU 已从公开货架下架',
  'key_preset_activation.title': '激活密钥预设映射',
  'key_preset_activation.description': '仅对一个组织、一把密钥和当前 SKU 做预览、确认与写入。',
  'key_preset_activation.close': '关闭激活面板',
  'key_preset_activation.fixed_scope': '固定 SKU 范围',
  'key_preset_activation.fixed_scope_hint': 'SKU、预设和 bare alias 来自当前详情页，不能切换为全局范围。',
  'key_preset_activation.exact_only': '仅精确范围',
  'key_preset_activation.preset': '命名空间预设',
  'key_preset_activation.namespace': '命名空间',
  'key_preset_activation.preset_id': '内部预设 ID',
  'key_preset_activation.alias': 'Bare alias',
  'key_preset_activation.org_id': '组织 ID',
  'key_preset_activation.key_id': 'API 密钥 ID',
  'key_preset_activation.key_id_hint': '输入密钥记录 ID，不要输入或粘贴明文 API Key。',
  'key_preset_activation.scope_required': '组织 ID 和 API 密钥 ID 都必须填写。',
  'key_preset_activation.preview': '预览精确变更',
  'key_preset_activation.preview_result': '服务端预览',
  'key_preset_activation.preview_key': '已验证密钥：{key}',
  'key_preset_activation.applied_version': '已应用版本',
  'key_preset_activation.current_version': '当前预设版本',
  'key_preset_activation.existing_mapping': '现有映射',
  'key_preset_activation.unresolved_aliases': '尚未解析的预设 alias',
  'key_preset_activation.inferred_tombstone': '这个缺口来自不完整的历史快照，不是租户明确删除。可以通过精确范围应用进行修复。',
  'key_preset_activation.disposition_label.ready': '可新增',
  'key_preset_activation.disposition_label.ready_to_subscribe': '可订阅',
  'key_preset_activation.disposition_label.already_managed': '已受管',
  'key_preset_activation.disposition_label.not_subscribed': '预设不一致',
  'key_preset_activation.disposition_label.not_callable': '不可调用',
  'key_preset_activation.disposition_label.tombstoned': '租户已排除',
  'key_preset_activation.disposition_label.user_override': '租户自定义',
  'key_preset_activation.disposition_label.managed_conflict': '受管冲突',
  'key_preset_activation.disposition.ready': '可以新增受管映射。应用只写入当前 alias，不会改动其他映射。',
  'key_preset_activation.disposition.ready_to_subscribe': '这把未绑定渠道的密钥可以显式订阅当前命名空间预设。应用只创建精确的密钥与预设关系并处理当前 alias，不会批量改动其他密钥。',
  'key_preset_activation.disposition.already_managed': '目标已由该预设管理；应用只记录幂等操作并推进已核对版本。',
  'key_preset_activation.disposition.not_subscribed': '该密钥已经订阅另一个预设，不能应用。',
  'key_preset_activation.disposition.not_callable': '当前密钥不能真实调用这个 SKU，不能应用。请先检查定价、可见性、成员权限与路由。',
  'key_preset_activation.disposition.tombstoned': '租户已明确删除此 alias；排除意图会被保留，不能应用。',
  'key_preset_activation.disposition.user_override': '同名 alias 是租户自定义映射；不会覆盖。',
  'key_preset_activation.disposition.managed_conflict': '同名 alias 由其他受管状态占用；不会覆盖。',
  'key_preset_activation.confirm_scope': '我确认只为密钥 {key} 处理 alias {alias}，并保留所有自定义与排除项。',
  'key_preset_activation.confirm_scope_subscribe': '我确认密钥 {key} 将订阅当前命名空间预设，并且只处理 alias {alias}；不会改动其他密钥或 alias。',
  'key_preset_activation.apply': '应用这一个映射',
  'key_preset_activation.applied': '已应用密钥预设映射',
  'key_preset_activation.version_conflict': '预设版本已变化，本次没有写入。必须重新预览后再确认。',
  'key_preset_activation.repreview_required': '旧预览已失效。重新运行服务端预览，核对新版本和处置结果。',
  'key_preset_activation.receipt': '操作回执',
  'key_preset_activation.idempotent_replay': '服务端识别为同一操作的幂等重放。',
  'key_preset_activation.recorded': '操作已按精确范围记录。',
  'key_preset_activation.cache_convergence': '密钥缓存收敛',
  'key_preset_activation.cache_immediate': '立即生效',
  'key_preset_activation.cache_degraded': '降级收敛',
  'key_preset_activation.cache_effective_after': '最晚生效时间',
  'key_preset_activation.cache_revision': '缓存版本',
  'key_preset_activation.operation_id': '操作 ID',
  'key_preset_activation.result': '结果',
  'key_preset_activation.result_label.applied': '已新增受管映射',
  'key_preset_activation.result_label.already_managed': '已确认现有受管映射',
  'key_preset_activation.result_label.subscribed_applied': '已订阅预设并新增受管映射',
  'key_preset_activation.result_label.subscribed_existing': '已订阅预设并保留现有映射',
  'key_preset_activation.status_label.applied': '已应用',
  'key_preset_activation.status_label.rolled_back': '已回滚',
  'key_preset_activation.provenance_label.user': '租户管理',
  'key_preset_activation.provenance_label.managed_preset': '预设管理',
  'key_preset_activation.preset_version': '预设版本',
  'key_preset_activation.created_at': '应用时间',
  'key_preset_activation.rolled_back_at': '回滚时间',
  'key_preset_activation.rollback_hint': '回滚只撤销本次新增的受管行；若租户后来改写了映射，服务端会拒绝回滚并保留现值。',
  'key_preset_activation.rollback_hint_already_managed': '回滚只关闭本次回执，不会删除原有受管映射。',
  'key_preset_activation.rollback_hint_subscribed_applied': '仅当受管映射和订阅仍由本次操作拥有，且没有后续激活依赖时，回滚才会删除两者。',
  'key_preset_activation.rollback_hint_subscribed_existing': '回滚只删除本次创建的预设订阅，原有租户映射保持不变。',
  'key_preset_activation.rollback': '回滚本次操作',
  'key_preset_activation.rollback_confirm_title': '确认回滚这次操作？',
  'key_preset_activation.rollback_confirm_applied': '只会删除本 operation 创建、且仍未被租户编辑的受管映射。若租户后来改过该 alias，服务端会返回冲突并保留现值。',
  'key_preset_activation.rollback_confirm_already_managed': '这次 operation 只确认了已存在的受管映射。回滚只会撤销 operation 标记，不会删除现有映射。',
  'key_preset_activation.rollback_confirm_subscribed_applied': '仅当受管映射和预设订阅仍由本次操作拥有，且没有后续激活依赖时，才会删除两者；否则服务端保留现状并返回冲突。',
  'key_preset_activation.rollback_confirm_subscribed_existing': '只会删除本次操作创建的预设订阅，原有租户管理映射保持不变；存在后续激活时会返回冲突，不会破坏当前状态。',
  'key_preset_activation.rollback_confirm': '确认回滚本次操作',
  'key_preset_activation.rollback_cancel': '保留操作',
  'key_preset_activation.rolled_back': '已回滚本次操作',
  'key_preset_activation.rollback_replayed': '该操作此前已回滚，当前状态未变',
  'key_preset_activation.non_effects': '此操作不会修改路由分配、SKU target、定价或组织可见性，也不会补齐其他密钥。',

  // ── Model Detail — Namespace Pricing (#134, re-keyed #910) ─────
  'model_detail.channel_pricing.title': '承载命名空间与售价',
  'model_detail.channel_pricing.empty': '该厂商暂无命名空间',
  'model_detail.channel_pricing.col_channel': '命名空间',
  'model_detail.channel_pricing.col_multiplier': '倍率',
  'model_detail.channel_pricing.col_input_price': '输入价 $/M',
  'model_detail.channel_pricing.col_output_price': '输出价 $/M',
  'model_detail.channel_pricing.col_supplier': '供应商',

  // ── Vendors (#143) ────────────────────────────────────────────
  'shell.nav.vendors': '厂商',
  'vendors.title': '厂商管理',
  'vendors.subtitle': '管理模型厂商、官方发现配置与密钥就绪状态',
  'vendors.create_btn': '新建厂商',
  'vendors.search.placeholder': '搜索厂商…',
  'vendors.search.aria_label': '搜索厂商',
  'vendors.loading': '正在加载厂商',
  'vendors.error.title': '无法加载厂商列表',
  'vendors.error.hint': '列表加载失败。请检查服务连接后重试。',
  'vendors.error.retry': '重试',
  'vendors.error.retrying': '正在重试…',
  'vendors.empty.title': '暂无厂商',
  'vendors.empty.hint': '登记第一个模型厂商，即可配置官方发现入口与凭据。',
  'vendors.empty.action': '新建厂商',
  'vendors.search_empty.title': '没有匹配的厂商',
  'vendors.search_empty.hint': '没有厂商匹配“{query}”。清除搜索可查看完整列表。',
  'vendors.search_empty.clear': '清除搜索',
  'vendors.table.scroll_hint': '横向滚动可查看全部厂商操作。',
  'vendors.col.slug': '标识',
  'vendors.col.display_name': '厂商名称',
  'vendors.col.discovery_config': '发现配置',
  'vendors.col.key_readiness': '官方密钥',
  'vendors.col.created_at': '创建时间',
  'vendors.col.actions': '操作',
  'vendors.row.manage': '管理',
  'vendors.row.discover': '发现模型',
  'vendors.row.discovering': '发现中…',
  'vendors.row.delete': '删除厂商',
  'vendors.delete_modal.title': '删除厂商？',
  'vendors.delete_modal.body':
    '仅当 {name} 的渠道、标准模型、SKU 及协议/目录依赖均已清理后，才能永久删除该厂商；届时只会一并删除其发现凭据和发现快照。此操作不可撤销。',
  'vendors.delete_modal.dependencies_error':
    '请先清理该厂商的渠道、标准模型、SKU 及协议/目录依赖，再删除厂商。',
  'vendors.delete_modal.busy_error': '该厂商正在执行模型发现或配置更新，请等待完成后重试删除。',
  'vendors.create_modal.title': '新建厂商',
  'vendors.create_modal.description': '先登记厂商，再按需配置官方模型发现入口。',
  'vendors.create_modal.slug': '厂商标识',
  'vendors.create_modal.slug_placeholder': '例如 anthropic',
  'vendors.create_modal.display_name': '显示名称',
  'vendors.create_modal.display_name_placeholder': '如 Anthropic',
  'vendors.create_modal.discovery_adapter': '发现协议',
  'vendors.create_modal.discovery_adapter_hint': '请谨慎选择：协议创建后不可修改。仅当该厂商无需自动发现时才留空。',
  'vendors.create_modal.model_discovery_url': '模型发现 URL',
  'vendors.create_modal.model_discovery_url_placeholder': '例如 https://api.example.com/v1/models',
  'vendors.create_modal.model_discovery_url_hint': '请输入部署策略明确允许的 HTTPS 来源；留空会保存为未配置。',
  'vendors.create_modal.cancel': '取消',
  'vendors.create_modal.submit': '创建',
  'vendors.create_modal.creating': '创建中…',
  'vendors.create_modal.config_error': '厂商未创建。请检查输入内容后重试。',
  'vendors.manage_modal.title': '管理 {name}',
  'vendors.manage_modal.description': '维护已批准的发现 URL，并轮换仅写入的官方密钥；发现协议不可修改。',
  'vendors.manage_modal.slug': '厂商标识',
  'vendors.manage_modal.slug_hint': '标识创建后不可修改。',
  'vendors.manage_modal.display_name': '显示名称',
  'vendors.manage_modal.discovery_adapter': '发现协议',
  'vendors.manage_modal.discovery_adapter_hint': '协议在厂商创建时固定，避免密钥与模型记录跨协议边界复用。',
  'vendors.manage_modal.model_discovery_url': '模型发现 URL',
  'vendors.manage_modal.model_discovery_url_hint': '请输入部署策略明确允许的 HTTPS 来源。修改或清空 URL 会使当前密钥失效；发现前请重新轮换密钥。',
  'vendors.manage_modal.config_section': '厂商与发现配置',
  'vendors.manage_modal.config_section_hint': '厂商信息和发现入口单独保存，不与密钥轮换合并执行。',
  'vendors.manage_modal.config_submit': '保存配置',
  'vendors.manage_modal.config_saving': '正在保存配置…',
  'vendors.manage_modal.config_error': '配置未保存。请检查输入内容后重试。',
  'vendors.manage_modal.url_change_warning': '保存此 URL 变更会使当前官方密钥失效。配置保存后请重新轮换密钥。',
  'vendors.manage_modal.key_section': '官方凭据',
  'vendors.manage_modal.key_section_hint': '配置保存后，再通过独立操作轮换仅写入凭据。',
  'vendors.manage_modal.key_status': '官方密钥状态',
  'vendors.manage_modal.official_key': '轮换官方 API 密钥',
  'vendors.manage_modal.official_key_hint': '仅写入。仅在确实需要轮换凭据时粘贴新密钥。',
  'vendors.manage_modal.official_key_placeholder': '输入新的官方 API 密钥',
  'vendors.manage_modal.key_submit': '轮换官方密钥',
  'vendors.manage_modal.key_rotating': '正在轮换密钥…',
  'vendors.manage_modal.key_error': '官方密钥未轮换，当前凭据保持不变。请重试。',
  'vendors.manage_modal.unsaved_config': '请先保存或放弃配置变更，再轮换官方密钥。',
  'vendors.manage_modal.manual_section': '手动登记模型',
  'vendors.manage_modal.manual_section_hint': '登记官方发现扫不到的模型（#1882）。登记后进入发现表成为「下架」候选，随后到模型页创建。',
  'vendors.manage_modal.manual_model': '模型 ID',
  'vendors.manage_modal.manual_model_placeholder': '如 doubao-seedance-2-0-260128',
  'vendors.manage_modal.manual_display_name': '显示名称（可选）',
  'vendors.manage_modal.manual_reason': '登记原因',
  'vendors.manage_modal.manual_reason_hint': '与登记一起存档：说明为何官方发现提供不了这个模型。',
  'vendors.manage_modal.manual_registering': '登记中…',
  'vendors.manage_modal.manual_submit': '登记模型',
  'vendors.manage_modal.manual_error': '模型未登记成功，请检查后重试。',
  'vendors.manage_modal.cancel': '关闭',
  'vendors.manage_modal.submit': '保存变更',
  'vendors.manage_modal.saving': '保存中…',
  'vendors.adapter.none': '未配置协议',
  'vendors.adapter.anthropic': 'Anthropic 协议',
  'vendors.adapter.openai': 'OpenAI 协议',
  'vendors.adapter.gemini': 'Gemini 协议',
  'vendors.discovery.url_not_configured': '未配置发现 URL',
  'vendors.discovery.blocker.adapter': '请先配置发现协议',
  'vendors.discovery.blocker.url': '请先配置模型发现 URL',
  'vendors.discovery.blocker.key_loading': '正在检查官方密钥状态',
  'vendors.discovery.blocker.key_error': '无法读取官方密钥状态',
  'vendors.discovery.blocker.key_missing': '请先配置官方 API 密钥',
  'vendors.discovery.blocker.in_progress': '正在执行官方模型发现',
  'vendors.key.status.loading': '正在检查密钥',
  'vendors.key.status.error': '密钥状态不可用',
  'vendors.key.status.missing': '未配置密钥',
  'vendors.key.status.configured': '密钥已配置',
  'vendors.toast.created': '厂商创建成功',
  'vendors.toast.model_registered': '模型 {model} 已登记为发现候选',
  'vendors.toast.deleted': '厂商已删除',
  'vendors.toast.duplicate': '厂商标识已存在',
  'vendors.toast.updated': '厂商发现配置已保存',
  'vendors.toast.updated_and_key_rotated': '厂商发现配置与官方密钥已保存',
  'vendors.toast.key_rotated': '官方 API 密钥已轮换',
  'vendors.toast.discovered': '官方模型发现完成：已发现 {seen}，缺失 {missing}',
  'vendors.validation.required': '此字段为必填项。',
  'vendors.validation.slug': '请输入 1–63 位小写字母、数字或连字符，并以字母开头。',
  'vendors.validation.url': '请输入使用默认端口的公网 HTTPS URL，且不要包含凭据、查询参数或片段。',
  'vendors.validation.url_absolute': '请输入完整的 HTTPS 发现 URL，或清空此字段。',
  'vendors.validation.url_https': '发现 URL 必须使用 HTTPS。',
  'vendors.validation.url_port': '请使用默认 HTTPS 端口，并移除自定义端口。',
  'vendors.validation.url_credentials': '请移除发现 URL 中的用户名和密码。',
  'vendors.validation.url_fragment': '请移除 URL 中 # 之后的片段。',
  'vendors.validation.url_query': '请移除发现 URL 的全部查询参数。官方密钥请单独配置；分页参数由服务器添加。',
  'vendors.validation.url_public': '请使用公网主机名或公网 IP；本地与保留地址不可用。',
  'vendors.validation.url_origin': '当前环境未批准此模型发现来源。请使用已批准来源，或联系运维添加。',
  'vendors.validation.url_length': '发现 URL 最长为 2,048 个字符。',
  'vendors.validation.discovery_pair': '发现协议与 URL 必须同时配置，或同时保持未配置。',
  'vendors.discovery.error.credentials': '厂商拒绝了当前密钥。请轮换官方 API 密钥后重试发现。',
  'vendors.discovery.error.rate_limited': '厂商限制了发现请求频率。请稍候再试。',
  'vendors.discovery.error.key_binding': '官方密钥缺失，或仍绑定旧的发现 URL。请轮换密钥后重试。',
  'vendors.discovery.error.configuration': '发现响应或配置无效。请核对已批准的 URL 与协议后重试。',
  'vendors.discovery.error.unavailable': '模型发现暂时不可用。请检查厂商端点后重试。',

  // ── Upstream Accounts (upstream detail accounts tab) ────────────
  'upstream_accounts.title': '供给工作台',
  'upstream_accounts.subtitle': '在同一个视图管理供应商账户、API 密钥、上游渠道实例和出入栈策略模板。',
  'upstream_accounts.action.channel_catalog': '渠道目录',
  'upstream_accounts.action.template_library': '模板库',
  'upstream_accounts.metric.accounts': '账户',
  'upstream_accounts.metric.enabled_accounts': '{count} 个启用',
  'upstream_accounts.metric.key_buckets': '密钥',
  'upstream_accounts.metric.enabled_keys': '{count} 个启用',
  'upstream_accounts.metric.enabled_keys_short': '启用密钥',
  'upstream_accounts.metric.disabled_keys': '停用密钥',
  'upstream_accounts.metric.disabled_key_detail': '不进入路由',
  'upstream_accounts.metric.workbench': '管理面',
  'upstream_accounts.metric.workbench_value': '合并',
  'upstream_accounts.metric.workbench_detail': '运行时对象保持拆分',
  'upstream_accounts.metric.rpm': 'RPM',
  'upstream_accounts.metric.discount': '折扣',
  'upstream_accounts.add_btn': '新建账户',
  'upstream_accounts.empty': '暂无账户',
  'upstream_accounts.count': '共 {count} 个账户',
  'upstream_accounts.add_modal.title': '新建供应商账户',
  'upstream_accounts.add_modal.label': '账户标签',
  'upstream_accounts.add_modal.label.placeholder': '如: hellsoul86@gmail.com',
  'upstream_accounts.add_modal.rpm': 'RPM 限制',
  'upstream_accounts.add_modal.rate_multiplier': '账户折扣倍率',
  'upstream_accounts.add_modal.submit': '创建',
  'upstream_accounts.add_modal.creating': '创建中…',
  'upstream_accounts.add_modal.cancel': '取消',
  'upstream_accounts.add_modal.error.label_required': '账户标签不能为空',
  'upstream_accounts.toast.created': '账户已创建',
  'upstream_accounts.toast.deleted': '账户已删除',
  'upstream_accounts.section.keys': 'API 密钥与渠道出栈',
  'upstream_accounts.channels.none': '未绑定渠道（匹配所有）',
  'upstream_accounts.keys.none': '该账户下暂无密钥',
  'upstream_accounts.keys.count': '{count} 个密钥',
  'upstream_accounts.keys.add_btn': '添加密钥',
  'upstream_accounts.keys.add_modal.title': '添加 API 密钥',
  'upstream_accounts.keys.add_modal.auth_secret_ref': '密钥值',
  'upstream_accounts.keys.add_modal.auth_secret_ref.placeholder': 'sk-...',
  'upstream_accounts.keys.add_modal.label': '标签（可选）',
  'upstream_accounts.keys.add_modal.rpm': 'RPM 限制',
  'upstream_accounts.keys.add_modal.submit': '创建',
  'upstream_accounts.keys.add_modal.creating': '创建中…',
  'upstream_accounts.keys.add_modal.channels': '上游渠道实例',
  'upstream_accounts.keys.add_modal.channels_hint': '先创建密钥，再到“渠道与出栈”里添加上游本地渠道实例。',
  'upstream_accounts.keys.add_modal.error.secret_required': '密钥值不能为空',
  'upstream_accounts.keys.toast.created': '密钥已创建',
  'upstream_accounts.keys.toast.channels_failed': '渠道绑定失败，请手动设置',
  'upstream_accounts.keys.toast.deleted': '密钥已删除',
  'upstream_accounts.keys.toast.updated': '密钥已更新',
  'upstream_accounts.keys.toast.channels_updated': '渠道绑定已更新',
  'upstream_accounts.keys.show_secret': '显示完整密钥',
  'upstream_accounts.keys.hide_secret': '隐藏完整密钥',
  'upstream_accounts.keys.edit_secret': '修改密钥',
  'upstream_accounts.keys.write_only': '仅可写',
  'upstream_accounts.key_table.key': '密钥',
  'upstream_accounts.key_table.rpm': 'RPM',
  'upstream_accounts.key_table.channels': '渠道与出栈',
  'upstream_accounts.key_table.actions': '操作',
  'upstream_accounts.bindings.loading': '正在加载绑定',
  'upstream_accounts.bindings.override': '覆盖',
  'upstream_accounts.keys.edit_modal.title': '修改 API 密钥',
  'upstream_accounts.keys.edit_modal.enabled': '启用密钥',
  'upstream_accounts.keys.edit_modal.error.no_changes': '没有需要保存的变更',
  'upstream_accounts.keys.edit_modal.error.rpm_invalid': 'RPM 限制必须是正整数',
  'upstream_accounts.keys.edit_channels': '编辑渠道绑定',
  'upstream_accounts.keys.configure_channels_egress': '渠道与出栈',
  'upstream_accounts.egress.config_body': '为这个 key 与渠道设置会保存的策略：入栈准入、准入强度、出栈处理、上游接口面和协议专属高级字段。',
  'upstream_accounts.egress.manage_templates': '管理渠道策略',
  'upstream_accounts.egress.default': '按协议默认直连',
  'upstream_accounts.egress.label': '策略模板',
  'upstream_accounts.egress.effective': '生效出栈配置',
  'upstream_accounts.egress.typed_config': '模板出栈配置',
  'upstream_accounts.egress.typed_config_empty': '使用渠道默认出栈；所选模板没有类型化出栈配置。',
  'upstream_accounts.egress.binding_override_title': '绑定覆写（此处只读）',
  'upstream_accounts.egress.binding_override_body': '该绑定携带类型化出栈配置覆写，运行时将整份替换模板配置。本面板只提供黑名单规则的编辑，覆写中的其余字段只读、并在保存时原样带回。',
  'upstream_accounts.egress.binding_override_clear': '清除该覆写',
  'upstream_accounts.egress.binding_override_clear_hint': '保留已绑定的模板，一次保存完成。',
  'upstream_accounts.egress.binding_override_clear_title': '清除该绑定的覆写',
  'upstream_accounts.egress.binding_override_clear_body': '{channel} 上已存的覆写将不再生效，该绑定回到跟随模板。',
  'upstream_accounts.egress.binding_override_clear_detail': '模板保持绑定，因此只需一次保存，绑定不会经历「无模板」的中间状态。此操作不可撤销——保存后已存文档即消失，若日后还需要，请先从上方面板复制留存。',
  'upstream_accounts.egress.binding_override_clear_confirm': '清除并保存',
  'upstream_accounts.egress.binding_blacklist_scope': '以下规则只作用于当前这一条渠道绑定（{channel}），不影响使用同一模板的其他绑定——这正是要在绑定级而非模板级武装规则的原因。',
  'upstream_accounts.egress.binding_blacklist_snapshot': '勾选后保存的覆写是模板文档的一份快照：写接口只接收完整文档，模板日后被改动不会自动传导到本绑定。一旦两者不再一致，此处会转为只读并给出提示。',
  'upstream_accounts.egress.binding_blacklist_diverged_title': '该覆写与当前模板不一致（此处只读）',
  'upstream_accounts.egress.binding_blacklist_diverged_body': '有两种可能，从界面无法区分：模板在覆写保存之后被改过，或这份覆写除黑名单外还改了别的字段。因此不在此提供编辑——编辑会把那处差异一并覆盖掉。若要换成基于当前模板的覆写，可用下方的「清除该覆写」再重新武装规则，或通过 API 直接改写。当前已武装的规则：',
  'upstream_accounts.egress.binding_blacklist_none_armed': '（无）',
  'upstream_accounts.egress.binding_blacklist_profile_mode': '当前模板的准入模式不支持在绑定级叠加黑名单。叠加会把文档的准入模式改写成 require-cc-black，只有模板本身是 allow-any-supported（或已是 require-cc-black）时，这一改写才只是「多拒一类流量」而不改变别的：require-cc 会丢掉白名单，allow-anthropic 会放宽，direct 会武装模板明确关掉的门，而未配置准入的模板会凭空多出一整份它从未写过的准入文档。',
  'upstream_accounts.egress.binding_blacklist_compose_failed': '无法基于当前模板生成覆写文档；覆写未作改动。',
  'upstream_accounts.egress.binding_blacklist_override_fragment': '该绑定已存的覆写是局部碎片：它在运行时会合并到模板上并正在生效，但写接口只收完整文档。此处的每一次编辑都会整份替换覆写文档，所以不提供黑名单勾选——否则会直接抹掉这份仍在生效的策略。要在此绑定上武装规则，请先通过 API 把它改写成完整覆写文档。',
  'upstream_accounts.egress.binding_firewall_scope': '这里选择的规则集只作用于当前这一条渠道绑定（{channel}）。一个模板下往往挂着多条绑定，某个 SKU 命名空间该拦的流量，换一个命名空间可能就是正常业务——在绑定级武装，才能做到只给这一个命名空间加防火墙，而不是全部一起加。',
  'upstream_accounts.egress.binding_firewall_snapshot': '在此选择规则集后保存的覆写是模板文档的一份快照：写接口只接收完整文档，模板日后被改动不会自动传导到本绑定。但被复制进来的只是规则集的「引用」，不是规则本身——规则集上激活新版本仍会作用到本绑定，除非上方已锁定了具体版本。',
  'upstream_accounts.egress.binding_firewall_diverged_title': '该覆写与当前模板不一致（此处只读）',
  'upstream_accounts.egress.binding_firewall_diverged_body': '有两种可能，从界面无法区分：模板在覆写保存之后被改过，或这份覆写还改了本页不负责编排的字段。因此不在此提供编辑——编辑会把那处差异一并覆盖掉。若要换成基于当前模板的覆写，可用下方的「清除该覆写」再重新选择规则集，或通过 API 直接改写。当前已存的引用：',
  'upstream_accounts.egress.binding_firewall_none': '（未引用任何规则集——防火墙层被跳过）',
  'upstream_accounts.egress.binding_firewall_override_fragment': '该绑定已存的覆写是局部碎片：它在运行时会合并到模板上并正在生效，但写接口只收完整文档。此处的每一次编辑都会整份替换覆写文档，所以不提供规则集选择器——否则会直接抹掉这份仍在生效的策略。要在此绑定上武装防火墙，请先通过 API 把它改写成完整覆写文档。',
  'upstream_accounts.egress.binding_firewall_compose_failed': '无法基于当前模板生成覆写文档；防火墙引用未作改动。',
  'upstream_accounts.egress.binding_override_save_warning': '保存将清除 {channels} 上的绑定覆写。',
  'upstream_accounts.egress.binding_override_unsupported': '{channels} 上已存的绑定覆写是局部碎片。它在运行时生效，但写接口只收完整文档，因此保存会清除它。',
  'upstream_accounts.egress.binding_override_unsupported_title': '本次保存将清除一个运行中的绑定覆写',
  'upstream_accounts.egress.binding_override_unsupported_detail': '已存的值在运行时会合并到画像文档上生效，但无法通过本界面写回，因此对该 key bucket 的任何一次保存都会移除它。若要保留，请先通过 API 将其改写为完整覆写文档。',
  'upstream_accounts.egress.binding_override_unsupported_confirm': '清除并保存',
  'upstream_accounts.egress.unassigned': '按协议默认直连',
  'upstream_accounts.egress.sheet_title': '渠道与出栈 · {key}',
  'upstream_accounts.egress.bound_channels': '已绑定渠道',
  'upstream_accounts.egress.no_bound_channels': '暂无已绑定渠道，请先添加一个渠道实例。',
  'upstream_accounts.egress.selected_channel': '当前渠道',
  'upstream_accounts.egress.add_channel': '添加渠道',
  'upstream_accounts.egress.remove_channel': '解除绑定',
  'upstream_accounts.egress.unsaved_changes': '有未保存更改',
  'upstream_accounts.egress.add_modal_title': '添加渠道绑定',
  'upstream_accounts.egress.add_modal_channel_id': '上游渠道 ID',
  'upstream_accounts.egress.add_modal_channel_hint': '从该上游共享渠道目录选择；目录行在“渠道”标签页维护。',
  'upstream_accounts.egress.no_available_channels': '没有可绑定的上游渠道',
  'upstream_accounts.egress.add_modal_profile': '初始出入栈配置模板',
  'upstream_accounts.egress.add_modal_submit': '确认添加',
  'upstream_accounts.egress.cascade.title': '此保存会级联删除路由对象',
  'upstream_accounts.egress.cascade.body': '解绑以下渠道会删除仍在引用它们的路由对象，可能中断正在承载的流量。请确认后再执行。',
  'upstream_accounts.egress.cascade.confirm': '确认删除并保存',
  'upstream_accounts.egress.cascade.sku_targets': '将删除的 SKU 路由目标（{count}）',
  'upstream_accounts.egress.cascade.sku_target_item': '{scope} · {sku} · {model} · 能力 {capability}',
  'upstream_accounts.egress.cascade.capabilities': '将标记为 mismatch 的能力（{count}）',
  'upstream_accounts.egress.cascade.capability_item': '渠道 {channel} 模型 {model} 的能力',
  'upstream_accounts.chip.enabled': '启用',
  'upstream_accounts.chip.disabled': '禁用',
  'upstream_accounts.chip.status_label': '状态',
  'upstream_accounts.rate_multiplier_short': '折扣倍率',

  'upstream_detail.channels.subtitle':
    '该上游共享的供给侧渠道。账户和 API Key 只接入这些渠道；平台渠道仍是独立的租户销售侧概念。',
  'upstream_detail.channels.create': '添加渠道',
  'upstream_detail.channels.empty': '暂无上游渠道。先在这里创建，再把账户密钥接入渠道。',
  'upstream_detail.channels.col.channel': '渠道',
  'upstream_detail.channels.col.label': '标识',
  'upstream_detail.channels.col.egress_binding': '出栈挂载',
  'upstream_detail.channels.col.endpoint': '接入点',
  'upstream_detail.channels.col.status': '状态',
  'upstream_detail.channels.col.actions': '操作',
  'upstream_detail.channels.label': '渠道标识',
  'upstream_detail.channels.enabled': '启用',
  'upstream_detail.channels.disabled': '停用',
  'upstream_detail.channels.binding.unmounted': '未挂载',
  'upstream_detail.channels.binding.unresolved': '已绑定，配置不可读',
  'upstream_detail.channels.binding.overridden': '含覆盖',
  'upstream_detail.channels.enable': '启用',
  'upstream_detail.channels.disable': '停用',
  'upstream_detail.channels.delete': '删除渠道',
  'upstream_detail.channels.modal.title': '添加上游渠道',
  'upstream_detail.channels.modal.channel_id': '上游渠道 ID',
  'upstream_detail.channels.modal.channel_slug': '上游渠道标识',
  'upstream_detail.channels.modal.hint': '这是该上游下的供给侧渠道，不是平台销售渠道。',
  'upstream_detail.channels.endpoint.label': '渠道接入点',
  'upstream_detail.channels.endpoint.inherited': '继承上游',
  'upstream_detail.channels.endpoint.override': '改写接入点',
  'upstream_detail.channels.endpoint.reset': '恢复继承',
  'upstream_detail.channels.endpoint.hint': '只能改 path，主机必须和上游一致。模型发现仍走上游接入点。',
  'upstream_detail.channels.modal.endpoint': '渠道接入点（选填）',
  'upstream_detail.channels.modal.submit': '创建渠道',
  'upstream_detail.channels.toast.created': '上游渠道已创建',
  'upstream_detail.channels.toast.updated': '上游渠道已更新',
  'upstream_detail.channels.toast.deleted': '上游渠道已删除',

  // ── Egress Profiles ───────────────────────────────────────────
  'egress_profiles.title': '渠道策略模板',
  'egress_profiles.subtitle': '全局模板定义可复用的协议族、入栈准入、准入强度、出栈处理和上游接口面。',
  'egress_profiles.create_btn': '新建模板',
  'egress_profiles.empty': '暂无渠道策略模板。',
  'egress_profiles.col.name': '模板',
  'egress_profiles.col.type': '通道',
  'egress_profiles.col.usage': '使用中',
  'egress_profiles.usage.none': '未使用',
  'egress_profiles.usage.kcb_count': '{count} 个渠道绑定',
  'egress_profiles.usage.sku_targets': 'SKU 目标 {count}',
  'egress_profiles.strategy.legacy': 'legacy',
  'egress_profiles.strategy.legacy_hint': '该模板还没有类型化出栈配置（迁移前残留）；打开编辑器补齐。',
  'egress_profiles.sidecar.editing_disabled': 'Capabilities 不可用：在 sidecar 发布恢复可达之前，模板创建与配置编辑均已禁用。',
  'egress_editor.section.pipeline': '出栈处理',
  'egress_editor.section.pipeline_hint': '决定请求走哪条通道、以何种方式送往上游。',
  'egress_editor.section.fixups': '修正项 (fixups)',
  'egress_editor.lane': '通道 (lane)',
  'egress_editor.lane_placeholder': '选择通道',
  'egress_editor.mode': '处理模式 (mode)',
  'egress_editor.mode_placeholder': '选择处理模式',
  'egress_editor.mode_hint.passthrough': 'passthrough：原字节转发，不做协议改写。',
  'egress_editor.mode_hint.fixup': 'fixup：仅对选中的字段做定点修正，其余原样转发。',
  'egress_editor.mode_hint.assemble': 'assemble：按选定的目标形态重新组装请求。',
  'egress_editor.matching': '匹配策略 (matching)',
  'egress_editor.matching_option_default': 'best-effort（默认）',
  'egress_editor.matching_hint.fixup': '仅影响 sidecar 不可用时的降级：best-effort 且显式选择 direct 准入时原字节直转；其余任何准入状态（包括不启用）与 strict 一样拒绝服务。',
  'egress_editor.matching_hint.assemble': 'strict 在渲染前校验请求形态、违规即拒；best-effort 折叠到目标形态并修复可修偏差。assemble 在 sidecar 不可用时总是拒绝服务，不降级。',
  'egress_editor.kind': '出栈类型 (kind)',
  'egress_editor.kind_hint': '由通道决定，不可单独配置。',
  'egress_editor.target': '重组目标 (target)',
  'egress_editor.target_hint': '来自 /v1/capabilities 发布的通道 catalog 的 shape-class id，或 "latest"。',
  'egress_editor.target_placeholder': '选择目标',
  'egress_editor.target_catalog_empty': '通道 catalog 为空；在 sidecar 发布 shape class 之前仅可选择 "latest"。',
  'egress_editor.fixup_fingerprint': '重算 fingerprint',
  'egress_editor.fixup_cch': '修正 cch',
  'egress_editor.option_unset': '未设置',
  'egress_editor.option_yes': '是',
  'egress_editor.option_no': '否',
  'egress_editor.admission_enabled': '② 启用身份准入 (admission)',
  'egress_editor.admission_hint.claude_code': '不启用时并非全放行：运行时按 allow-any-supported 处理，未识别形态的客户端仍会被拒。要真正全放行需启用并选择 direct。',
  'egress_editor.admission_hint.codex': 'Codex 通道的准入是二元的：只要不是 direct（包括不启用本区块），网关都会 fail closed，仅放行 Codex CLI 形态的流量。',
  'egress_editor.admission_cc_pin_warning': '选择 direct 时，该模板与已固定能力的 Claude Code SKU 目标不兼容（写入时会被拒绝）。不启用准入是兼容的——按 allow-any-supported 处理。',
  'egress_editor.admission_mode': '准入模式',
  'egress_editor.admission_mode_hint.require_cc': '仅放行识别为 Claude Code 的客户端。',
  'egress_editor.admission_mode_hint.require_cc_black': '与 allow-any-supported 完全一致，额外叠加一份黑名单：未勾选任何规则时行为不变，勾选后仅按规则拒绝被点名的那一类流量。',
  'egress_editor.admission_mode_hint.allow_anthropic': '放行 Anthropic 系客户端（含 plain Anthropic）。',
  'egress_editor.admission_mode_hint.allow_any_supported': '放行任何受支持形态的客户端。',
  'egress_editor.admission_mode_hint.direct': '不做客户端身份识别，直接放行。选择后最低等级 / matched-partial / plain Anthropic 设置不会下发，相关控件已隐藏。',
  'egress_editor.admission_mode_hint.codex_armed': '在 Codex 通道上，非 direct 的各模式行为完全相同：门保持武装（gate stays armed），仅放行 Codex CLI 形态流量，其余 fail closed。',
  'egress_editor.admission_mode_hint.codex_direct': '解除 Codex 门（Disarms the codex gate）：不做形态识别，任何流量直接放行。',
  'egress_editor.require_cc_default_note': 'require-cc 下未设置的维度各自套用默认值：最低等级默认 standard；matched-partial 默认要求指纹 / manifest / 无冲突全部匹配，surface 限 main-loop 与 title-gen，入口类别限 first-party-interactive。显式设置某一维度只覆盖该维度。',
  'egress_editor.blacklist': '黑名单规则 (blacklist)',
  'egress_editor.blacklist_inert_note': '未勾选任何规则：该模式与 allow-any-supported 行为完全一致，不会拒绝任何流量。',
  'egress_editor.blacklist_profile_scope_warning': '注意作用范围：在模板上勾选，会对挂在该模板下的每一条 key-渠道绑定生效。多个 SKU 命名空间常常共用同一个模板，其中可能就有「本来就该走裸 API 且完全正常」的命名空间——对这类流量应当到对应绑定上单独武装（上游账号 → 渠道与出栈），而不是在这里一刀切。',
  'egress_editor.blacklist_action': '命中后的处理 (action)',
  'egress_editor.blacklist_action_hint': 'synthesize-200：本地返回一条完整的非流式 Anthropic Message，不计费；model-unavailable：404 MODEL_NOT_AVAILABLE；reject：403 REQUEST_NOT_SUPPORTED。',
  'egress_editor.blacklist_rule.one_token_measurement':
    'Claude Code 单 token 上下文测量 (oneTokenMeasurement)',
  'egress_editor.blacklist_rule.first_party_health_probe': '第一方连接自检探针 (firstPartyHealthProbe)',
  'egress_editor.blacklist_rule.bare_api_on_cc_lane': '裸 Anthropic API 流量 (bareApiOnCcLane)',
  'egress_editor.blacklist_rule.agent_sdk_on_cc_lane': 'Agent SDK 流量 (agentSdkOnCcLane)',
  'egress_editor.blacklist_rule.missing_cc_beta_header': '缺少 anthropic-beta 头 (missingCcBetaHeader)',
  'egress_editor.mpi_empty_warning': '已启用但未设置任何条件：保存时不会写入 matched-partial 策略，重新打开后此开关会回到未启用。',
  'egress_editor.admission_minimum_level': '最低身份等级',
  'egress_editor.admission_minimum_level_hint': '低于该等级的客户端将被拒绝；未设置表示不设门槛。',
  'egress_editor.model_allowlist': '模型白名单',
  'egress_editor.model_allowlist_hint': '每行一个模型（或逗号分隔）；留空表示不启用白名单。',
  'egress_editor.model_allowlist_hint.direct': '每行一个模型（或逗号分隔）。direct 模式下运行时不校验此清单，但它仍决定与固定能力 SKU 目标的兼容性。',
  'egress_editor.mpi_enabled': '启用 matched-partial 完整性策略',
  'egress_editor.mpi_hint': '对判级为 matched-partial 的客户端追加的完整性校验条件。',
  'egress_editor.mpi_target_level': '目标等级 (targetLevel)',
  'egress_editor.mpi_require_fingerprint_match': '要求指纹匹配 (requireFingerprintMatch)',
  'egress_editor.mpi_require_manifest_match': '要求 manifest 匹配 (requireManifestMatch)',
  'egress_editor.mpi_require_no_conflicts': '要求无冲突 (requireNoConflicts)',
  'egress_editor.mpi_allowed_surface_buckets': '允许的 surface 类别 (allowedSurfaceBuckets)',
  'egress_editor.mpi_allowed_entrypoint_classes': '允许的入口类别 (allowedEntrypointClasses)',
  'egress_editor.mpi_allowed_entrypoints': '允许的入口 (allowedEntrypoints)',
  'egress_editor.list_hint': '每行一条（或逗号分隔）。',
  'egress_editor.codex_adapter': 'Codex 适配器 (adapter.codex)',
  'egress_editor.codex_adapter_hint': 'Codex 通道的出栈适配参数。',
  'egress_editor.codex_backend': '后端 (backend)',
  'egress_editor.codex_transport': '传输 (transport)',
  'egress_editor.codex_store': '服务端存储 (store)',
  'egress_editor.codex_drop_max_output_tokens': '丢弃 max_output_tokens',
  'egress_editor.codex_drop_unresolvable_reasoning_items': '丢弃无 encrypted_content 的 reasoning 项（store:false）',
  'egress_editor.codex_originator_override': '覆写 originator',
  'egress_editor.codex_base_url': 'Base URL',
  'egress_editor.codex_beta_features': 'Beta 特性',
  'egress_editor.openai_adapter': 'OpenAI 适配器 (adapter.openai)',
  'egress_editor.openai_adapter_hint': 'OpenAI 通道的出栈适配器参数。此处没有控件的字段随文档存储，编辑时原样保留。',
  'egress_editor.openai_images': '图片生成出口 (images)',
  'egress_editor.openai_images_hint': '图片请求以哪种方式发往此上游。经 Responses 改写：图片由改写后的提示词生成，质量由上游决定，尺寸参数不生效。直连官方 Images 接口：尺寸与质量原样送达上游。两者是不同的交付形态，不是同一张图的两条路。仅当实测确认上游会遵守这些参数时才选 native。',
  'egress_editor.openai_images_option_unset': '未设置（默认：经 Responses 改写）',
  'egress_editor.openai_images_option.via_responses': 'via-responses（经 Responses 改写生成）',
  'egress_editor.openai_images_option.native': 'native（直连官方 Images 接口）',
  'egress_editor.openai_prompt_cache_key': '注入 prompt_cache_key（会话 id）以保持上游缓存亲和',
  'egress_editor.ark_adapter': '任务适配器 (adapter.arkVideo)',
  'egress_editor.ark_adapter_hint': '这个上游如何承接异步生成任务：拨号使用哪套实测任务语法，以及交付量在计费时如何校准。',
  'egress_editor.ark_vendor': '上游适配器 (vendor)',
  'egress_editor.ark_vendor_hint': '适用于此上游的实测任务语法与响应特性适配器，每个选项都对应一次真实测量。',
  'egress_editor.ark_coefficient': '交付量系数 (deliveryQuantityCoefficient)',
  'egress_editor.ark_coefficient_hint': '结算金额按「终态报告 × 该系数」计。留空即 1.0（直通）。必须为正数。',
  'egress_editor.ark_spellings': '参数拼写映射 (parameterSpellings)',
  'egress_editor.ark_spellings_hint': '规范值 → 此上游的线上拼写。随文档存储并在编辑中保留；暂无表单控件。',
  'egress_editor.ark_spellings_empty': '参数拼写映射：直通（规范值原样发送）。',
  'egress_editor.grok_adapter': 'Grok 视频适配器 (adapter.grokVideo)',
  'egress_editor.grok_adapter_hint': '这个上游如何承接 Grok 视频：拨号使用哪套实测号池语法，以及交付量在计费时如何校准。',
  'egress_editor.grok_vendor': '上游适配器 (vendor)',
  'egress_editor.grok_vendor_hint': '适用于此上游的实测号池 /xai/v1/video 语法适配器，每个选项都对应一次真实测量。',
  'egress_editor.grok_coefficient': '交付量系数 (deliveryQuantityCoefficient)',
  'egress_editor.grok_coefficient_hint': '结算金额按「终态报告 × 该系数」计。留空即 1.0（直通）。必须为正数。',
  'egress_editor.grok_image_adapter': 'Grok 静图适配器 (adapter.grokImage)',
  'egress_editor.grok_image_adapter_hint': '这个上游如何承接 Grok 静图：拨号使用哪套实测号池语法。静图是同步 POST，不是视频任务。',
  'egress_editor.grok_image_vendor': '上游适配器 (vendor)',
  'egress_editor.grok_image_vendor_hint': '适用于此上游的实测号池 /xai/v1/images/generations 语法适配器，每个选项都对应一次真实测量。',
  'egress_editor.degraded.title': 'Capabilities 不可用——编辑已禁用',
  'egress_editor.degraded.body': '协议 sidecar 的 capabilities 发布不可达，配置写入路径已关闭。当前文档以只读方式展示；不会代入任何默认值。',
  'egress_editor.summary.unconfigured': '旧版 / 未配置',
  'egress_editor.error.target_required': 'Assemble 模式需要 target（catalog shape-class id 或 "latest"）。',
  // Admission firewall (#1758/#1760). The inbound area is two serial layers,
  // and the copy's whole job is that their "off" states do not mean the same
  // thing — see `EgressFirewallFields`.
  'egress_editor.section.inbound': '入站两层',
  'egress_editor.section.inbound_hint': '请求进入本通道后先过 ① 防火墙规则集，再过 ② 身份准入。两层串行，不是二选一。',
  'egress_editor.firewall': '① 防火墙规则集（前置层）',
  'egress_editor.firewall_hint': '规则在线可改：编辑产生新的 draft 版本，激活即刻生效，不需要发版、不需要重启网关。',
  'egress_editor.firewall_ruleset': '引用的规则集',
  'egress_editor.firewall_ruleset_none': '不引用（跳过本层）',
  'egress_editor.firewall_list_unavailable': '规则集列表读取失败，已保留当前引用不动。',
  'egress_editor.firewall_version': '版本',
  'egress_editor.firewall_version_hint': '跟随激活版本：规则集一激活新版本，本配置立刻跟着变。钉死某个版本：规则集怎么激活都与本配置无关。',
  'egress_editor.firewall_version_follow': '跟随激活版本（当前 v{version}）',
  'egress_editor.firewall_version_follow_unarmed': '跟随激活版本（该规则集当前未激活任何版本）',
  'egress_editor.firewall_version_pinned': '钉死 v{version}',
  'egress_editor.firewall_skip_note': '不引用规则集 = 这一层真的被跳过，请求直接进入 ②。这与 ② 不勾选的含义不同。',
  'egress_editor.firewall_profile_scope_warning': '这是 profile 级引用：会对所有使用本 profile 的绑定同时生效。适合一个命名空间的规则集，可能会拒掉另一个命名空间的正常流量。',
  'egress_editor.firewall_manage_link': '管理规则集与版本 →',
  'egress_editor.layers.title': '两层的关系',
  'egress_editor.layers.order': '顺序：先 ① 防火墙，再 ② 身份准入。① 放行的请求仍然要过 ②。',
  'egress_editor.layers.allow_not_exempt': '① 的 allow 不能免除 ②：它只表示防火墙这一层不拦，② 该拒还是拒。',
  'egress_editor.layers.only_stricter': '① 只能拦得更多：它不会放宽 ② 的任何判断，也打不开 ② 关着的门。',
  'egress_editor.layers.off_semantics': '两个「关掉」不是一个意思：① 不引用规则集 = 真的跳过；② 不勾选 = 按 allow-any-supported 跑，未识别形态的客户端仍会被拒。',
  'firewall_editor.order_hint': '顺序即优先级：自上而下匹配，命中第一条即停止，后面的规则不再评估。',
  'firewall_editor.add_rule': '添加规则',
  'firewall_editor.empty': '还没有规则。至少要有一条才能保存。',
  'firewall_editor.position': '第 {position} 条',
  'firewall_editor.position_hint': '上下移动只改本地草稿；保存后是一个新版本，不会动线上正在跑的版本。',
  'firewall_editor.move_up': '上移一位',
  'firewall_editor.move_down': '下移一位',
  'firewall_editor.remove_rule': '删除这条规则',
  'firewall_editor.rule_id': '规则 ID',
  'firewall_editor.rule_action': '动作',
  'firewall_editor.rule_action_hint': 'allow 放行并停止评估；deny 拒绝；synthesize 由网关直接合成响应，不打上游。',
  'firewall_editor.rule_description': '说明',
  'firewall_editor.rule_description_hint': '必填。它不参与规则哈希，改说明不会产生新哈希；但几个月后有人翻拦截记录时，它是唯一能说明这条规则为什么存在的东西。',
  'firewall_editor.rule_deny_as': '拒绝形态',
  'firewall_editor.rule_deny_as_hint': 'reject 直接拒绝；model-unavailable 以模型不可用的形态回绝。',
  'firewall_editor.rule_synthesize_as': '合成形态',
  'firewall_editor.rule_synthesize_as_hint': 'synthesize-200 由网关合成一个 200 响应返回。',
  'firewall_editor.rule_when': '匹配条件 (when)',
  'firewall_editor.rule_when_hint': '表达式 DSL 的 JSON 文档。保存时才解析，输入过程中不会打断你。',
  'firewall_rulesets.title': '防火墙规则集',
  'firewall_rulesets.subtitle': '准入防火墙的规则集、版本与激活。',
  'firewall_rulesets.create_btn': '新建规则集',
  'firewall_rulesets.empty': '还没有规则集。',
  'firewall_rulesets.explain.title': '这个页面在管什么',
  'firewall_rulesets.explain.online': '这是一层在线可改的前置防护：写好版本、点激活就生效，不需要发版、不需要改代码、不需要重启网关。',
  'firewall_rulesets.explain.frozen': '每个版本写下即冻结：编辑产生的是新的 draft 版本，只有 active_version 指向的那一个会被线上请求读到。回滚就是激活一个旧版本。',
  'firewall_rulesets.explain.order': '规则顺序即优先级：自上而下匹配，命中第一条即停止。调整顺序同样只能通过提交新版本完成。',
  'firewall_rulesets.col.name': '规则集',
  'firewall_rulesets.col.scope': '作用域',
  'firewall_rulesets.col.active': '线上生效',
  'firewall_rulesets.col.latest': '最新版本',
  'firewall_rulesets.col.updated': '更新时间',
  'firewall_rulesets.col.actions': '操作',
  'firewall_rulesets.scope.shared': '共享',
  'firewall_rulesets.scope.binding': '绑定专属',
  'firewall_rulesets.scope_hint.shared': '共享：任何 profile 或绑定都可以引用，改动会影响所有引用方。',
  'firewall_rulesets.scope_hint.binding': '绑定专属：归属某一个 key 桶 × 渠道，影响面限于该绑定。',
  'firewall_rulesets.status.unarmed': '未激活任何版本',
  'firewall_rulesets.status.active': '生效中 v{version}',
  'firewall_rulesets.status.draft_pending': '有未激活的 draft',
  'firewall_rulesets.action.delete': '删除规则集',
  'firewall_rulesets.field.name': '名称',
  'firewall_rulesets.field.scope': '作用域',
  'firewall_rulesets.field.owner_key_bucket': '归属 key 桶 ID',
  'firewall_rulesets.field.owner_channel': '归属渠道 ID',
  'firewall_rulesets.field.owner_hint': '绑定专属规则集必填，用来限定它的影响面。',
  'firewall_rulesets.field.extends': '继承自（规则集 ID）',
  'firewall_rulesets.field.extends_hint': '可选。父规则集的规则会拼在本规则集之前参与匹配。',
  'firewall_rulesets.field.note': '备注',
  'firewall_rulesets.field.note_hint': '写给下一个人看：这个版本改了什么、为什么改。',
  'firewall_rulesets.create.title': '新建规则集',
  'firewall_rulesets.create.hint': '创建出来的是第一个 draft 版本，在你到详情页激活它之前，不会影响任何线上请求。',
  'firewall_rulesets.create.submit': '创建为 draft',
  'firewall_rulesets.issues.title': '这次写入被拒绝，共 {count} 个问题：',
  'firewall_rulesets.version.col.version': '版本',
  'firewall_rulesets.version.col.state': '状态',
  'firewall_rulesets.version.col.note': '备注',
  'firewall_rulesets.version.col.hash': '解析后哈希',
  'firewall_rulesets.version.col.author': '提交人 / 时间',
  'firewall_rulesets.version.col.actions': '操作',
  'firewall_rulesets.version.empty': '还没有版本。',
  'firewall_rulesets.version.author_unknown': '未记录',
  'firewall_rulesets.version.state.active': '线上生效',
  'firewall_rulesets.version.state.draft': 'draft',
  'firewall_rulesets.version.action.inspect': '查看这个版本',
  'firewall_rulesets.version.action.rearm': '重新激活',
  'firewall_rulesets.version.action.activate': '激活',
  'firewall_rulesets.editor.unreadable': '这个版本的规则文档本编辑器无法完整还原，因此不提供编辑。它仍然是有效的线上策略，请另行处理，不要让编辑器用猜测覆盖它。',
  'firewall_rulesets.editor.draft_note': '正在以 v{version} 为基础编辑。保存会生成一个新的 draft 版本，不会改变当前生效的版本。',
  'firewall_rulesets.editor.save': '保存为新 draft 版本',
  'firewall_rulesets.detail.back': '← 返回规则集列表',
  'firewall_rulesets.detail.disarm': '停用（不激活任何版本）',
  'firewall_rulesets.detail.versions': '版本历史',
  'firewall_rulesets.detail.diff': '与线上版本的差异（v{from} → v{to}）',
  'firewall_rulesets.detail.editor': '编辑规则链',
  'firewall_rulesets.detail.unarmed_hint': '当前未激活任何版本，引用它的配置按「跳过本层」处理。',
  'firewall_rulesets.detail.armed_hint': '引用它且跟随激活版本的配置，正在按这个版本评估请求。',
  'firewall_rulesets.diff.unreadable': '有一侧的规则文档不是可读的规则数组，无法逐条比对。',
  'firewall_rulesets.diff.identical': '与线上版本没有差异。',
  'firewall_rulesets.diff.no_active': '当前没有生效版本，无从比对：激活即是这条规则链第一次上线。',
  'firewall_rulesets.diff.kind.added': '新增',
  'firewall_rulesets.diff.kind.removed': '删除',
  'firewall_rulesets.diff.kind.changed': '修改',
  'firewall_rulesets.diff.kind.moved': '移位',
  'firewall_rulesets.diff.kind.unchanged': '未变',
  'firewall_rulesets.detail.replay': '重放 v{version}',
  'firewall_rulesets.replay.intro': '拿一批真实请求样本，让 v{version} 和当前在膛的版本各判一遍，逐条看差异。激活门禁读的就是这份重放：没有覆盖「将要上线的这条链」的重放，服务端会拒绝激活。',
  'firewall_rulesets.replay.field': '样本语料（JSON）',
  'firewall_rulesets.replay.hint': '一个数组，或 {"samples": [...]}；每条形如 {"headers": {...}, "body": {...}}，最多 {max} 条。脱敏是提交方的事，而且必须替换不能删除：规则问的是「有没有这个头」，删掉它就问不出来了。样本本身不会被存储。',
  'firewall_rulesets.replay.submit': '重放到 v{version}',
  'firewall_rulesets.replay.none': '本次会话还没有重放过。',
  'firewall_rulesets.replay.error.not_json': '这不是合法的 JSON。',
  'firewall_rulesets.replay.error.not_array': '顶层要么是数组，要么是带 samples 数组的对象。',
  'firewall_rulesets.replay.error.empty': '空语料不会被当作证据：服务端直接拒绝，这里也一样。',
  'firewall_rulesets.replay.error.too_many': '一次最多 {max} 条，这里有 {count} 条。',
  'firewall_rulesets.replay.error.not_object': '第 {index} 条不是对象。',
  'firewall_rulesets.replay.error.bad_header': '第 {index} 条的请求头 {header} 不是字符串或字符串数组；服务端会拒绝，而不是悄悄丢掉它。',
  'firewall_rulesets.replay.context_dropped': '这些 context 字段没有被发送：{keys}。服务端只认 client_format / lane / stream / endpoint 四个，其余一律丢弃——否则提交方可以自造一个规则会读的字段，把判定结果引到自己想要的地方。',
  'firewall_rulesets.replay.record.version': '候选 v{version}',
  'firewall_rulesets.replay.record.baseline': '基线 v{version}',
  'firewall_rulesets.replay.record.no_baseline': '没有基线',
  'firewall_rulesets.replay.record.samples': '{count} 条样本',
  'firewall_rulesets.replay.unresolvable': '基线不可解析：在膛版本的规则链现在解不开，这个规则集此刻是放行状态。所以本次没有比对，只报告候选版本自己的判定，zero_difference 记为「未比对」。这是合法状态，不是错误——修好它恰恰要靠激活一个能解析的版本。',
  'firewall_rulesets.replay.not_compared': '本次没有比对：当前没有在膛版本，报告只列出候选版本的判定。',
  'firewall_rulesets.replay.zero_difference': 'zero_difference',
  'firewall_rulesets.replay.zero_yes': '是（无差异）',
  'firewall_rulesets.replay.zero_no': '否（有差异）',
  'firewall_rulesets.replay.zero_unknown': '未比对',
  'firewall_rulesets.replay.zero_note': '这是报告字段，门禁不读它。它是「是」也不代表可以放心激活：语料没覆盖到的请求，这里一个字都没说。',
  'firewall_rulesets.replay.fingerprint': '语料指纹',
  'firewall_rulesets.replay.fingerprint_note': '它只回答「是不是同一批样本」，不回答「是不是同一批语义」；换一批同样能触发这些规则的样本，指纹就不一样。它不是记录的编号，也不能拿来去重。',
  'firewall_rulesets.replay.context_absent': '—',
  'firewall_rulesets.replay.applied_context_note': '每条样本下面那行是判定时 $.derived.* 实际取到的值，不是你在语料里写的那份。两者会不一样：白名单外的字段被丢掉了，没写的字段就是 null——规则读到的是 null，不是你以为在测的那个 lane。对不上就说明这次重放测的不是你想测的东西。',
  'firewall_rulesets.replay.col.sample': '样本',
  'firewall_rulesets.replay.col.diff': '差异',
  'firewall_rulesets.replay.col.candidate': '候选版本判定',
  'firewall_rulesets.replay.col.baseline': '基线判定',
  'firewall_rulesets.replay.diff.unchanged': '未变',
  'firewall_rulesets.replay.diff.pass_to_intercept': '放行→拦截',
  'firewall_rulesets.replay.diff.intercept_to_pass': '拦截→放行',
  'firewall_rulesets.replay.diff.action_changed': '动作变了',
  'firewall_rulesets.replay.diff.rule_changed': '命中规则变了',
  'firewall_rulesets.replay.diff.errored': '判定出错',
  'firewall_rulesets.replay.outcome.passthrough': '未命中',
  'firewall_rulesets.replay.outcome.match': '命中',
  'firewall_rulesets.replay.outcome.error': '出错',
  'firewall_rulesets.replay.verdict.none': '未比对',
  'firewall_rulesets.replay.no_rule': '没有命中任何规则',
  'firewall_rulesets.replay.description_absent': '逐条结果只给规则 id，不给 description：那是服务端有意不返回的——description 是把判定标准用人话又说了一遍的那一栏，可读即可绕。规则 id 拿到版本详情里就能对上。',
  'firewall_rulesets.activate.title': '激活 v{version}？',
  'firewall_rulesets.activate.body': '这会把「{name}」的生效版本切到这个版本。',
  'firewall_rulesets.activate.blast': '立即对所有引用本规则集且跟随激活版本的配置生效，不需要发版。',
  'firewall_rulesets.activate.confirm': '激活',
  'firewall_rulesets.activate.evidence.nothing': '无（当前没有在膛版本）',
  'firewall_rulesets.activate.evidence.rearm': '这是重新激活当前已经在膛的版本，门禁不要求重放。',
  'firewall_rulesets.activate.evidence.disarm': '停用不受门禁约束——它是最快的回滚。',
  'firewall_rulesets.activate.evidence.ready': '本次会话已经重放过 v{version}：记录 {id}，基线 {baseline}。',
  'firewall_rulesets.activate.evidence.stale': '已有的重放比的是基线 {baseline}，但现在在膛的是 {active}。门禁按当前在膛的版本认证据，先重放一次更稳。',
  'firewall_rulesets.activate.evidence.missing': '本次会话没有对这个版本的重放记录。门禁要求激活前存在一份覆盖「将要上线的这条链」的重放，否则会被拒绝。',
  'firewall_rulesets.activate.evidence.advisory': '以上只是本地判断：门禁在服务端，它还看得到别人跑过的重放、以及父规则集激活后被改写的链哈希。以服务端的回答为准。',
  'firewall_rulesets.activate.gate.title': '门禁拒绝了这次激活',
  'firewall_rulesets.activate.gate.hint': '以下是服务端原文，未经改写——它写明了重放比的是哪个版本、现在在膛的又是哪个：',
  'firewall_rulesets.disarm.title': '停用这个规则集？',
  'firewall_rulesets.disarm.body': '停用后不激活任何版本，引用它的配置按「跳过本层」处理；版本历史全部保留。',
  'firewall_rulesets.disarm.confirm': '停用',
  'firewall_rulesets.delete.title': '删除规则集？',
  'firewall_rulesets.delete.body': '将删除「{name}」及其全部版本历史。仍在引用它的配置会失去这一层防护。',
  'firewall_rulesets.toast.created': '规则集已创建（draft，尚未生效）',
  'firewall_rulesets.toast.deleted': '规则集已删除',
  'firewall_rulesets.toast.version_created': '新的 draft 版本已保存，尚未生效',
  'firewall_rulesets.toast.activated': '已激活 v{version}',
  'firewall_rulesets.toast.disarmed': '已停用，当前不激活任何版本',
  'egress_profiles.col.strategy': '策略',
  'egress_profiles.col.status': '状态',
  'egress_profiles.col.updated': '更新',
  'egress_profiles.col.actions': '操作',
  'egress_profiles.status.enabled': '启用',
  'egress_profiles.status.disabled': '停用',
  'egress_profiles.action.edit': '编辑模板',
  'egress_profiles.action.enable': '启用模板',
  'egress_profiles.action.disable': '停用模板',
  'egress_profiles.action.delete': '删除模板',
  'egress_profiles.action.delete_blocked': '模板正被渠道绑定使用，无法删除',
  'egress_profiles.delete.title': '删除渠道策略模板',
  'egress_profiles.delete.body': '将永久删除模板“{name}”。',
  'egress_profiles.modal.create_title': '新建渠道策略模板',
  'egress_profiles.modal.edit_title': '编辑渠道策略模板',
  'egress_profiles.modal.name': '模板名称',
  'egress_profiles.modal.wire_api': 'OpenAI 接口面',
  'egress_profiles.modal.wire_api.both': '两种都支持（默认）',
  'egress_profiles.modal.wire_api.chat_completions': '只支持 chat/completions',
  'egress_profiles.modal.wire_api.responses': '只支持 responses',
  'egress_profiles.modal.create_hint': '新模板创建后，可在上游的渠道绑定中引用。',
  'egress_profiles.modal.save_warning_count': '保存将影响继承该模板的 {count} 个渠道绑定。',
  'egress_profiles.modal.save_warning_none': '当前没有渠道绑定继承该模板。',
  'egress_profiles.modal.create_submit': '创建模板',
  'egress_profiles.modal.edit_submit': '保存模板',
  'egress_profiles.toast.created': '渠道策略模板已创建',
  'egress_profiles.toast.updated': '渠道策略模板已更新',
  'egress_profiles.toast.enabled': '渠道策略模板已启用',
  'egress_profiles.toast.disabled': '渠道策略模板已停用',
  'egress_profiles.toast.deleted': '渠道策略模板已删除',
  'egress_profiles.sidecar.title': 'Protocol sidecar',
  'egress_profiles.sidecar.ready': '就绪',
  'egress_profiles.sidecar.degraded': '降级',
  'egress_profiles.sidecar.ready_hint': 'Gateway 正在从可信 sidecar 读取 caller-owned transform 和流式格式化能力。',
  'egress_profiles.sidecar.degraded_hint': 'Gateway 无法证明 sidecar 就绪；在恢复前应视为 protocol transform 不可用。',
  'egress_profiles.sidecar.api': 'API',
  'egress_profiles.sidecar.config_hash': '配置哈希',
  'egress_profiles.instance_note.cta': '打开上游列表',

  // ── Key Buckets (#143) ────────────────────────────────────────
  'key_buckets.title': 'API 密钥桶',
  'key_buckets.add_btn': '添加密钥',
  'key_buckets.empty': '暂无密钥桶',
  'key_buckets.col.label': '标签',
  'key_buckets.col.key_preview': '密钥预览',
  'key_buckets.col.status': '状态',
  'key_buckets.enabled': '启用',
  'key_buckets.disabled': '停用',
  'key_buckets.add_modal.title': '添加 API 密钥桶',
  'key_buckets.add_modal.label': '标签',
  'key_buckets.add_modal.label_placeholder': '如 生产密钥 #1',
  'key_buckets.add_modal.cancel': '取消',
  'key_buckets.add_modal.submit': '添加',
  'key_buckets.add_modal.creating': '添加中…',
  'key_buckets.toast.created': '密钥桶添加成功',
  'key_buckets.toast.deleted': '密钥桶已删除',
  'key_buckets.confirm_delete': '确认删除',

  // ── Credit lines (epic #1033 E4-C1, #1125) ──────────────────────
  'credit.section.title': '授信额度',
  'credit.section.apply': '申请授信',
  'credit.section.temporary_apply': '申请临时授信',
  'credit.section.empty': '暂无授信申请记录。',
  'credit.section.load_error': '加载授信信息失败',
  'credit.section.temporary_applications.title': '临时授信申请',
  'credit.section.temporary_applications.empty': '暂无临时授信申请。',
  'credit.section.temporary_applications.load_error': '加载临时授信申请失败',
  'credit.section.temporary_applications.col.created_at': '申请时间',
  'credit.section.temporary_applications.col.requested': '申请额度',
  'credit.section.temporary_applications.col.expected_arrival': '预计到账',
  'credit.section.temporary_applications.col.status': '状态',
  'credit.section.temporary_applications.col.grant': '授信 ID',
  'credit.section.history_link': '查看历史授信',
  'credit.overview.status.prepaid': '未动用授信 · 预付余额剩余 {amount}',
  'credit.overview.status.long_term': '长期授信使用中 · 已用 {used} / {cap} · 欠款 {debt}',
  'credit.overview.status.temporary': '临时授信使用中 · 已用 {used} / {cap} · 欠款 {debt}',
  'credit.overview.status.over_limit': '消费超出可用额度 {over} · 欠款 {debt}',
  'credit.overview.kpi.prepaid': '预付余额',
  'credit.overview.kpi.long_term': '长期额度',
  'credit.overview.kpi.temporary': '临时授信',
  'credit.overview.kpi.consumption': '累计消费',
  'credit.overview.applied_rate': '锁定汇率 {rate}',
  'credit.overview.layer.prepaid': '预付余额',
  'credit.overview.layer.long_term': '长期授信',
  'credit.overview.layer.temporary': '临时授信',
  'credit.overview.layer.over_limit': '超额',
  'credit.overview.layer.used_of_cap': '{used} / {cap}',
  'credit.overview.layer.limit': '额度 {limit}',
  'credit.overview.timeline.empty': '近几个月没有账单或临时授信动用。',
  'credit.overview.timeline.summary':
    '近 {months} 个月 · 按时付清 {onTime}/{closed} 期 · 最长逾期 {maxLate} 天 · 动用临时授信 {tmp} 次 · 当前未结清',
  'credit.overview.timeline.col.period': '账期',
  'credit.overview.timeline.col.bar': '账单与付款',
  'credit.overview.timeline.col.amount': '出账金额',
  'credit.overview.timeline.col.due': '到期日',
  'credit.overview.timeline.col.paid': '实付日',
  'credit.overview.timeline.col.overdue_days': '逾期天数',
  'credit.overview.timeline.col.status': '状态',
  'credit.overview.timeline.kind.temporary': '临时授信',
  'credit.overview.timeline.kind.one_shot': '一次性账单',
  'credit.overview.status_chip.paid_on_time': '按时付清',
  'credit.overview.status_chip.paid_late': '晚付 {days} 天',
  'credit.overview.status_chip.overdue_unpaid': '逾期未付',
  'credit.overview.status_chip.not_due': '未到期',
  'credit.overview.status_chip.no_payment_due': '无需付款',
  'credit.overview.status_chip.draw': '动用',
  'credit.overview.dash': '—',
  // Contract read-back (#1957).
  'credit.contract.title': '已签署合同',
  'credit.contract.download': '下载',
  'credit.contract.empty': '该客户暂无合同存档。',
  // #1958: 按申请引用的 key 过滤时，空结果说明的是"这份引用找不到"，
  // 不是"这个客户没有合同" —— 后者是系统在这里保证不了的肯定句。
  'credit.contract.empty_filtered': '未找到本次申请引用的合同存档。',
  'credit.kpi.limit': '授信额度',
  'credit.kpi.cycle': '账期',
  'credit.kpi.grace': '账单出具后付款天数',
  'credit.kpi.days': '{days} 天',
  'credit.kpi.inherited': '沿用平台默认值',
  'credit.kpi.custom': '客户自定义',
  'credit.kpi.none': '无授信 / 需预付',
  'credit.col.status': '状态',
  'credit.col.limit': '申请额度',
  'credit.col.applied_rate': '锁定汇率',
  'credit.col.cycle': '申请账期',
  'credit.col.reason': '申请理由',
  'credit.col.requester': '申请人',
  'credit.col.created_at': '提交时间',
  'credit.status.pending_approval': '待审批',
  'credit.status.approved': '已批准',
  'credit.status.rejected': '已驳回',
  'credit.apply.title': '申请授信额度',
  'credit.apply.approval.stage': '第 {n} 级{role}',
  'credit.apply.approval.stage_sep': '、',
  'credit.apply.approval.role_sep': '/',
  'credit.apply.description':
    '需要 {stages} 级审批：{chain}。申请人不能自批。提交不会改变客户额度。',
  'credit.apply.customer': '客户',
  'credit.apply.limit_label': '授信额度（人民币）',
  'credit.apply.limit_hint': '平台默认上限 {max}',
  'credit.apply.cycle_label': '账期（账单出具后付款天数）',
  'credit.apply.cycle_hint': '{min}–{max} 天，不设档位。常用 15 / 30 / 60。到期日 = 账单期末 + 该天数。',
  'credit.apply.reason_label': '申请理由',
  'credit.apply.reason_hint': '{min}–{max} 字。说明为什么给这个客户这笔额度与账期。',
  'credit.apply.error.reason': '请填写 {min}–{max} 字的申请理由（首尾空格不计）。',
  'credit.apply.contract_label': '合同（必传）',
  'credit.apply.contract_hint': '最大 {mb}。授信必须附合同。',
  'credit.apply.hint.needs_approval': '提交后进入审批，额度不会立刻生效；审批人不能是申请人。',
  'credit.apply.confirm': '提交',
  'credit.apply.retry': '重试提交',
  'credit.apply.pending_retry': '合同已上传，仅需重试提交申请。',
  'credit.apply.error.limit': '请输入有效的授信额度（非负金额）。',
  'credit.apply.error.cycle': '请输入 {min}–{max} 天的账期（整数，不设档位）。',
  'credit.apply.error.contract_required': '请上传合同附件。',
  'credit.apply.error.oversize': '附件超过 {mb} 上限。',
  'credit.apply.error.upload_failed': '合同上传失败：{detail}',
  'credit.apply.toast.pending': '授信申请已提交，等待审批',
  'credit.temporary.apply.title': '申请临时授信',
  'credit.temporary.apply.description':
    '客户汇款在途时的短期过桥。上限 ¥{cap}。需要 {stages} 级审批：{chain}。申请人不能自批。提交不会立刻生效。',
  'credit.temporary.apply.limit_label': '授信额度（人民币）',
  'credit.temporary.apply.limit_hint': '绝对上限 ¥{cap}。超过请走长期授信。',
  'credit.temporary.apply.expected_amount_label': '预计到账金额（人民币）',
  'credit.temporary.apply.expected_amount_hint': '客户声称将汇入的金额。实际到账低于 70% 时告警。',
  'credit.temporary.apply.expected_date_label': '预计到账日',
  'credit.temporary.apply.expected_date_hint': '额度在该日后再缓冲 5 个日历日（UTC+8）到期。',
  'credit.temporary.apply.evidence_text_label': '付款依据（文字）',
  'credit.temporary.apply.evidence_text_hint': '文字说明即可——邮件截图转述、微信记录或口头承诺。不分等级。',
  'credit.temporary.apply.evidence_file_label': '付款依据（附件，可选）',
  'credit.temporary.apply.evidence_file_hint': '可选附件，最大 {mb}。只填文字也被接受。',
  'credit.temporary.apply.error.limit': '请输入正数授信额度。',
  'credit.temporary.apply.error.expected_amount': '请输入正数预计到账金额。',
  'credit.temporary.apply.error.expected_date': '请填写预计到账日。',
  'credit.temporary.apply.error.evidence': '请填写文字说明、上传附件，或两者都提供。',
  'credit.temporary.apply.toast.pending': '临时授信申请已提交，等待财务审批',
  'credit.temporary.detail.limit': '申请额度',
  'credit.temporary.detail.expected_amount': '预计到账',
  'credit.temporary.detail.expected_date': '预计到账日',
  'credit.temporary.detail.applied_rate': '锁定汇率',
  'credit.temporary.evidence.title': '付款依据',
  'credit.temporary.evidence.download': '下载',
  'credit.temporary.evidence.empty': '该客户没有付款依据附件。',
  'credit.exemption.open': '紧急豁免',
  'credit.exemption.title': '开启紧急豁免',
  'credit.exemption.description':
    '暂停逾期阶梯，并让已批额度重新计入可花判定，直到到期时刻。客户会被告知：豁免至该时刻，届时若未结清将停止服务。到期不是第二个宽限期。不豁免债务。仅超级管理员；到期时刻和理由都必填。',
  'credit.exemption.expires_label': '到期时刻',
  'credit.exemption.expires_hint': '必填。到期时刻自动失效，超级管理员也可以立即撤销。',
  'credit.exemption.reason_label': '理由',
  'credit.exemption.reason_hint': '必填。写在历史记录上，事后可查。',
  'credit.exemption.confirm': '开启豁免',
  'credit.exemption.revoke': '撤销豁免',
  'credit.exemption.revoke_title': '撤销当前豁免？',
  'credit.exemption.revoke_body':
    '额度计入状态立即回到豁免前。逾期阶段和债务不变。',
  'credit.exemption.revoke_confirm': '撤销',
  'credit.exemption.revoke_toast': '豁免已撤销。',
  'credit.exemption.error.expires': '必须填写到期时刻，不允许永久豁免。',
  'credit.exemption.error.reason': '必须填写理由。',
  'credit.exemption.chip': '人工豁免中 · 剩 {days} 天',
  'credit.overdue.stage.frozen': '已逾期 · 新授信冻结',
  'credit.overdue.stage.stop_notice': '已发停服通知',
  'credit.overdue.stage.stopped': '额度已不计入可花',
  'credit.overdue.stage.collections': '转催收',
  'credit.overdue.page.title': '授信监控',
  'credit.overdue.page.caption': '可见范围内的授信敞口',
  'credit.overdue.page.empty': '没有授信账户。',
  'credit.overdue.page.load_error': '加载授信监控失败',
  'credit.overdue.col.account': '客户',
  'credit.overdue.col.stage': '阶段',
  'credit.overdue.col.days': '逾期天数',
  'credit.overdue.col.exemption': '豁免',
  'credit.overdue.heartbeat.ok': '阶梯上次已跑 · 扫描 {scanned}',
  'credit.overdue.heartbeat.stale': '阶梯 cron 已停滞 —— 欠款客户可能永远不停服',
  'credit.monitor.kpi.total_limit': '授信总敞口（可见范围）',
  'credit.monitor.kpi.used': '已用敞口（可见范围）',
  'credit.monitor.kpi.overdue_total': '逾期总额（可见范围）',
  'credit.monitor.overdue_count': '逾期 {count} 户',
  'credit.monitor.todo.overdue': '已逾期',
  'credit.monitor.todo.due_soon': '接近逾期（7 天内）',
  'credit.monitor.todo.temporary_expiring': '临时授信将到期',
  'credit.monitor.filter.all': '全部',
  'credit.monitor.sort.label': '排序',
  'credit.monitor.sort.default': '默认',
  'credit.monitor.sort.usage': '已用量',
  'credit.monitor.sort.name': '名称',
  'credit.monitor.col.layers': '额度构成与消耗',
  'credit.monitor.col.status': '状态',
  'credit.monitor.col.next_due': '下次到期',
  'credit.monitor.status.overdue': '逾期 {days} 天',
  'credit.monitor.next_due.overdue': '{date} 已逾期',
  'credit.monitor.next_due.due': '{date}',
  'credit.monitor.next_due.temporary': '{date} 到期',
  'credit.monitor.expand': '展开账期',
  'credit.monitor.collapse': '收起账期',
  'credit.monitor.table.caption': '账户列表',
  'credit.monitor.table.empty_filtered': '没有符合当前筛选的账户。',
  'credit.history.title': '历史授信',
  'credit.history.subtitle': '可见范围内每一笔授信，含已结束。汇总是全量，不是当前页。',
  'credit.history.summary.scope': '汇总为当前筛选下的可见范围全量，不是本页行数。',
  'credit.history.kpi.total': '合计（可见范围）',
  'credit.history.kpi.active': '生效中（可见范围）',
  'credit.history.kpi.ended': '已结束（可见范围）',
  'credit.history.kpi.avg_days': '临时授信还款平均天数（可见范围）',
  'credit.history.filter.customer': '客户',
  'credit.history.filter.customer_placeholder': '客户名 / 邮箱 / org id',
  'credit.history.filter.salesperson': '关联销售',
  'credit.history.filter.salesperson_placeholder': '销售名 / 邮箱 / ops user id',
  'credit.history.filter.grant_type': '授信类型',
  'credit.history.filter.status': '状态',
  'credit.history.filter.end_reason': '结束原因',
  'credit.history.filter.starts_from': '生效日起',
  'credit.history.filter.starts_to': '生效日止',
  'credit.history.filter.all': '全部',
  'credit.history.status.all': '全部',
  'credit.history.status.active': '生效中',
  'credit.history.status.ended': '已结束',
  'credit.history.type.long_term': '长期',
  'credit.history.type.temporary': '临时',
  'credit.history.type.temporary_bump': '临时加额',
  'credit.history.end_reason.repaid': '已还款',
  'credit.history.end_reason.expired': '已到期',
  'credit.history.end_reason.manual': '手动结束',
  'credit.history.end_reason.superseded': '被替换',
  'credit.history.col.customer': '客户',
  'credit.history.col.salesperson': '关联销售',
  'credit.history.col.grant_type': '授信类型',
  'credit.history.col.starts_at': '生效时间',
  'credit.history.col.ends_at': '失效时间',
  'credit.history.col.amount': '授信金额',
  'credit.history.col.used': '已用额度',
  'credit.history.col.repayment': '还款进度',
  'credit.history.col.days_to_topup': '授信后第 N 天完成充值',
  'credit.history.used.tooltip':
    '生效中按当前欠款；已结束按结束该授信的那笔交易发生前的欠款。',
  'credit.history.used.measured_now': '按当前余额',
  'credit.history.used.measured_ended': '按结束前一刻',
  'credit.history.repayment.tooltip':
    '已还 = 授信期内到账 + 该授信一次性账单的结算；待还 = 生效中为当前欠款、已到期为账单未结部分、已回款为 0、被替换为转入新授信',
  'credit.history.repayment.repaid': '已还',
  'credit.history.repayment.outstanding': '待还',
  'credit.history.repayment.settled': '已结清',
  'credit.history.repayment.partial': '部分回款 {pct}%',
  'credit.history.repayment.unpaid': '未回款',
  'credit.history.repayment.carried_over': '转入新授信',
  'credit.history.repayment.billed': '已转账单 {status}',
  'credit.history.repayment.statement.open': '未结',
  'credit.history.repayment.statement.closed': '已结',
  'credit.history.amount.rate_tooltip': '锁定汇率 {rate} · {usd}',
  'credit.history.ends.ongoing': '至今',
  'credit.history.ends.scheduled': '计划失效',
  'credit.history.ends.actual': '实际结束',
  'credit.history.days.day': '第 {n} 天',
  'credit.history.expand': '展开事件',
  'credit.history.collapse': '收起事件',
  'credit.history.events.empty': '这笔授信没有运营事件。',
  'credit.history.table.caption': '授信记录',
  'credit.history.empty': '没有符合当前筛选的授信。',
  'credit.history.load_error': '加载历史授信失败',
  'credit.history.pagination.aria_label': '历史授信分页',
  'credit.history.pagination.range': '显示第 {start}–{end} 条，共 {total} 条',
  'credit.history.audience.customer': '客户',
  'credit.history.audience.internal': '内部',
  'credit.history.event.unknown': '未知事件',
  'credit.history.event.temporary_credit_expiring': '临时授信即将到期',
  'credit.history.event.temporary_credit_arrival_shortfall': '到账不足',
  'credit.history.event.temporary_credit_one_shot_issued': '一次性账单已出',
  'credit.history.event.credit_due_soon_7d': '7 日内到期',
  'credit.history.event.credit_due_soon_3d': '3 日内到期',
  'credit.history.event.credit_due_today': '今日到期',
  'credit.history.event.credit_overdue_frozen': '逾期冻结',
  'credit.history.event.credit_stop_notice': '停服预告',
  'credit.history.event.credit_service_stopped': '已停服',
  'credit.history.event.credit_collections': '催收',
  'credit.history.event.credit_overdue_cleared': '逾期已清',
  'credit.history.event.credit_exemption_opened': '开通豁免',
  'credit.history.event.credit_exemption_revoked': '撤销豁免',
  'credit.history.event.credit_overdue_reminder': '逾期提醒',
  'credit.temporary.evidence.empty_filtered': '本申请引用的付款依据附件找不到。',
  'billing_statements.title': '客户账单',
  'billing_statements.subtitle': '授信客户的月度账单（应收）。只读；「逾期」取自催收判定，不由到期日推算。',
  'billing_statements.filter.account': '客户账户',
  'billing_statements.filter.account_placeholder': 'acc_…',
  'billing_statements.filter.status': '状态',
  'billing_statements.filter.overdue': '仅看逾期',
  'billing_statements.status.all': '全部',
  'billing_statements.status.open': '未结清',
  'billing_statements.status.closed': '已结清',
  'billing_statements.col.account': '客户',
  'billing_statements.col.period': '周期',
  'billing_statements.col.closing_ar': '闭口应收',
  'billing_statements.col.due_date': '到期日',
  'billing_statements.col.status': '状态',
  'billing_statements.col.owner': '归属运营',
  'billing_statements.owner.unassigned': '未分配',
  'billing_statements.empty': '没有符合条件的账单。',
  'billing_statements.load_error': '加载账单失败',
  'billing_statements.load_more': '加载更多',
  'credit.settings.title': '授信默认值',
  'credit.settings.subtitle': '平台级授信默认值：申请是否在默认额度内的比较上限、默认账期，以及每月出账锚定日。',
  'credit.settings.panel': '默认值',
  'credit.settings.forbidden': '仅管理员可配置授信默认值。',
  'credit.settings.load_error': '加载授信默认值失败',
  'credit.settings.limit_label': '默认授信额度（USD）',
  'credit.settings.limit_hint': '当前 {current}',
  'credit.settings.grace_label': '默认账期（账单出具后付款天数）',
  'credit.settings.grace_hint': '账户未批准自定义账期时的到期日回落。到期日 = 账单期末 + 该天数。',
  'credit.settings.anchor_label': '账单锚定日（UTC+8）',
  'credit.settings.anchor_hint': '每月对齐账期的日期，取值 1–28。',
  'credit.settings.save': '保存',
  'credit.settings.error.limit': '请输入有效的默认授信额度（非负金额）。',
  'credit.settings.error.grace': '请输入有效的默认账期（非负整数天数）。',
  'credit.settings.error.anchor': '账单锚定日取值须为 1–28。',
  'credit.settings.toast.saved': '授信默认值已保存',

  'credit.mail.title': '授信邮件',
  'credit.mail.empty': '这个客户还没有处理过授信邮件。',
  'credit.mail.load_error': '无法加载授信邮件记录。',
  'credit.mail.mute': '静音授信邮件',
  'credit.mail.unmute': '取消静音',
  'credit.mail.mute_toast': '已对该客户静音授信邮件。',
  'credit.mail.unmute_toast': '已取消该客户的授信邮件静音。',
  'credit.mail.muted_chip': '授信邮件已静音',
  'credit.mail.col.when': '时间',
  'credit.mail.col.result': '结果',
  'credit.mail.col.template': '模板',
  'credit.mail.col.subject': '主题',
  'credit.mail.col.to': '收件人',
  'credit.mail.col.reason': '原因',
  'credit.mail.result.sent': '已发送',
  'credit.mail.result.suppressed': '已抑制',
  'credit.mail.result.failed': '失败',
  'credit.mail.reason.unmapped_event': '无法识别的邮件事件，未发送',
  'credit.mail.reason.no_recipient': '没有可用的收件地址',
  'credit.mail.reason.reserved_invalid_recipient': '收件地址无效（保留域名）',
  'credit.mail.reason.missing_due_date': '账单没有到期日，未发送',
  'credit.mail.reason.zero_debt': '当前无欠款，未发送',
  'credit.mail.reason.mail_disabled': '授信邮件已被管理员统一停发',
  'credit.mail.reason.account_muted': '该客户的授信邮件已被静音',
  'credit.mail.reason.stale': '事件过期，未发送',
  'credit.mail.reason.superseded': '已被更新的事件替代，未发送',
  'credit.mail.reason.provider_unavailable': '邮件服务不可用',
  'credit.mail.reason.render_failed': '邮件内容生成失败',
  'credit.mail.reason.send_failed': '发送失败',
  'credit.mail.switch.caption': '授信客户邮件',
  'credit.mail.switch.hint':
    '下一轮扫描立刻停发全部授信客户邮件，无需重新发布。停发期间待发事件会留下来。',
  'credit.mail.switch.toggle': '发送授信客户邮件',
  'credit.mail.switch.on': '发送中',
  'credit.mail.switch.off': '已停发',
  'credit.mail.switch.load_error': '无法加载授信邮件开关。',
  'credit.mail.switch.enable_title': '恢复发送授信客户邮件？',
  'credit.mail.switch.enable_body':
    '下一轮扫描会把排队中的授信邮件发给未静音的客户。',
  'credit.mail.switch.disable_title': '停发授信客户邮件？',
  'credit.mail.switch.disable_body':
    '下一轮扫描不会发送。排队中的事件会留下来，并记一条抑制记录。',
  'credit.mail.switch.confirm': '确认',
  'credit.mail.switch.reason_label': '原因',
  'credit.mail.switch.reason_hint': '可选。会出现在开关状态和审计日志里。',
  'credit.mail.switch.enabled_toast': '授信客户邮件已打开。',
  'credit.mail.switch.disabled_toast': '授信客户邮件已关闭。',

  // ── SKU activation readiness (#1162) ───────────────────────────
  'sku_activation.title': 'SKU 上线就绪',
  'sku_activation.subtitle': '通过服务端持有的上线计划核对平台货架、供给、SKU 路由、Key 别名、缓存、审计回滚与公网冒烟。',
  'sku_activation.read_only': '计划门禁',
  'sku_activation.production.title': '当前检查生产环境',
  'sku_activation.production.body': '创建或完成计划会记录生产上线状态；每次完成都由数据 owner 服务重新校验。请核对 SKU、租户与上游范围。',
  'sku_activation.scope.title': '检查范围',
  'sku_activation.scope.no_mutation': '该操作只运行 readiness，不会调用任何 apply / rollback 端点。',
  'sku_activation.form.scope_required': '必须填写 SKU ID 和至少一个上游。',
  'sku_activation.plan.id': '上线计划 ID',
  'sku_activation.plan.load_hint': '加载服务端持有的既有计划。',
  'sku_activation.plan.load': '加载计划',
  'sku_activation.plan.create': '创建计划',
  'sku_activation.plan.clear': '清除计划',
  'sku_activation.plan.title': '上线计划',
  'sku_activation.plan.checklist': '上线变更检查清单',
  'sku_activation.plan.required_default': '需要 owner 变更。',
  'sku_activation.plan.mirror': '审计镜像',
  'sku_activation.plan.not_required': '当前已满足，无需 owner receipt。',
  'sku_activation.plan.completed': 'Owner receipt 已校验并完成。',
  'sku_activation.plan.completion_placeholder': '粘贴聚焦 owner 端点返回的证据。',
  'sku_activation.plan.complete': '校验并完成',
  'sku_activation.plan.required_first': '请先创建或加载上线计划。',
  'sku_activation.plan.environment': '环境',
  'sku_activation.plan.status_label': '计划状态',
  'sku_activation.plan.status.active': '进行中',
  'sku_activation.plan.status.completed': '已完成',
  'sku_activation.plan.status.cancelled': '已取消',
  'sku_activation.plan.org': '组织',
  'sku_activation.plan.key': 'API Key',
  'sku_activation.plan.upstreams': '允许的上游',
  'sku_activation.plan.skus': 'SKU',
  'sku_activation.plan.configs': '路由配置',
  'sku_activation.plan.none': '无',
  'sku_activation.plan.kind.platform_shelf': '平台货架',
  'sku_activation.plan.kind.supply_capability': '上游供给能力',
  'sku_activation.plan.kind.routing_coverage': '路由覆盖',
  'sku_activation.plan.kind.key_alias': 'Key 别名',
  'sku_activation.plan.mutation_status.pending': '待处理',
  'sku_activation.plan.mutation_status.completed': '已完成',
  'sku_activation.plan.requirement_status.required': '必须处理',
  'sku_activation.plan.requirement_status.not_required': '无需处理',
  'sku_activation.plan.receipt': '回执',
  'sku_activation.plan.revision': '版本',
  'sku_activation.plan.mirror_status.pending': '待投递',
  'sku_activation.plan.mirror_status.delivered': '已投递',
  'sku_activation.plan.mirror_status.failed': '投递失败',
  'sku_activation.plan.mirror_status.not_applicable': '不适用',
  'sku_activation.plan.mirror_status.unknown': '未知',
  'sku_activation.plan.reason.platform_listed': '平台模型已上架。',
  'sku_activation.plan.reason.platform_not_listed': '平台模型尚未上架。',
  'sku_activation.plan.reason.key_alias_ready': 'Key 别名已指向该 SKU。',
  'sku_activation.plan.reason.key_alias_missing': 'Key 别名缺失或指向其他 SKU。',
  'sku_activation.plan.reason.supply_missing': '没有可路由的精确供给链。',
  'sku_activation.plan.reason.supply_ready': '已存在可路由的精确供给链。',
  'sku_activation.plan.reason.routing_missing': '没有路由目标通过精确供给链校验。',
  'sku_activation.plan.reason.routing_ready': '已有路由目标通过精确供给链校验。',
  'sku_activation.plan.reason.server': '服务端原因：{reason}',
  'sku_activation.plan.completion_aria': '变更 {mutation} 的完成证据',
  'sku_activation.server_code': '服务端代码',
  'sku_activation.form.environment': '环境',
  'sku_activation.form.org_id': 'Org ID',
  'sku_activation.form.api_key_id': 'API Key ID',
  'sku_activation.form.sku_ids': 'SKU IDs',
  'sku_activation.form.upstream_ids': '允许的上游 IDs',
  'sku_activation.form.config_ids': '路由配置 IDs',
  'sku_activation.form.evidence': '证据 JSON',
  'sku_activation.form.evidence_hint': '粘贴 mutation 自有的缓存、审计、回滚和 smoke 回执后重新检查。',
  'sku_activation.form.evidence_invalid': '证据必须是有效的 JSON 对象。',
  'sku_activation.form.optional': '可选；留空时检查环境级事实。',
  'sku_activation.form.key_hint': '可选；填写后同时检查 canonical 与 bare model。',
  'sku_activation.form.list_hint': '使用空格、逗号或换行分隔，最多 20 个。',
  'sku_activation.form.upstream_hint': '建议明确填写，防止供给跨池；留空会形成 blocker。',
  'sku_activation.form.config_hint': '可选；留空使用租户当前生效配置。',
  'sku_activation.form.sku_required': '至少填写一个 SKU ID。',
  'sku_activation.form.org_required_for_key': '填写 API Key ID 时必须同时填写 Org ID。',
  'sku_activation.action.run': '运行只读检查',
  'sku_activation.action.running': '检查中…',
  'sku_activation.action.inspect': '证据',
  'sku_activation.action.open': '打开',
  'sku_activation.action.open_source': '打开事实源',
  'sku_activation.toast.completed': '就绪检查已完成',
  'sku_activation.empty.title': '等待检查范围',
  'sku_activation.empty.body': '提交后会按固定顺序展示七个阶段，并自动定位第一个 blocker。',
  'sku_activation.result.title': '检查结论',
  'sku_activation.result.ready': '可上线',
  'sku_activation.result.blocked': '未就绪',
  'sku_activation.result.fingerprint': '证据指纹',
  'sku_activation.result.checked_at': '检查时间',
  'sku_activation.result.first_blocker': '首个 blocker',
  'sku_activation.result.none': '无',
  'sku_activation.evidence.title': '证据与处置',
  'sku_activation.evidence.empty': '选择一个检查项查看证据。',
  'sku_activation.evidence.action': '对应的聚焦端点',
  'sku_activation.evidence.read_only_note': '这里仅显示处置入口，不会从本页执行写操作。',
  'sku_activation.evidence.none': '该检查项没有附加证据。',
  'sku_activation.evidence.rollback': '回滚凭据',
  'sku_activation.evidence.rollback_none': '未声明 mutation，或没有可验证的 CAS 回滚包。',
  'sku_activation.lineage.title': '请求路径血缘',
  'sku_activation.lineage.empty': '当前范围没有可展示的 canonical / bare 路由血缘。',
  'sku_activation.lineage.default': '默认 SKU 池',
  'sku_activation.lineage.no_target': '无目标',
  'sku_activation.stage.platform_shelf': '平台货架',
  'sku_activation.stage.supply': '上游供给',
  'sku_activation.stage.routing': 'SKU 路由',
  'sku_activation.stage.key_alias': 'Key 别名',
  'sku_activation.stage.cache': '缓存收敛',
  'sku_activation.stage.audit_rollback': '审计与回滚',
  'sku_activation.stage.smoke': '公网冒烟',
  'sku_activation.status.ready': '就绪',
  'sku_activation.status.warning': '警告',
  'sku_activation.status.blocked': '阻断',
  'sku_activation.status.waiting': '等待',
  'sku_activation.status.degraded': '降级',
  'sku_activation.status.not_applicable': '不适用',

  // ── 邮件通知（#1911）─────────────────────────────────────────────
  'email_notices.title': '邮件通知',
  'email_notices.subtitle':
    '给指定人群手写一封邮件并群发。人群、文案、发送时机各自独立：按模型圈人只是其中一种人群策略，上下架公告只是其中一种文案。',
  'email_notices.new': '新建通知',
  'email_notices.views_label': '在通知和模板之间切换',
  'email_notices.view.notices': '通知',
  'email_notices.view.templates': '邮件模板',
  'email_notices.tabs_label': '按状态筛选通知',
  'email_notices.tab.all': '全部',
  'email_notices.panel.list': '通知',
  'email_notices.panel.delivery': '逐地址投递',
  'email_notices.empty': '当前筛选下没有通知。',
  'email_notices.loading': '正在加载通知…',
  'email_notices.error.load': '加载通知失败',
  'email_notices.error.load_hint': '接口没有返回列表。稍后重试，或检查 ops-api 与平台的连通性。',
  'email_notices.error.generic': '操作失败，请重试。',
  'email_notices.error.audience_window_unavailable':
    '该受众窗口超出已保留的评级历史，名单会不完整。请缩短窗口（或修改已保存的草稿）后重试。',
  'email_notices.column.task_title': '任务标题',
  'email_notices.column.subject': '邮件标题',
  'email_notices.column.audience': '人群',
  'email_notices.column.status': '状态',
  'email_notices.column.gates': '发送闸门',
  'email_notices.column.created': '创建于',

  'email_notices.status.draft': '草稿',
  'email_notices.status.sent': '已发送',
  'email_notices.status.cancelled': '已取消',

  'email_notices.section.copy': '内容',
  'email_notices.section.copy_hint':
    '任务标题只有运营看得到，邮件标题和正文是客户收到的原文。正文完全自由，模板只是起草稿。',
  'email_notices.field.task_title': '任务标题（内部）',
  'email_notices.field.task_title_hint': '只在这个页面出现，用来在一堆通知里认出这一条，例如「Claude Code Opus4.6 下架通知」。',
  'email_notices.field.subject': '邮件标题（客户可见）',
  'email_notices.field.subject_hint': '这行字会直接出现在客户的收件箱里。',
  'email_notices.field.body': '正文',
  'email_notices.field.body_hint': '原样发出，不会自动拼接任何段落。可用占位符：{placeholders}',
  'email_notices.placeholder_unknown': '这些占位符不可用：{names}。发出去会连大括号一起进客户的收件箱，所以先在这里拦下。',
  'email_notices.field.remind_on': '提醒日期（备忘）',
  'email_notices.field.remind_on_hint':
    '纯备忘：到日子只往运营群发一张卡片，不发信、不改状态、不动任何模型。留空就不提醒。',

  'email_notices.template.load_label': '从模板载入起草稿',
  'email_notices.template.load_hint': '把模板的标题和正文填进下面两个框，之后随便改，和模板再无关系。',
  'email_notices.template.load_placeholder': '选择一份模板',
  'email_notices.template.load_action': '载入',

  'email_notices.section.audience': '人群',
  'email_notices.section.audience_hint':
    '先选一种圈人方式，再填这种方式的参数。目前只有「按模型圈人」，以后加新策略不影响已有的通知。',
  'email_notices.field.audience_type': '圈人方式',
  'email_notices.field.audience_type_hint': '换一种方式会清空当前这一种的参数。',
  'email_notices.audience.models_used': '按模型圈人',
  'email_notices.models_loading': '正在加载模型目录…',
  'email_notices.models_empty': '没有可选的模型。',
  'email_notices.selected_count': '已选 {count} 个模型。',
  'email_notices.models_search_label': '筛选模型',
  'email_notices.models_search_placeholder': '按厂商或模型名筛选，例如 anthropic',
  'email_notices.models_select_filtered': '全选筛选结果（{count}）',
  'email_notices.models_clear_filtered': '清空筛选结果（{count}）',
  'email_notices.models_bulk_scope':
    '全选和清空只作用于当前筛选出的 {shown} 个模型，不影响筛选之外已勾选的；清空搜索框即为全部 {total} 个。',
  'email_notices.models_no_match': '没有模型匹配当前筛选条件。',
  'email_notices.field.window_days': '回溯天数',
  'email_notices.field.window_days_hint': '发给这些天内调用过所选模型的账号，最多 {max} 天。',
  'email_notices.audience.preview_action': '看看有多少人',
  'email_notices.audience.preview_hint': '只查不写，草稿不用先保存，看多少次都可以。',
  'email_notices.audience.preview_incomplete': '先选至少一个模型、填好回溯天数，才能查。',
  'email_notices.audience.preview_count':
    '这个人群目前有 {count} 个账号（回溯 {days} 天，自 {from} 起）。',
  'email_notices.audience.preview_none': '回溯 {days} 天（自 {from} 起）内没有账号命中这个人群。',
  'email_notices.audience.preview_more': '……另有 {count} 个账号未列出。',
  'email_notices.audience.preview_not_frozen':
    '这只是此刻的估算，名单没有冻结。正式发送前会在第 3 段重新计算并逐个列出。',

  'email_notices.section.recipients': '收件人',
  'email_notices.section.recipients_hint':
    '先看清楚这封信会发给谁。重算不写库，可以随便看；定稿会把名单冻结成实际投递用的行。',
  'email_notices.gate.recipients_open': '名单未冻结',
  'email_notices.gate.recipients_frozen': '名单已冻结（{count} 个地址）',
  'email_notices.recipients.compute': '计算收件人',
  'email_notices.recipients.recompute': '重新计算',
  'email_notices.recipients.export': '导出 CSV',
  'email_notices.recipients.window': '回溯 {days} 天，自 {from} 起。',
  'email_notices.recipients.summary': '候选 {total} 个，已剔除 {excluded} 个，将冻结 {kept} 个。',
  'email_notices.recipients.column.email': '邮箱',
  'email_notices.recipients.column.brand': '品牌',
  'email_notices.recipients.column.requests': '请求数',
  'email_notices.recipients.column.last_used': '最近调用',
  'email_notices.recipients.column.status': '状态',
  'email_notices.recipients.column.action': '操作',
  'email_notices.recipients.none': '窗口期内没有账号命中这个人群。',
  'email_notices.recipients.exclude': '剔除',
  'email_notices.recipients.restore': '恢复',
  'email_notices.recipients.finalize': '冻结这 {count} 个收件人',
  'email_notices.recipients.blocked_audience_dirty': '人群有未保存的改动。上面的人数是按屏幕上的选择算的，而冻结是服务端按已保存的人群重算——先保存草稿，两者才是同一批人。',
  'email_notices.recipients.locked': '通知已不是草稿，收件人名单不能再改。',
  'email_notices.recipients.not_computed': '还没有计算收件人。点「计算收件人」看看这封信会发给谁。',

  'email_notices.section.send': '发送',
  'email_notices.section.send_hint':
    '群发域名没有发信历史，首批必须小量预热。两道闸门都通过才能正式发送。',
  'email_notices.gate.one_passed': '闸门一 收件人已冻结',
  'email_notices.gate.one_blocked': '闸门一 收件人未冻结',
  'email_notices.gate.two_passed': '闸门二 已试发成功',
  'email_notices.gate.two_blocked': '闸门二 未试发成功',
  'email_notices.gate.one_short': '名单',
  'email_notices.gate.two_short': '试发',
  'email_notices.gate.two_short_recorded': '有试发记录',
  'email_notices.send.test_to': '试发到',
  'email_notices.send.test_hint':
    '发给你自己。试发走的是真实群发凭据、真实模板、真实 SMTP 会话，和每个收件人收到的是同一个发件人。',
  'email_notices.send.test_action': '试发一封',
  'email_notices.send.action': '正式发送给 {count} 个地址',
  'email_notices.send.blocked_terminal': '通知已取消，不能再发送。',
  'email_notices.send.blocked_recipients': '先冻结收件人名单。',
  'email_notices.send.blocked_empty_list': '冻结的收件人名单是空的，没有可发送的地址。回到第 3 段重新计算，或放宽回溯天数。',
  'email_notices.send.blocked_test': '先试发成功一次。',
  'email_notices.send.blocked_all_sent': '名单里的地址都已投递。',
  'email_notices.send.confirm_title': '正式发送这封邮件？',
  'email_notices.send.confirm_consequence':
    '这一下会把标题为《{subject}》的邮件发给 {count} 个真实客户邮箱。邮件发出去无法召回。',
  'email_notices.send.confirm_idempotence':
    '已投递成功的地址不会重复收到——主键就是幂等键，所以重试是安全的。',
  'email_notices.send.confirm_type_label': '输入收件人数量以确认：',
  'email_notices.send.confirm_action': '确认发送',
  'email_notices.send.progress': '正在发送 {done} / {total}：成功 {sent}，失败 {failed}。请保持页面打开。',

  'email_notices.delivery.sent_count': '已送达 {count}',
  'email_notices.delivery.failed_count': '失败 {count}',
  'email_notices.delivery.retry_all': '重试全部 {count} 个失败地址',
  'email_notices.delivery.retry': '重试',
  'email_notices.delivery.column.attempts': '尝试次数',
  'email_notices.delivery.column.error': '最近错误',
  'email_notices.delivery.empty': '还没有冻结收件人，没有投递记录。',
  'email_notices.recipient_status.pending': '待发送',
  'email_notices.recipient_status.sent': '已送达',
  'email_notices.recipient_status.failed': '失败',

  'email_notices.cancel.action': '取消通知',
  'email_notices.cancel.confirm_title': '取消这封通知？',
  'email_notices.cancel.confirm_body':
    '取消不会发出任何邮件。已经收到的客户不会被告知作废——需要更正的话，请另发一封新的通知。',
  'email_notices.cancel.confirm_action': '确认取消',

  'email_notices.drawer.new_title': '新建邮件通知',
  'email_notices.drawer.new_description': '先写内容和人群，保存草稿后再处理收件人与发送。',
  'email_notices.drawer.title': '{title}',
  'email_notices.drawer.description': '{id} · {status}',
  'email_notices.drawer.create': '保存草稿',
  'email_notices.drawer.save': '保存修改',
  'email_notices.drawer.gates_locked':
    '第 3、4 段要先有草稿才能操作——收件人和发送都是针对某一条已保存的通知。先保存草稿。',
  'email_notices.drawer.edit_clears_gates':
    '保存修改会清空已冻结的收件人名单和试发记录：按旧人群算出来的名单、按旧文案做的试发，都不能当作这次的依据。',

  'email_notices.toast.created': '草稿已创建。',
  'email_notices.toast.updated': '修改已保存，两道闸门已重置。',
  'email_notices.toast.finalized': '收件人名单已冻结（{count} 个地址）。',
  'email_notices.toast.test_sent': '试发已成功送到 {to}，正式发送已解锁。',
  'email_notices.toast.sent': '本次成功 {sent}，失败 {failed}，未处理 {deferred}。',
  'email_notices.toast.cancelled': '通知已取消。',

  // ── 邮件模板（#1911）─────────────────────────────────────────────
  'email_templates.panel': '邮件模板',
  'email_templates.empty': '还没有模板。',
  'email_templates.error.load': '加载模板失败',
  'email_templates.column.name': '模板',
  'email_templates.column.key': 'key',
  'email_templates.column.subject': '当前标题',
  'email_templates.column.updated': '更新于',
  'email_templates.save': '保存模板',
  'email_templates.drawer.title_none': '邮件模板',
  'email_templates.field.subject': '邮件标题',
  'email_templates.field.body': '正文',
  'email_templates.allowed_label': '这份模板可用的占位符',
  'email_templates.allowed_hint': '发信时替换成真实值。写了列表以外的名字会被拦下，不会带着大括号发到客户手里。',
  'email_templates.test.caption': '试发',
  'email_templates.test.hint': '试发用的是已保存的那一版。刚改完想验证，先保存再试发。',
  'email_templates.test.to': '试发到',
  'email_templates.test.action': '试发一封',
  'email_templates.toast.saved': '模板已保存。',
  'email_templates.toast.test_sent': '试发已成功送到 {to}。',
  'email_templates.name.balance_alert_warning': '余额告警 · 提醒',
  'email_templates.name.balance_alert_critical': '余额告警 · 紧急',
  'email_templates.name.balance_alert_recovered': '余额告警 · 已恢复',
  'email_templates.name.notice_delist': '下架通知起草稿',
  'email_templates.name.notice_list': '上架通知起草稿',
  'email_templates.hint.balance_alert_warning':
    '这份文案由定时任务自动发出，无人值守。改完立刻对下一轮生效。',
  'email_templates.hint.balance_alert_critical':
    '这份文案由定时任务自动发出，无人值守，而且是余额最危险那一档。改完立刻对下一轮生效。',
  'email_templates.hint.balance_alert_recovered':
    '这份文案由定时任务自动发出，无人值守。改完立刻对下一轮生效。',
  'email_templates.hint.notice_delist':
    '只是新建通知时的起草稿，不会自己发给任何人。载入之后可以随便改。',
  'email_templates.hint.notice_list':
    '只是新建通知时的起草稿，不会自己发给任何人。载入之后可以随便改。',
  'email_templates.name.credit_due_soon_7d': '授信 · 到期前 7 天',
  'email_templates.name.credit_due_soon_3d': '授信 · 到期前 3 天',
  'email_templates.name.credit_due_today': '授信 · 账单今天到期',
  'email_templates.name.credit_frozen': '授信 · 新授信已冻结',
  'email_templates.name.credit_overdue_reminder': '授信 · 逾期催缴',
  'email_templates.name.credit_stopped': '授信 · 逾期停服',
  'email_templates.name.credit_restored': '授信 · 结清已恢复',
  'email_templates.hint.credit_due_soon_7d':
    '这份文案由授信逾期扫描自动发出，无人值守。改完立刻对下一封生效。',
  'email_templates.hint.credit_due_soon_3d':
    '这份文案由授信逾期扫描自动发出，无人值守。改完立刻对下一封生效。',
  'email_templates.hint.credit_due_today':
    '这份文案由授信逾期扫描自动发出，无人值守。到期日当天发出，不会声称授信已经冻结。',
  'email_templates.hint.credit_frozen':
    '这份文案由授信逾期扫描自动发出，无人值守。对应阶梯 D+1 冻结，不是到期日当天。',
  'email_templates.hint.credit_overdue_reminder':
    '这份文案由授信逾期扫描自动发出，无人值守。D+1 起每 3 天一封，冻结日和停服日不发。',
  'email_templates.hint.credit_stopped':
    '这份文案由授信逾期扫描自动发出，无人值守。对应阶梯 D+7 停服。',
  'email_templates.hint.credit_restored':
    '这份文案由授信逾期扫描自动发出，无人值守。结清后只发一次。',
  'email_templates.name.credit_temp_expiring_3d': '授信 · 临时授信到期前 3 天',
  'email_templates.name.credit_temp_expiring_1d': '授信 · 临时授信到期前 1 天',
  'email_templates.hint.credit_temp_expiring_3d':
    '临时授信进入到期前 3 天窗口时由扫描自动发出。时点与内部 3 天播报同一口径。',
  'email_templates.hint.credit_temp_expiring_1d':
    '临时授信进入到期前 1 天窗口时由扫描自动发出。时点与内部 1 天播报同一口径。',
  'email_templates.name.codex_capacity_elevated': 'Codex 容量 · 升高',
  'email_templates.name.codex_capacity_recovered': 'Codex 容量 · 已恢复',
  'email_templates.hint.codex_capacity_elevated':
    'Codex 车道 at-capacity 持续升高时发出。发信是第二轮的事；这份文案只是提醒，不含任何实测数字。',
  'email_templates.hint.codex_capacity_recovered':
    'Codex 车道 at-capacity 回落时发出。发信是第二轮的事；这份文案只是提醒，不含任何实测数字。',

  // ── 客户余额告警运营面（#1892）────────────────────────────────────
  'balance_alerts.title': '余额告警',
  'balance_alerts.subtitle':
    '余额告警是一个会自动给付费客户发信的通道。这个页面是它的仪表和刹车：总开关、下一轮会发给谁、已经发给过谁，以及按账号停用。',
  'balance_alerts.tabs_label': '按视角切换余额告警',
  'balance_alerts.tab.preview': '下一轮会发给谁',
  'balance_alerts.tab.history': '已经发出的信',
  'balance_alerts.tab.suppressions': '已停用的账号',
  'balance_alerts.panel.preview': '当前目标人群（预览，不发信）',
  'balance_alerts.panel.history': '发信历史（已投递）',
  'balance_alerts.panel.suppressions': '账户级抑制',

  'balance_alerts.error.generic': '这次没成功，请重试。',
  'balance_alerts.error.load': '读不到数据',
  'balance_alerts.error.load_hint': '接口没有返回内容。重试，或确认 ops-api 能连到 platform-api。',

  'balance_alerts.switch.caption': '全局开关',
  'balance_alerts.switch.state_open': '已启用',
  'balance_alerts.switch.state_closed': '未启用（被人为关闭）',
  'balance_alerts.switch.state_never_opened': '未启用（从未开启过）',
  'balance_alerts.switch.headline_open': '余额告警已启用：下一轮会真的给客户发信。',
  'balance_alerts.switch.headline_closed':
    '余额告警未启用——有人主动把它关掉了。在重新打开之前，一封信都不会发出去。',
  'balance_alerts.switch.headline_never_opened':
    '余额告警未启用——出厂即关闭，至今没有人打开过它。至今没有任何一封余额告警发给过客户。',
  'balance_alerts.switch.attribution': '最后一次变更：{who}，{when}。',
  'balance_alerts.switch.attribution_never': '没有任何人改动过这个开关，它还停在迁移建出来的初始状态。',
  'balance_alerts.switch.actor_unknown': '（未记录操作人）',
  'balance_alerts.switch.reason_shown': '备注：{reason}',
  'balance_alerts.switch.toggle_label': '启用余额告警',
  'balance_alerts.switch.toggle_hint': '开关存在数据库里，改完当轮生效，不需要发版。每次变更都进 ops_audit。',
  'balance_alerts.switch.reason_label': '原因（可选）',
  'balance_alerts.switch.reason_hint': '会写在开关上，也会进审计。下一个看到这一页的人靠它判断现在这个状态是怎么来的。',
  'balance_alerts.switch.enable_title': '打开余额告警？',
  'balance_alerts.switch.enable_consequence':
    '打开之后，下一轮就会给越线的付费客户发出真实邮件，讲的是他们自己的钱。信发出去收不回来。',
  'balance_alerts.switch.enable_detail':
    '这次点击本身不发信：真正发信的是下一轮定时任务。先去「下一轮会发给谁」看一眼名单，确认那就是你想发的人。',
  'balance_alerts.switch.enable_action': '打开告警',
  'balance_alerts.switch.disable_title': '关掉余额告警？',
  'balance_alerts.switch.disable_consequence': '关掉之后，下一轮就不再发出任何余额告警邮件。',
  'balance_alerts.switch.disable_detail':
    '已经发出去的信不受影响，去重状态也不动：客户越线的事实还在，只是这个通道不再说话。',
  'balance_alerts.switch.disable_action': '关掉告警',
  'balance_alerts.switch.error': '读不到开关状态',
  'balance_alerts.switch.error_hint':
    '在读到真实状态之前，这里不会显示「已启用」或「未启用」——猜错任何一边都会让人对「现在到底有没有在发信」做出错误判断。',

  'balance_alerts.severity.warning': '警告',
  'balance_alerts.severity.critical': '严重',
  'balance_alerts.verdict.healthy': '健康',
  'balance_alerts.verdict.indeterminate': '数据不足',

  'balance_alerts.column.account': '账号',
  'balance_alerts.column.verdict': '判定',
  'balance_alerts.column.spendable': '可花余额',
  'balance_alerts.column.days_left': '剩余天数',
  'balance_alerts.column.burn': '日均消耗',
  'balance_alerts.column.next_pass': '下一轮会怎样',
  'balance_alerts.column.actions': '操作',
  'balance_alerts.column.sent_at': '发信时刻',
  'balance_alerts.column.message': '这封信是什么',
  'balance_alerts.column.spendable_at_send': '发信当时可花余额',
  'balance_alerts.column.days_left_at_send': '发信当时剩余天数',
  'balance_alerts.column.brand': '品牌',
  'balance_alerts.column.reason': '原因',
  'balance_alerts.column.suppressed_at': '停用时间',
  'balance_alerts.column.suppressed_by': '停用人',

  'balance_alerts.preview.banner_open':
    '这是预测，不是记录：如果现在跑一轮，下面这些账号里有 {count} 个会收到信。还没有发。',
  'balance_alerts.preview.banner_closed':
    '这是预测，不是记录：下面 {count} 个账号越线了，但全局开关关着，这一轮一封信都不会发出去。',
  'balance_alerts.preview.empty': '当前没有越线的账号。',
  'balance_alerts.preview.burn_per_day': '{amount}/天',
  'balance_alerts.preview.window': '按最近 {days} 天（自 {from} 起）的消耗测算。',
  'balance_alerts.preview.recompute': '重新计算',
  'balance_alerts.preview.suppress': '停用告警',

  'balance_alerts.next_pass.mails': '会发一封新的',
  'balance_alerts.next_pass.escalates': '会发升级通知',
  'balance_alerts.next_pass.suppressed': '已停用，不发',
  'balance_alerts.next_pass.channel_closed': '开关关着，不发',
  'balance_alerts.next_pass.already_alerted': '已告知过（{severity}），不重发',

  'balance_alerts.history.banner':
    '这是记录，不是预测：下面 {count} 行都是已经投递成功的信，两个金额是发信当时的数字，不是现在的。',
  'balance_alerts.history.empty': '还没有发出过任何余额告警。',
  'balance_alerts.history.coverage_complete':
    '这就是全部记录——历史上发出过的 {count} 条余额告警都在这里了。',
  'balance_alerts.history.coverage_capped':
    '只显示了最近 {count} 条，可能还有更早的记录没有显示在这里。',
  'balance_alerts.history.kind_recovery': '恢复通知（解除 {severity}）',
  'balance_alerts.history.escalated': '由警告升级',

  'balance_alerts.suppressions.empty': '当前没有被停用告警的账号。',
  'balance_alerts.suppressions.semantics':
    '停用不是永久静音：它会清掉该账号的告警状态，期间不发信也不写状态；恢复之后如果仍然越线，客户会收到一封全新的告警。',
  'balance_alerts.suppressions.no_email': '（账号已不存在）',
  'balance_alerts.suppressions.restore': '恢复告警',

  'balance_alerts.suppress.confirm_title': '停用 {email} 的余额告警？',
  'balance_alerts.suppress.confirm_consequence':
    '这会清掉该账号当前的告警状态，包括「从什么时候开始越线」这个时间——它只在发信历史里还留得住。',
  'balance_alerts.suppress.confirm_not_permanent':
    '这不是永久静音：恢复之后如果他仍然越线，会收到一封全新的告警，因为对他来说这件事从来没有被告知过。',
  'balance_alerts.suppress.confirm_action': '停用告警',
  'balance_alerts.suppress.reason_label': '原因（可选）',
  'balance_alerts.suppress.reason_hint': '会写在抑制记录上，也会进审计。',

  'balance_alerts.restore.confirm_title': '恢复这个账号的余额告警？',
  'balance_alerts.restore.confirm_consequence':
    '如果这个账号现在仍然越线，下一轮就会给他发一封新的告警信。',
  'balance_alerts.restore.confirm_detail':
    '这次点击本身不发信，也不会补发停用期间「本该发的」信——下一轮是从头重新判定的。',
  'balance_alerts.restore.confirm_action': '恢复告警',

  'balance_alerts.toast.enabled': '余额告警已打开，下一轮开始发信。',
  'balance_alerts.toast.disabled': '余额告警已关闭，下一轮起不再发信。',
  'balance_alerts.toast.suppressed': '{email} 的余额告警已停用。',
  'balance_alerts.toast.suppressed_cleared':
    '{email} 的余额告警已停用，并清掉了他当前处于告警中的状态。',
  'balance_alerts.toast.restored': '告警已恢复；若仍越线，下一轮会发出新的告警。',
  'balance_alerts.toast.restore_noop': '这个账号本来就没有被停用，没有任何改动。',

  'codex_capacity.title': 'Codex 容量告警',
  'codex_capacity.subtitle':
    'Codex 容量告警会在上游打满时自动给付费客户发信。这个页面是它的仪表和刹车：总开关、当前闩锁、可调项、下一轮的演算、事件、发信历史、静音名单、SKU 排除名单，以及白名单试发。',
  'codex_capacity.tabs_label': '按视角切换 Codex 容量告警',
  'codex_capacity.tab.settings': '配置',
  'codex_capacity.tab.preview': '预览（不发不写）',
  'codex_capacity.tab.incidents': '事件历史',
  'codex_capacity.tab.history': '发信历史',
  'codex_capacity.tab.suppressions': '静音名单',
  'codex_capacity.tab.excluded_skus': 'SKU 排除名单',
  'codex_capacity.tab.test_send': '试发',

  'codex_capacity.panel.settings': '告警参数',
  'codex_capacity.panel.preview': '这一趟的演算（只计算，不写库，不发信）',
  'codex_capacity.panel.incidents': '事件历史（新的在前）',
  'codex_capacity.panel.history': '发信历史（已投递）',
  'codex_capacity.panel.suppressions': '按账号静音',
  'codex_capacity.panel.excluded_skus': 'SKU 排除名单',
  'codex_capacity.panel.test_send': '白名单试发',

  'codex_capacity.error.generic': '这次没成功，请重试。',
  'codex_capacity.error.load': '读不到数据',
  'codex_capacity.error.load_hint': '接口没有返回内容。重试，或确认 ops-api 能连到 platform-api。',

  'codex_capacity.switch.caption': '全局开关',
  'codex_capacity.switch.state_open': '已启用',
  'codex_capacity.switch.state_closed': '未启用（被人为关闭）',
  'codex_capacity.switch.state_never_opened': '未启用（从未开启过）',
  'codex_capacity.switch.headline_open':
    'Codex 容量告警已启用：下一轮如果窗口越线，会真的给客户发信。',
  'codex_capacity.switch.headline_closed':
    'Codex 容量告警未启用——有人主动把它关掉了。在重新打开之前，一封信都不会发出去。',
  'codex_capacity.switch.headline_never_opened':
    'Codex 容量告警未启用——出厂即关闭，至今没有人打开过它。至今没有任何一封 Codex 容量告警发给过客户。',
  'codex_capacity.switch.attribution': '最后一次变更：{who}，{when}。',
  'codex_capacity.switch.attribution_never': '没有任何人改动过这个开关，它还停在迁移建出来的初始状态。',
  'codex_capacity.switch.actor_unknown': '（未记录操作人）',
  'codex_capacity.switch.reason_shown': '备注：{reason}',
  'codex_capacity.switch.toggle_label': '启用 Codex 容量告警',
  'codex_capacity.switch.toggle_hint': '开关存在数据库里，改完当轮生效，不需要发版。每次变更都进 ops_audit。',
  'codex_capacity.switch.reason_label': '原因（可选）',
  'codex_capacity.switch.reason_hint': '会写在开关上，也会进审计。下一个看到这一页的人靠它判断现在这个状态是怎么来的。',
  'codex_capacity.switch.enable_title': '打开 Codex 容量告警？',
  'codex_capacity.switch.enable_consequence':
    '打开之后，下一轮如果 Codex 打满，就会给付费客户发出真实邮件。信发出去收不回来。',
  'codex_capacity.switch.enable_detail':
    '这次点击本身不发信：真正发信的是下一轮定时任务。先去「预览」看一眼名单，确认那就是你想发的人。',
  'codex_capacity.switch.enable_action': '打开告警',
  'codex_capacity.switch.disable_title': '关掉 Codex 容量告警？',
  'codex_capacity.switch.disable_consequence': '关掉之后，下一轮就不再发出任何 Codex 容量告警邮件。',
  'codex_capacity.switch.disable_detail':
    '已经发出去的信不受影响，闩锁也不动：比例还是那个比例，只是这个通道不再说话。',
  'codex_capacity.switch.disable_action': '关掉告警',
  'codex_capacity.switch.error': '读不到开关状态',
  'codex_capacity.switch.error_hint':
    '在读到真实状态之前，这里不会显示「已启用」或「未启用」——猜错任何一边都会让人对「现在到底有没有在发信」做出错误判断。',

  'codex_capacity.toast.enabled': 'Codex 容量告警已打开，下一轮越线就会发信。',
  'codex_capacity.toast.disabled': 'Codex 容量告警已关闭，下一轮起不再发信。',
  'codex_capacity.toast.settings_saved': '配置已保存。',
  'codex_capacity.toast.suppressed': '{account} 的 Codex 容量告警已静音。',
  'codex_capacity.toast.restored': '{account} 的静音已解除。',
  'codex_capacity.toast.restore_noop': '这个账号本来就没有被静音，没有任何改动。',
  'codex_capacity.toast.excluded_added': 'SKU {sku} 已从告警判定和租户曲线里排除。',
  'codex_capacity.toast.excluded_removed': 'SKU {sku} 已重新进入告警判定和租户曲线。',
  'codex_capacity.toast.excluded_noop': '这个 SKU 本来就不在排除名单里，没有任何改动。',
  'codex_capacity.toast.test_sent': '试发结束：成功 {sent} 封，失败 {failed} 封。',

  'codex_capacity.state.caption': '当前闩锁',
  'codex_capacity.state.phase_clear': '空闲',
  'codex_capacity.state.phase_firing': '告警中',
  'codex_capacity.state.window_ratio': '当前窗口：{pct}%（样本量 {count}）。',
  'codex_capacity.state.window_undersampled':
    '当前窗口样本不足：{count} 次请求（需要 {min}）。按「未知」处理，不当成恢复。',
  'codex_capacity.state.clear_below_since': '自 {when} 起持续低于恢复线。',
  'codex_capacity.state.clear_below_since_none': '当前没有打上「低于恢复线」的时间戳。',
  'codex_capacity.state.next_step.disabled': '告警未启用。在打开开关之前，不发信、也不改相位。',
  'codex_capacity.state.next_step.archive_stale':
    '归档已经落后。在 rollup 写出新桶之前，不触发、不恢复、也不补发。这里请求数往下掉，不代表线上已经恢复。',
  'codex_capacity.state.next_step.catch_up':
    '这一趟仍会给本事件里还没收到升高信的 {count} 个账号补发。',
  'codex_capacity.state.archive_stale':
    '归档已过期。最新桶开始于 {when}。这不是恢复。',
  'codex_capacity.state.next_step.quiet_hours':
    '正处在免打扰时段。观察还在写，但不发信、也不改相位，直到免打扰结束。',
  'codex_capacity.state.next_step.cooldown':
    '上次恢复之后的冷却还没过。新的触发会等；观察还在写。',
  'codex_capacity.state.next_step.fire':
    '这一趟会触发：发升高信并打开事件（比例已达到或超过 {pct}%）。',
  'codex_capacity.state.next_step.recover':
    '这一趟会恢复：发恢复信并关闭事件（比例仍低于 {pct}%）。',
  'codex_capacity.state.next_step.firing_remaining':
    '如果再持续低于 {pct}% 达 {minutes} 分钟，就发恢复信。',
  'codex_capacity.state.next_step.firing_no_stamp':
    '比例还没有低于恢复线（{pct}%）。要持续低于这条线 {minutes} 分钟才会发恢复信。',
  'codex_capacity.state.next_step.undersampled':
    '窗口内请求不够（{count} < {min}）——按未知处理，不当成恢复。这一趟不发信。',
  'codex_capacity.state.next_step.hold': '比例低于触发线（{pct}%）。这一趟不发信。',
  'codex_capacity.state.error': '读不到当前闩锁',
  'codex_capacity.state.error_hint':
    '读失败时不会猜「空闲」——猜成空闲会把正在发生的事件藏起来。',

  'codex_capacity.settings.save': '保存配置',
  'codex_capacity.settings.reason_label': '原因（可选）',
  'codex_capacity.settings.reason_hint': '会写在配置行上，也会进审计。',
  'codex_capacity.settings.error.required': '这项不能空。',
  'codex_capacity.settings.error.number': '必须是数字。',
  'codex_capacity.settings.error.integer': '必须是整数。',
  'codex_capacity.settings.error.fire_pct': '触发阈值必须大于 {min}、且不超过 {max}（百分比）。',
  'codex_capacity.settings.error.clear_pct_min': '恢复阈值至少 {min}（百分比）。',
  'codex_capacity.settings.error.clear_pct_vs_fire': '恢复阈值不能高于触发阈值。',
  'codex_capacity.settings.error.window_minutes': '观测窗口至少 {min} 分钟。',
  'codex_capacity.settings.error.clear_window_minutes': '恢复确认窗口至少 {min} 分钟。',
  'codex_capacity.settings.error.retention_hours': '归档保留至少 {min} 小时。',
  'codex_capacity.settings.error.rollup_lookback_minutes': 'rollup 回算至少 {min} 分钟。',
  'codex_capacity.settings.error.min_window_requests': '窗口内最小请求数不能小于 0。',
  'codex_capacity.settings.error.audience_days': '圈人回溯必须是 {min} 到 {max} 天。',
  'codex_capacity.settings.error.max_sends_per_pass': '单趟最多发信数至少为 1。',
  'codex_capacity.settings.error.cooldown_hours': '冷却至少 0 小时（0 = 关闭）。',
  'codex_capacity.settings.error.quiet_hours_pair': '免打扰起止小时必须成对填写，或者都留空（关闭）。',
  'codex_capacity.settings.error.quiet_hours_range': '免打扰小时必须是 {min} 到 {max}（一天中的小时）。',
  'codex_capacity.settings.error.quiet_hours_tz': '免打扰时区不能为空。',
  'codex_capacity.settings.error.window_exceeds_retention':
    '观测窗口不能长过归档保留（{max} 分钟）。',
  'codex_capacity.settings.error.clear_window_exceeds_retention':
    '恢复确认窗口不能长过归档保留（{max} 分钟）。',
  'codex_capacity.settings.fire_pct.label': '触发阈值（%）',
  'codex_capacity.settings.fire_pct.hint':
    '滚动窗口里打满比例达到或超过这个百分数就打开事件。必须大于 {min}、不超过 {max}。单位：百分比。',
  'codex_capacity.settings.clear_pct.label': '恢复阈值（%）',
  'codex_capacity.settings.clear_pct.hint':
    '告警中时，比例低于这个百分数就开始恢复计时。至少 {min}，且不能高于触发阈值。单位：百分比。',
  'codex_capacity.settings.window_minutes.label': '观测窗口（分钟）',
  'codex_capacity.settings.window_minutes.hint':
    '计算比例时往回看多长。至少 {min} 分钟。',
  'codex_capacity.settings.clear_window_minutes.label': '恢复确认窗口（分钟）',
  'codex_capacity.settings.clear_window_minutes.hint':
    '比例必须持续低于恢复线多久才发恢复信。至少 {min} 分钟。这是墙上时钟，不是单次窗口读数。',
  'codex_capacity.settings.min_window_requests.label': '窗口内最小请求数',
  'codex_capacity.settings.min_window_requests.hint':
    '低于这个样本量，窗口按未知处理，不当成恢复——不触发也不恢复。单位：2xx 请求数。',
  'codex_capacity.settings.audience_days.label': '圈人回溯（天）',
  'codex_capacity.settings.audience_days.hint':
    '发给谁：最近这么多天用过 Codex 的账号。{min} 到 {max}。',
  'codex_capacity.settings.max_sends_per_pass.label': '单趟最多发信数',
  'codex_capacity.settings.max_sends_per_pass.hint':
    '一轮最多投递多少封。多出来的下一轮补发。至少 1。',
  'codex_capacity.settings.cooldown_hours.label': '冷却（小时）',
  'codex_capacity.settings.cooldown_hours.hint':
    '恢复之后，这么多小时内不再触发。0 = 关闭。单位：小时。',
  'codex_capacity.settings.quiet_start.label': '免打扰开始（小时）',
  'codex_capacity.settings.quiet_start.hint':
    '时区里的 0–23 点。和结束一起留空 = 关闭。开始大于结束表示跨午夜。',
  'codex_capacity.settings.quiet_end.label': '免打扰结束（小时）',
  'codex_capacity.settings.quiet_end.hint':
    '时区里的 0–23 点。和开始一起留空 = 关闭。窗口内只观察，不发信、不改相位。',
  'codex_capacity.settings.quiet_tz.label': '免打扰时区',
  'codex_capacity.settings.quiet_tz.hint': 'IANA 时区名，例如 Asia/Shanghai。',
  'codex_capacity.settings.retention_hours.label': '归档保留（小时）',
  'codex_capacity.settings.retention_hours.hint': 'Codex 容量桶保留多久。至少 {min} 小时。',
  'codex_capacity.settings.rollup_lookback.label': 'rollup 回算（分钟）',
  'codex_capacity.settings.rollup_lookback.hint': '一轮 rollup 往回重读 usage logs 多远。至少 {min} 分钟。',

  'codex_capacity.preview.banner':
    '这是演算，不是发信：这一趟会{decision}。会发给 {count} 个账号。还没有发，也没有写库。',
  'codex_capacity.preview.banner_closed':
    '这是演算，不是发信：全局开关关着，即使窗口越线这一趟也不会发给任何人。',
  'codex_capacity.preview.empty': '这一趟不会发给任何人。',
  'codex_capacity.preview.recompute': '重新计算',
  'codex_capacity.preview.decision_fire': '触发（发升高信，打开事件）',
  'codex_capacity.preview.decision_recover': '恢复（发恢复信，关闭事件）',
  'codex_capacity.preview.decision_hold': '按兵不动（这一趟不发信，除非要补发）',
  'codex_capacity.preview.decision_catch_up': '补发（给本事件里还没收到升高信的账号）',
  'codex_capacity.preview.decision_archive_stale': '跳过（归档过期——不触发、不恢复、不补发）',
  'codex_capacity.column.actions': '操作',
  'codex_capacity.preview.column.account': '账号',
  'codex_capacity.preview.column.email': '邮箱（已脱敏）',
  'codex_capacity.preview.masked_note': '地址已经由后端脱敏。这个页面不会试图还原。',
  'codex_capacity.preview.suppressed': '另有 {count} 个被静音的账号没有出现在这份名单里。',

  'codex_capacity.incidents.empty': '还没有打开过任何事件。',
  'codex_capacity.incidents.column.started': '开始',
  'codex_capacity.incidents.column.ended': '结束',
  'codex_capacity.incidents.column.duration': '时长',
  'codex_capacity.incidents.column.fire_ratio': '触发时比例',
  'codex_capacity.incidents.column.peak': '峰值比例',
  'codex_capacity.incidents.column.elevated': '升高信人数',
  'codex_capacity.incidents.column.recovered': '恢复信人数',
  'codex_capacity.incidents.open': '仍在进行',
  'codex_capacity.incidents.view_history': '发信历史',
  'codex_capacity.incidents.duration_minutes': '{minutes} 分钟',

  'codex_capacity.history.banner': '这是记录，不是预测：下面 {count} 行都是已经投递成功的信。',
  'codex_capacity.history.empty': '还没有发出过任何 Codex 容量告警。',
  'codex_capacity.history.coverage_complete': '这就是全部记录——列出的 {count} 封都在这里了。',
  'codex_capacity.history.coverage_capped':
    '只显示了最近 {count} 条，可能还有更早的记录没有显示在这里。',
  'codex_capacity.history.filter': '已按事件 {id} 过滤。',
  'codex_capacity.history.filter_clear': '显示全部',
  'codex_capacity.history.column.sent_at': '发信时刻',
  'codex_capacity.history.column.account': '账号',
  'codex_capacity.history.column.kind': '种类',
  'codex_capacity.history.column.window': '发信时窗口比例',
  'codex_capacity.history.column.incident': '事件',
  'codex_capacity.history.kind.elevated': '升高',
  'codex_capacity.history.kind.recovered': '恢复',
  'codex_capacity.history.test_send': '试发',
  'codex_capacity.history.test_send_hint': '白名单演习。没有扳动闩锁。',

  'codex_capacity.suppressions.empty': '当前没有被静音的账号。',
  'codex_capacity.suppressions.semantics':
    '静音会让下一轮跳过这个具名客户。已经发出去的信不会收回。',
  'codex_capacity.suppressions.add': '静音一个账号',
  'codex_capacity.suppressions.account_id': '账号 id',
  'codex_capacity.suppressions.reason': '原因（可选）',
  'codex_capacity.suppressions.confirm_title': '静音 {account} 的 Codex 容量告警？',
  'codex_capacity.suppressions.confirm_consequence':
    '下一轮会跳过这个具名客户。已经发出去的信不受影响。',
  'codex_capacity.suppressions.confirm_action': '静音',
  'codex_capacity.suppressions.restore_title': '解除 {account} 的静音？',
  'codex_capacity.suppressions.restore_consequence':
    '如果事件还开着，下一轮可能把欠他的升高信或恢复信补上。',
  'codex_capacity.suppressions.restore_action': '解除静音',
  'codex_capacity.suppressions.column.account': '账号',
  'codex_capacity.suppressions.column.reason': '原因',
  'codex_capacity.suppressions.column.created': '静音时间',
  'codex_capacity.suppressions.column.by': '静音人',
  'codex_capacity.suppressions.remove': '解除静音',

  'codex_capacity.excluded.empty': '当前没有排除任何 SKU。',
  'codex_capacity.excluded.semantics':
    '排除的 SKU 既不进告警判定，也不进租户 24 小时曲线。按次计费 SKU 的 token-zero 2xx 是设计态，不是打满信号。',
  'codex_capacity.excluded.add': '排除一个 SKU',
  'codex_capacity.excluded.sku': 'SKU 名称',
  'codex_capacity.excluded.confirm_title': '排除 {sku}？',
  'codex_capacity.excluded.confirm_consequence':
    '这个 SKU 会同时离开告警窗口和租户 Codex 容量图。',
  'codex_capacity.excluded.confirm_action': '排除 SKU',
  'codex_capacity.excluded.remove_title': '把 {sku} 加回去？',
  'codex_capacity.excluded.remove_consequence':
    '这个 SKU 会重新计入告警窗口和租户曲线。',
  'codex_capacity.excluded.remove_action': '取消排除',
  'codex_capacity.excluded.column.sku': 'SKU',
  'codex_capacity.excluded.column.created': '排除时间',
  'codex_capacity.excluded.column.by': '排除人',
  'codex_capacity.excluded.remove': '取消排除',

  'codex_capacity.test_send.account_ids': '账号 id',
  'codex_capacity.test_send.account_ids_hint':
    '一行一个，或逗号分隔。任何一个解析失败，整次调用一封都不发。',
  'codex_capacity.test_send.kind': '哪一封',
  'codex_capacity.test_send.kind_elevated': '升高（打满）',
  'codex_capacity.test_send.kind_recovered': '恢复（解除）',
  'codex_capacity.test_send.submit': '发试发邮件',
  'codex_capacity.test_send.need_accounts': '至少写一个账号 id。',
  'codex_capacity.test_send.confirm_title': '真的发出 Codex 容量试发邮件？',
  'codex_capacity.test_send.confirm_consequence':
    '这是一次真实的邮件投递，会打到这些账号当前的真实地址。信发出去收不回。开发环境和预发也会真发，不只生产。',
  'codex_capacity.test_send.confirm_detail':
    '历史行会标成试发，闩锁不会被扳动。但这并不让这封信本身变成演习。',
  'codex_capacity.test_send.confirm_action': '发出真实邮件',

  'egress_lines.title': '边缘网关',
  'egress_lines.subtitle': '在役网关打哪个上游、走哪条出网路径。和「VPS 节点」不同：那里是机队注册表，这里是出网决策。',
  'egress_lines.authority_note':
    '格子里的路径来自各节点上报的当前态（按上游区域聚合），不是中心的裁决，也不会拿节点的全局 selected 去顶替另一个目的地。超过 5 分钟的快照会标成 stale 并置灰。',
  'egress_lines.matrix.empty_cell_hint':
    '「无流量」表示这个网关近期没有打过该目标区域的上游。「未知（快照过期）」表示有过流量但快照太旧。同区目的地固定为直连。',
  'egress_lines.matrix.presence_note':
    '默认只列出最近 {minutes} 分钟内有流量或有快照的在役实例。滚动发布时新旧两台都会出现；被取代的实例收进下方历史分组，24 小时窗口内仍可展开排查。',
  'egress_lines.matrix.title': '线路矩阵',
  'egress_lines.matrix.empty': '最近 24 小时没有带节点标识的流量，也没有探针快照。出口未激活时探针不会上报。',
  'egress_lines.matrix.node': '节点',
  'egress_lines.matrix.freshness': '新鲜度',
  'egress_lines.matrix.freshness_ago': '数据 {seconds} 秒前',
  'egress_lines.matrix.freshness_unknown': '尚无采集时间',
  'egress_lines.matrix.stale': 'stale',
  'egress_lines.matrix.traffic_1h': '近 1 小时',
  'egress_lines.matrix.traffic_24h': '近 24 小时',
  'egress_lines.matrix.exit': '出口分布',
  'egress_lines.matrix.exit_empty': '无流量',
  'egress_lines.matrix.requests': '{count} 次',
  'egress_lines.matrix.tokens': '{count} token',
  'egress_lines.badge.excellent': '优',
  'egress_lines.badge.good': '良',
  'egress_lines.badge.degraded': '降级',
  'egress_lines.badge.breaker': '熔断',
  'egress_lines.badge.same_region': '同区直连',
  'egress_lines.badge.no_traffic': '无流量',
  'egress_lines.badge.stale_unknown': '未知（快照过期）',
  'egress_lines.badge.empty': '无数据',
  'egress_lines.matrix.serving': '在役',
  'egress_lines.matrix.historical': '历史',
  'egress_lines.matrix.empty_serving': '最近没有在役实例。被取代的实例在下方历史分组。',
  'egress_lines.matrix.historical_group': '近 24 小时的历史实例 ({count})',
  'egress_lines.replication.title': '同步链路',
  'egress_lines.replication.empty': '还没有复制健康采样。未开通的环境保持静默。',
  'egress_lines.replication.link': '链路',
  'egress_lines.replication.direction': '方向',
  'egress_lines.replication.downlink.direction': '下行 · 东京→上海 · 配置面表集',
  'egress_lines.replication.uplink.direction': '上行 · 上海→东京 · usage 表集',
  'egress_lines.replication.status': '状态',
  'egress_lines.replication.lag': '积压',
  'egress_lines.replication.lag_hint':
    'lag_bytes 是槽仍保留的 WAL（restart_lsn），不是未送达的数据量，全库批量写入会让它瞬间跳高，只在 2 GiB 时判严重。apply_lag_bytes（confirmed_flush_lsn）是订阅端尚未确认的量——下行是 CN，上行是东京（经 CN 发布端槽）：512 MiB 告警、2 GiB 严重。正常时几 MB 会随写入振荡。',
  'egress_lines.replication.lag_hint_label': '积压含义',
  'egress_lines.replication.lag_thresholds': 'warn {warn} / crit {crit}',
  'egress_lines.replication.delay': '延迟',
  'egress_lines.replication.delay_hint_label': '延迟含义',
  'egress_lines.replication.delay_hint':
    '订阅端 latest_end_time 距今。上行在东京本地采样；下行由东京健康 cron 拉 CN 网关的只读端点。拉不到或采样超过 10 分钟，状态是「未知」而不是「正常」。积压是槽容量，延迟是时间，两件事。',
  'egress_lines.replication.drift': '漂移',
  'egress_lines.replication.sampled': '采样时间',
  'egress_lines.replication.sampled_ago': '{seconds} 秒前',
  'egress_lines.replication.sampled_unknown': '无采样时间',
  'egress_lines.replication.status.absent': '未初始化',
  'egress_lines.replication.status.ok': '正常',
  'egress_lines.replication.status.warn': '告警',
  'egress_lines.replication.status.crit': '严重',
  'egress_lines.replication.status.unknown': '未知/过期',
  'egress_lines.links.title': '线路列表',
  'egress_lines.links.empty': '还没有注册的出口。',
  'egress_lines.links.id': '线路',
  'egress_lines.links.region': '区域',
  'egress_lines.links.endpoint': '端点',
  'egress_lines.links.status': '状态',
  'egress_lines.links.heartbeat': '心跳',
  'egress_lines.links.note': '备注',
  'egress_lines.links.toggle': '启用线路',
  'egress_lines.links.toast.disabled': '已停用该线路',
  'egress_lines.links.toast.enabled': '已重新启用该线路',
  'egress_lines.links.confirm.disable_title': '停用这条出口线路？',
  'egress_lines.links.confirm.enable_title': '重新启用这条出口线路？',
  'egress_lines.links.confirm.body': '将写入 {id} 的 stored status。心跳推导的 degraded 不会被这次操作改写。',
  'egress_lines.links.confirm.disable': '停用线路',
  'egress_lines.links.confirm.enable': '启用线路',
  'egress_lines.links.dispatch': '派发资格',
  'egress_lines.links.dispatch.standby': '备用',
  'egress_lines.links.dispatch.admitted': '已放行',
  'egress_lines.links.dispatch.admit': '放行接流',
  'egress_lines.links.dispatch.revoke': '撤回放行',
  'egress_lines.links.dispatch.lag_note':
    '放行不是即时切流：网关通过 60 秒快照观察派发资格，最长可能延迟 60 秒才开始或停止接流。',
  'egress_lines.links.dispatch.hint_label': '派发资格含义',
  'egress_lines.links.dispatch.hint':
    '备用是已注册但不接租户流量；已放行才会进入候选集。网关观察这次变更最长 60 秒。',
  'egress_lines.links.dispatch.confirm.title': '放行这条出口线路？',
  'egress_lines.links.dispatch.confirm.body':
    '{id} 将进入候选集，开始承载真实租户流量。网关通过 60 秒快照观察这次变更，最长可能延迟 60 秒才接流。',
  'egress_lines.links.dispatch.confirm.admit': '放行接流',
  'egress_lines.links.dispatch.toast.enabled': '已放行接流。网关最长 60 秒后才会接流。',
  'egress_lines.links.dispatch.toast.disabled':
    '已撤回放行。该线路回到备用态，不再进候选集。网关最长 60 秒后才会停流。',
  'egress_lines.node.title': '节点 {node}',
  'egress_lines.node.subtitle': '网关区域 {region}',
  'egress_lines.node.paths': '候选路径分数',
  'egress_lines.node.paths_empty': '这个节点还没有快照。',
  'egress_lines.node.path': '路径',
  'egress_lines.node.sort_key': '排序键',
  'egress_lines.node.success': '成功率',
  'egress_lines.node.samples': '样本',
  'egress_lines.node.qualified': '合格',
  'egress_lines.node.selected': '当前',
  'egress_lines.node.dwell': '驻留起点',
  'egress_lines.node.yes': '是',
  'egress_lines.node.no': '否',
  'egress_lines.node.current': '当前选择',
  'egress_lines.node.live': '实际走',
  'egress_lines.node.live_chip': '实际走',
  'egress_lines.node.selection': '当前选择 {path}，驻留起点 {dwell}',
  'egress_lines.node.timeline': '切换时间线',
  'egress_lines.node.timeline_empty': '还没有切换记录。',
  'egress_lines.node.when': '时间',
  'egress_lines.node.dest': '目标区域',
  'egress_lines.node.from': '从',
  'egress_lines.node.to': '到',
  'egress_lines.node.reason': '原因',
  'egress_lines.node.scores': '两侧分数',
  'egress_lines.traffic.title': '出网决策 (网关 × 上游)',
  'egress_lines.traffic.empty': '还没有按上游拆开的实测样本。冷启动只走直连，探索成功后才会出现行。',
  'egress_lines.traffic.hint':
    '每台在役网关下面是它打过的上游：实际走哪条路、候选为什么没赢、最近好不好。理由由网关裁决器上报，不是页面拿数字反推。',
  'egress_lines.traffic.table_caption': '同一份数据的扁平表，便于复制路径和上游 id。',
  'egress_lines.traffic.upstream': '上游',
  'egress_lines.traffic.upstream_unknown': '未知上游',
  'egress_lines.traffic.ttfb': 'p50 TTFT',
  'egress_lines.traffic.last_sample': '最近样本',
  'egress_lines.traffic.last_explore': '最近探索',
  'egress_lines.node.traffic': '实测 (路径 × 上游)',
  'egress_lines.node.upstream': '上游',
  'egress_lines.node.ttfb': 'p50 TTFT',
  'egress_lines.node.last_explore': '最近探索',
  'egress_lines.decision.live': '实际走',
  'egress_lines.decision.candidate': '候选',
  'egress_lines.decision.health': '近况',
  'egress_lines.decision.health_empty': '还没有这条路径的样本',
  'egress_lines.decision.same_region': '同区',
  'egress_lines.decision.cross_region': '跨区',
  'egress_lines.decision.region_unknown': '区域未知',
  'egress_lines.decision.samples': '{count} 样本',
  'egress_lines.decision.no_samples': '无样本',
  'egress_lines.decision.success': '{pct}% 成功',
  'egress_lines.decision.ttfb': 'p50 TTFT {ms}ms',
  'egress_lines.decision.reason.same_region_direct': '同区直连',
  'egress_lines.decision.reason.no_evidence_direct': '没有证据，默认直连',
  'egress_lines.decision.reason.pinned': '运维钉死',
  'egress_lines.decision.reason.breaker_open': '对方熔断打开',
  'egress_lines.decision.reason.faster': '更快：{selected} vs {path} {vs}',
  'egress_lines.decision.reason.hold_margin': '差距不够，保持不动（{selected} vs {vs}）',
  'egress_lines.decision.reason.hold_unopposed': '没有可比较的对手，保持不动。等探索，不要调阈值',
  'egress_lines.decision.reason.hold_dwell': '最短驻留未满，保持不动（挑战者 {vs}）',
  'egress_lines.decision.reason.bootstrap': '首次选路',
  'egress_lines.decision.reason.recovery': '在任者已不合格，切到更好的路径',
  'egress_lines.decision.reason.unknown': '尚无裁决理由（旧快照）',
  'egress_lines.tabs_label': '边缘网关视图',
  'egress_lines.tab.matrix': '线路矩阵',
  'egress_lines.tab.policies': '出口策略',
  'egress_lines.tab.clusters': '集群成员',
  'egress_lines.policies.list_title': '策略',
  'egress_lines.policies.new': '新建',
  'egress_lines.policies.new_name': '策略名称',
  'egress_lines.policies.floor_badge': '兜底',
  'egress_lines.policies.rule_count': '{count} 条规则',
  'egress_lines.policies.assigned_count': '指派 {count} 个客户',
  'egress_lines.policies.unassigned_customers': '所有未指派客户',
  'egress_lines.policies.assigned_title': '已指派客户',
  'egress_lines.policies.assign': '指派',
  'egress_lines.policies.unassign': '解除指派',
  'egress_lines.policies.assign_placeholder': '按邮箱或公司名搜索',
  'egress_lines.policies.no_email': '无邮箱',
  'egress_lines.policies.assign_confirm_title': '确认指派该客户？',
  'egress_lines.policies.assign_confirm_body': '将 {customer} 指派到策略「{policy}」。',
  'egress_lines.policies.unassign_confirm_title': '确认解除指派？',
  'egress_lines.policies.unassign_confirm_body': '将 {customer} 从策略「{policy}」上解除。',
  'egress_lines.policies.no_assignments': '还没有指派客户',
  'egress_lines.policies.inherit_note': '规则按精确度匹配。本策略未命中的请求继承全局默认；灰色行是继承下来的规则，仅展示。',
  'egress_lines.policies.emergency': '紧急直连',
  'egress_lines.policies.emergency_confirm_title': '打开紧急直连？',
  'egress_lines.policies.emergency_confirm_body': '该策略下的全部流量将改为直连，直到关闭此开关。',
  'egress_lines.policies.preview': '预览',
  'egress_lines.policies.save': '保存并生效',
  'egress_lines.policies.add_rule': '加一条规则',
  'egress_lines.policies.delete_rule': '删',
  'egress_lines.policies.col.index': '#',
  'egress_lines.policies.col.ingress': '接入点',
  'egress_lines.policies.col.upstream': '上游',
  'egress_lines.policies.col.via': '出口',
  'egress_lines.policies.col.fallback': '集群不可用时',
  'egress_lines.policies.col.source': '来源',
  'egress_lines.policies.col.note': '备注',
  'egress_lines.policies.source.own': '本策略',
  'egress_lines.policies.source.inherited': '继承 · 全局默认',
  'egress_lines.policies.via.direct': '直连',
  'egress_lines.policies.via.cluster': '{region} 集群',
  'egress_lines.policies.fallback.direct': '降级直连',
  'egress_lines.policies.fallback.none': '失败',
  'egress_lines.policies.fallback.na': '—',
  'egress_lines.policies.upstream.any': '*',
  'egress_lines.policies.upstream.region': 'region = {region}',
  'egress_lines.policies.upstream.region_legacy': 'region = {region}（旧规则）',
  'egress_lines.policies.ingress.any': '*',
  'egress_lines.policies.ingress.gateway': '{region} 网关',
  'egress_lines.policies.saved': '策略已保存并生效',
  'egress_lines.policies.created': '策略已创建',
  'egress_lines.policies.assigned': '客户已指派',
  'egress_lines.policies.unassigned': '已解除指派',
  'egress_lines.policies.create_name_required': '请填写策略名称',
  'egress_lines.policies.empty': '还没有出口策略',
  'egress_lines.policies.version': 'v{version}',
  'egress_lines.policies.preview_title': '预览：这条请求会怎么走',
  'egress_lines.policies.preview_org': '客户',
  'egress_lines.policies.preview_ingress': '接入点',
  'egress_lines.policies.preview_upstream': '上游',
  'egress_lines.policies.preview_resolve': '解析',
  'egress_lines.policies.preview.via': '结论',
  'egress_lines.policies.preview.matched': '命中规则',
  'egress_lines.policies.preview.inherited': '继承',
  'egress_lines.policies.preview.inherited_yes': '是（全局默认）',
  'egress_lines.policies.preview.inherited_no': '否（本策略命中）',
  'egress_lines.policies.preview.fallback': '集群不可用',
  'egress_lines.policies.preview.members': '集群成员',
  'egress_lines.policies.preview.member_id': 'egress_id',
  'egress_lines.policies.preview.member_eligible': '可用',
  'egress_lines.policies.preview.member_reason': '原因',
  'egress_lines.policies.preview.yes': '是',
  'egress_lines.policies.preview.no': '否',
  'egress_lines.policies.preview.rule_index': '第 {index} 条',
  'egress_lines.clusters.title': '{region} 集群 · {count} 个成员',
  'egress_lines.clusters.empty': '还没有出口线路',
  'egress_lines.clusters.heartbeat_age': '心跳年龄',
  'egress_lines.clusters.reason': '健康原因',
  'egress_lines.clusters.age_seconds': '{seconds}s 前',
  'egress_lines.clusters.age_unknown': '未知',
  'egress_lines.policies.issue.same_region': '出口集群不能与接入点同区',
  'egress_lines.policies.issue.empty_rules': '至少需要一条规则',
  'egress_lines.policies.version_conflict': '策略已被他人更新，请重新加载后再保存。',
  'egress_lines.policies.delete': '删除策略',
  'egress_lines.policies.delete_confirm_title': '删除这条策略？',
  'egress_lines.policies.delete_confirm_body': '未指派的非兜底策略会被永久删除。',
  'egress_lines.policies.deleted': '策略已删除',
  'egress_lines.policies.preview_ingress_empty': '没有可选接入点',
  'egress_lines.policies.preview.policy_version': '策略版本',
  'egress_lines.policies.preview.cached_at': '节点缓存',
  'egress_lines.policies.preview.selected_path': '本节点路径',
  'egress_lines.policies.preview.selected_reason': '选路原因',
  'egress_lines.policies.preview.cluster_down': '集群空集',
  'egress_lines.policies.preview.cluster_down_yes': '是',
  'egress_lines.policies.preview.cluster_down_no': '否',
  'egress_lines.policies.preview.reason.policy_direct': '策略直连',
  'egress_lines.policies.preview.reason.policy_cluster': '策略集群',
  'egress_lines.policies.preview.reason.emergency_direct': '紧急直连',
  'egress_lines.policies.preview.reason.cluster_down': '集群不可用',
  'egress_lines.policies.preview.reason.token_unset': '出口令牌未配置',
  'egress_lines.member_reason.ok': '可用',
  'egress_lines.member_reason.standby': '备用（未放行）',
  'egress_lines.member_reason.stale_heartbeat': '心跳过期',
  'egress_lines.member_reason.breaker_open': '断路器打开',
  'egress_lines.member_reason.probe_failing': '探针失败',
  'egress_lines.member_reason.same_region': '同区排除',

  'upstream_detail.config.region': '所在区域',
  'upstream_detail.config.region.hint': '端点实际应答的区域。自动建议（RTT 三角测量）是后续增强，这一期不造假建议。',
  'upstream_detail.config.pin': '钉死出口线路',
  'upstream_detail.config.pin.empty': '未钉死（按评分选路）',
  'upstream_detail.config.pin.badge': 'PIN',
  'upstream_detail.config.pin.hint': '评分裁决之前尊重这个 pin。被钉的线路必须是 derived-active，否则会被忽略——pin 不是绕过健康检查的通道。',

  'tenant_routing.effective.egress_path.direct': '出网：同区直连（{gateway} → {dest}）',
  'tenant_routing.effective.egress_path.via': '出网：经 {path} 到 {dest}（本节点 {gateway}）',
  'tenant_routing.effective.egress_path.unknown': '出网：上游区域未知，按直连处理',
  'tenant_routing.effective.egress_path.no_scores': '出网：尚无评分，本节点走直连',
  'tenant_routing.effective.egress_path.pinned': '出网：钉死 {path} 到 {dest}',

  // ── 系统健康透视 ────────────────────────────────────────────────
  'health_perspective.title': '系统健康透视',
  'health_perspective.sub': '逐个下游 SKU 看清由谁在扛、扛得怎么样、赚不赚钱',
  'health_perspective.window.5m': '5 分钟',
  'health_perspective.window.1h': '1 小时',
  'health_perspective.window.24h': '24 小时',
  'health_perspective.window.7d': '7 天',
  'health_perspective.window.aria': '时间窗口',
  'health_perspective.window.bounds': '{from} 至 {to}',
  'health_perspective.auto_refresh': '自动刷新',
  'health_perspective.refresh': '刷新',
  'health_perspective.refreshing': '刷新中',
  'health_perspective.generated_at': '数据时间 {time}',
  'health_perspective.routing_source': '链路取自路由配置「{name}」',
  'health_perspective.routing_source_unknown': '未找到全局默认路由配置，链路为空',
  'health_perspective.loading': '正在拼装健康视图',
  'health_perspective.error.title': '读不到健康数据',
  'health_perspective.error.hint': '流量、计费、路由三层里至少一层没返回。重试一次；持续失败去 /system/cron-runs 看结算与探针任务。',
  'health_perspective.degraded.title': '部分数据缺失',
  'health_perspective.degraded.traffic': '流量层读取失败：健康与承接占比无法判定，表格里显示为「未知」而不是零。',
  'health_perspective.degraded.money_sku': 'SKU 计费层读取失败：毛利留空，未用零代替。',
  'health_perspective.degraded.money_upstream': '上游计费层读取失败：上游毛利留空，未用零代替。',
  'health_perspective.degraded.routing': '路由层读取失败：重试梯队为空，SKU 行本身仍可看。',
  'health_perspective.degraded.upstreams': '上游名单读取失败：链路只能显示渠道标识，显示不出是哪家供应商。其余数据不受影响。',

  'health_perspective.summary.margin': '综合毛利率',
  'health_perspective.summary.requests': '请求数',
  'health_perspective.summary.downstream_error_rate': '下游失败率',
  'health_perspective.summary.tenant_error_rate': '客户侧失败率',
  'health_perspective.summary.tenant_error_hint': '客户最终看到的失败占比，含被网关按规则拒掉的那些。被重试链救回来的请求算成功，不在这里。它既不判定上游、也不判定我们 —— 那两个看旁边的「上游失败率」和「下游失败率」。',
  'health_perspective.summary.supply_error_rate': '上游失败率',
  'health_perspective.summary.fallback_rate': '兜底率',
  'health_perspective.summary.first_byte': '首包均值',
  'health_perspective.summary.revenue': '收入',
  'health_perspective.summary.cost': '采购成本',
  'health_perspective.summary.profit': '毛利',
  'health_perspective.summary.unrouted': '未挂链路 SKU',
  'health_perspective.summary.single_point': '单点供应 SKU',
  'health_perspective.summary.unhealthy_upstreams': '不健康上游',
  'health_perspective.summary.unsettled': '未结算',
  'health_perspective.summary.unsettled_hint': '这个窗口有流量但还没有已计价的账本行 —— 计费由 rating 管线异步结算，短窗口下正常。这里不显示 0%，那等于宣称按成本价卖。',

  'health_perspective.scope.aria': '表格范围',
  'health_perspective.scope.traffic': '有流量',
  'health_perspective.scope.problems': '有问题',
  'health_perspective.scope.all': '全部',
  'health_perspective.scope.count': '表格显示 {shown} / {total} 个 SKU',
  'health_perspective.scope.note': '上面的 KPI 始终按全量口径统计。范围只筛选表格，不会改动任何一个头部数字。',
  'health_perspective.scope.empty.title': '这个范围里没有 SKU',
  'health_perspective.scope.empty.hint': '窗口内没有符合这个范围的 SKU。切到「全部」可以看到目录里零流量的那些。',

  'health_perspective.table.title': '每个 SKU 的重试梯队',
  'health_perspective.table.col.sku': '下游 SKU',
  'health_perspective.table.col.outcome': '结果构成',
  'health_perspective.table.col.requests': '请求数',
  'health_perspective.table.col.observed_share': '实测承接',
  'health_perspective.table.col.supply': '上游失败率',
  'health_perspective.table.col.downstream': '下游失败率',
  'health_perspective.table.col.latency': '首包',
  'health_perspective.table.col.margin': '毛利率',
  'health_perspective.table.denominator': '{errors} / {requests}',
  'health_perspective.table.cell.supply_detail': '失败 {errors} · 兜底救回 {fallbacks}',
  'health_perspective.table.cell.worst_leg': '最差链路 {channel} {rate}',
  'health_perspective.table.cell.slow_share': '慢请求 {rate}',
  'health_perspective.table.cell.downstream_detail': '失败 {errors} / 计入 {requests}',
  'health_perspective.table.supply_chip.ok': '上游正常',
  'health_perspective.table.supply_chip.warn': '上游波动',
  'health_perspective.table.supply_chip.bad': '上游故障',
  'health_perspective.table.supply_chip.no_traffic': '上游无流量',
  'health_perspective.table.supply_chip.unknown': '上游情况未知',
  'health_perspective.table.subject_note':
    'SKU 等级看「下游失败率」：被网关按请求自身理由拒掉的（准入、认证、参数、余额、账号不可用）两边都不计 —— 闸门按规则挡住请求，不是我们的故障。但「模型不可用」算我们头上：租户和请求我们都收了，然后没有地方可以转发，这是我们的问题不是闸门的功劳。上游好坏不看这个 SKU 的汇总数，而是看它自己每条链路各打了多少量、各失败多少：一条链路对别的 SKU 正常、对这个 SKU 全失败，是真实发生过的事。客户实际失败率另外列出，是关于某个客户的事实，不给任何一方定性。',
  'health_perspective.table.default_order': '默认按严重程度排，同级再按请求数；点列头可改排序',
  'health_perspective.table.endpoint_unknown': '未知',
  'health_perspective.table.empty.title': '这个窗口没有可看的东西',
  'health_perspective.table.empty.hint': '窗口内既无流量也无路由目标。换个更长的窗口，或去 /supply/tenant-routing 确认全局默认池是否配了目标。',
  'health_perspective.table.rung.active': '首发 {pct}',
  'health_perspective.table.rung.reserve': '纯兜底',
  'health_perspective.table.rung.disabled': '已关闭',
  'health_perspective.table.rung.volume': '拨 {requests}（{share}）',
  'health_perspective.table.rung.attempt_failed': '重试失败 {attempts}（429 {rateLimited}）',
  'health_perspective.table.rung.failed': '失败 {errors}（{rate}）',
  'health_perspective.table.rung.failed_at_least': '失败 ≥{errors}（≥{rate}）',
  'health_perspective.table.rung.refused': '未拨上游 {refused}',
  'health_perspective.table.rung.failed_unknown': '失败率未知（这条链路没报未拨上游的数量，无法把闸门拒掉的和真失败分开）',
  'health_perspective.table.rung.no_traffic': '无流量',
  'health_perspective.table.rung.legs_unavailable': '这台网关没有按 SKU 拆分的链路口径，看不到各条链路实际承接多少',
  'health_perspective.table.rung.bar_aria': '实际承接 {share}（{requests} 次），其中未拨上游 {refused} 次、真失败 {errors} 次（{rate}）；配置 {configured}',
  'health_perspective.table.rung.bar_aria_unknown': '实际承接 {share}（{requests} 次），失败率未知；配置 {configured}',
  'health_perspective.table.ladder_axis_note': '梯队每条横条画在同一根轴上，满轴 = 这个 SKU 的全部请求（每个请求恰好一次首发）：条长 = 这条链路被拨到的请求占比（含首发被 429、后来被兜底救回的），灰段 = 其中没拨到上游就被闸门拒掉的（不算这条链路的失败），红段 = 真正在这条链路上失败的（含重试链跳过的失败拨号），竖线 = 配置给它的首发比例。首发链路的条长和竖线对不上，就是配置与实际背离；纯兜底链路拨到的都是重试，不作比较。',

  'health_perspective.unattributed.title': '未归属请求（未解析到 SKU）',
  'health_perspective.unattributed.share': '占全量 {value}',
  'health_perspective.unattributed.refused': '未拨上游即结束',
  'health_perspective.unattributed.rate_limited': '限流 429',
  'health_perspective.unattributed.server_errors': '上游失败',
  'health_perspective.unattributed.remainder': '其余（非失败）',
  'health_perspective.unattributed.composition_unavailable':
    '「其余」不是被拒的请求，这份数据也拆不开它：免费的 count_tokens（本地估算、不转发不计费）和尚未结束的在途请求都落在这里，流量接口没有按路由或错误码分组的口径。别把整块当成拒绝量。',
  'health_perspective.unattributed.not_graded':
    '这些请求没有解析出 SKU，因此没有重试梯队、不参与任何 SKU 的失败率，也不做健康评级 —— 一次免费的本地估算和一次余额拒绝没有共同的好坏可判。',

  'health_perspective.refusal.title': '拒绝原因',
  'health_perspective.refusal.request_not_supported': '请求不被支持',
  'health_perspective.refusal.authentication_failed': '身份校验失败',
  'health_perspective.refusal.invalid_request': '请求不合法',
  'health_perspective.refusal.insufficient_balance': '余额不足',
  'health_perspective.refusal.account_unavailable': '账号不可用',
  'health_perspective.refusal.model_not_available': '模型不可用',
  'health_perspective.refusal.uncoded': '未记录错误码',
  'health_perspective.refusal.excluded': '不计入我方失败',
  'health_perspective.refusal.counted': '计入我方失败',
  'health_perspective.refusal.entry': '{label} {count}',
  'health_perspective.refusal.rule': '只有「调用方自己的请求我们本就不该服务」这几类才不计入我方失败率：请求不被支持、身份校验失败、请求不合法、余额不足、账号不可用。其余一律计入 —— 「模型不可用」是我们没东西可路由，未记录错误码的也算我们的，新出现的错误码默认也算。',
  'health_perspective.refusal.unknown': '这台网关没报错误码分布，无法判断这些拒绝里哪些是我们自己的问题。按最保守处理：一条都不扣，全部计入我方失败率。',
  'health_perspective.refusal.unknown_short': '错误码分布未知，未做扣除',
  'health_perspective.refusal.more': '另有 {count} 类',

  'health_perspective.bar.aria': '请求 {requests}：成功 {success}、网关拒绝（未拨上游）{refused}、我方失败 {failed}；上游失败 {serverErrors}，其中兜底救回 {fallbacks}',
  'health_perspective.bar.counts': '成功 {success} · 网关拒绝 {refused} · 我方失败 {failed}',
  'health_perspective.bar.client': '客户实际失败 {rate}（含网关拒绝）· 限流 {rateLimited} · 上游 429 已兜底 {upstreamRateLimited}',
  'health_perspective.bar.legend.success': '成功',
  'health_perspective.bar.legend.refused': '网关拒绝（未拨上游，不计入下游失败率）',
  'health_perspective.bar.legend.failed': '我方失败',
  'health_perspective.bar.legend.supply': '下方细条 = 上游失败',
  'health_perspective.bar.legend.rescued': '其中被兜底救回',
  'health_perspective.bar.legend.scale': '条长 = 请求数，按平方根缩放（不是线性）：只用于比大小，准确数值看右侧列',

  'health_perspective.grade.ok': '正常',
  'health_perspective.grade.warn': '警告',
  'health_perspective.grade.bad': '严重',
  'health_perspective.grade.idle': '无流量',
  'health_perspective.grade.unknown': '未知',

  'health_perspective.node.requests': '请求数',
  'health_perspective.node.observed_share': '实测承接',
  'health_perspective.node.downstream_error_rate': '下游失败率（计入我方）',
  'health_perspective.node.refused_before_upstream': '网关拒绝（未拨上游）',
  'health_perspective.node.tenant_error_rate': '客户侧失败率',
  'health_perspective.node.rate_limited_rate': '限流率 429',
  'health_perspective.node.upstream_rate_limited': '上游 429 已兜底',
  'health_perspective.node.upstream_rate_limited_note': '重试失败 {attempts} · 上游拒绝 401/402/403/429 {refused}',
  'health_perspective.node.server_error_rate': '上游失败率',
  'health_perspective.node.fallback_rate': '兜底率',
  'health_perspective.node.first_byte': '首包均值',
  'health_perspective.node.margin': '毛利率',
  'health_perspective.node.revenue': '收入',
  'health_perspective.node.cost': '采购成本',
  'health_perspective.node.profit': '毛利',
  'health_perspective.node.coverage': '可服务上游数',
  'health_perspective.node.coverage.covered': '多点覆盖',
  'health_perspective.node.coverage.single_point': '单点供应',
  'health_perspective.node.coverage.unrouted': '未挂链路',
  'health_perspective.node.targets_active': '首发目标',
  'health_perspective.node.targets_reserve': '兜底目标',
  'health_perspective.node.targets_disabled': '已关闭目标',
  'health_perspective.node.policy': '重试策略',
  'health_perspective.node.policy.full_chain': '整链重试',
  'health_perspective.node.policy.network_only': '仅网络错误重试',
  'health_perspective.node.policy.same_cache_domain': '同缓存域内重试',
  'health_perspective.node.policy.fail_fast': '快速失败',
  'health_perspective.node.serves_skus': '承接 SKU 数',
  'health_perspective.node.model_count': '渠道下模型数',
  'health_perspective.node.channel': '上游渠道',
  'health_perspective.node.upstream_model': '上游模型',
  'health_perspective.node.capability': 'capability 状态',
  'health_perspective.node.legacy_bucket': '未解析到 SKU 的请求',
  'health_perspective.node.unattributed_upstream': '未归属上游',
  'health_perspective.node.fallback_fired': '已触发兜底',

  'health_perspective.inspector.empty.title': '选一行',
  'health_perspective.inspector.empty.hint': '点表格里任意 SKU 或梯队上的上游模型，这里显示它的健康、梯队和毛利。',
  'health_perspective.inspector.section.traffic': '实测流量',
  'health_perspective.inspector.section.traffic_channel': '实测流量（渠道级，非该模型自身）',
  'health_perspective.inspector.borrowed_note': '这里的数字是所在「渠道」的合计，不是这个上游模型自己的 —— 流量表没有渠道×模型这一档。想看某个 SKU 在这条链路上实际打了多少、失败多少，回表格看该 SKU 的梯队。',
  'health_perspective.inspector.section.money': '毛利',
  'health_perspective.inspector.section.supply': '供应结构',
  'health_perspective.inspector.section.chain': '重试梯队',
  'health_perspective.inspector.chain.priority': '优先级 {value}',
  'health_perspective.inspector.chain.capability': 'capability {status}',
  'health_perspective.inspector.chain.empty': '这个 SKU 在全局默认池里没有任何目标。',
  'health_perspective.inspector.divergence.title': '配置与实测背离',
  'health_perspective.inspector.divergence.hint': '配置给了 {configured}，实际承接 {observed}。跑多了通常是首发在失败、请求顺着重试链掉到这里；跑少了先查熔断器和钉住的 capability 状态。',
  'health_perspective.inspector.open_sku': '打开 SKU 详情',
  'health_perspective.inspector.open_upstream': '打开上游详情',
  'health_perspective.inspector.open_traffic': '在流量监控里下钻',
  'health_perspective.inspector.open_routing': '打开路由配置',

  'health_perspective.risks.title': '网关已判定的风险',
  'health_perspective.risks.empty': '这个窗口没有越过阈值的项。',
  'health_perspective.risks.kind.error_rate': '错误率',
  'health_perspective.risks.kind.fallback_rate': '兜底率',
  'health_perspective.risks.kind.slow_first_byte_rate': '首包慢请求率',
  'health_perspective.risks.threshold': '当前 {current}，阈值 {threshold}',

  'quotas.title': '配额中心',
  'quotas.sub': '一个地方看清、改好所有租户配额：先按客户看透视，再决定改哪一层。默认没人被限。',
  'quotas.banner': '配额旁路已开启。操作人 {by}，自 {at} 起。期间本应拒绝 {count} 次。',
  'quotas.banner.unknown_operator': '未知',
  'quotas.bypass.title': '紧急旁路',
  'quotas.bypass.hint': '一键放行所有 RPM/TPM 与任务准入限制 · 30s 内全网生效 · 审计留痕',
  'quotas.bypass.on': '旁路已开启 — 所有配额暂停执行，本应拒绝的请求在计数',
  'quotas.bypass.off': '旁路关闭 — 配额正常执行',
  'quotas.bypass.by': '操作人 {by}',
  'quotas.bypass.at': '自 {at}',
  'quotas.bypass.would_reject': '开启以来本应拒绝 {count} 次',
  'quotas.bypass.admin_only': '开启后顶部红条常驻，所有角色都能看到；只有 ops-admin 能开关',
  'quotas.search.title': '查找客户 / SKU',
  'quotas.search.org_placeholder': '搜索客户：名称 / 邮箱 / org id …',
  'quotas.search.sku_placeholder': '搜索 SKU：模型名 / 命名空间 …',
  'quotas.search.hint': '选中即打开抽屉；不用记任何 id',
  'quotas.search.tpm_unit': 'TPM 单位：百万 token/分钟（M）',
  'quotas.drawer.task_lane': '按并发准入管理',
  'quotas.task_lane.edit_protocol': '编辑默认并发（模型协议）',
  'quotas.task_lane.ignores_rpm': '该 SKU 不应设 RPM/TPM',
  'quotas.task_lane.clear_leftover': '清除遗留 RPM/TPM',
  'quotas.image_lane.rpm_only': '图片 SKU 只按 RPM 限额',
  'quotas.image_lane.ignores_tpm': '该 SKU 不吃 TPM',
  'quotas.search.org_empty': '没有匹配的客户',
  'quotas.search.sku_empty': '没有匹配的 SKU',
  'quotas.search.has_override': '有专属',
  'quotas.search.has_sku_default': '有默认',
  'quotas.dedicated.title': '专属配额客户',
  'quotas.dedicated.search': '按名称 / 邮箱筛选',
  'quotas.dedicated.org': '客户',
  'quotas.dedicated.org_all': '整体 RPM / TPM（含缓存）/ 非缓存 TPM',
  'quotas.dedicated.sku_count': '专属 SKU',
  'quotas.dedicated.refusals_24h': '近 24h 被拒',
  'quotas.dedicated.updated': '最近更新',
  'quotas.dedicated.open': '打开 →',
  'quotas.dedicated.empty': '还没有客户设置专属配额',
  'quotas.dedicated.none_match': '没有匹配的客户',
  'quotas.dedicated.more': '+{n}',
  'quotas.refusals.title': '最近被限的客户',
  'quotas.refusals.window.1h': '1 小时',
  'quotas.refusals.window.24h': '24 小时',
  'quotas.refusals.window.7d': '7 天',
  'quotas.refusals.org': '客户',
  'quotas.refusals.sku': 'SKU',
  'quotas.refusals.kind': '原因',
  'quotas.refusals.count': '次数',
  'quotas.refusals.last': '最近一次',
  'quotas.refusals.limit': '当时生效的限额',
  'quotas.refusals.empty': '这个窗口没有客户被限。',
  'quotas.refusals.loading': '正在加载…',
  'quotas.refusals.adjust': '调整 →',
  'quotas.refusals.kind.l1_reject_minute': 'RPM/TPM 超限',
  'quotas.refusals.dimension.tpm_uncached': '其中非缓存 TPM {count}',
  'quotas.refusals.kind.task_admission_reject': '任务并发超限',
  'quotas.refusals.kind.quota_bypass_would_reject': '旁路放行',
  'quotas.refusals.kind.task_admission_bypass_would_reject': '旁路放行',
  'quotas.editor.title': '默认限额（对所有客户生效）',
  'quotas.editor.tab.hint': '客户专属设置永远优先于这里',
  'quotas.editor.tab.global': '全局兜底 *×*',
  'quotas.editor.tab.sku': '按 SKU *×sku',
  'quotas.editor.global_audience': '新注册 / 没单独配过的客户',
  'quotas.editor.global_audience_hint': '对每个 SKU 各自独立计数',
  'quotas.editor.cover_counts': '当前有 {skus} 个 SKU、{orgs} 个客户覆盖了它',
  'quotas.editor.covering': '覆盖了它的客户',
  'quotas.editor.covering_none': '—',
  'quotas.editor.group_meta': '{count} 个 SKU · {overrides} 有设置',
  'quotas.editor.rpm': 'RPM',
  'quotas.editor.tpm': 'TPM（含缓存）',
  'quotas.editor.tpm_uncached': '非缓存 TPM',
  'quotas.editor.tpm_uncached_hint': '非缓存 TPM 只算输入侧未命中缓存的 token（不含输出、推理、缓存读），请求结束后才扣，可欠账；欠账期间拒绝新请求。',
  'quotas.editor.save': '保存',
  'quotas.editor.clear': '清除（=不限）',
  'quotas.editor.inherit': '继承',
  'quotas.editor.fill_peak': '按 7 天峰值小时 × {multiplier} 填入',
  'quotas.editor.fill_peak.multiplier': '倍数',
  'quotas.editor.fill_peak.need_org': '先选一个客户',
  'quotas.editor.none': '这一层还没有行。',
  'quotas.editor.source.org_sku': '客户专属',
  'quotas.editor.source.org_all': '客户整体',
  'quotas.editor.source.sku': 'SKU 默认',
  'quotas.editor.source.global': '全局兜底',
  'quotas.editor.source.none': '不限',
  'quotas.defaults.footer': '按客户 / 客户×SKU 两层不再是独立表格——都在客户抽屉里改。',
  'quotas.drawer.org_all': '客户整体限额 org×*',
  'quotas.drawer.org_all_hint': '所有 SKU 合计共用一个桶；留空 = 不设，按各 SKU 默认走',
  'quotas.drawer.org_all_preview': '该客户所有 SKU 合计不超过 {rpm} RPM；有专属值的 SKU 不受影响',
  'quotas.drawer.filter': '筛选 SKU 名 / 命名空间',
  'quotas.drawer.only_overridden': '只看有专属设置的',
  'quotas.drawer.rule': '生效值 = 专属 → 客户整体 → SKU 默认 → 全局；RPM / TPM（含缓存）/ 非缓存 TPM 各自独立回落',
  'quotas.drawer.sku': 'SKU',
  'quotas.drawer.effective_rpm': '生效 RPM',
  'quotas.drawer.effective_tpm': '生效 TPM（含缓存）',
  'quotas.drawer.effective_tpm_uncached': '生效非缓存 TPM',
  'quotas.drawer.source': '来源',
  'quotas.drawer.org_sku_rpm': '专属 RPM',
  'quotas.drawer.org_sku_tpm': '专属 TPM（含缓存）',
  'quotas.drawer.org_sku_tpm_uncached': '专属非缓存 TPM',
  'quotas.drawer.task': '任务并发上限',
  'quotas.drawer.refusals_1h': '近 1h',
  'quotas.drawer.refusals_stat': '近 1h 被拒',
  'quotas.drawer.peak_stat': '7 天峰值 RPM',
  'quotas.drawer.sku_stat': '可见 SKU',
  'quotas.drawer.rejected': '拒 {count}',
  'quotas.drawer.save': '保存全部改动',
  'quotas.drawer.discard': '放弃',
  'quotas.drawer.dirty': '有未保存的改动 — 生效值列已按草稿实时预览',
  'quotas.drawer.clean': '没有未保存的改动',
  'quotas.drawer.saved': '已保存 · 30s 内全网生效',
  'quotas.drawer.unsaved_title': '放弃未保存的改动？',
  'quotas.drawer.unsaved_body': '关闭抽屉会丢掉尚未保存的限额草稿。',
  'quotas.drawer.unsaved_confirm': '放弃改动',
  'quotas.link_edit': '在配额中心编辑 →',
  'quotas.toast.saved': '已保存',
  'quotas.toast.cleared': '已清除',
  'quotas.toast.invalid': 'RPM 必须是 ≥ 1 的整数；TPM 为百万 token/分钟（M），最多三位小数；均可留空。',
  'quotas.toast.invalid_row': '{row} 的 RPM/TPM 无效，未写入任何改动',
  'quotas.toast.bypass_on': '配额旁路已开启',
  'quotas.toast.bypass_off': '配额旁路已关闭',
  'quotas.toast.peak_filled': '已按 7 日峰值小时填入，请确认后保存',
  'quotas.toast.peak_failed': '读不到该客户的用量峰值',
  'guaranteed.title': '重点保障中心',
  'guaranteed.subtitle': '探针、链内熔断、Jev 三个机制三态：关 / 影子（只记不动）/ 摘除（退役首发，留在重试链尾，可自愈）。终极兜底：关 / 影子 / 开启（非 2xx 时逐腿重试，不摘任何腿）。',
  'guaranteed.create_sku': '新增 SKU',
  'guaranteed.create_org': '新增客户覆盖',
  'guaranteed.create_sku_title': '新增 SKU',
  'guaranteed.create_org_title': '新增客户覆盖',
  'guaranteed.customer': '客户',
  'guaranteed.sku': 'SKU',
  'guaranteed.note': '备注',
  'guaranteed.delete': '删除',
  'guaranteed.delete_confirm': '删除这条重点保障？关闭后该范围恢复普通路由。',
  'guaranteed.edit_note': '改备注',
  'guaranteed.save': '保存',
  'guaranteed.cancel': '取消',
  'guaranteed.close': '关闭',
  'guaranteed.org_placeholder': '搜索客户名、org id 或邮箱',
  'guaranteed.sku_placeholder': '选择文本 SKU',
  'guaranteed.note_placeholder': '可选备注',
  'guaranteed.empty': '还没有重点保障条目',
  'guaranteed.saved': '已保存',
  'guaranteed.deleted': '已删除',
  'guaranteed.config_count': '{sku} 个 SKU · {org} 条客户覆盖',
  'guaranteed.col.sku': 'SKU / 备注',
  'guaranteed.col.probe': '探针',
  'guaranteed.col.chain': '链内熔断',
  'guaranteed.col.jev': 'Jev 判官',
  'guaranteed.col.stats': '最近 24h',
  'guaranteed.stats_hint_label': '最近 24h',
  'guaranteed.stats_hint': '兜底重试：首腿失败后被终极兜底带去重试链的请求。仍失败：整条链走完仍失败。影子本会重试：兜底处于影子时本来会重试的数量（未拨号）。',
  'guaranteed.stat.requests': '请求',
  'guaranteed.stat.retry': '兜底重试',
  'guaranteed.stat.failed': '仍失败',
  'guaranteed.stat.shadow': '影子本会重试',
  'guaranteed.probe_spend.title': '探针花费 · 最近 {hours}h',
  'guaranteed.probe_spend.window': '{since} – {until}',
  'guaranteed.probe_spend.col.leg': '下游 SKU / 上游腿',
  'guaranteed.probe_spend.col.shots': '发数',
  'guaranteed.probe_spend.col.tokens': '输入 / 输出 tokens',
  'guaranteed.probe_spend.col.logged': '挂牌价计',
  'guaranteed.probe_spend.col.procurement': '采购成本',
  'guaranteed.probe_spend.col.charged': '系统账户扣款（挂牌价）',
  'guaranteed.probe_spend.col.allocated': '已扣上游钱包',
  'guaranteed.probe_spend.col.status': '对账',
  'guaranteed.probe_spend.total': '合计',
  'guaranteed.probe_spend.no_sku': '（无 SKU）',
  'guaranteed.probe_spend.leg_count': '{count} 条腿',
  'guaranteed.probe_spend.unpriced': '无采购价 {count} 发',
  'guaranteed.probe_spend.pre_fix': '修复前 {count} 发',
  'guaranteed.probe_spend.count': '{count} 笔',
  'guaranteed.probe_spend.wallet_note': '上游钱包按『已扣上游钱包』扣减；它应等于采购成本（按当日汇率折人民币）。',
  'guaranteed.probe_spend.status.pending': '待分摊 {count} 笔',
  'guaranteed.probe_spend.status.mismatch_count': '差 {diff} 笔',
  'guaranteed.probe_spend.status.match': '一致',
  'guaranteed.probe_spend.status.mismatch': '差 {diff}',
  'guaranteed.probe_spend.status.uncharged': '未扣款',
  'guaranteed.probe_spend.status.no_funding_batch': '无充值批次 {count} 笔',
  'guaranteed.probe_spend.status.no_funding_batch_hint': '分摊器已处理，但该上游没有充值批次，钱包余额无法扣减',
  'guaranteed.probe_spend.status.unbilled': '应扣未扣 {count} 发',
  'guaranteed.probe_spend.status.unbilled_hint': '修复后写入、消耗了 token 却没有扣款的探针（查不到挂牌价或采购价），上游钱包没有因此扣减',
  'guaranteed.probe_spend.status.unknown': '账本不可读',
  'guaranteed.probe_spend.empty': '窗口内没有探针花费。',
  'guaranteed.probe_spend.loading': '加载中…',
  'guaranteed.probe_spend.error': '探针花费读取失败。',
  'guaranteed.probe_spend.copy': '复制 SQL',
  'guaranteed.probe_spend.expand_all': '全部展开',
  'guaranteed.probe_spend.collapse_all': '全部收起',
  'guaranteed.probe_spend.reconcile.title': '按表核对',
  'guaranteed.probe_spend.reconcile.columns': '挂牌价计 = usage_logs.cost_usd_micros 之和；系统账户扣款（挂牌价）= 系统账户 reason=probe 的扣款（balance_transactions.amount_usd_micros 取负）及笔数；采购成本 = 带完整采购快照的行按 cost_usd_micros × upstream_sku_cost_multiplier × upstream_account_rate_multiplier 求和（美元）；已扣上游钱包 = 这些扣款在 revenue_ledger 里带 source_batch_id 的切片的 cny_upstream_cost_micros（人民币，按批次汇率）及已分摊笔数——上游钱包就按这一列扣减。对账按笔数走完整条链，不涉及汇率：可扣款的发数 = 扣款笔数 = 已分摊笔数，且扣款金额 = 可扣款行的挂牌价计，为一致；修复后写入、消耗了 token 却没扣款的行（查不到挂牌价或采购价，unpriced_shots），为应扣未扣 N 发；扣款对了、分摊器也处理了，但该上游没有充值批次（revenue_ledger 里只有 source_batch_id 为空的已结算行，overflow_charges），为无充值批次 N 笔——钱包扣不下去，要补录充值批次；扣款对了但分摊器（独立定时任务）还没处理到的（只有扣款时的占位行），为待分摊 N 笔；两边都为 0 为未扣款；其余为差。修复前 N 发 = cost_snapshot_source 为空的行（修复前写入，本来就不扣款）；无采购价 N 发 = 修复后写入、消耗了 token 但没有挂牌价或采购价的行。两张表归不同服务，下面两条 SQL 就是页面上数字的来源，窗口已代入，可直接在库里跑。',
  'guaranteed.probe_spend.reconcile.log_sql': '探针日志（gateway · usage_logs）',
  'guaranteed.probe_spend.reconcile.ledger_sql': '扣款与分摊（platform · balance_transactions / revenue_ledger）',
  'guaranteed.probe_spend.reconcile.ledger_missing': 'platform 账本这次没读到，已扣款 / 已分摊两列显示为 —，对账状态为「账本不可读」。',
  'guaranteed.probe.mode': '探针模式',
  'guaranteed.probe.quality_tip': '按坏发比例判：窗口内这条腿本区探针的失败/超时/空回/超慢发数占比达到摘除线、且坏发不少于最少坏发数就摘（总发数不够不判；故障期间的发不计入）。恢复只看最近的恢复判定窗口：坏率回到恢复线以下、且最近连续通过轮数达标才恢复，一轮好不算。恢复线不能高于摘除线。',
  'guaranteed.probe.slow_tip': '2xx 但慢于这条线的一发算失败：计入质量坏率，也计入“一轮全失败”的摘除判断；战争波次的每一发也在这条线中止。不能超过单发超时（默认慢线 16 秒、超时 20 秒，留出余量记下 16 秒以上的真实耗时）。',
  'guaranteed.probe.first_byte_late_tip': '只对流式请求生效：请求已发给上游，超过这个时长还没收到首字节，就给这条腿记 1 发开战证据（计入 60 秒错数 / 错率和 15 秒快速道）。不中断请求，不影响结果、计费、熔断器、链内熔断和 Jev；这一发之后真的失败也不重复计。建议：快模型 15 秒（15000）；推理 / 大模型不建议开。它不是首字节超时（负载设置里另配）。',
  'guaranteed.probe.timing_tip': '一轮内每两发之间隔多少秒（0 = 连发），让上游一次短暂卡顿打不中整轮；单发超过超时秒数未返回即算这一发失败。一轮最长 {budget} 秒（发数 × 超时 + 间隔 + 30 秒结算），必须放得进探针间隔，放不进的改动不会提交。',
  'guaranteed.probe.confirm_tip': '一轮全部失败但还没到摘除轮数时，下一轮不等整个间隔，在这一轮结束后这么多秒就复查（最长不超过探针间隔；也不会早于上一轮开始后 {budget} 秒，即一轮的最长用时）。',
  'guaranteed.probe_shape': '探针形状',
  'guaranteed.probe_shape_ping': 'ping',
  'guaranteed.probe_shape_arithmetic': '算术',
  'guaranteed.probe_shape_echo': '回显',
  'guaranteed.probe_shape_custom': '自定义',
  'guaranteed.probe_custom_text': '自定义文本',
  'guaranteed.probe_custom_max': 'max tokens',
  'guaranteed.probe_custom_empty': '自定义探针文本不能为空',
  'guaranteed.probe_custom_invalid': '自定义文本须不超过 200 字且不含控制字符',
  'guaranteed.probe_custom_max_invalid': 'max_tokens 须为 1 到 64 的整数',
  'guaranteed.probe_summary.minutes': '{n} 分',
  'guaranteed.probe_summary.seconds': '{n} 秒',
  'guaranteed.probe_summary.normal': '常规 {every}',
  'guaranteed.probe_summary.alert': '警戒 {every}',
  'guaranteed.probe_summary.war': '战争 {shots}×{every} 秒',
  'guaranteed.probe_summary.quality': '质量 {window} 分 {pct}%',
  'guaranteed.probe_flow.normal': '常规',
  'guaranteed.probe_flow.war': '战争',
  'guaranteed.probe_flow.alert': '警戒',
  'guaranteed.probe_flow.every': '每 {every}',
  'guaranteed.probe_flow.war_cadence': '{shots} 发 / {every} 秒',
  'guaranteed.probe_flow.to_war': '真实流量 {window} 秒 {first} 错且错率 ≥ {pct}%，或最近 {fast_window} 秒 ≥ {fast_min} 错且 ≥ {fast_pct}%（兜底腿 {fallback} 错）/ 上游 4xx 服务端签名 1 发 / 熔断器打开 / Jev 判定',
  'guaranteed.probe_flow.to_alert': '{waves} 波正常或满 {max}',
  'guaranteed.probe_flow.to_normal': '平静 {calm} 分钟',
  'guaranteed.probe_flow.cooldown': '冷却：打完一场战争后至少 {base} 分钟内不再开战（短时间反复开战会越冷越久），这期间再触发只留在警戒。',
  'guaranteed.probe_effect.normal': '真宕机约 {fast}–{slow} 分钟摘（连续 {rounds} 轮全失败，按每发都超时估）',
  'guaranteed.probe_effect.quality': '常规节奏下窗口内约 {shots} 发，≥ {bad} 发坏即摘（近 {window} 分钟）',
  'guaranteed.probe_effect.quality_recover': '；恢复：近 {recover_window} 分钟坏率 ≤ {pct}%（至少 {shots} 发）且连续 {rounds} 轮通过',
  'guaranteed.probe_effect.quality_unjudged': '常规节奏下 {window} 分钟窗口只有约 {shots} 发，不到 {min} 发，质量判据基本不会触发',
  'guaranteed.probe_effect.alert': '每 {every}一轮，真宕机约 {fast}–{slow} 分钟摘；平静 {calm} 分钟回常规',
  'guaranteed.probe_effect.alert_late_off': '；首字节迟到：关',
  'guaranteed.probe_effect.alert_late_on': '；流式请求 {seconds} 秒没首字节计 1 错',
  'guaranteed.probe_effect.war': '触发后约 {fast}–{slow} 秒摘；需持续坏 ≥ {sustain} 秒',
  'guaranteed.probe_effect.war_one_wave': '触发后约 {fast}–{slow} 秒摘；一波坏就摘，短卡顿也会命中',
  'guaranteed.probe_effect.war_end': '。连续好 {exit} 秒或满 {max}结束（到点不再开新波；上一波已坏则补打判定波，最多 {extra} 波），一场最多约 {shots} 发',
  'guaranteed.probe_effect.cooldown': '战后冷却依次 {sequence} 分钟；连续 {reset} 分钟没有新触发（含被冷却挡下的）才从头算，持续出问题会一直停在上限',
  'guaranteed.probe_settings.open': '探测设置',
  'guaranteed.probe_settings.title': '{sku} · 探测设置',
  'guaranteed.probe_settings.description': '三个层级都能摘除，但只凭探针请求判断：流中途报错、只针对真实请求形状的故障，探针看不到（这类靠链内熔断 / Jev）。常规按间隔探；真实流量报错或 Jev 判定就开战（并发波次）；战后转警戒加密探，平静后回常规。三条快线（固定规则，不可配）：① 签名快线——上游回 4xx 但错误类型是服务端类（server_error / api_error / internal_error / internal_server_error），1 发就开战，不受两主机门槛限制，仍受冷却限制；② 15 秒快速道——60 秒错数够线时，最近 15 秒 ≥ 3 错且错率 ≥ 60% 也开战，不必等 60 秒错率；③ 熔断快线——熔断器因连续上游 5xx（至少达到熔断的连续失败门槛）把这条腿从关闭转为打开，即开战；这串失败里夹着 429、408、超时、连接失败，或流内被改写成 5xx 的失败（流在首帧前断开、流内限流 Throttling、流内超时），就不算；和真实流量一样要过两主机门槛（两台主机都见到失败，或这条腿只有一台主机在服务），也受冷却限制。开战只是加密探测，摘不摘仍由波次决定（影子只打标）。',
  'guaranteed.probe_settings.close': '关闭',
  'guaranteed.probe_settings.save': '保存',
  'guaranteed.probe_settings.changed': '已改 {n} 项，未保存',
  'guaranteed.probe_settings.unchanged': '没有未保存的修改',
  'guaranteed.probe_settings.restore': '恢复默认',
  'guaranteed.probe_settings.default': '默认 {value}',
  'guaranteed.probe_settings.probes_off': '探针当前关闭：这里的设置可以先保存，打开探针后才生效。',
  'guaranteed.probe_settings.discard_title': '放弃未保存的修改？',
  'guaranteed.probe_settings.discard_body': '还有 {n} 项修改没保存，关闭后会丢掉。',
  'guaranteed.probe_settings.discard_confirm': '放弃修改并关闭',
  'guaranteed.probe_settings.discard_cancel': '继续编辑',
  'guaranteed.probe_settings.section.normal': '常规',
  'guaranteed.probe_settings.section.normal_hint': '按间隔一轮一轮探；一轮里每发都失败才算这一轮失败。',
  'guaranteed.probe_settings.section.quality': '质量判据',
  'guaranteed.probe_settings.section.quality_hint': '按窗口内坏发（失败/超时/空回/超慢）占比判，抓间歇性卡顿。',
  'guaranteed.probe_settings.section.alert': '警戒',
  'guaranteed.probe_settings.section.alert_hint': '战争结束后按更短间隔探；真实流量报错达到阈值（60 秒线或 15 秒快速道）、上游 4xx 服务端签名 1 发、或流式请求首字节迟到计错够线，都会开战（冷却中则留在警戒）。',
  'guaranteed.probe_settings.section.war': '战争',
  'guaranteed.probe_settings.section.war_hint': '真实流量、上游 4xx 服务端签名或 Jev 触发：并发波次快速判定，连续几波坏就摘。',
  'guaranteed.probe_settings.section.cooldown': '战争冷却',
  'guaranteed.probe_settings.section.cooldown_hint': '一场战争后多久内不再开战；短时间内反复开战，冷却按倍数变长。',
  'guaranteed.probe_settings.field.interval': '探测间隔',
  'guaranteed.probe_settings.field.shots': '每轮发数',
  'guaranteed.probe_settings.field.spacing': '发间隔',
  'guaranteed.probe_settings.field.timeout': '单发超时',
  'guaranteed.probe_settings.field.slow': '慢线（慢于算失败）',
  'guaranteed.probe_settings.field.confirm': '失败后复查',
  'guaranteed.probe_settings.field.fail_rounds': '连续几轮失败摘除',
  'guaranteed.probe_settings.field.pass_rounds': '连续几轮通过恢复',
  'guaranteed.probe_settings.field.quality_window': '窗口',
  'guaranteed.probe_settings.field.quality_min': '最少发数',
  'guaranteed.probe_settings.field.quality_trip': '摘除坏率',
  'guaranteed.probe_settings.field.quality_recover': '恢复坏率',
  'guaranteed.probe_settings.field.quality_min_bad': '最少坏发数',
  'guaranteed.probe_settings.field.quality_recover_window': '恢复判定窗口',
  'guaranteed.probe_settings.field.alert_interval': '警戒探测间隔',
  'guaranteed.probe_settings.field.alert_calm': '平静时长',
  'guaranteed.probe_settings.field.alert_failures': '开战阈值（首发腿 60 秒错数，兜底腿 ×2）',
  'guaranteed.probe_settings.field.alert_fail_pct': '开战错率（60 秒内这条腿的错数占比，429 / 408 不计）',
  'guaranteed.probe_settings.field.alert_first_byte_late': '首字节迟到（流式请求多久没首字节计 1 错，0 = 关）',
  'guaranteed.probe_settings.field.wave_shots': '每波发数',
  'guaranteed.probe_settings.field.wave_every': '波间隔',
  'guaranteed.probe_settings.field.wave_fail': '坏波阈值（一波失败几发）',
  'guaranteed.probe_settings.field.waves_to_trip': '连续几波坏摘除',
  'guaranteed.probe_settings.field.war_max': '最长持续',
  'guaranteed.probe_settings.field.exit_good': '连续几波好提前结束',
  'guaranteed.probe_settings.field.cool_base': '基础冷却',
  'guaranteed.probe_settings.field.cool_factor': '倍数',
  'guaranteed.probe_settings.field.cool_max': '冷却上限',
  'guaranteed.probe_settings.field.cool_reset': '重置时长',
  'guaranteed.probe_settings.unit.minutes': '分钟',
  'guaranteed.probe_settings.unit.seconds': '秒',
  'guaranteed.probe_settings.unit.ms': '毫秒',
  'guaranteed.probe_settings.unit.shots': '发',
  'guaranteed.probe_settings.unit.rounds': '轮',
  'guaranteed.probe_settings.unit.pct': '%',
  'guaranteed.probe_settings.unit.failures': '错',
  'guaranteed.probe_settings.unit.waves': '波',
  'guaranteed.probe_settings.unit.times': '倍',
  'guaranteed.probe_settings.err.integer': '请输入整数',
  'guaranteed.probe_settings.err.range': '范围 {min}–{max}',
  'guaranteed.probe_settings.err.first_byte_late': '0（关）或 {min}–{max}',
  'guaranteed.probe_settings.err.round_budget': '一轮最长 {budget} 秒（{shots} 发 × {timeout} 秒超时 + 发间隔 {spacing} 秒 + 30 秒结算），放不进 {interval} 秒的间隔：间隔至少 {minutes} 分钟，或减少发数 / 缩短超时和发间隔',
  'guaranteed.probe_settings.err.confirm_interval': '不能超过探测间隔（{interval} 秒）',
  'guaranteed.probe_settings.err.alert_budget': '不能短于一轮最长用时（{budget} 秒）',
  'guaranteed.probe_settings.err.slow_timeout': '慢线（{slow} 毫秒）不能超过单发超时（{timeout} 秒 = {max} 毫秒）：一发到超时就中止，更慢的线永远到不了；请调大单发超时或调低慢线',
  'guaranteed.leg.level.war': '战争中',
  'guaranteed.leg.level.war_waves': '战争 · 第 {wave} 波 {failed}/{shots} 败',
  'guaranteed.leg.level.war_tip': '真实流量报错或 Jev 判定触发了战争：并发波次探测，连续坏波达到阈值就摘；好波够数或到时转警戒。',
  'guaranteed.leg.level.alert': '警戒',
  'guaranteed.leg.level.alert_since': '警戒 · 自 {since}',
  'guaranteed.leg.level.alert_tip': '战争结束后（或冷却中又被触发）处于警戒：按警戒间隔加密探；平静时长内没有新触发、也没有失败轮就回常规，失败轮会重新计时。',
  'guaranteed.leg.level.cooldown': '冷却中 · 至 {until} · 第 {n} 次',
  'guaranteed.leg.level.cooldown_tip': '刚打完战争：冷却结束前再被触发只留在警戒、不再开战。第 N 次 = 重置时长内的战争次数，次数越多冷却越长。',
  'guaranteed.legs.kv.levels': '层级',
  'guaranteed.events.chip.level_war': '开战 · 并发探测',
  'guaranteed.events.chip.level_war_end': '战争结束 · 转警戒',
  'guaranteed.events.chip.level_alert': '进入警戒',
  'guaranteed.events.chip.level_normal': '回到常规',
  'guaranteed.events.chip.level_change': '探测层级变化',
  'guaranteed.events.chip.war_blocked': '冷却中 · 未开战',
  'guaranteed.events.kind.probe_level': '探测层级',
  'guaranteed.events.kind.probe_war_blocked': '冷却拦下开战',
  'guaranteed.events.detail.trip_level.normal': '常规轮摘除',
  'guaranteed.events.detail.trip_level.alert': '警戒中摘除',
  'guaranteed.events.detail.trip_level.war': '战争中摘除',
  'guaranteed.events.detail.shadow_level.normal': '常规轮判应摘',
  'guaranteed.events.detail.shadow_level.alert': '警戒中判应摘',
  'guaranteed.events.detail.shadow_level.war': '战争中判应摘',
  'guaranteed.events.detail.waves': '波次 {waves} 败',
  'guaranteed.events.detail.war_seconds': '开战 {seconds} 秒',
  'guaranteed.events.detail.flap': '放回后 {seconds} 秒又被{by}摘除，下次放回需等 {hold} 分钟（第 {flap} 次）',
  'guaranteed.events.detail.flap_expiry': '到期放回后 {seconds} 秒又被{by}摘除，下次放回需等 {hold} 分钟（第 {flap} 次）',
  'guaranteed.events.detail.flap_capped': '放回后 {seconds} 秒又被{by}摘除（第 {flap} 次）：探针放回要等 {hold} 分钟，{expiry} 分钟到期会先放回',
  'guaranteed.events.detail.flap_no_probe': '放回后 {seconds} 秒又被{by}摘除（第 {flap} 次）：探针未设为摘除，只在 {expiry} 分钟到期时放回',
  'guaranteed.events.detail.flap_expiry_capped': '到期放回后 {seconds} 秒又被{by}摘除（第 {flap} 次）：探针放回要等 {hold} 分钟，{expiry} 分钟到期会先放回',
  'guaranteed.events.detail.flap_expiry_no_probe': '到期放回后 {seconds} 秒又被{by}摘除（第 {flap} 次）：探针未设为摘除，只在 {expiry} 分钟到期时放回',
  'guaranteed.events.detail.source.traffic': '真实流量 {seconds} 秒 {failures} 错',
  'guaranteed.events.detail.source.traffic_plain': '真实流量报错',
  'guaranteed.events.detail.source.jev': 'Jev 判定上游故障',
  'guaranteed.events.detail.source.signature': '上游 {status} 带服务端错误类型（{signature}），1 发开战',
  'guaranteed.events.detail.source.signature_plain': '上游 4xx 服务端错误签名',
  'guaranteed.events.detail.source.breaker': '熔断器因连续上游 5xx 打开（关闭 → 打开）',
  'guaranteed.events.detail.source.timer': '战争到时',
  'guaranteed.events.detail.source.calm': '平静期满',
  'guaranteed.events.detail.source.good_waves': '连续好波',
  'guaranteed.events.detail.source.cooldown_block': '冷却中，留在警戒',
  'guaranteed.events.detail.source.off': '探针已关闭',
  'guaranteed.events.detail.source.removed': '这条腿已不在探测范围',
  'guaranteed.events.detail.source.reset': '运维重置（层级与冷却一并清除）',
  'guaranteed.events.detail.ops_reset': '运维重置：质量判定从此刻重新计',
  'guaranteed.events.detail.ops_reset_trip': '运维重置：解除{by}摘除，质量判定从此刻重新计',
  'guaranteed.events.detail.ops_reset_shadow': '运维重置：清除{by}影子标记，质量判定从此刻重新计',
  'guaranteed.events.detail.source.aborted': '战争中断（执行进程已退出，未出结论）：不计入冷却',
  'guaranteed.events.detail.source.aborted_cooled': '战争再次中断（连续第 2 次，执行进程未能续租）：按一次战争计入冷却',
  'guaranteed.events.detail.cooldown': '冷却至 {until}（第 {n} 次）',
  'guaranteed.write.field.probe_alert_calm': '平静时长（分钟）',
  'guaranteed.write.field.probe_wave_shots': '每波发数',
  'guaranteed.write.field.probe_wave_every': '波间隔（秒）',
  'guaranteed.write.field.probe_wave_fail': '坏波阈值',
  'guaranteed.write.field.probe_waves_to_trip': '连续坏波摘除',
  'guaranteed.write.field.probe_war_max': '战争最长（秒）',
  'guaranteed.write.field.probe_exit_good': '连续好波结束',
  'guaranteed.write.field.probe_cool_base': '基础冷却（分钟）',
  'guaranteed.write.field.probe_cool_factor': '冷却倍数',
  'guaranteed.write.field.probe_cool_max': '冷却上限（分钟）',
  'guaranteed.write.field.probe_cool_reset': '冷却重置（分钟）',
  'guaranteed.chain_mode': '链内熔断',
  'guaranteed.fallback_mode': '终极兜底',
  'guaranteed.fallback': '终极兜底',
  'guaranteed.inherit': '继承',
  'guaranteed.jev.mode': 'Jev 判官',
  'guaranteed.jev.criteria': '命中',
  'guaranteed.jev.hits': '次',
  'guaranteed.jev.seconds': '秒',
  'guaranteed.jev.skips': '近 1 小时 Jev 跳过：{list}',
  'guaranteed.jev.skips_sep': '、',
  'guaranteed.jev.skips_none': '近 1 小时没有跳过 Jev 的失败请求',
  'guaranteed.jev.skips_hint': 'Jev 开着时，失败但没有交给 Jev 判的请求（两区合计）。每台网关每 5 分钟上报一次，最近几分钟的可能还没到；流量太少、10 分钟内没能上报的网关会丢掉那段计数。',
  'guaranteed.jev.skip.tenant_aborted': '租户挂断',
  'guaranteed.jev.skip.media_lane': '图片 / 视频车道',
  'guaranteed.jev.skip.rate_limit_or_timeout': '限流或超时（408 / 429 / 504）',
  'guaranteed.jev.skip.status_not_judged': '其它状态码（非 500 / 502 / 503）',
  'guaranteed.jev.skip.client_fault': '已知客户端错误',
  'guaranteed.jev.skip.queue_full': 'Jev 队列满',
  'guaranteed.jev_trip_min_hits': 'Jev 摘除命中次数',
  'guaranteed.jev_trip_window_seconds': 'Jev 窗口（秒）',
  'guaranteed.overlay.fallback': '终极兜底',
  'guaranteed.overlay.retries': '24h 兜底重试',
  'guaranteed.overlay.requests': '请求',
  'guaranteed.overlay.note': '备注',
  'guaranteed.overlay.title': '客户覆盖',
  'guaranteed.overlay.hint': '可覆盖终极兜底和保障配置；探针 / 链内熔断 / Jev 跟 SKU',
  'guaranteed.overlay.empty': '没有客户覆盖 · 全部客户按上面的 SKU 配置走',
  'guaranteed.section.legs': '腿状态',
  'guaranteed.section.events': '事件',
  'guaranteed.section.config': '配置',
  'guaranteed.leg.empty': '还没有已配置 SKU 的物理腿，也没有被手动摘除的腿',
  'guaranteed.leg.chip.ok': '正常',
  'guaranteed.leg.chip.shadow': '影子·判应摘',
  'guaranteed.leg.chip.tripped': '已摘除',
  'guaranteed.leg.chip.tripped_unarmed': '摘除记录（机制已关）',
  'guaranteed.leg.verdict.unarmed_short': '机制已关，不影响路由',
  'guaranteed.leg.verdict.unarmed': '该 SKU 所有机制都不在「摘除」档，这条记录不影响路由；开到「摘除」或点清除即消',
  'guaranteed.leg.next_probes_off': '探针关',
  'guaranteed.leg.next_due': '待下轮',
  'guaranteed.leg.partly_disabled': '{disabled}/{total} 目标已禁用',
  'guaranteed.leg.partly_disabled_tip': '这条物理腿的部分路由目标被手动禁用，剩余目标仍在路由里',
  'guaranteed.leg.unconfigured': '未纳入重点保障',
  'guaranteed.leg.unconfigured_tip': '这个 SKU 没有在重点保障中心配置；这里只列出它被用户手动摘除的腿，方便维护（可立即探测、试打、加回）',
  'guaranteed.leg.partly_disabled_notes': '已禁用目标的备注：{notes}',
  'guaranteed.leg.manual_probe': '最近探测 HTTP {code} · {ago}',
  'guaranteed.leg.probe_now_tip': '立即探测：按 SKU 探针配置打一轮，结果计入这条腿的探针状态（连续失败/通过轮数，可能摘除或恢复）。已手动摘除的腿只记录结果，不会自动加回。',
  'guaranteed.leg.chip.manual': '用户手动摘除',
  'guaranteed.leg.chip.immune': '金身 {minutes} 分钟',
  'guaranteed.leg.unprobed': '未探测',
  'guaranteed.leg.chip.failing': '探针失败 · 未到摘除线',
  'guaranteed.leg.verdict.failing': '探针失败',
  'guaranteed.leg.rounds_ratio': '连续 {failed}/{threshold} 轮',
  'guaranteed.leg.rounds_streak': '已连续 {failed} 轮失败',
  'guaranteed.leg.search': '搜索',
  'guaranteed.leg.search_placeholder': '搜 SKU / 上游 / 模型',
  'guaranteed.leg.refreshed': '刷新 {seconds} 秒前',
  'guaranteed.leg.col.last': '上次探测',
  'guaranteed.leg.col.backoff': '退避',
  'guaranteed.leg.minutes_ago': '{minutes} 分钟前',
  'guaranteed.leg.minutes_in': '{minutes} 分钟后',
  'guaranteed.leg.never': '—',
  'guaranteed.leg.members_tip': '这条物理腿（上游×账号×通道×模型）被 {count} 条路由目标引用（默认层 + 各路由配置层），摘除/恢复对全部同时生效',
  'guaranteed.leg.trigger.probe': '探针',
  'guaranteed.leg.trigger.chain': '链内熔断',
  'guaranteed.leg.trigger.jev': 'Jev 判官',
  'guaranteed.leg.trigger.quality': '质量不达标',
  'guaranteed.leg.trigger.silent': '静默摘腿',
  'guaranteed.leg.trigger.broken': '断流摘腿',
  'guaranteed.leg.trigger.with_evidence': '{trigger}：{evidence}',
  'guaranteed.leg.quality_detail': '近 {minutes} 分钟 {pct}% 探针失败/超慢（{bad}/{total} 发）',
  'guaranteed.leg.trigger.shadow': '{trigger}（影子）',
  'guaranteed.leg.verdict.immune': '运维清除摘除 · {ago} · {minutes} 分钟内不再被摘（5xx 熔断不受影响）',
  'guaranteed.leg.verdict.serving': '未摘除 · {detail}',
  'guaranteed.leg.verdict.not_removed': '未真正摘除',
  'guaranteed.leg.jev_expiry_only': '探针未设为摘除：只有 30 分钟到期才放回',
  'guaranteed.jev.expiry_only_hint': '探针未设为摘除时，Jev 摘除的腿不会被探针提前放回，只在 30 分钟后到期放回。',
  'guaranteed.leg.reset': '清除摘除',
  'guaranteed.leg.reset_shadow': '清除影子判定',
  'guaranteed.leg.reset_title': '清除摘除',
  'guaranteed.leg.reset_body': '清除这个 SKU 在这条物理腿上全体成员的摘除和 shadow 状态。同时结束这条物理腿上所有 SKU 的警戒 / 战争和战后冷却（探针层级按物理腿记，不分 SKU）。',
  'guaranteed.leg.reset_quality_blind': '重置后质量判定从零开始：按这个 SKU 当前配置，约 {minutes} 分钟内只按连续失败轮摘，不按质量摘（探测间隔 × 质量最少发数 ÷ 每轮发数，按常规节奏）。',
  'guaranteed.leg.reset_quality_blind_formula': '重置后质量判定从零开始：约「探测间隔 × 质量最少发数 ÷ 每轮发数」分钟内（按常规节奏）只按连续失败轮摘，不按质量摘。',
  'guaranteed.leg.immunity': '保护时长',
  'guaranteed.leg.immunity.0': '不保护',
  'guaranteed.leg.immunity.5': '5 分钟（默认）',
  'guaranteed.leg.immunity.10': '10 分钟',
  'guaranteed.leg.immunity.30': '30 分钟',
  'guaranteed.leg.immunity.60': '60 分钟',
  'guaranteed.leg.probe_now': '立即探测',
  'guaranteed.leg.probe_now_done': '已探测一轮 · HTTP {code}',
  'guaranteed.leg.probe_now_done_nocode': '已探测一轮',
  'guaranteed.leg.disable': '永久禁用目标',
  'guaranteed.leg.enable': '加回',
  'guaranteed.leg.enable_title': '加回目标',
  'guaranteed.leg.enable_body': '重新启用这条腿的全体成员，首发和重试链都会重新走它。',
  'guaranteed.leg.enable_done': '已加回',
  'guaranteed.leg.namespace_other': '其它',
  'guaranteed.leg.locate': '在租户路由中查看',
  'guaranteed.leg.locate_title': '这条腿的路由目标在哪',
  'guaranteed.leg.locate.group_live': '接流量',
  'guaranteed.leg.locate.group_dead': '不接流量 · {count} 处',
  'guaranteed.leg.locate.default_name': 'SKU 默认目标',
  'guaranteed.leg.locate.orgs': '已分配客户 {count}',
  'guaranteed.leg.locate.targets': '目标 启用 {enabled} · 停用 {disabled}',
  'guaranteed.leg.locate.deprecated': '已过时',
  'guaranteed.leg.reach.global': '全局默认路由',
  'guaranteed.leg.reach.override': '紧急覆盖',
  'guaranteed.leg.reach.org_assigned': '客户专属配置',
  'guaranteed.leg.reach.default_fallthrough': 'SKU 默认目标（全局未覆盖时兜底）',
  'guaranteed.leg.reach.unassigned_config': '未分配客户的配置',
  'guaranteed.leg.reach.shadowed_by_global_exclusive': 'SKU 默认目标（被全局默认路由遮住）',
  'guaranteed.leg.reach.unknown': '位置未知',
  'guaranteed.leg.members_where': '{total} 个目标：{parts}',
  'guaranteed.leg.members_where_unreachable': '不接流量 {count}',
  'guaranteed.leg.members_where_unreachable_dep_all': '不接流量 {count}（全部在已过时配置）',
  'guaranteed.leg.members_where_unreachable_dep': '不接流量 {count}（其中已过时配置 {deprecated}）',
  'guaranteed.leg.tag.unreachable': '不接流量',
  'guaranteed.leg.tag.unreachable_tip': '这条腿的路由目标都在请求走不到的位置（未分配客户的配置，或被全局默认路由遮住的 SKU 默认目标），探针不再定时探测它；立即探测仍可用',
  'guaranteed.leg.tag.live_deprecated': '含已过时配置',
  'guaranteed.leg.tag.live_deprecated_tip': '这条腿有路由目标在已标记过时、但仍分配给客户的配置里，照常接流量、照常探测',
  'guaranteed.legs.scope': '{skus} 个重点保障 SKU · {legs} 条接流量的物理腿（JP / CN 两区合并成一条）',
  'guaranteed.legs.tripped_at': '→ {at} 摘除',
  'guaranteed.legs.cn_not_configured': '上海网关未配置，没有清除',
  'guaranteed.legs.reset.region': '清除哪个区',
  'guaranteed.legs.reset.region_all': '两区',
  'guaranteed.legs.reset.region_cleared': '已清除',
  'guaranteed.legs.reset.region_nothing': '无摘除可清',
  'guaranteed.legs.reset.done_regions': '清除摘除：{outcome}',
  'guaranteed.legs.region_failed': '失败：{error}',
  'guaranteed.legs.probe_now_regions': '已探测：{outcome}',
  'guaranteed.legs.role.primary_plain': '首发',
  'guaranteed.legs.takeover': '{regions} 接管首发',
  'guaranteed.legs.takeover_badge': '兜底腿接管首发',
  'guaranteed.legs.takeover_tip': '某条生效路由在某区的权重 > 0 的腿全被摘除或禁用：首发改由重试链顺序最前的兜底腿接管',
  'guaranteed.legs.lamps.manual': '已手动摘除 {count} 条',
  'guaranteed.legs.sec.manual': '已手动摘除',
  'guaranteed.legs.sec.manual_hint': '在所有生效路由里的目标都被手动禁用：首发和重试链都不走，不计入接流量的腿；可立即探测、试打、加回',
  'guaranteed.legs.sec.overridden': '紧急覆盖接管，暂不生效',
  'guaranteed.legs.overridden_tag': '紧急覆盖接管',
  'guaranteed.legs.overridden_tip': '紧急覆盖接管，暂不生效（仍定时探测）：这条腿所在的路由在紧急覆盖期间不接流量，覆盖解除后恢复',
  'guaranteed.legs.override_notice': '紧急覆盖已接管全部流量（{name}）：其余路由暂不生效，下面按覆盖配置显示首发与兜底。',
  'guaranteed.legs.total.first_removed': '首发摘除',
  'guaranteed.legs.ns_first_removed': '首发摘除 {count}',
  'guaranteed.legs.first_removed_badge': '首发摘除 {count}',
  'guaranteed.legs.first_removed_tip': '首发腿（至少在一条生效路由里权重 > 0）已被摘除：这个 SKU 正用比配置更少的首发腿接流量，比兜底腿被摘更危险',
  'guaranteed.legs.lamps.first': '首发腿 {count} 条',
  'guaranteed.legs.lamps.reserve': '兜底腿 {count} 条',
  'guaranteed.legs.lamps.dead': '不接流量 {count} 条',
  'guaranteed.legs.routing.org_chip': '{name} · {count} 客户',
  'guaranteed.legs.role.title': '{routing}：权重 {weight} · {role}',
  'guaranteed.legs.ids_tip': '上游 {upstream}\n账号 {account}\nkey 桶 {bucket}',
  'guaranteed.legs.meta.next': '下次',
  'guaranteed.legs.meta.members_pre': '共用',
  'guaranteed.legs.meta.members_post': '个路由目标',
  'guaranteed.legs.kv.cadence': '探针',
  'guaranteed.legs.kv.cadence_value': '每 {interval} 分钟 · {shots} 发/轮 · 间隔 {spacing} 秒 · 超时 {timeout} 秒 · {shape}',
  'guaranteed.legs.kv.trip': '摘除',
  'guaranteed.legs.kv.trip_value': '连续 {n} 轮失败（失败后 {confirm} 秒复查）',
  'guaranteed.legs.kv.recover': '恢复',
  'guaranteed.legs.kv.recover_value': '连续 {n} 轮通过',
  'guaranteed.legs.kv.quality': '质量',
  'guaranteed.legs.kv.quality_value': '近 {window} 分钟坏率 ≥ {trip}% 且 ≥ {min_bad} 发坏摘（至少 {min} 发）· 近 {recover_window} 分钟 ≤ {recover}% 恢复 · 慢于 {slow} 毫秒算失败',
  'guaranteed.legs.stat.requests': '24h 请求',
  'guaranteed.legs.stat.entered': '进入重试链',
  'guaranteed.legs.stat.exhausted': '重试仍失败',
  'guaranteed.legs.share.orgs': '{count} 个客户',
  'guaranteed.legs.ago.hours': '{hours} 小时前',
  'guaranteed.legs.ago.days': '{days} 天前',
  'guaranteed.legs.total.red': '红',
  'guaranteed.legs.total.record': '红框',
  'guaranteed.legs.total.yellow': '黄',
  'guaranteed.legs.total.green': '绿',
  'guaranteed.legs.total.immune': '金身',
  'guaranteed.legs.total.unprobed': '未探测',
  'guaranteed.legs.total.grey': '灰',
  'guaranteed.legs.abnormal': '只看异常（有红 / 黄灯的 SKU）',
  'guaranteed.legs.section_configured': '重点保障 SKU · {count}',
  'guaranteed.legs.section_configured_filtered': '重点保障 SKU · {shown} / {count}',
  'guaranteed.legs.section_other': '未纳入重点保障 · {count} 个 SKU（只列手动摘除的腿）',
  'guaranteed.legs.none_match': '没有匹配的 SKU。关掉「只看异常」看全部。',
  'guaranteed.legs.other_none': '没有。',
  'guaranteed.legs.ns_skus': '{count} 个 SKU',
  'guaranteed.legs.ns_red': '红 {count}',
  'guaranteed.legs.ns_yellow': '黄 {count}',
  'guaranteed.legs.no_legs': '无腿',
  'guaranteed.legs.lamp_title': '{leg} — {state}',
  'guaranteed.legs.lamp_title_dead': '{leg} — 不接流量（{state}）',
  'guaranteed.legs.rail.probe': '探针',
  'guaranteed.legs.rail.chain': '熔断',
  'guaranteed.legs.rail.jev': 'Jev',
  'guaranteed.legs.rail.fallback': '终极兜底',
  'guaranteed.legs.rail.fallback_orgs': '{mode}（{count} 个客户开启）',
  'guaranteed.legs.rail.overlay_only': '仅客户覆盖',
  'guaranteed.legs.rail.fallback_overlay_on': '开启 · {count} 个客户',
  'guaranteed.legs.short.ok': '正常',
  'guaranteed.legs.short.failing': '失败',
  'guaranteed.legs.short.shadow': '影子',
  'guaranteed.legs.short.tripped': '摘除',
  'guaranteed.legs.short.record': '记录',
  'guaranteed.legs.short.immune': '金身',
  'guaranteed.legs.short.unprobed': '未探测',
  'guaranteed.legs.short.manual': '停用',
  'guaranteed.legs.mode.probe': '探针',
  'guaranteed.legs.mode.chain': '链内熔断',
  'guaranteed.legs.mode.jev': 'Jev 判官',
  'guaranteed.legs.mode.fallback': '终极兜底',
  'guaranteed.legs.mode.overlays': '客户覆盖',
  'guaranteed.legs.mode.overlays_value': '{count} 条 · 终极兜底开启 {on}',
  'guaranteed.legs.select': '选一个 SKU。',
  'guaranteed.legs.share.title': '首发流量分配',
  'guaranteed.legs.share.hint': '每条生效路由各自按权重折算；被摘除的腿在该区让出首发（斜纹）。',
  'guaranteed.legs.share.override_hint': '紧急覆盖已接管全部流量，其余路由暂不生效。',
  'guaranteed.legs.share.none': '这条路由没有权重 > 0 的腿，首发按重试链顺序',
  'guaranteed.legs.share.no_routing': '没有生效的路由：这个 SKU 的腿都在请求走不到的位置。',
  'guaranteed.legs.share.disabled': '已禁用',
  'guaranteed.legs.share.retired': '{regions} 已退役首发',
  'guaranteed.legs.routing.override': '紧急覆盖 · {name}',
  'guaranteed.legs.routing.global': '全局默认',
  'guaranteed.legs.routing.global_named': '全局默认 · {name}',
  'guaranteed.legs.routing.default': 'SKU 默认目标',
  'guaranteed.legs.routing.org': '客户配置 {name}（{count} 个客户）',
  'guaranteed.legs.routing.unknown': '路由（网关未报层级）',
  'guaranteed.legs.role.primary': '首发 {percent}%',
  'guaranteed.legs.role.fallback': '兜底',
  'guaranteed.legs.role.unknown': '权重未知',
  'guaranteed.legs.role.disabled': '（已禁用）',
  'guaranteed.legs.sec.first': '首发腿',
  'guaranteed.legs.sec.first_hint': '至少在一条生效路由里权重 > 0，按权重接首发流量',
  'guaranteed.legs.sec.reserve': '兜底腿',
  'guaranteed.legs.sec.reserve_hint': '在所有生效路由里权重都是 0，只在重试链里；有权重 > 0 的腿在线时首发不选它',
  'guaranteed.legs.sec.dead': '不接流量',
  'guaranteed.legs.sec.empty': '没有。',
  'guaranteed.legs.region.tip': '{region}：{state}',
  'guaranteed.legs.region.missing_tip': '这条腿没有 {region} 区的探针记录',
  'guaranteed.legs.ev.both': '两区',
  'guaranteed.legs.legend.title': '灯：',
  'guaranteed.legs.legend.ok': '绿 正常（未摘除）',
  'guaranteed.legs.legend.warn': '黄 影子判应摘 / 探针失败未到摘除线',
  'guaranteed.legs.legend.bad': '红 已摘除（退役首发，留链尾）',
  'guaranteed.legs.legend.record': '红框 摘除记录（机制已关，不影响路由）',
  'guaranteed.legs.legend.immune': '橙菱 金身（清除后保护期）',
  'guaranteed.legs.legend.manual': '灰 用户手动摘除',
  'guaranteed.legs.legend.unprobed': '灰框 未探测',
  'guaranteed.legs.legend.dead': '竖线后暗灰 不接流量',
  'guaranteed.legs.legend.regions': 'JP / CN 不一致时取较差的一区。',
  'guaranteed.legs.legend.members': '「共用路由目标」= 这条物理腿被多少条路由目标引用，摘除/恢复对全部同时生效。',
  'guaranteed.mode.off': '关',
  'guaranteed.mode.shadow': '影子',
  'guaranteed.mode.act': '摘除',
  'guaranteed.col.fallback': '终极兜底',
  'guaranteed.col.guard': '保障配置',
  'guaranteed.cutoff.off': '关',
  'guaranteed.cutoff.hint_label': '无响应切断说明',
  'guaranteed.cutoff.hint': '可设 10–120 秒。流式请求按首个实际内容判断（响应头、保活注释、文本块/消息的空开场不算；推理或思考块一旦开始就算内容，跨协议转换的车道也按上游原始线形判断）。否则切到下一条腿重试，不降权。非流式请求不切。链路最后一条腿不切；后续腿自身失败时租户仍会失败。上游线形上看不出推理已开始的 SKU 不要开启（如在 chat 线上隐藏推理的 o 系列：回答之前只有开场和保活）。',
  'guaranteed.guard.open': '打开保障配置',
  'guaranteed.guard.title': '{sku} · 保障配置',
  'guaranteed.guard.description': '每一行是一种坏信号，往右依次是：怎么判定 → 这次请求怎么办 → 累计多了摘不摘腿。默认全关。',
  'guaranteed.guard.division': '分工：普通的不可用（HTTP 5xx、429、连不上、超时）由路由默认熔断自动处理，这里不配。这里只配路由熔断看不出来的怪信号：上游回 200 但不吐东西、开头后失败、流中静默、断流、一阵一阵的过载。没在这里配的 SKU，一律按路由默认走。',
  'guaranteed.guard.close': '关闭',
  'guaranteed.guard.save': '保存',
  'guaranteed.guard.saving': '保存 {n} 处改动',
  'guaranteed.guard.changed': '已改 {n} 项，未保存',
  'guaranteed.guard.unchanged': '没有未保存的修改',
  'guaranteed.guard.discard_title': '放弃未保存的修改？',
  'guaranteed.guard.discard_body': '还有 {n} 项修改没保存，关闭后会丢掉。',
  'guaranteed.guard.discard_confirm': '放弃修改并关闭',
  'guaranteed.guard.discard_cancel': '继续编辑',
  'guaranteed.guard.hint_label': '保障配置说明',
  'guaranteed.guard.hint': '六种怪信号，每种都是：判定 → 当场处置 → 累计摘腿 → 按探针形态恢复。普通不可用由路由默认熔断处理。客户可以覆盖阈值、当场处置和自己的摘腿；恢复只在 SKU 上配。默认全关。',
  'guaranteed.guard.off': '关',
  'guaranteed.guard.inherit': '继承 SKU',
  'guaranteed.guard.legacy': '来自旧配置',
  'guaranteed.guard.scope': '作用域',
  'guaranteed.guard.scope_sku': 'SKU 全局',
  'guaranteed.guard.add_org': '+ 添加客户覆盖',
  'guaranteed.guard.write_gate': '暂不能保存：CN 下行复制尚未就绪，此时写入会卡住 CN 复制。',
  'guaranteed.guard.load_failed': '保障配置读失败，暂不能打开或保存。',
  'guaranteed.guard.overlay_count': '{n} 个客户覆盖',
  'guaranteed.guard.overlay_fields': '{n} 项覆盖',
  'guaranteed.guard.mode.off': '关',
  'guaranteed.guard.mode.shadow': '影子',
  'guaranteed.guard.mode.act': '开',
  'guaranteed.guard.yes': '是',
  'guaranteed.guard.no': '否',
  'guaranteed.guard.legend.off': '关：不检测',
  'guaranteed.guard.legend.shadow': '影子：只记录「本该怎么做」，不动作',
  'guaranteed.guard.legend.act': '开：真动作',
  'guaranteed.guard.legend.parallel': '各行互不依赖、并行生效，同一请求谁先命中按谁处置',
  'guaranteed.guard.col.signal': '坏信号',
  'guaranteed.guard.col.judge': '判定',
  'guaranteed.guard.col.walk': '当场处置（这次请求）',
  'guaranteed.guard.col.trip': '累计摘腿（之后的请求）',
  'guaranteed.guard.unit.seconds': '秒',
  'guaranteed.guard.unit.times': '次',
  'guaranteed.guard.unit.percent': '%',
  'guaranteed.guard.unit.shots': '枪',
  'guaranteed.guard.field.window': '滑窗',
  'guaranteed.guard.field.min_hits': '最少命中',
  'guaranteed.guard.field.min_ratio': '最少占比',
  'guaranteed.guard.field.max_hold': '最长摘除',
  'guaranteed.guard.field.retry_after_cap': '回给客户端的 retry-after 上限',
  'guaranteed.guard.field.retry_after_cap_hint': '留空 = 不设上限，按上游给的值回。',
  'guaranteed.guard.field.retry_after_reasoning': '租户只收到推理时，换腿续写（B）',
  'guaranteed.guard.field.chat_lane': 'chat / completions 车道也换腿',
  'guaranteed.guard.field.chat_lane_note': '仅在换腿为「执行」时生效，影子不覆盖 chat 车道。首个内容之前上游报服务端类错误（server_error / api_error / internal / overloaded）才换，末腿不换；被换掉的腿不计费、计入熔断和链路证据、不进摘腿窗口。',
  'guaranteed.guard.walk_none_title': '无法当场处置',
  'guaranteed.guard.walk_none_body': '内容已经发给租户，换不了腿。',
  'guaranteed.guard.trip_off_numbers': '关着的摘腿会记住这些数字，打开时沿用。',
  'guaranteed.guard.signal.overload.title': '上游过载',
  'guaranteed.guard.signal.overload.short': '过载',
  'guaranteed.guard.signal.overload.desc': '上游明确说「忙」。很常见，通常一阵一阵来，和「不吐东西」是两回事，单独处理。',
  'guaranteed.guard.signal.overload.judge': 'server_is_overloaded、overloaded_error、HTTP 529 / 503、"at capacity"。HTTP 错误、JSON 错误、流里的 error / response.failed 都算；只要还没给租户发出实际内容就能判（开头事件最多扣住 8 MiB 检查）。',
  'guaranteed.guard.signal.overload.walk_title': '换腿',
  'guaranteed.guard.signal.overload.walk_note': '换腿时不算这条腿的故障：不熔断、不扣 smart 分、不进其他信号的计数，是否摘腿只看右边的「过载摘腿」。最后一条腿也过载：回 MODEL_BUSY（可重试）。',
  'guaranteed.guard.signal.overload.trip_title': '过载摘腿',
  'guaranteed.guard.signal.overload.trip_note': '适合「一轮一轮」的过载潮：例如 5 分钟内 ≥5 次且占 ≥30% 就摘掉，最长 20 分钟后自动放回，探针连续干净可以提前放回。建议先开「影子」看几天。',
  'guaranteed.guard.signal.first_content_stream.title': '流开头不吐东西',
  'guaranteed.guard.signal.first_content_stream.short': '流式',
  'guaranteed.guard.signal.first_content_stream.tag': '流式',
  'guaranteed.guard.signal.first_content_stream.desc': '连接上了，但迟迟没有第一个实际内容（keep-alive、开头事件不算内容）。',
  'guaranteed.guard.signal.first_content_stream.threshold': '首个实际内容超过',
  'guaranteed.guard.signal.first_content_stream.walk_title': '切断并换腿',
  'guaranteed.guard.signal.first_content_stream.walk_note': '最后一条腿不切，继续等。',
  'guaranteed.guard.signal.first_content_stream.trip_title': '摘腿',
  'guaranteed.guard.signal.first_content_nonstream.title': '迟迟不出答案',
  'guaranteed.guard.signal.first_content_nonstream.short': '非流式',
  'guaranteed.guard.signal.first_content_nonstream.tag': '非流式',
  'guaranteed.guard.signal.first_content_nonstream.desc': '上游流式、网关聚合成整答的车道（codex / 强制流式）。纯 JSON 上游等于整答时长。',
  'guaranteed.guard.signal.first_content_nonstream.threshold': '首个实际内容超过',
  'guaranteed.guard.signal.first_content_nonstream.walk_title': '切断并换腿',
  'guaranteed.guard.signal.first_content_nonstream.walk_note': '最后一条腿不切。',
  'guaranteed.guard.signal.first_content_nonstream.trip_title': '摘腿',
  'guaranteed.guard.signal.prelude_fail.title': '开头之后就失败 / 空流',
  'guaranteed.guard.signal.prelude_fail.short': '开头失败',
  'guaranteed.guard.signal.prelude_fail.tag': '仅 Responses',
  'guaranteed.guard.signal.prelude_fail.desc': '只发了 response.created 之类的开头事件，接着就失败、直接结束，或零用量 incomplete。过载另有专门一行。',
  'guaranteed.guard.signal.prelude_fail.judge': '见到即判。开头事件先扣住不发给租户，最长等 60 秒；开头事件最多扣住 8 MiB 检查。',
  'guaranteed.guard.signal.prelude_fail.walk_title': '换腿',
  'guaranteed.guard.signal.prelude_fail.walk_note': '换腿对租户无感：租户只会看到新腿的开头事件。',
  'guaranteed.guard.signal.prelude_fail.trip_title': '摘腿',
  'guaranteed.guard.signal.stall.title': '流到一半静默',
  'guaranteed.guard.signal.stall.short': '静默',
  'guaranteed.guard.signal.stall.desc': '已经有内容了，之后长时间不再吐。keep-alive / 空帧不重置计时。',
  'guaranteed.guard.signal.stall.threshold': '静默超过',
  'guaranteed.guard.signal.stall.judge': '低于约 32 秒会误切 luna 正常的长间隔。',
  'guaranteed.guard.signal.stall.walk_title': '切断',
  'guaranteed.guard.signal.stall.walk_note': '租户还没收到东西 → 换腿重来；收到了正文 → 只能断开。仅 chat 格式支持续写。',
  'guaranteed.guard.signal.stall.trip_title': '静默摘腿',
  'guaranteed.guard.signal.broken.title': '流断了',
  'guaranteed.guard.signal.broken.short': '断流',
  'guaranteed.guard.signal.broken.desc': '已经开始给租户发了，然后没有正常结束就关了，或以服务端错误结束。租户自己断开、静默切断不算。',
  'guaranteed.guard.signal.broken.judge': '见到即判。',
  'guaranteed.guard.signal.broken.trip_title': '断流摘腿',
  'guaranteed.guard.chip.walk': '{signal}换腿',
  'guaranteed.guard.chip.walk_seconds': '{signal} {seconds}s',
  'guaranteed.guard.chip.trip': '{signal}摘腿',
  'guaranteed.guard.chip.shadow': '{label}·影子',
  'guaranteed.guard.trip_without_walk': '当场处置为「关」时这个信号没有命中来源，摘腿永远不会触发。把当场处置设为「影子」或「开」（影子只计数、不换腿）。',
  'guaranteed.guard.trip_without_walk_stall': '只有真切断（当场处置「开」）才算命中；当场处置为「关」或「影子」时摘腿永远不会触发。',
  'guaranteed.guard.stats.window': '近 24 小时',
  'guaranteed.guard.stats.trips': '摘腿',
  'guaranteed.guard.stats.shadow_trips': '影子摘腿',
  'guaranteed.guard.stats.releases': '放回',
  'guaranteed.guard.stats.capped': '≥{n}',
  'guaranteed.guard.stats.unknown': '不可判（满页）',
  'guaranteed.guard.stats.failed': '统计读取失败',
  'guaranteed.guard.stats.hits_gap': '命中与换腿次数暂无统计接口，这里只显示摘腿与放回。',
  'guaranteed.guard.org.title': '客户覆盖 · {org}',
  'guaranteed.guard.org.desc': '只影响这个客户。阈值和当场处置可以覆盖；摘腿可以选「继承」或「独立」。证据（命中）全局共享，决策只影响这个客户。SKU 健康探针摘腿、路由默认熔断不受这里影响。',
  'guaranteed.guard.org.col.disposal': '当场处置覆盖',
  'guaranteed.guard.org.col.global': 'SKU 全局摘腿',
  'guaranteed.guard.org.col.own': '自己的摘腿',
  'guaranteed.guard.org.disposal.inherit': '沿用 SKU',
  'guaranteed.guard.org.disposal.custom': '覆盖',
  'guaranteed.guard.org.sku_value': 'SKU：{value}',
  'guaranteed.guard.org.global.inherit': '继承',
  'guaranteed.guard.org.global.own': '独立',
  'guaranteed.guard.org.borrow': '借用全局命中',
  'guaranteed.guard.org.own_off_warn': '该客户不会避开全局已摘的腿。',
  'guaranteed.guard.org.profile_missing': 'SKU 上该信号的恢复形态未启用：该客户摘下的腿只能到期放回。',
  'guaranteed.guard.org.censor_note': '这个客户按自己阈值切掉的样本不计入 SKU 全局摘腿（被更严阈值提前切掉的样本不算全局证据）。',
  'guaranteed.guard.recover.title': '摘腿之后怎么恢复',
  'guaranteed.guard.recover.profiles_title': '探针形态',
  'guaranteed.guard.recover.profiles_desc': '被摘下的腿不接真实流量，定时打合成探针。不同故障要用不同形态才判得出健康。车道（codex-cli / claude-code-cli / chat-stream）按腿自动决定。',
  'guaranteed.guard.recover.col.profile': '形态',
  'guaranteed.guard.recover.col.success': '算「成功」的条件',
  'guaranteed.guard.recover.col.interval': '波次间隔',
  'guaranteed.guard.recover.col.shots': '每波枪数',
  'guaranteed.guard.recover.col.timeout': '单发超时',
  'guaranteed.guard.recover.col.enabled': '启用',
  'guaranteed.guard.recover.profile.short': '短探针',
  'guaranteed.guard.recover.profile.long_stream': '长流探针',
  'guaranteed.guard.recover.short_criteria': '首个实际内容不超过该故障那层的阈值，且正常结束（completed / [DONE]），无 error、无过载。',
  'guaranteed.guard.recover.long_min_stream': '持续吐字至少',
  'guaranteed.guard.recover.long_max_gap': '中途静默不超过',
  'guaranteed.guard.recover.long_and_short': '同时满足短探针的全部条件。',
  'guaranteed.guard.recover.lock_title': '每种故障锁定一种形态',
  'guaranteed.guard.recover.lock_desc': '同一条腿上一次探针成功，给所有能被这次成功证明的故障各记 1 次（长流成功也证明短探针的条件），各故障攒够自己的次数各自放回；一次失败只清零它能判的那些故障。',
  'guaranteed.guard.recover.col.fault': '故障（摘腿原因）',
  'guaranteed.guard.recover.col.lock': '恢复用的探针形态',
  'guaranteed.guard.recover.col.clean': '成功几次放回',
  'guaranteed.guard.recover.col.hold': '最长摘除（到期兜底放回）',
  'guaranteed.guard.recover.recommend_long': '推荐改长流',
  'guaranteed.guard.recover.profile_missing': '该形态未启用：摘下的腿只能到期放回。',
  'guaranteed.guard.err.integer': '须为整数',
  'guaranteed.guard.err.number': '须为数字',
  'guaranteed.guard.err.range': '须为 {min} 到 {max}',
  'guaranteed.guard.err.policy': '配置不成立：{message}',
  'guaranteed.events.chip.guard_trip': '保障 · 摘除',
  'guaranteed.events.chip.guard_recover': '保障 · 解除',
  'guaranteed.events.chip.shadow_guard': '保障 · 影子摘除',
  'guaranteed.events.chip.shadow_guard_recover': '保障 · 影子解除',
  'guaranteed.leg.trigger.overload': '过载摘腿',
  'guaranteed.leg.trigger.first_content_stream': '流开头不吐东西摘腿',
  'guaranteed.leg.trigger.first_content_nonstream': '迟迟不出答案摘腿',
  'guaranteed.leg.trigger.prelude_fail': '开头后失败摘腿',
  'guaranteed.leg.trigger.org_scope': '仅客户 {org}',
  'guaranteed.fallback.off': '关',
  'guaranteed.fallback.shadow': '影子',
  'guaranteed.fallback.act': '开启',
  'guaranteed.write.field_value': '{field} → {value}',
  'guaranteed.write.field_join': '，',
  'guaranteed.write.field.probe_mode': '探针模式',
  'guaranteed.write.field.probe_shape': '探针形状',
  'guaranteed.write.field.probe_interval': '探针间隔（分钟）',
  'guaranteed.write.field.probe_shots': '每轮发数',
  'guaranteed.write.field.probe_fail_rounds': '摘除所需连续失败轮数',
  'guaranteed.write.field.probe_pass_rounds': '恢复所需连续通过轮数',
  'guaranteed.write.field.probe_spacing': '每轮发间隔（秒）',
  'guaranteed.write.field.probe_timeout': '单发超时（秒）',
  'guaranteed.write.field.probe_confirm': '失败复查延迟（秒）',
  'guaranteed.write.field.probe_slow': '慢线（毫秒）',
  'guaranteed.write.field.probe_quality_window': '质量窗口（分钟）',
  'guaranteed.write.field.probe_quality_min': '质量最少发数',
  'guaranteed.write.field.probe_quality_trip': '质量摘除坏率（%）',
  'guaranteed.write.field.probe_quality_recover': '质量恢复坏率（%）',
  'guaranteed.write.field.probe_quality_min_bad': '质量最少坏发数',
  'guaranteed.write.field.probe_quality_recover_window': '质量恢复判定窗口（分钟）',
  'guaranteed.write.field.probe_alert_failures': '开战阈值（60 秒错数）',
  'guaranteed.write.field.probe_alert_fail_pct': '开战错率（%）',
  'guaranteed.write.field.probe_alert_first_byte_late': '首字节迟到（毫秒）',
  'guaranteed.write.field.probe_alert_interval': '警戒探测间隔（秒）',
  'guaranteed.write.field.chain': '链内熔断',
  'guaranteed.write.field.fallback': '终极兜底',
  'guaranteed.write.field.jev': 'Jev 判官',
  'guaranteed.write.field.jev_min_hits': 'Jev 命中次数',
  'guaranteed.write.field.jev_window': 'Jev 窗口（秒）',
  'guaranteed.write.field.no_response_cutoff': '无响应切断',
  'guaranteed.write.field.note': '备注',
  'guaranteed.write.saving': '正在写入：{what}…',
  'guaranteed.write.saving_short': '正在写入…',
  'guaranteed.write.creating': '正在新增…',
  'guaranteed.write.deleting': '删除',
  'guaranteed.write.saved': '已保存',
  'guaranteed.write.deleted': '已删除',
  'guaranteed.write.failed': '写入失败：{message}',
  'guaranteed.write.busy': '这一行正在写入，请等它完成',
  'guaranteed.write.jp.immediate': '东京：已生效（路由缓存已即时换代；探针 / 链内熔断 / Jev 的开关与判据各进程 30 秒内跟上）',
  'guaranteed.write.jp.degraded': '东京：缓存换代未完成，约 {seconds} 秒内生效',
  'guaranteed.write.jp.degraded_done': '东京：已生效（缓存已按过期时间更新；探针 / 链内熔断 / Jev 的开关与判据各进程 30 秒内跟上）',
  'guaranteed.write.jp.unknown': '东京：已写入（网关未回报生效时间）',
  'guaranteed.write.cn.waiting': '上海：等待下行复制…',
  'guaranteed.write.cn.unreachable': '上海：暂时读不到上海网关，重试中…',
  'guaranteed.write.cn.received': '上海：已收到，约 {seconds} 秒内生效（上海缓存按过期时间更新，复制不会让它立即失效）',
  'guaranteed.write.cn.removed': '上海：已删除，约 {seconds} 秒内生效（上海缓存按过期时间更新，复制不会让它立即失效）',
  'guaranteed.write.cn.timeout_unreachable': '上海：60 秒未确认（读不到上海网关）',
  'guaranteed.write.cn.timeout_lagging': '上海：60 秒未确认（复制可能滞后）',
  'guaranteed.write.cn.not_configured': '上海：本环境没有上海区域',
  'guaranteed.shadow_note': '影子不拨号，不能证明重试能救回。',
  'guaranteed.events.window': '最近 10 天 · {count} 条',
  'guaranteed.events.empty': '暂无事件',
  'guaranteed.events.sku': 'SKU',
  'guaranteed.events.upstream': '上游',
  'guaranteed.events.kind': '类型',
  'guaranteed.events.all_sku': '全部 SKU',
  'guaranteed.events.all_upstream': '全部上游',
  'guaranteed.events.fault_kind': '故障种类',
  'guaranteed.events.all_fault_kind': '全部故障种类',
  'guaranteed.events.all_kind': '全部类型',
  'guaranteed.events.kind.target_probe_trip': '摘除',
  'guaranteed.events.kind.target_probe_recover': '探针 · 恢复',
  'guaranteed.events.kind.chain_trip_suppressed': '链内被压制',
  'guaranteed.events.kind.shadow_trip': '影子 · 判应摘',
  'guaranteed.events.kind.shadow_recover': '影子 · 恢复',
  'guaranteed.events.kind.guaranteed_shadow_enter': '影子 · 本会重试',
  'guaranteed.events.kind.trip_skipped_immune': '金身跳过',
  'guaranteed.events.kind.target_probe_skip': '跳过',
  'guaranteed.events.kind.no_response_cutoff': '无响应切断（换下一腿，不降权）',
  'guaranteed.events.kind.first_content_would_cut': '首内容会被切断（影子，未切）',
  'guaranteed.events.kind.stall_cutoff': '流中静默切断（按已收到内容处置，不降权）',
  'guaranteed.events.kind.jev_verdict': 'Jev 判决',
  'guaranteed.events.kind.judge_error': 'Jev 判官出错',
  'guaranteed.events.chip.probe_recover': '探针 · 恢复',
  'guaranteed.events.chip.shadow_recover': '影子 · 恢复',
  'guaranteed.events.chip.ops_reset': '运维重置',
  'guaranteed.events.chip.probe_trip': '探针 · 摘除',
  'guaranteed.events.chip.chain_trip': '链内熔断 · 摘除',
  'guaranteed.events.chip.jev_trip': 'Jev · 摘除',
  'guaranteed.events.chip.quality_trip': '质量 · 摘除',
  'guaranteed.events.chip.silent_trip': '静默 · 摘除',
  'guaranteed.events.chip.silent_recover': '静默 · 解除',
  'guaranteed.events.chip.broken_trip': '断流 · 摘除',
  'guaranteed.events.chip.broken_recover': '断流 · 解除',
  'guaranteed.events.chip.shadow_broken': '断流 · 影子摘除',
  'guaranteed.events.chip.shadow_broken_recover': '断流 · 影子解除',
  'guaranteed.events.chip.shadow_silent': '影子 · 静默判应摘',
  'guaranteed.events.chip.shadow_silent_recover': '影子 · 静默解除',
  'guaranteed.events.chip.jev_verdict_trip': 'Jev 判决 · 摘除',
  'guaranteed.events.chip.jev_verdict_would': 'Jev 判决 · 影子摘除',
  'guaranteed.events.chip.jev_verdict_record': 'Jev 判决 · 记录',
  'guaranteed.events.chip.shadow_probe': '影子 · 探针判应摘',
  'guaranteed.events.chip.shadow_chain': '影子 · 链内判应摘',
  'guaranteed.events.chip.shadow_jev': '影子 · Jev 判应摘',
  'guaranteed.events.chip.shadow_quality': '影子 · 质量判应摘',
  'guaranteed.events.chip.shadow_enter': '影子 · 本会重试',
  'guaranteed.events.chip.immune': '金身跳过',
  'guaranteed.events.chip.recover_wave': '恢复探测波',
  'guaranteed.events.chip.no_response_cutoff': '无响应切断（换下一腿，不降权）',
  'guaranteed.events.chip.first_content_would_cut': '首内容会被切断（影子）',
  'guaranteed.events.chip.stall_cutoff': '流中静默切断（按已收到内容处置，不降权）',
  'guaranteed.events.chip.stall_cutoff_shadow': '流中静默切断 · 影子（会切未切）',
  'guaranteed.events.chip.chain_suppressed': '链内被压制',
  'guaranteed.events.chip.judge_error': 'Jev 判官出错',
  'guaranteed.events.col.time': '时间',
  'guaranteed.events.col.kind': '类型',
  'guaranteed.events.col.sku': 'SKU',
  'guaranteed.events.col.upstream': '上游 / 上游模型',
  'guaranteed.events.col.customer': '客户',
  'guaranteed.events.col.details': '详情',
  'guaranteed.events.col.region': '区域',
  'guaranteed.events.reason.probe_disabled': '探针关，链内摘除被压制',
  'guaranteed.events.reason.immune': '金身期内',
  'guaranteed.events.verdict.upstream_server': '判为上游故障',
  'guaranteed.events.verdict.client': '判为客户端问题',
  'guaranteed.events.verdict.unknown': '判不出',
  'guaranteed.events.detail.fail_ok': '失败 {fail} / 通过 {ok}',
  'guaranteed.events.detail.hits': '命中 {hits}',
  'guaranteed.events.detail.orgs': '{orgs} 个客户',
  'guaranteed.events.detail.rounds': '{rounds} 轮',
  'guaranteed.events.detail.source.cli_probe': '合成探针波次',
  'guaranteed.events.detail.source.expiry': '到期解除',
  'guaranteed.events.detail.source.mode_off': '模式关闭解除',
  'guaranteed.events.detail.handling.a': 'A 透明换腿',
  'guaranteed.events.detail.handling.b': 'B 续腿',
  'guaranteed.events.detail.handling.c': 'C 终止帧',
  'guaranteed.events.detail.handling.would_cut': '影子 · 会切未切',
  'guaranteed.events.detail.cutoff_ms': '阈值 {seconds} s',
  'guaranteed.events.detail.max_silence': '最大静默 {seconds} s',
  'guaranteed.events.detail.probe_kind': '{kind}',
  'guaranteed.events.detail.wave': '第 {n} 波',
  'guaranteed.events.detail.shots': '{n} 枪',
  'guaranteed.events.detail.clean': '干净',
  'guaranteed.events.detail.not_clean': '未干净',
  'guaranteed.org_direct': '将直接使用这个 org id',
  'guaranteed.sku_group_other': '其它',
  'tenant_routing.probe.dot_never': '从未探测',
  'tenant_routing.leg.chip.tripped_probe': '已摘除·探针',
  'tenant_routing.leg.chip.tripped_quality': '已摘除·质量',
  'tenant_routing.leg.chip.tripped_chain': '已摘除·链内',
  'tenant_routing.leg.chip.tripped_jev': '已摘除·Jev',
  'tenant_routing.leg.chip.tripped_silent': '已摘除·静默',
  'tenant_routing.leg.chip.tripped_broken': '已摘除·断流',
  'tenant_routing.leg.chip.tripped_guard': '已摘除·保障',
  'tenant_routing.leg.tip.ok': '正常 — 未摘除，是否首发由路由权重决定。',
  'tenant_routing.leg.tip.failing': '探针失败，未到摘除线 — 路由不变，再失败到阈值才会摘除。',
  'tenant_routing.leg.tip.shadow': '影子判定：本应摘除，但影子档只记录不动路由。',
  'tenant_routing.leg.tip.tripped': '已摘除 — 退役首发，仍在重试链尾，恢复条件满足后自动回来。',
  'tenant_routing.leg.tip.tripped_unarmed': '有摘除记录，但该 SKU 的机制都不在摘除档，不影响路由。',
  'tenant_routing.leg.tip.immune': '金身保护中，还剩 {minutes} 分钟，期间不会被摘除。',
  'tenant_routing.leg.tip.manual': '目标已手动停用（首发与重试链都不走）',
  'tenant_routing.leg.tip.manual_also_tripped': '另有摘除记录：{evidence}',
  'tenant_routing.leg.tip.manual_also_shadow': '另有影子判定：{evidence}',
  'tenant_routing.leg.tip.unprobed': '尚未探测，路由不受影响。',
  'tenant_routing.leg.other_region.tripped': '{region}：已摘除（{trigger}）',
  'tenant_routing.leg.other_region.tripped_unarmed': '{region}：摘除记录，机制已关（{trigger}）',
  'tenant_routing.leg.other_region.shadow': '{region}：影子判应摘（{trigger}）',
  'tenant_routing.leg.other_region.last_probe': '最近探测 {ago}',
  'tenant_routing.leg.other_region.judged': '判定于 {ago}',
  'tenant_routing.leg.other_region.with_when': '{state}· {when}',
  'tenant_routing.breaker.trip_duration.permanent': '永久（禁用目标）',
  'tenant_routing.breaker.permanent_title': '禁用目标',
  'tenant_routing.breaker.permanent_body': '目标会被禁用，首发和重试链都不再走，直到加回。这不是限时熔断。',
  'tenant_routing.breaker.permanent_confirm': '禁用目标',
} as const;
